/**
 * Side-effectful actions: the ONLY layer that talks to the network. Each
 * action fetches, then applies a PURE store transition — components never
 * fetch and the store never fetches (testable seams on both sides).
 * Friendly copy lives here (SEC-5: raw status codes never reach the screen).
 */
import { ApiError, deleteJson, getBlob, getJson, getOr, postJson } from './api.js';
// ⛔ THE SHARED FORMATTER, NEVER A LOCAL ONE. AccountInvoices.tsx's header
// records the measured cost of a divergent copy: PortalRequests.tsx carries one
// that forces two decimals and drops en-IE grouping, so the same amount renders
// differently two tabs apart on one screen. The mark-paid mismatch sentence
// quotes a figure Helen reads back over the phone, so it uses the same one.
import { civilDateLabel, civilDayAtTime, euro } from './format.js';
// ⛔ The state vocabulary is IMPORTED from the shared component, never restated.
// `STAGE_STATE_TEXT` is the map the component itself indexes, so `readLifecycle`
// below accepts exactly the states that can render and no others — a second
// hand-kept list here would be the drift that lets an unstyled state through.
import { STAGE_STATE_TEXT, type StageState } from '@ecmb/ui';
import { DUBLIN_AIRPORT_LABEL, GAZETTEER, routingKeyOf } from '../../shared/gazetteer.js';
import { isDeterministic, matchTown } from '../../shared/town-match.js';
import {
  isSendErrorCode,
  REFUND_OUTCOME_UNKNOWN_COPY,
  SMS_UNREACHABLE_COUNTRY_COPY,
  SMS_UNREADABLE_NUMBER_COPY,
  resolveRecipientEmail,
  type Channel,
  type SendErrorCode,
} from '../../shared/send-schema.js';
// board 2106 M5 — the balance ask's wire vocabulary. The GUARD, not a local
// membership test: `isBalanceAskErrorCode` is the boundary reader the schema
// module exports for this one call site, and a second list here could drift from
// the one the route answers with.
import {
  isBalanceAskErrorCode,
  type BalanceAskChannel,
  type BalanceAskErrorCode,
  type BalanceAskState,
  type BalanceTextFailure,
} from '../../shared/balance-ask-schema.js';
import { resolveJourneyEnds, type StopRole } from '../../shared/journey-ends.js';
// TYPE only: `bound` itself is server-side and must never reach the bundle.
import type { BoundedList } from '../../shared/bounded-list.js';
import {
  isLinkedPortalOutcome,
  isLinkedRescheduleRefusal,
  isRescheduleRefusal,
  type LinkedPortalOutcome,
  type LinkedRescheduleRefusal,
  type RescheduleReason,
  type RescheduleRefusal,
} from '../../shared/reschedule-reasons.js';
// ⭐ Board 2788 M6 (DD-17) - the Dublin day.
import { dublinCivilDate } from '../../shared/dublin-date.js';
// ⭐ board 2871 - the server's own return-leg read and rule, run before the lazy create.
import { isReturnDay, readReturnLeg, returnLegCauseOf, returnLegRefusal, type ReturnLegCause } from '../../shared/return-leg.js';
// ⭐ Board 2814 M2 - the duplicate guard's one sentence, shared with the crossing route.
import { maybeDuplicateSentence } from '../../shared/crossing-duplicate.js';
// ⭐ Board 2814 M4 (DD-11 rows 7, 8, 14) - the settle reasons.
import { type SettleReason } from '../../shared/portal-request-settle.js';
/**
 * ⭐⭐ Board 2812 M1 - 2814's `needsHelenNow` AND ITS PURE CLOSURE NOW LIVE IN
 * `shared/needs-helen-now.ts`, so the server counts a home's requests by the same
 * rule. Imported for this file's own callers and RE-EXPORTED under the same names,
 * so every screen and spec that imports them from here is untouched.
 */
import {
  bookingRequestMismatch,
  canEndRunHere,
  countNeedsHelenNow,
  CROSSINGS_UNKNOWN_LINE,
  dublinWallClockOf,
  needsHelenNow,
  NO_ANSWER_PAST_LINE,
  PAST_LINE,
  requestLeadDayTime,
  settledLine,
  type HelenPlace,
  type HelenPrimary,
  type HelenRow,
} from '../../shared/needs-helen-now.js';
export {
  bookingRequestMismatch,
  canEndRunHere,
  countNeedsHelenNow,
  CROSSINGS_UNKNOWN_LINE,
  dublinWallClockOf,
  needsHelenNow,
  NO_ANSWER_PAST_LINE,
  PAST_LINE,
  requestLeadDayTime,
  settledLine,
  type HelenPlace,
  type HelenPrimary,
  type HelenRow,
};
// T608 M6b — the ONE token->recorded-designation writer, shared with buildStops
// (core/normalise.ts) so intake and re-quote record the same string AND refuse
// to record one in the same two unsafe cases.
// T2090 - the airport vocabulary the operator-path direction writer
// (`airportEndPatch`, below) reads: the two enums, the token->designation map's
// reader and its inverse. Never a hand-spelled 'T1'/'T2' here; service-schema
// forbids a second definition of the designation.
import {
  AIRPORT_TERMINALS,
  isTerminalDesignation,
  terminalAddressLine,
  terminalDesignation,
  type AirportDirection,
  type AirportTerminal,
} from '../../shared/service-schema.js';
import { composeRouteLabel, containsHubBoundary, formatRouteLabel, splitRouteLabel } from '../../shared/route-label.js';
// PREDICATE ONLY (T608 M6a, gate fix) — the golf restore below re-derives the
// rate key from the two stop ends and compares it to the persisted one. Never a
// key WRITER: slugify is lossy, so a recomposed key could name another rate.
// Client-bundle-safe: already imported by screens/Admin.tsx and admin/RateList.tsx,
// and pricing-flatten pulls in only ../engine/index.js plus a type.
import { slugify } from '../../shared/pricing-flatten.js';
// Board 2089 M3: the picker's own predicate, so the kiosk's read of a stored
// `extra.phoneCountry` accepts exactly what the parser accepts.
import { isPhoneCountry } from '../../shared/phone.js';
// ⛔ THE ENGINE'S OWN BAND VOCABULARY, imported rather than re-spelled. The
// house shape (screens/admin/PortalRequests.tsx already imports BANDS from
// here) and the only way the over-band token can live once. `check-copy.sh`
// bans the four PRICED literals under this region, and since T1871 M6 its
// `scan_over_band` arm bans the over-band token across all of src.
import { bandForCount, OVER_BAND, toCanonicalBand } from '../../engine/bands.js';
// T730 compose: `instanceId` goes (T651 M6 deleted its last caller with the
// legacy capture store); `pickupEndDisplay` stays (T660 M6 added the only
// caller, at the team-alert pickup line below).
import { bands, loadRegistry, serviceById, type Registry } from '../store/registry.js';
import {
  dropoffEndPlace, golfCourseEnds, golfStatedEnds, pickupEndDisplay, pickupEndPlace, quotePending,
} from '../store/selectors.js';
import type { Store } from '../store/store.js';
import {
  setInboxCount,
  setInboxCountError,
  setRateChangesCount,
  setRateChangesError,
  setPortalCount,
  setPortalCountError,
  setBookingError,
  clearBookingError,
  beginQuoteRequest,
  applyQuoteResult,
  patchJob,
  sourceFromInquiry,
  sourceFromBooking,
  furthestReadyScreen,
  navigate,
  login as loginTransition,
  logout as logoutTransition,
  type JobState,
  type OperatorStop,
  type QuoteView,
  type InquiryPrefill,
  type QuoteOptions,
} from '../store/state.js';

export type LoginResult =
  | { ok: true; registry: Registry }
  | { ok: false; message: string };

/**
 * POST /api/login → cookie session → identity echo → registry fetch → store login.
 *
 * T642 M6: the body is `{ username, password }`. It used to be
 * `{ passcode, user }` — one shared secret plus a name the CLIENT chose, which
 * the server believed and stamped on everything that session did. The server
 * removed that contract at M4 and answers the old shape with a deliberate 400,
 * so this is not a compatible rename: both fields are gone.
 *
 * ⛔ THE OPERATOR NAME IS READ BACK FROM THE SERVER, never taken from the form.
 * `GET /api/me` returns the session's `display_name`, which is the exact string
 * the server writes into every `actor` column. Seeding the store from what was
 * typed would put the USERNAME on the rail while the audit trail says something
 * else — two names for one person on two surfaces (server/app.ts handleMe).
 *
 * Generic failure copy — never the server body (SEC-5).
 *
 * ⛔ A 429 IS NOT A CREDENTIAL ANSWER, AND MUST NOT BE RENDERED AS ONE. The
 * server has two of them on this route and NEITHER looked at the password: the
 * throttle refuses an over-cap origin before the credential is read
 * (server/app.ts authThrottled), and M5's load-shed refuses when the hashing
 * queue is full, before any derivation (KdfOverloaded). M5 deliberately gave the
 * shed its own sentence and its own `retry-after` because "the tool is saturated"
 * is a DIFFERENT fact from "you have guessed too often" — and its session log
 * records telling the operator something false about why sign-in failed as a
 * defect it had to fix. A blanket catch here undid that at the last hop: Helen
 * would be told her password is wrong while the service is merely busy, which is
 * the one message she cannot act on (there is nothing to correct, and re-typing a
 * correct password is what digs a throttle deeper).
 *
 * ONE ARM FOR BOTH 429s, and the copy is true of both: the client cannot tell
 * them apart without reading the server body, which SEC-5 forbids, and
 * `retry-after` is a header ApiError does not carry. "Your password was not
 * checked" is exactly what both statuses mean.
 */
export async function doLogin(store: Store, username: string, password: string): Promise<LoginResult> {
  try {
    await postJson('/api/login', { username, password });
  } catch (e) {
    if (e instanceof ApiError && e.status === 429) {
      return {
        ok: false,
        message: 'Sign-in is busy right now, so your password was not checked. Wait a minute and try again.',
      };
    }
    return { ok: false, message: "That username or password didn't work. Check them and try again." };
  }
  let me: { display_name: string };
  try {
    me = await getJson<{ display_name: string }>('/api/me');
  } catch {
    // Signed in, but we cannot say WHO. Proceeding would render a blank rail and
    // attribute nothing on screen, so it stops here with honest retry copy —
    // the same posture as the registry failure below.
    return { ok: false, message: 'Signed in, but loading your account failed. Try again in a moment.' };
  }
  let registry: Registry;
  try {
    registry = await loadRegistry();
  } catch {
    // Logged in but the registry failed — the app cannot proceed on a
    // partial registry (every screen reads it). Honest retry copy.
    return { ok: false, message: "Signed in, but loading the price data failed. Try again in a moment." };
  }
  store.update((s) => loginTransition(s, me.display_name));
  // The inbox badge derives via the App's home-navigation effect (§7.5 —
  // login lands on home). Rate-changes has no nav hook yet; derive it here.
  void refreshRateChangesCount(store);
  return { ok: true, registry };
}

export async function doLogout(store: Store): Promise<void> {
  try {
    await postJson('/api/logout', {});
  } catch {
    // Cookie clear failed server-side; still reset the client (PII boundary).
  }
  store.update(logoutTransition);
}

// ---------------------------------------------------------------------------
// Operator administration (T642 M6, R7) — the "Alex can add or remove a person
// without a developer" path. Every route below is behind the /api/* session
// gate; the privilege model is flat and every act is audited server-side.
// ---------------------------------------------------------------------------

export type AdminOperator = { username: string; displayName: string; active: boolean };
export type AdminOperatorEvent = {
  actor: string;
  subject: string;
  kind: string;
  detail: string | null;
  at: string;
};
export type AdminOperators = { operators: AdminOperator[]; events: AdminOperatorEvent[] };

/** A generated password the operator must be shown ONCE. Never persisted: there
 *  is no recovery read (the column holds a hash), so losing it means a reset. */
export type NewCredential = { username: string; password: string };
export type AdminResult<T> = { ok: true; value: T } | { ok: false; message: string };

/**
 * Friendly copy from the STATUS, never the server body (SEC-5). The route
 * sentences are deliberately terse and developer-shaped; these are what Alex
 * reads.
 */
function adminFailure(e: unknown, verb: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) return 'That login name or display name is already in use. Pick another.';
  if (status === 403) {
    // The two refusals (never yourself, never the last account) are the only
    // 403s these routes emit, and their sentences ARE the actionable ones — an
    // invented generic here would hide which rule was hit.
    const body = e instanceof ApiError ? (e.body as { error?: unknown } | null) : null;
    return typeof body?.error === 'string' ? body.error : 'That change is not allowed.';
  }
  if (status === 404) return 'No account with that login name.';
  if (status === 400) {
    return 'A login name is 2 to 32 letters, digits, dots, dashes or underscores. A display name is the name that will appear on every quote they price, and it has to be unused.';
  }
  return `Could not ${verb}. Try again in a moment.`;
}

export async function listOperatorAccounts(): Promise<AdminOperators> {
  return getJson<AdminOperators>('/api/operators');
}

export async function addOperatorAccount(
  username: string,
  displayName: string,
): Promise<AdminResult<NewCredential>> {
  try {
    const res = await postJson<{ username: string; password: string }>('/api/operators', {
      username,
      displayName,
    });
    return { ok: true, value: { username: res.username, password: res.password } };
  } catch (e) {
    return { ok: false, message: adminFailure(e, 'add that operator') };
  }
}

export async function setOperatorAccountActive(
  username: string,
  active: boolean,
): Promise<AdminResult<null>> {
  try {
    await postJson('/api/operators/active', { username, active });
    return { ok: true, value: null };
  } catch (e) {
    return { ok: false, message: adminFailure(e, active ? 'restore that account' : 'remove that access') };
  }
}

export async function resetOperatorAccountPassword(
  username: string,
): Promise<AdminResult<NewCredential>> {
  try {
    const res = await postJson<{ username: string; password: string }>(
      '/api/operators/reset-password',
      { username },
    );
    return { ok: true, value: { username: res.username, password: res.password } };
  } catch (e) {
    return { ok: false, message: adminFailure(e, 'reset that password') };
  }
}

/**
 * The inbox freshness cadence, shared by BOTH pollers that read
 * `/api/inquiries?status=inbox`: the rail badge tick (App.tsx) and the Inbox
 * screen's own list tick (screens/Inbox.tsx).
 *
 * ⛔ IT LIVES HERE, IN A LEAF, ON PURPOSE. It began as a module-private const in
 * App.tsx (T807). Exporting it from there and importing it into a SCREEN would
 * have made `App -> Inbox -> App` this codebase's first screen-to-app-root
 * import cycle — index.tsx is App.js's only importer today. Both App.tsx and
 * screens/Inbox.tsx already import from this module, so a leaf home gives the
 * two pollers ONE cadence without inverting the layering.
 *
 * ⛔ ONE CADENCE IS THE POINT — do not re-introduce a local copy in either
 * poller or in their specs. Two independently-tuned intervals reading the same
 * endpoint can sit a full phase apart, which is how the badge and the list come
 * to disagree about the same inquiries. A quarter of Bookings.tsx's
 * LIST_REFRESH_MS, which is the existing accepted baseline on the busier screen.
 */
export const INBOX_POLL_MS = 30_000;

/** Badge truth (§7.5): count = new + normalised-but-unquoted. A failed read
 *  is an ERROR badge, never a silent zero. */
/**
 * T807: returns a PollOutcome (same tri-state as refreshPayment below) so the badge
 * tick in App.tsx can stop itself. Every existing caller uses `void
 * refreshInboxCount(store)` and is unaffected by the widened return.
 *
 * 'stop' on 401 AND 429, never 401 alone -- 401: the session is gone, so retrying
 * only burns the throttle; 429: already throttled, so retrying is what dug the hole
 * deeper. The error branch still sets the badge's error mark, so a failed read shows
 * '!' rather than a silent zero.
 */
/**
 * ⭐⭐ Board 2812 M3 (plan step 3, Q246, SC-13) - THE WEB BADGE IS THE INBOX PLUS THE
 * UNSENT PILE'S TRUE TOTAL, one number with the To do's Need you now figure: each
 * saved, unsent web quote is its own Need you now card, and its enquiry left the
 * inbox when the quote was saved, so the sum counts each job ONCE by construction.
 * The second read is `pile=unsent&limit=1`, which evaluates that pile alone (never
 * Outcome) and serves its `total`.
 * ⛔ EITHER READ FAILING IS THE '!' MARK, never half a count; a 401 or 429 from
 * EITHER stops the tick, as before (`allSettled`, so a 500 on one cannot hide a
 * 401 on the other).
 */
export async function refreshInboxCount(store: Store): Promise<PollOutcome> {
  const [inbox, unsent] = await Promise.allSettled([
    getJson<{ inquiries: unknown[] }>('/api/inquiries?status=inbox'),
    getJson<{ unsent?: { total?: unknown } }>('/api/todo-piles?channel=web&pile=unsent&limit=1'),
  ]);
  const unsentTotal = unsent.status === 'fulfilled' ? unsent.value.unsent?.total : undefined;
  if (inbox.status === 'fulfilled' && typeof unsentTotal === 'number') {
    store.update((s) => setInboxCount(s, inbox.value.inquiries.length + unsentTotal));
    return 'ok';
  }
  store.update(setInboxCountError);
  const stop = [inbox, unsent].some((r) => r.status === 'rejected'
    && r.reason instanceof ApiError && (r.reason.status === 401 || r.reason.status === 429));
  return stop ? 'stop' : 'retry';
}

/**
 * Nursing-home-requests badge (T928 slice 1) — how many requests still wait on
 * Helen. Returns a PollOutcome like refreshInboxCount, so the App tick can stop
 * itself on a dead session rather than hammering a throttle.
 * ⭐⭐ Board 2814 M2 (plan DD-11, 2812 R-10) - THE NUMBER IS THE "NEEDS YOU NOW"
 * LENGTH: every request `needsHelenNow` places in Needs you now, and nothing else,
 * so the badge, the `Requests · N` tab label and the screen's list count one set.
 * ⛔ A `null` crossings map (the kiosk's own read failed) is the ERROR mark, never
 * a number: the place of every link-dependent row is unknown, so any count would
 * be a confident wrong answer.
 *
 * ⛔ `listPortalRequests` THROWS rather than degrading, deliberately (see its
 * own note: an empty inbox is a MEANING here). So this must catch — an
 * unhandled rejection would kill the poll interval and freeze the badge at its
 * last value, which is the one failure mode a badge must never have. The catch
 * sets the error mark, so a dead portal seam renders '!' rather than a zero
 * that says "nothing is waiting".
 */
export async function refreshPortalCount(store: Store): Promise<PollOutcome> {
  try {
    // ⭐ T1746 M1 (B4) - the read answers `{requests, crossings}`; the crossing
    // map costs one local `= ANY` per tick, bounded by `inquiries_portal_request_uk`.
    // ⭐ Board 2814 M2 (DD-11): the count DOES read it now. A request's place in
    // Needs you now depends on its booking (a confirmed request with a live one is
    // a booking, not a waiting row), so the map is passed to `needsHelenNow`, and a
    // `null` map (the kiosk's own read failed) is the error mark below.
    // ⭐ Board 2814 M4 (DD-11 row 14): an unreadable settlements map is the same
    // error mark, because a settled row's place is unknown without it.
    const { requests, crossings, settlements } = await listPortalRequests();
    if (crossings === null || settlements === null) {
      store.update(setPortalCountError);
      return 'ok';
    }
    store.update((s) => setPortalCount(s, countNeedsHelenNow(requests, crossings, new Date(), settlements)));
    return 'ok';
  } catch (e) {
    store.update(setPortalCountError);
    return e instanceof ApiError && (e.status === 401 || e.status === 429) ? 'stop' : 'retry';
  }
}

/** Price-changes badge: throw-on-!ok parity (R13) — a failed reconcile read
 *  shows '!', never "all up to date". */
export async function refreshRateChangesCount(store: Store): Promise<void> {
  try {
    const conflicts = await getJson<unknown[]>('/api/rate-changes');
    store.update((s) => setRateChangesCount(s, conflicts.length));
  } catch {
    store.update(setRateChangesError);
  }
}

// ---------------------------------------------------------------------------
// Quote flow (M3)
// ---------------------------------------------------------------------------

/** Server QuoteResult shape (mirrored; data always from POST /api/quote). */
type ServerQuoteResult = {
  status: 'priced' | 'estimated' | 'manual';
  lineItems: { label: string; amount: { cents: number }; kind: string }[];
  computedTotal: { cents: number } | null;
  finalTotal: { cents: number } | null;
  flags: { code: string; severity: 'info' | 'warn' | 'block'; message: string }[];
  estimate?: { anchoredPlace?: string };
};

function toQuoteView(r: ServerQuoteResult): QuoteView {
  return {
    status: r.status,
    lines: r.lineItems.map((l) => ({ label: l.label, cents: l.amount.cents, kind: l.kind })),
    totalCents: r.finalTotal?.cents ?? r.computedTotal?.cents ?? null,
    flags: r.flags,
    ...(r.estimate?.anchoredPlace ? { anchoredPlace: r.estimate.anchoredPlace } : {}),
  };
}

/**
 * Re-price the current job via POST /api/quote under the monotonic seq guard:
 * a stale (raced) response is discarded by `applyQuoteResult`, never rendered.
 * On fetch failure for the LATEST request, the quote view clears (no stale
 * price can be Accepted) — screens render honest retry copy.
 */
export async function fetchQuote(store: Store): Promise<void> {
  const j = store.getState().job;
  if (!j.service || !j.band) return;
  /**
   * ⛔⛔ T1871 M5 — A BAND THE ENGINE CANNOT PRICE NEVER REACHES THE DOOR, AND
   * THE SUPPRESSION LIVES HERE RATHER THAN AT A CALL SITE.
   *
   * `/api/quote` hard-400s an over-band job on purpose (docs/pricing-spec.md:97:
   * over-band is hand-priced by judgment, never auto-repriced) and that refusal
   * stays. What was wrong was letting the POST fly at all. There are ELEVEN
   * `fetchQuote` call sites; the one an over-band web lead actually arrives
   * through is the Inbox "Quote this" edge below, not the Location "Continue"
   * edge, so a guard at any single site closes one path and leaves ten. This is
   * the one chokepoint every edge already passes through.
   *
   * ⛔ THE GUARD IS `toCanonicalBand(...) === null`, NEVER A LITERAL COMPARISON
   * against the over-band token. A hand-spelled token here is a second spelling
   * of a fact that must live once (`engine/bands.ts`), and since T1871 M6
   * `check-copy.sh`'s `scan_over_band` arm reds on one. The derivation is still
   * the better guard on its own merits: it also catches a band that is merely
   * UNKNOWN ('lots'), which the engine refuses for the same reason, and which no
   * literal comparison can see.
   *
   * ⛔ AND IT IS NOT A BARE `return`. Skipping the seq transition would leave
   * `job.quote` at its PREVIOUS value, so moving a party from 12 to 17 would
   * strand a stale `12-16` engine price beside a 17-passenger job with `hasBar`
   * true and the TotalBar rendering the wrong money — the exact stale-pair
   * failure the pairing rule below exists to prevent. So the transition runs:
   * `quotePending` settles (`selectors.ts` — req === applied), the in-flight
   * save guard stays passable exactly as `resetForNewQuote`'s comment already
   * promises for "fetchQuote's own early-return", and the engine price is
   * CLEARED.
   *
   * ⭐ WHAT IS PRESERVED, AND IT IS THE WHOLE POINT: Helen's hand price and
   * `wasHandQuoted`. `applyQuoteResult` forces `wasHandQuoted: false` on every
   * result that does not carry it, and `persistCurrentQuote` is its only other
   * writer — so letting a plain failure stand here would mean Helen records the
   * agreed price, toggles ONE add-on, and the euro figure vanishes from her
   * screen with no message while the server row still holds it.
   * `finalTotalCents` is carried only when it IS a hand price; a superseded
   * ENGINE total is cleared with the quote it came from.
   *
   * ⛔⛔ AND THE CARRY IS NOT UNCONDITIONAL — THE BAND-CHANGE CASE IS CLEARED
   * BEFORE IT EVER GETS HERE, IN `setBand`. Measured (T1871 M5 review, BLOCK):
   * Helen agrees EUR 800 by hand on a `12-16` job and saves; the party turns out
   * to be 30; the count derives the over-band token, and a carry keyed only on
   * "is it a hand price?" preserved that EUR 800 beside the NEW band — the
   * stale pair this whole docblock exists to prevent, emitted to Helen and Owen
   * by the driver/WhatsApp preview as a new band beside the previous band's
   * money. The preview's OWN guard cannot catch it: this transition settles
   * `begin` + `apply` inside one synchronous update, so `quotePending` is
   * already false by the time the preview asks.
   *
   * ⭐ THE RULE, AND WHERE IT HAS TO LIVE: carry the hand price only when the
   * BAND DID NOT CHANGE in the same update. `fetchQuote` cannot decide that —
   * it reads one state, after the write, and a band's previous value is not in
   * it. `setBand` holds both halves at its own call site, so it clears the hand
   * price there and this guard then sees an honest `wasHandQuoted: false`. That
   * also RESTORES the pre-M5 semantics exactly: before the suppression, a band
   * change ran a real POST whose success went through `applyQuoteResult` with
   * `wasHandQuoted: result.wasHandQuoted ?? false`, i.e. a band change has
   * ALWAYS cleared the hand price. Only the un-priceable case had regressed.
   */
  if (toCanonicalBand(j.band) === null) {
    store.update((s) => {
      const begun = beginQuoteRequest(s);
      const hand = begun.state.job.wasHandQuoted;
      return applyQuoteResult(begun.state, begun.seq, {
        finalTotalCents: hand ? begun.state.job.finalTotalCents : null,
        wasHandQuoted: hand,
        quote: null,
      });
    });
    return;
  }
  let seq = 0;
  store.update((s) => {
    const begun = beginQuoteRequest(s);
    seq = begun.seq;
    return begun.state;
  });
  try {
    const result = await postJson<ServerQuoteResult>('/api/quote', {
      service: j.service,
      locationId: j.locationId,
      toLocationId: j.toLocationId,
      band: j.band,
      options: j.options,
      // T387 M3: venue became a PRICED input (the engine consults venue rates
      // for manual services). Must stay in lockstep with the persist
      // requestJson below — the structural pin in quote-flow.spec.tsx fails
      // loud if the two shapes diverge.
      venue: j.venue,
    });
    const view = toQuoteView(result);
    store.update((s) =>
      applyQuoteResult(s, seq, { finalTotalCents: view.totalCents, quote: view }),
    );
  } catch {
    store.update((s) => applyQuoteResult(s, seq, { finalTotalCents: null, quote: null }));
  }
}

// ---------------------------------------------------------------------------
// Priced-input writes — the pairing rule, in one place
// ---------------------------------------------------------------------------

/**
 * THE RULE: every field in the /api/quote body (service, locationId,
 * toLocationId, band, options, venue) is a PRICED INPUT, and moving one without
 * re-pricing leaves the job holding new inputs beside the previous inputs'
 * price — a pair the engine never computed. `quotePending` cannot catch that:
 * it only knows about requests that actually started, so an un-paired write
 * reads as SETTLED and sails straight through the save guard into the booking
 * row and the WhatsApp alert.
 *
 * These are the only writers screens may use for those fields. Keeping the
 * patch and the re-price welded together here is what makes the rule enforceable
 * instead of a convention every future caller has to remember — which is exactly
 * how T372's own "Change" link re-opened the bug M2 had just closed.
 *
 * (T387 M3: `venue` JOINED the priced inputs — the engine consults standing
 * venue rates for manual services — so it moved from a plain patch to the
 * setVenueAndQuote paired writer below.)
 */
/**
 * ⭐ T1871 M5 — `pinnedByOperator` SAYS WHO DECIDED, and it is the whole reason
 * this is a parameter rather than a second writer.
 *
 * `true` = Helen's own act (a Passengers tile, a Quote chip, the over-band
 * forward edge). It sets `bandPinnedByOperator`, and from then on a changed
 * headcount re-derives the band for this job ONLY while the derivation is a
 * priced band — board ask 804, answered "Re-derive when the count fits a priced
 * band"; an over-band count still yields to her pick. The sentence here used to
 * end "no longer re-derives the band for this job", which the ruling falsifies.
 * See that field's docblock for the two derived shapes that were refuted.
 *
 * `false` (the default) = a MACHINE re-derivation, today only the count->band
 * derivation in App.tsx. It must NOT pin, or the first count keystroke would
 * lock the band against Helen's own later correction. Defaulting to false is
 * the safe direction: a new caller that forgets the flag fails to pin, which
 * costs a re-derive, rather than pinning something nobody chose.
 */
export function setBand(store: Store, band: string, pinnedByOperator = false): void {
  store.update((s) => {
    /**
     * ⛔⛔ T1871 M5 (BLOCK) — A BAND CHANGE CLEARS HELEN'S HAND PRICE, AND THIS
     * IS THE ONLY PLACE THAT CAN KNOW IT CHANGED. `fetchQuote`, one line below,
     * sees a single state AFTER the write; the previous band is not in it. Here
     * both halves are in hand, which is why the rule lives at this call site
     * rather than at the chokepoint that consumes it.
     *
     * THE HARM IT STOPS, measured: a hand price agreed on a `12-16` job,
     * carried across a re-derivation to the over-band token, and then emitted by
     * the team preview as the NEW band beside the PREVIOUS band's money.
     * `fetchQuote`'s suppression docblock carries the full account.
     *
     * ⭐ SCOPED TO THE HAND PRICE, DELIBERATELY. An ENGINE total needs nothing
     * here: the POST below replaces it through `applyQuoteResult`, or the
     * suppression clears it. Only `wasHandQuoted` survives that machinery, so
     * only `wasHandQuoted` has to be revoked — and revoking it on a band that
     * did NOT move would break the add-on-toggle carry this milestone shipped
     * (re-picking the ACTIVE band is the failed-re-price retry path, so the
     * no-change case is a real, reachable call and not a theoretical one).
     */
    const bandChanged = s.job.band !== band;
    /**
     * ⭐ T1949 (board ask 823) — ANNOUNCE THE CLEAR, DO NOT CHANGE IT. The
     * clear below is byte-identical in behaviour to what M5 shipped; this
     * constant only NAMES the condition so the announcement can key off the
     * same thing rather than re-deriving it and drifting.
     *
     * ⛔ THE ANNOUNCEMENT IS WRITTEN ONLY ON A BAND CHANGE, and that is what
     * makes each of the three arms right:
     *   - a change that clears a hand price   -> announce the amount
     *   - a change that clears nothing        -> null, so a STALE announcement
     *     from an earlier change is wiped rather than left contradicting the
     *     screen
     *   - a NO-CHANGE call                    -> untouched. Re-picking the
     *     ACTIVE band is the failed-re-price retry path (`BandControl`), a
     *     real reachable call, and it must not silently drop the notice she
     *     has not read yet.
     *
     * ⚠️ `wasHandQuoted` TRUE with `finalTotalCents` NULL would clear silently
     * and announce nothing (there is no amount to name). No writer can produce
     * that pair today - traced through `applyQuoteResult`, the suppression
     * carry above, `persistCurrentQuote`, this clear and `EMPTY_JOB` - but the
     * two fields are typed independently and `patchJob` takes any partial, so
     * NOTHING ENFORCES IT. Pinned by a spec rather than left to hold by luck.
     */
    const clearingHandPrice = bandChanged && s.job.wasHandQuoted;
    return patchJob(s, {
      ...(pinnedByOperator ? { band, bandPinnedByOperator: true } : { band }),
      ...(clearingHandPrice
        ? { finalTotalCents: null, wasHandQuoted: false }
        : {}),
      ...(bandChanged
        ? { handPriceClearedCents: clearingHandPrice ? s.job.finalTotalCents : null }
        : {}),
    });
  });
  void fetchQuote(store);
}

/**
 * ⛔⛔ T1871 M5 (P0-4) — THE REAL HEADCOUNT, AND IT IS WRITTEN ON EVERY
 * KEYSTROKE AND DERIVES THE BAND ON NONE OF THEM. That split is the whole of
 * this milestone's merge blocker; `commitPassengersExact` below is its other
 * half.
 *
 * THIS REVERSES T651 M7 (R21), which made this a plain patch in App.tsx with the
 * note "routing it through setBand's pair would re-price the job every keystroke
 * of a number that moves no money". Board #1871 makes the count the thing Helen
 * actually knows ("there are 17 of us"), so it DOES move money now — and the
 * moment it does, it belongs beside the other paired writers rather than inline
 * in a JSX prop. `state.ts`'s `passengersExact` docblock carries the amendment.
 *
 * ⛔⛔ THE MEASURED DEFECT THE SPLIT EXISTS FOR — reproduced twice, by a
 * ux-review on the served kiosk and again at the unit level. This function used
 * to re-derive the band on EVERY keystroke, and A TWO-DIGIT HEADCOUNT IS TYPED
 * AS A ONE-DIGIT COUNT FIRST. That transient prefix was treated as a real value:
 * it derived a priced band, fired `setBand`'s band-change carry, and CLEARED THE
 * OPERATOR'S AGREED HAND PRICE. The second digit could not undo it, because by
 * then guard 2 below refused the over-band derivation. From a `12-16` job at
 * EUR 800, hand-quoted and pinned:
 *
 *     setPassengersExact(store, 22)                   -> 12-16, 80000, true
 *     setPassengersExact(store, 2) then (store, 22)   -> 1-4,   null,  false
 *
 * ⛔ SAME FINAL VALUE, OPPOSITE OUTCOME, DECIDED PURELY BY HOW IT WAS TYPED —
 * and the atomic column is the one ask 804 rules CORRECT ("Re-derive when the
 * count fits a priced band"; an over-band count yields to the pin). So the
 * shipped behaviour contradicted the ruling it implements, for every over-band
 * final value. On the real screen it read worse still: an over-band job agreed
 * at EUR 800, Helen types the true headcount `12`, the keystroke `1` derives
 * `1-4` and destroys the EUR 800, and the screen settles on a EUR 160 grid price
 * with Accept live — which the save door permits, because band `8-12` and count
 * 12 agree. EUR 640 gone, no banner, no undo, saveable. ⚠️ EVERY hand-typed
 * count >= 10 passes through a single-digit prefix, so this fired on essentially
 * every real correction.
 *
 * ⚠️ WHY EVERYTHING WAS GREEN AGAINST IT, and the rule that follows for any
 * future arm: `playwright-cli`'s `fill` sets a value ATOMICALLY, which is why
 * the first ux-review pass and both the M5 and M6 live drives missed it. ANY
 * coverage that sets the count in ONE step is green against this defect. A
 * regression arm must drive the SEQUENCE; a single call can never catch it.
 *
 * ⭐ WHY A COMMIT EDGE AND NOT A DEBOUNCE. The docblock that stood here named
 * both the trigger and the trap in one paragraph — "NO DEBOUNCE, deliberately.
 * simplified: per-keystroke debounce, add when a measured kiosk drive shows the
 * intermediate re-price is a real cost", and then, against it, "a timer fires
 * AFTER the keystroke, so one armed before Helen taps a band chip lands after
 * her pick and overwrites it … unless the timer re-reads the pin too". The drive
 * has now fired the trigger, so the simplification is spent — but the trap is
 * real, IT IS NOT CLOSED BY RE-READING THE PIN, and a timer carries three
 * hazards a commit edge has none of:
 *   1. THE PIN CANNOT DISARM IT. Ask 804 makes a pinned band YIELD to a priced
 *      derivation, so a timer armed at count 12 still overwrites a `12-16` she
 *      taps 200ms later, pin re-read or not. Only cancelling on her act would —
 *      module-scope mutable state, shared by every store.
 *   2. IT CAN OUTLIVE THE JOB. A timer armed by the last keystroke before Accept
 *      fires after `resetForNewQuote` and derives a band on the NEXT job.
 *   3. IT OPENS A WINDOW WHERE THE COUNT AND THE BAND DISAGREE, which is exactly
 *      what ask 805's save door refuses — a refusal that would have resolved
 *      itself a moment later.
 * `blur` has none of the three: it fires BEFORE the click that caused it, so a
 * band chip or an Accept press always lands AFTER the commit, never before it.
 *
 * ⛔ THE COUNT ITSELF IS STILL WRITTEN ON EVERY KEYSTROKE, deliberately, and
 * deferring it too would be a second defect. P0-3's over-band state on both
 * screens and ask 805's save door read the count's VALUE live; a deferred count
 * would blind both of them mid-edit. Only the DERIVATION waits.
 */
export function setPassengersExact(store: Store, value: number | null): void {
  store.update((s) => patchJob(s, { passengersExact: value }));
}

/**
 * ⛔⛔ THE SETTLED COUNT DERIVES THE BAND — the other half of the split above,
 * and the ONLY writer that may move the band from the headcount.
 *
 * Wired to the count box's `blur` (BandControl -> Quote.tsx -> App.tsx), never
 * to its input. It reads the count OUT OF THE STORE rather than taking it as an
 * argument, so the band it derives is always the band of the count the job
 * actually holds — a caller handing over a value the store never took would
 * manufacture the very stale pair the paired-writer rule exists to prevent.
 *
 * The count is still never a `/api/quote` field. It moves money only by moving
 * the BAND, which is exactly why the derived write goes through `setBand` and
 * not a second patch: a patch would leave the job holding a new band beside the
 * previous band's price, the stale pair the pairing rule above prevents.
 *
 * ⛔ THREE GUARDS, EACH FOR A MEASURED REASON:
 *  1. `== null`, NEVER truthiness. `Number(null) === 0` and `Number('') === 0`,
 *     so a truthiness test reads "not captured" as a party of nobody. A cleared
 *     box leaves the band alone: clearing a count is not a statement about the
 *     band, and the count's ABSENCE may never drive anything (it is null on
 *     every legacy row and on every nursing-home crossing, which collects no
 *     count at all by decision).
 *     ⚠️ HONEST SCOPE: this one is DEFENCE IN DEPTH, not the operative refusal,
 *     and it is written down rather than claimed. Swapping it for `!count` is
 *     behaviourally identical today because `bandForCount(0)` already returns
 *     null — MEASURED, by mutating it and watching the suite stay green. It
 *     becomes load-bearing the moment the derivation bands 0, and
 *     `over-band-hand-price.spec.tsx` pins that coupling so the day it changes
 *     is a red rather than a silent "party of nobody".
 *  2. `bandPinnedByOperator` WINS FOR AN OVER-BAND DERIVATION, AND ONLY THERE.
 *     ⛔ AMENDED BY BOARD ASK 804, answered "Re-derive when the count fits a
 *     priced band" — the previous rule here was "the pin wins for the rest of
 *     the job", full stop. The ask was raised on a live over-band booking
 *     re-opened and edited to 9: the pin held the band at the over-band token
 *     for a party of nine, and only a second tap of the right tile could correct
 *     it. So a count that fits one of the FOUR PRICED bands re-derives THROUGH
 *     the pin. The pin still wins where the derivation is the over-band token,
 *     which is the pairing Helen means: a deliberate `12-16` for a party of 22,
 *     two minibuses rather than a coach (`state.ts`, refuted alternative 1).
 *     ⛔ THE FLAG IS NEVER CLEARED BY A MACHINE RE-DERIVE. Un-pinning on
 *     agreement is refuted alternative 1 at `bandPinnedByOperator`, and 804
 *     ruled the DERIVATION, not the flag: her act is still recorded, and it
 *     still outranks every over-band count for the rest of the job.
 *     ⚠️ AND THE RE-DERIVE CLEARS ANY HAND PRICE, through `setBand`'s existing
 *     band-change carry — this call passes the default `pinnedByOperator`
 *     (false), so it is a machine write like any other. That is intended and
 *     ruled: a price agreed for `12-16` does not belong to the `4-8` the
 *     corrected count derives. ⛔ IT IS ALSO EXACTLY THE HARM THE SPLIT ABOVE
 *     EXISTS TO BOUND: ruled on a count Helen SETTLED on, never on a prefix of
 *     one she is still typing. It is the half of 804 she has to be TOLD about,
 *     which is carried to the sitting rather than solved in code.
 *  3. A BAND DELTA, not merely a changed count. `setBand` is deliberately not
 *     idempotent (`BandControl` records that re-picking the ACTIVE band is the
 *     failed-re-price retry path), so firing it on 9 -> 10, where the band does
 *     not move, would re-price the job for a keystroke that changed nothing. It
 *     is also what makes this function IDEMPOTENT, so a second commit edge on an
 *     unchanged count (blur, then blur again) writes nothing.
 *
 * `bandForCount` returning null — a typo, or a count past the server's own
 * ceiling — also leaves the band alone. Fail closed; never invent a band.
 */
export function commitPassengersExact(store: Store): void {
  const j = store.getState().job;
  const count = j.passengersExact;
  if (count == null) return;
  const derived = bandForCount(count);
  if (derived === null || derived === j.band) return;
  // ⛔ THE PIN'S EARLY RETURN LIVES HERE, BELOW THE DERIVATION, AND THAT IS THE
  // WHOLE OF ASK 804 ("Re-derive when the count fits a priced band").
  // `bandForCount` is THREE-valued, so "the pin stays only for over-band" and
  // "the pin stays whenever the derivation is not a priced band" are different
  // rules: the third value, `null`, is already handled one line above and leaves
  // the band alone for every job, pinned or not. Fail closed; never invent a
  // band. Placing this test above the derivation is what the ask overturned.
  if (j.bandPinnedByOperator && derived === OVER_BAND) return;
  setBand(store, derived);
}

/** `value` spans both add-on shapes: the bool toggles and the extra-pickups
 *  count stepper (Addons' own onChange hands over `number | boolean`). */
export function setOption(store: Store, key: keyof QuoteOptions, value: boolean | number): void {
  store.update((s) => patchJob(s, { options: { ...s.job.options, [key]: value } as QuoteOptions }));
  void fetchQuote(store);
}

export function setPickup(store: Store, id: string | null, label: string | null): void {
  // A resolved pick (or a clear) supersedes any hand-quoted off-gazetteer place
  // for this end, so the marker resets — else a stale marker would read the end
  // as "ready" after Helen changes her mind back to a listed town.
  store.update((s) => patchJob(s, { locationId: id, locationLabel: label, pickupOffGazetteer: false }));
  void fetchQuote(store);
}

export function setDropoff(store: Store, id: string | null, label: string | null): void {
  store.update((s) => patchJob(s, { toLocationId: id, toLocationLabel: label, dropoffOffGazetteer: false }));
  void fetchQuote(store);
}

/**
 * T2090 - the airport journey's DIRECTION, captured on the operator path.
 *
 * JobState carries no direction. Until this row every reader keyed the airport
 * end off `pickupTerminal` as a PROXY: set by an arrivals lead
 * (`quoteThisInquiry`) and by the T515 arrivals reopen, null for every kiosk
 * walk-in, so a walk-in that really collects FROM the airport persisted and
 * alerted the airport as its DESTINATION (the `airportDropoffFallback` docblock
 * and docs/test-gaps.md `airport-fallback-arrivals-divergence`). The Location
 * screen's direction and terminal rows now let Helen state it, and this block
 * is the ONE writer: on a flip it MOVES the address end's capture to the other
 * side, then stamps the terminal end, so the proxy every reader already keys on
 * becomes a captured fact and none of them needs a second key.
 *
 * `isArrivalShaped` is shared by the writer and the reader (`airportEndOf`), so
 * the two can never disagree about which end is the address end.
 *
 * Not a priced input: no key on the patch reaches the engine (the test pins the
 * key set), so `setAirportEnd` does not re-price, unlike its neighbours above.
 */
export type AirportEnd = { direction: AirportDirection; terminal: AirportTerminal | null };

/**
 * The terminal PLACE as the kiosk records it (`pickupTerminal` on an arrival,
 * the drop-off text on a departure), one per token. 'not-sure' is the airport
 * itself: "not sure" is not a terminal, and the place must still name the end.
 * simplified: a kiosk-side twin of the site's TERMINAL_END (InquiryForm.astro);
 * move beside AIRPORT_TERMINAL_DESIGNATION in shared/service-schema.ts when the
 * site imports the kiosk vocabulary for that copy.
 */
const terminalPlaceLabel: Record<AirportTerminal, string> = {
  't1': 'Dublin Airport Terminal 1',
  't2': 'Dublin Airport Terminal 2',
  'not-sure': DUBLIN_AIRPORT_LABEL,
};

/** The place for a token, through the same `Object.hasOwn` guard
 *  `terminalDesignation` (service-schema.ts) uses on its own map: a plain object
 *  literal answers every `Object.prototype` name with a truthy non-string, and
 *  the token is enum-typed today but reaches here from a click handler, so the
 *  guard is parity with the shared reader rather than a reachable defect. */
function placeLabelOf(token: AirportTerminal): string {
  return Object.hasOwn(terminalPlaceLabel, token) ? terminalPlaceLabel[token] : DUBLIN_AIRPORT_LABEL;
}

/** The token whose recorded place label is `place`, trimmed and case-folded
 *  (a pre-T384 lead could carry 'Dublin airport' by hand), else null. EXACT
 *  labels only, never a prefix: see `terminalShaped`. */
function terminalTokenOf(place: string | null): AirportTerminal | null {
  if (place === null) return null;
  const v = place.trim().toLowerCase();
  return AIRPORT_TERMINALS.find((t) => terminalPlaceLabel[t].toLowerCase() === v) ?? null;
}

/** The token whose recorded DESIGNATION is `line`, trimmed and case-folded the
 *  way `isTerminalDesignation` reads an editable field. Derived from
 *  `terminalDesignation`, so 'not-sure' (null) matches nothing. */
function designationTokenOf(line: string | null): AirportTerminal | null {
  if (line === null) return null;
  const v = line.trim().toLowerCase();
  if (v === '') return null;
  return AIRPORT_TERMINALS.find((t) => terminalDesignation(t)?.toLowerCase() === v) ?? null;
}

/** Does a captured TEXT name the airport end? A recorded place label, or a bare
 *  designation that a reopen put back into a search box. NOTHING LOOSER: this
 *  predicate feeds `isArrivalShaped`, which the screen READS (`airportEndOf`)
 *  and the writer FLIPS on, and a prefix test ("starts with the airport's
 *  name") classified a departure whose off-gazetteer pickup was 'Dublin Airport
 *  Lodge, Cloghran' as an arrival: the chips showed Coming from the airport, a
 *  truthful Going to the airport press then swapped the Lodge and its Eircode
 *  onto the drop-off, and a T1 press persisted the airport at BOTH ends
 *  (Captain rework 1, security-review Med, measured). The T515-era 'Dublin
 *  Airport T2' label is still honoured, but by the `pickupTerminal` FIELD's
 *  contract (non-null only ever holds the terminal place), not by its spelling. */
function terminalShaped(x: string | null): boolean {
  return x !== null && (terminalTokenOf(x) !== null || isTerminalDesignation(x));
}

/**
 * Is the ADDRESS end the drop-off? True on a captured terminal; on a terminal-
 * shaped pickup text (a reopened off-gazetteer arrival keeps the terminal in
 * `pickupSearchText`, because the reopen guard needs a town on the drop-off row
 * that a hand-quoted place never has); and on a TOWN carried on the drop-off
 * end (the M6c lone-destination record reopens with `dropoffTown` from its
 * destination row). The third clause is STRUCTURAL on purpose: it used to read
 * "a drop-off text that is not the airport", i.e. free text graded by its
 * spelling, and the writer flips on this answer, so a mis-read moves a
 * customer's street. A town is written only by the intake, the prefill and the
 * reopen carry, always on the ADDRESS end (`journeyStops` nulls it on the
 * airport row), and `airportEndPatch`'s swap keeps it there. A walk-in with
 * nothing captured is a departure, which is what every reader assumed before
 * this row and what the screen shows pressed until Helen says otherwise.
 */
export function isArrivalShaped(j: JobState): boolean {
  return j.pickupTerminal !== null
    || terminalShaped(j.pickupSearchText)
    || j.dropoffTown !== null;
}

/**
 * An ARRIVAL's destination when nothing else names it: the priced town, else the
 * lead's structured town (T384 `extra.town`, carried as the search seed when it
 * did not resolve), else the town carry, else the customer's street on the
 * address end; null when the record knows NOTHING about the address end. Both
 * airport fallbacks read this one function (Captain rework 1, the two Lows):
 * `journeyStops` refuses the destination row on null rather than persisting the
 * airport at both ends, and the team alert prints no finish line for the same
 * job, so the record and the alert never name different ends. Reachable only
 * by a from-airport lead that gave no town, no address and no street - the site
 * composes the town when the address block is filled - or a hand-built POST.
 */
function arrivalAddressEndFallback(j: JobState): string | null {
  return j.locationLabel ?? j.locationSearchSeed ?? j.dropoffTown ?? (j.dropoffAddressLine.trim() || null);
}

/** The keys a direction press may write, and the ONLY keys: the priced pair
 *  (`locationId`/`locationLabel`, `toLocationId`/`toLocationLabel`), the band,
 *  the options and the venue are not in this type, so a patch from here cannot
 *  move the price by construction. */
type AirportEndPatch = Pick<JobState,
  | 'pickupTerminal' | 'pickupAddressLine' | 'pickupEircode' | 'pickupTown' | 'pickupSearchText'
  | 'dropoffAddressLine' | 'dropoffEircode' | 'dropoffTown' | 'dropoffSearchText'>;

/** One end's capture, moved as a set on a flip: the street, the Eircode, the
 *  town carry and the customer's own text all belong to the ADDRESS end. */
type EndCapture = Pick<AirportEndPatch, 'pickupAddressLine' | 'pickupEircode' | 'pickupTown' | 'pickupSearchText'>;

/**
 * The pure core of `setAirportEnd`. `terminal === null` is a direction press:
 * it keeps whatever terminal the record already carries.
 */
export function airportEndPatch(
  j: JobState,
  direction: AirportDirection,
  terminal: AirportTerminal | null,
): AirportEndPatch {
  // Service-gated like `quoteThisInquiry`'s own `pickupTerminal` seed (which
  // gates on `inq.service === 'airport'` too): only the airport tab mounts the
  // rows today, but a writer that stamps a terminal onto a local-other job
  // would rewrite its stops, so the gate lives on the writer, not the caller.
  if (j.service !== 'airport') {
    return {
      pickupTerminal: j.pickupTerminal,
      pickupAddressLine: j.pickupAddressLine, pickupEircode: j.pickupEircode,
      pickupTown: j.pickupTown, pickupSearchText: j.pickupSearchText,
      dropoffAddressLine: j.dropoffAddressLine, dropoffEircode: j.dropoffEircode,
      dropoffTown: j.dropoffTown, dropoffSearchText: j.dropoffSearchText,
    };
  }
  // (i) What the record already says the terminal end is, read from the end
  // that holds it BEFORE anything moves. On an arrival the `pickupTerminal`
  // FIELD is the place by contract, whatever its spelling (the T515-era 'Dublin
  // Airport T2' included); every other slot is free text and counts only when it
  // IS a label or a bare designation ('T2', put back into a search box by a
  // reopen, is promoted to its place). A street is never written into
  // `pickupTerminal` as the PLACE, and no text is graded by its prefix.
  const wasArrival = isArrivalShaped(j);
  let existing = wasArrival
    ? j.pickupTerminal ?? (terminalShaped(j.pickupSearchText) ? j.pickupSearchText : null)
    : (terminalShaped(j.dropoffSearchText) ? j.dropoffSearchText : null);
  const bare = existing !== null && terminalTokenOf(existing) === null ? designationTokenOf(existing) : null;
  if (bare !== null) existing = terminalPlaceLabel[bare];
  // (ii) A FLIP moves the address end's capture to the other side, as a set.
  // No flip, no move: a re-press of the current direction changes no capture.
  const toArrival = direction === 'from-airport';
  const pickup: EndCapture = {
    pickupAddressLine: j.pickupAddressLine, pickupEircode: j.pickupEircode,
    pickupTown: j.pickupTown, pickupSearchText: j.pickupSearchText,
  };
  const dropoff: EndCapture = {
    pickupAddressLine: j.dropoffAddressLine, pickupEircode: j.dropoffEircode,
    pickupTown: j.dropoffTown, pickupSearchText: j.dropoffSearchText,
  };
  const flip = toArrival !== wasArrival;
  const [p, d] = flip ? [dropoff, pickup] : [pickup, dropoff];
  // (iii) The terminal end's place and its designation. The designation is
  // written only over an empty line or an earlier designation: a street Helen
  // typed on DriverDetails for the airport end is never overwritten. And an
  // earlier designation is replaced only by an EXPLICIT terminal press (which
  // may clear it: 't1' then 'not-sure' writes '') or by a place that yields a
  // token; a DIRECTION press on a place the map cannot read back (the T515-era
  // 'Dublin Airport T2', a customer's own 'Dublin Airport Terminal 2, arrivals
  // hall') keeps the 'T2' the record already carries rather than blanking the
  // driver line and losing the chip's read-back (security review, round 1).
  const place = terminal !== null ? placeLabelOf(terminal) : existing;
  const line = terminalDesignation(terminal ?? terminalTokenOf(place)) ?? '';
  const stamp = (current: string): string => {
    if (current === '') return line;
    if (!isTerminalDesignation(current)) return current;
    return terminal !== null || line !== '' ? line : current;
  };
  // (iv) Stamp the terminal end. On an arrival the terminal lives in
  // `pickupTerminal` (T515), so a terminal-shaped pickup text is cleared rather
  // than left to pre-type the search with a place the gazetteer never holds; on
  // a departure it lives in the drop-off text, where `null` keeps today's
  // `journeyStops` fallback row. A same-direction press with no terminal is a
  // NO-OP on the place: the drop-off text a departure already carries (a lead's
  // composed label, a customer's own wording) is neither re-derived nor blanked.
  if (toArrival) {
    return {
      pickupTerminal: place ?? DUBLIN_AIRPORT_LABEL,
      pickupAddressLine: stamp(p.pickupAddressLine),
      pickupEircode: p.pickupEircode,
      pickupTown: p.pickupTown,
      // On a FLIP the text arriving here was the departure's drop-off text, i.e.
      // its airport end by contract (a lead's composed label, a customer's own
      // wording), never an address: cleared whatever its spelling. Without a
      // flip only a label or designation is cleared; an address stays.
      pickupSearchText: flip || terminalShaped(p.pickupSearchText) ? null : p.pickupSearchText,
      dropoffAddressLine: d.pickupAddressLine,
      dropoffEircode: d.pickupEircode,
      dropoffTown: d.pickupTown,
      dropoffSearchText: d.pickupSearchText,
    };
  }
  return {
    pickupTerminal: null,
    pickupAddressLine: p.pickupAddressLine,
    pickupEircode: p.pickupEircode,
    pickupTown: p.pickupTown,
    pickupSearchText: p.pickupSearchText,
    dropoffAddressLine: stamp(d.pickupAddressLine),
    dropoffEircode: d.pickupEircode,
    dropoffTown: d.pickupTown,
    dropoffSearchText: terminal !== null || flip ? place : j.dropoffSearchText,
  };
}

/**
 * The inverse of `airportEndPatch`, for the screen's pressed state. The
 * terminal end's designation line first - it is the more specific fact, and a
 * record can carry 'T2' beside a place the map cannot read back (the T515-era
 * 'Dublin Airport T2', or the bare airport a flip-and-back degraded a customer's
 * own wording to; Captain rework 1) - then the recorded place label, else no
 * terminal pressed. An explicit Not sure press writes an EMPTY line beside the
 * bare airport, so it still reads back as not-sure through the place.
 */
export function airportEndOf(j: JobState): AirportEnd {
  const arrival = isArrivalShaped(j);
  const place = arrival ? (j.pickupTerminal ?? j.pickupSearchText) : (j.toLocationLabel ?? j.dropoffSearchText);
  const line = arrival ? j.pickupAddressLine : j.dropoffAddressLine;
  return {
    direction: arrival ? 'from-airport' : 'to-airport',
    terminal: designationTokenOf(line) ?? terminalTokenOf(place),
  };
}

/** NOT paired with a re-price (T372's rule is for PRICED inputs): the patch
 *  carries no key the engine reads, and the test pins that key set. */
export function setAirportEnd(store: Store, direction: AirportDirection, terminal: AirportTerminal | null): void {
  store.update((s) => patchJob(s, airportEndPatch(s.job, direction, terminal)));
}

/**
 * T373 M4 — hand-quote an off-gazetteer end (a place the gazetteer doesn't list).
 * A SAME-SHAPE paired writer (T372's rule): the place is a priced input — it
 * changes the journey — so the patch welds to a re-price exactly like setPickup.
 * The re-price returns `manual` (locationId stays null), which settles
 * `quotePending` so the save guard doesn't wrongly block. The true place lives in
 * the label (→ booking_stops + journey summary); the marker passes the readiness
 * gate despite the null id.
 */
export function setPickupOffGazetteer(store: Store, place: string): void {
  store.update((s) => patchJob(s, { locationLabel: place, locationId: null, pickupOffGazetteer: true }));
  void fetchQuote(store);
}

export function setDropoffOffGazetteer(store: Store, place: string): void {
  store.update((s) => patchJob(s, { toLocationLabel: place, toLocationId: null, dropoffOffGazetteer: true }));
  void fetchQuote(store);
}

/** A priced golf pair as the registry publishes it (core/data/rates-repo.ts). */
export type GolfPairStub = { locationId: string; locationLabel: string; a: string; b: string };

/**
 * T906 S1 — resolve two stated golf places to a priced rate row, or null.
 *
 * PURE on purpose: no store, no fetch. Both the picker (which then re-prices)
 * and the RE-OPEN path (which must not write the store a second time, nor fire
 * a second quote — it already dispatches one) need this same answer, and a
 * shared action would have given the second caller both side effects.
 *
 * UNORDERED, per Alex's ruling on 2026-08-07: one row per physical leg, and
 * Owen's sheet prices "Each Way", so hotel→course and course→hotel are the same
 * row at the same price. Which DIRECTION the journey runs is not decided here —
 * it lives in the stated ends, so a reversed pick prices from the grid while
 * still persisting and messaging the journey the way round it actually happens.
 *
 * Case-insensitive because the ends can arrive from a persisted stop row or an
 * operator's typing, not only from a registry tile. It never re-derives a key
 * from the names: the 3 airport legs key off a whole route name, so name
 * arithmetic would silently un-price them (see listGolfPairs).
 */
export function resolveGolfPair(
  pairs: readonly GolfPairStub[],
  a: string | null,
  b: string | null,
): GolfPairStub | null {
  if (a === null || b === null) return null;
  const want = [a.trim().toLowerCase(), b.trim().toLowerCase()].sort();
  if (want[0] === '' || want[1] === '') return null;
  return (
    pairs.find((p) => {
      const got = [p.a.trim().toLowerCase(), p.b.trim().toLowerCase()].sort();
      return got[0] === want[0] && got[1] === want[1];
    }) ?? null
  );
}

/**
 * T906 S1 — state one END of a golf journey, then re-price the pair.
 *
 * A SAME-SHAPE paired writer (T372's rule): a golf end is a priced input, so
 * the patch welds to the re-price exactly like `setPickup`. Setting either end
 * re-resolves BOTH, because a pair is only priced once both ends are known and
 * changing either one can move it on or off the grid.
 *
 * ⛔ It writes `locationId`/`locationLabel` ONLY from a matched row's STORED
 * bytes, and nulls them on a miss. It never composes a label: composing is
 * wrong for all 3 airport legs (whose stored label is
 * '<hub> — Dublin Airport → <hub>') and for every reversed pick, and a
 * synthetic composite is exactly what `precedentPlace` would then key on.
 */
export function setGolfEnd(
  store: Store,
  end: 'pickup' | 'destination',
  place: string | null,
  pairs: readonly GolfPairStub[],
): void {
  store.update((s) => {
    const origin = end === 'pickup' ? place : s.job.golfPickupPlace;
    const destination = end === 'destination' ? place : s.job.golfDestinationPlace;
    const hit = resolveGolfPair(pairs, origin, destination);
    return patchJob(s, {
      golfPickupPlace: origin,
      golfDestinationPlace: destination,
      locationId: hit?.locationId ?? null,
      locationLabel: hit?.locationLabel ?? null,
    });
  });
  void fetchQuote(store);
}

/**
 * T387 M3 — venue became a priced input (the engine's ordered lookup consults
 * standing venue rates for wedding / private-tour). SAME-SHAPE paired writer
 * per THE RULE: the patch and the re-price are welded, so a venue change can
 * never leave the previous venue's price on screen.
 */
export function setVenueAndQuote(store: Store, venue: string | null): void {
  store.update((s) => patchJob(s, { venue }));
  void fetchQuote(store);
}

/**
 * T385 item 6 — widen the miss envelope: log a gazetteer miss the moment the
 * escape hatch is TAKEN or the search is ABANDONED, not only when a quote
 * later persists. POSTs the existing session-authed /api/location-miss with
 * `resolution` omitted (→ NULL = unresolved, L3 semantics); the T373 M4
 * in-transaction writer keeps recording `resolution='manual'` on persist —
 * 387's queue dedupes the two row generations by normalized place.
 * Best-effort demand signal: a failure must never block or distract Helen.
 */
export function reportLocationMiss(query: string, service: string | null): void {
  void postJson('/api/location-miss', { query, service }).catch(() => {
    // Swallowed by design — the miss log is a demand signal, not a workflow.
  });
}

/** T373 M5 — a prior agreed price to the same place (client mirror of the
 *  server PrecedentEntry). */
export type PrecedentEntry = { bookingId: number; agreedCents: number };

/**
 * The last agreed price(s) to a place — "we quoted this before, here is the
 * number", which kills the substitution motive. Read-only and best-effort:
 * getOr swallows any failure to an empty list, so a precedent-fetch blip just
 * hides the line — it never blocks the quote or throws.
 */
export async function loadPrecedent(
  place: string,
  excludeBookingId: number | null,
): Promise<PrecedentEntry[]> {
  const q = new URLSearchParams({ place });
  if (excludeBookingId !== null) q.set('excludeBookingId', String(excludeBookingId));
  const data = await getOr<{ entries: PrecedentEntry[] }>(`/api/quote-precedent?${q.toString()}`, { entries: [] });
  return data.entries;
}

/**
 * ⛔ A HAND-WRITTEN MIRROR of `QuotePriceRefusalCode` in `server/app.ts`, for the
 * reason `PushRefusalCode` below is one: that module pulls in `pg`, which would
 * drag a node-only dependency into the browser bundle. The mirror is NOT free to
 * rot - `test/server/app.spec.ts` asserts the two unions are mutually assignable,
 * so either side gaining, losing or misspelling a member fails the typecheck.
 */
export type QuotePriceRefusalCode = 'quote_price_out_of_range';
const QUOTE_PRICE_OUT_OF_RANGE: QuotePriceRefusalCode = 'quote_price_out_of_range';

/**
 * Is this 400 the money door's OWN refusal? Read off the CODE and never off the
 * server's sentence (SEC-5), exactly as `pushRefusalCodeOf` does further down.
 *
 * ⛔ THE STATUS IS NOT ENOUGH, and that is the whole reason the code exists. The
 * persist route raises six other 400s (requestJson, band, stops, passengersExact,
 * computedTotalCents, booking id); keying on `400` alone would give every one of
 * them the price sentence, which is a wrong cause rather than a vague one.
 */
function isQuotePriceRefusal(e: unknown): boolean {
  const body = e instanceof ApiError && e.status === 400 ? e.body : null;
  if (typeof body !== 'object' || body === null || !('code' in body)) return false;
  return (body as { code: unknown }).code === QUOTE_PRICE_OUT_OF_RANGE;
}

/**
 * ⛔ A HAND-WRITTEN MIRROR of `PassengerMismatchRefusalCode` in `server/app.ts`,
 * for the reason the price code above is one: that module pulls in `pg`. Pinned
 * against rot the same way - `test/server/app.spec.ts` asserts the two unions
 * are mutually assignable, so a misspelling on either side fails the typecheck.
 */
export type PassengerMismatchRefusalCode = 'passengers_band_count_mismatch';
const PASSENGERS_BAND_COUNT_MISMATCH: PassengerMismatchRefusalCode = 'passengers_band_count_mismatch';

/**
 * Board ask 805's refusal, off the CODE and never off the sentence (SEC-5).
 * ⛔ ONE PREDICATE FOR ALL THREE DOORS, because the server sends one code from
 * all three: the capture door refuses it before the booking exists, and the
 * quote and park doors refuse it after. Keying on `400` alone would give every
 * other field refusal on those routes this cause.
 */
function isPassengerMismatchRefusal(e: unknown): boolean {
  const body = e instanceof ApiError && e.status === 400 ? e.body : null;
  if (typeof body !== 'object' || body === null || !('code' in body)) return false;
  return (body as { code: unknown }).code === PASSENGERS_BAND_COUNT_MISMATCH;
}

export type PersistOutcome =
  | { kind: 'saved'; bookingId: number }
  | { kind: 'price-check' }
  | { kind: 'mismatch' }
  // T374 — the server refused the write because its recompute disagrees with the
  // computedTotalCents on screen (a snapshot moved under the quote, or a stale
  // client). DISTINCT from 'mismatch' (409, stale-job): this is a price event,
  // and like 'pending' it must NOT navigate to Done — nothing was written.
  | { kind: 'price-changed' }
  /**
   * ⛔ T1871 M6 REVIEW (security MEDIUM) - THE MONEY DOOR'S OWN 400, GIVEN AN
   * OUTCOME OF ITS OWN. It used to fall into `'error'`, whose sentence ends "try
   * again in a moment" - true of a transient failure and FALSE of this one: the
   * server refuses the same amount every time, so the operator was told to repeat
   * an action that can never succeed, and the agreed price she typed was lost
   * with no trace on any surface. Distinct from `'price-changed'` (422, the
   * engine and the client disagree): here the figure itself is out of range.
   * Nothing was written, so like every refusal above it must NOT reach Done.
   */
  | { kind: 'price-out-of-range' }
  /**
   * ⛔ T1871 M7 (board ask 805, "Refuse disagreement at the save door") - the
   * band on the job and the headcount on the job cannot both be right, and the
   * server refuses to store the pair. Deterministic, exactly like
   * 'price-out-of-range': the same pairing is refused every time, so it must not
   * fall into 'error' and be answered with "try again in a moment". It is
   * REACHABLE from the kiosk by design - pin a band, then type a count no band
   * fits - so the copy has to name the two fields she can actually correct.
   * Nothing was written, so like every refusal above it must NOT reach Done.
   */
  | { kind: 'band-count-mismatch' }
  /* ⛔ `'cash-no-series'` WAS HERE (board 2814 M4): the cash run's 409 when a
     schedule was sent with it. Since M6 (DD-8) the client sends no schedule, and
     the server refuses a `nursingSeries` key with a 400 before that 409 is read. */
  /** ⭐ board 2871 - the Return date and time cannot be saved, for `cause`. Refused
   *  before the lazy create or by the quote door's coded 400; nothing was written. */
  | { kind: 'return-invalid'; cause: ReturnLegCause }
  | { kind: 'pending' }
  // T660 M5 / P0-12 — the lazy-create was refused 409 because the operator-typed
  // email is on file for a DIFFERENT existing customer. Nothing was written (no
  // bookings row, no customers UPDATE, no customer_events row), so like
  // 'price-changed' it must NOT navigate to Done.
  | { kind: 'email-conflict' }
  | { kind: 'error' };

/**
 * ⭐ board 2871 - THE RETURN DATE AND TIME AS THE QUOTE SCREEN SHOWS THEM: Helen's
 * draft first, then the stored return as its Dublin day and time, then (day only)
 * the return day the customer stated on the site form when it is a real calendar
 * day (`isReturnDay`), then the journey date. A null draft day therefore falls through to
 * those defaults. ⛔ The time is never prefilled from the customer's free text:
 * a shown time that is not stored would read as saved when nothing is sent.
 */
export function shownReturnLeg(j: JobState): { day: string; time: string } {
  const stored = dublinWallClockOf(j.returnAt);
  const stated = isReturnDay(j.returnDate) ? j.returnDate : null;
  return {
    day: j.returnDraft?.day ?? stored?.day ?? stated ?? j.journeyDate ?? '',
    time: j.returnDraft?.time ?? stored?.time ?? '',
  };
}

/**
 * The return keys the quote and park bodies carry: only with the switch on and
 * once Helen has typed, so an untouched draft sends nothing (don't touch, a
 * re-save never blanks a stored return). ⭐ board 2871 (Captain's ruling, C2a):
 * an emptied Return time clears a STORED return (`''` for both keys) once the
 * date box is empty too (`returnLegCauseFor` refuses a date beside an empty
 * time first); with nothing stored there is nothing to clear, so nothing is sent.
 */
function returnLegKeys(j: JobState): { returnDay?: string; returnTime?: string } {
  if (!j.options.returnTrip || j.returnDraft === null) return {};
  if (j.returnDraft.time === '') return j.returnAt !== null ? { returnDay: '', returnTime: '' } : {};
  const shown = shownReturnLeg(j);
  return { returnDay: shown.day, returnTime: shown.time };
}

/**
 * The server's read and rule on exactly those keys, against the job's own journey
 * date and time, BEFORE the lazy create: a refused return must not strand a new
 * booking (a time with no date on an undated walk-in is `invalid`).
 * ⭐ board 2871 (C2a): both boxes may be left empty, never a date without a time.
 * So an emptied time is `invalid`, never a silent clear, while the date box still
 * holds a date Helen ENTERED or the STORED return's day: the screen would show a
 * day the save was about to throw away.
 */
function returnLegCauseFor(j: JobState, keys: { returnDay?: string; returnTime?: string }): ReturnLegCause | null {
  const draft = j.options.returnTrip ? j.returnDraft : null;
  if (draft !== null && draft.time === '') {
    const enteredDate = typeof draft.day === 'string' && draft.day !== '';
    const showsStoredDay = j.returnAt !== null && shownReturnLeg(j).day !== '';
    if (enteredDate || showsStoredDay) return 'invalid';
  }
  const read = readReturnLeg(keys);
  if (read.kind === 'invalid') return 'invalid';
  return read.kind === 'set' ? returnLegRefusal(read.leg, { day: j.journeyDate, time: j.journeyTime }) : null;
}

/** The quote and park doors' coded return-leg 400, as its cause (SEC-5: the code, never the words). */
function returnRefusalOf(e: unknown): ReturnLegCause | null {
  const body = e instanceof ApiError && e.status === 400 ? e.body : null;
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  return returnLegCauseOf((body as { code: unknown }).code);
}

/**
 * Accept/Override persist parity: an inquiry-sourced/reopened job posts the
 * quote to its booking; a walk-in lazy-creates the booking FIRST — but only
 * with a customer name (name-gate). A nameless Accept is an honest price
 * check: nothing persists, Done says so. 409 = stale-job mismatch (R15 A2).
 *
 * The in-flight guard lives HERE, at the write, not merely on the buttons.
 * `band` and `options` update synchronously while `quote` lands only on fetch
 * resolve, so a save mid-re-price would pair a new band with the previous
 * band's price (`band: j.band` sits next to `computedTotalCents: j.quote…`
 * below). Disabled buttons are an affordance; this is the invariant, so a
 * future caller — a new edit control, another screen — inherits it instead of
 * having to remember it.
 */
export async function persistCurrentQuote(
  store: Store,
  opts: { finalTotalCents?: number | null; note?: string | null },
  // ⛔ T651 M7 — the third `registry?` parameter is GONE, not deprecated. Its
  // only purpose was to namespace the localStorage capture key by database
  // (T414 F4); with the store retired there is no key to namespace, and an
  // unused optional parameter on the money write is exactly the kind of thing a
  // later caller passes something meaningful to and expects to be read.
): Promise<PersistOutcome> {
  const state = store.getState();
  if (quotePending(state)) return { kind: 'pending' };
  const j = state.job;
  let bookingId = j.bookingId;
  // ⭐ board 2871 - checked before the lazy create, with no POST at all.
  const returnKeys = returnLegKeys(j);
  const returnCause = returnLegCauseFor(j, returnKeys);
  if (returnCause !== null) return { kind: 'return-invalid', cause: returnCause };

  if (bookingId === null) {
    const name = j.customerName.trim();
    if (!name) return { kind: 'price-check' };
    const created = await createBookingForJob(store, j, name);
    if (created === 'email-conflict') return { kind: 'email-conflict' };
    // The capture door carries ask 805's refusal too, and for a walk-in it is
    // the door that meets it FIRST - so leaving this leg mapped to 'error' would
    // give the whole refusal the transient sentence on the commonest path.
    if (created === 'band-count-mismatch') return { kind: 'band-count-mismatch' };
    if (created === null) return { kind: 'error' };
    bookingId = created;
  }

  try {
    await postJson(`/api/bookings/${bookingId}/quote`, {
      requestJson: JSON.stringify({
        service: j.service,
        locationId: j.locationId,
        toLocationId: j.toLocationId,
        band: j.band,
        options: j.options,
        // T387 M3: paired with the /api/quote body above — the T374 gate
        // recomputes from THIS json, so a venue here but not there (or vice
        // versa) 422s every venue-priced save.
        venue: j.venue,
      }),
      // T374 gate coupling: this is the engine's OWN number (toQuoteView derives
      // totalCents = finalTotal ?? computedTotal, and the /api/quote body carries
      // no override, so the engine leaves finalTotal = computedTotal). The server
      // recompares it against computeQuote(...).computedTotal. If a future change
      // adds `override`/`estimate` to the /api/quote body, this and the recompute
      // diverge and every overridden quote false-positives 422 — the STRUCTURAL
      // pin in quote-flow.spec.tsx fails loud if that shape asymmetry appears.
      computedTotalCents: j.quote?.totalCents ?? null,
      finalTotalCents: opts.finalTotalCents ?? null,
      note: opts.note ?? null,
      // createdBy is NOT sent: the server derives the actor from the session.
      // Sending it would imply the server trusts it, which is the forgery this
      // milestone removed.
      venue: j.venue,
      // T373 M4 — the off-gazetteer place per end (the true destination, not a
      // stand-in town). The server logs a location_miss in the SAME transaction
      // as the quote, so the demand signal cannot be silently dropped the way a
      // client-side by-product writer was in the M2 rebuild.
      pickupOffGazetteer: j.pickupOffGazetteer ? j.locationLabel : null,
      dropoffOffGazetteer: j.dropoffOffGazetteer ? j.toLocationLabel : null,
      // T651 M7 (R21) — DOOR B, and it needs to be here as well as on Door A:
      // an inquiry-sourced or re-opened job never touches createBookingForJob
      // (persistCurrentQuote short-circuits when j.bookingId is already set), so
      // a count corrected on the Quote screen would otherwise reach the row on
      // walk-ins only. ⛔ TOP-LEVEL, never inside requestJson: quote-flow.spec.tsx
      // pins that json's exact six keys, because the T374 price gate recomputes
      // from it and a seventh key 422s the save.
      passengersExact: j.passengersExact,
      // T400 — the journey write-back (latest quote wins, like band/flags).
      // Omitted when the job can't reconstruct any journey: the server treats
      // absent as "don't touch", so a persist never wipes normalise's record.
      ...(() => {
        const stops = journeyStops(j);
        return stops.length > 0 ? { stops } : {};
      })(),
      // ⛔ board 2814 M6 (DD-8): NO `nursingSeries`. The tile that captured a
      // recurring schedule is gone, and the server refuses the key (400).
      // ⭐ board 2871 - TOP-LEVEL, never inside requestJson (its six keys are pinned).
      ...returnKeys,
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return { kind: 'mismatch' };
    // ⭐ board 2871 - the quote door's coded return-leg 400.
    const refusedReturn = returnRefusalOf(e);
    if (refusedReturn !== null) return { kind: 'return-invalid', cause: refusedReturn };
    // 422 = the server's price gate refused an engine-unverifiable total (T374).
    if (e instanceof ApiError && e.status === 422) return { kind: 'price-changed' };
    // 400 + the money door's code = the hand price is out of range (T1871 M6
    // review). Every OTHER 400 still falls through to 'error' on purpose: only
    // the coded one has a cause the screen can name.
    if (isQuotePriceRefusal(e)) return { kind: 'price-out-of-range' };
    // 400 + ask 805's code = the band and the count disagree (T1871 M7).
    if (isPassengerMismatchRefusal(e)) return { kind: 'band-count-mismatch' };
    return { kind: 'error' };
  }
  store.update((s) =>
    patchJob(s, {
      doneOutcome: 'saved',
      // Mirror an override into client state (M3 gate BLOCK): Done must
      // headline the AGREED money value, never the superseded engine total.
      // T1949 (ask 823): a NEW agreed price retires the notice about the old
      // one. This is the ONE write site for an agreed hand price, so it is the
      // one place that can know the announcement has been acted on.
      ...(typeof opts.finalTotalCents === 'number'
        ? { finalTotalCents: opts.finalTotalCents, wasHandQuoted: true, handPriceClearedCents: null }
        : {}),
    }),
  );
  // Badge truth after a mutation (§7.5): quoting removes an inbox item.
  void refreshInboxCount(store);
  return { kind: 'saved', bookingId };
}

/** T400 — write one operator stop-capture row (Addons per-stop fields).
 *  Pads the working set up to the touched index so a row typed before earlier
 *  rows exist cannot land on the wrong stop. Plain patch — never a priced
 *  input, never a quote re-fetch. */
export function setOperatorStop(
  store: Store,
  index: number,
  patch: Partial<OperatorStop>,
): void {
  store.update((s) => {
    const rows: OperatorStop[] = [...s.job.operatorStops];
    while (rows.length <= index) rows.push({ address: '', eircode: '' });
    rows[index] = { ...rows[index]!, ...patch };
    return patchJob(s, { operatorStops: rows });
  });
}

/** Wire shape of one journey stop (mirrors the server BookingStopInput). */
type JourneyStopWire = {
  address: string;
  addressLine?: string | null;
  eircode?: string | null;
  town?: string | null;
  /**
   * v20 (T608 M6c) — the role this builder KNOWS as it pushes the row.
   *
   * REQUIRED, unlike every other field here, and that is the point: a future
   * `stops.push` that forgets it fails the typecheck instead of silently writing
   * a role-less row that reads downstream as "written before v20". The server
   * re-narrows it against the same closed vocabulary (parseStops), so this type
   * is a discipline for this builder, never a trusted input.
   */
  role: StopRole;
};

/**
 * THE journey-stops contract (T400) — the ONE builder both persist paths use
 * (walk-in create AND quote write-back). Do not add a second stops writer:
 * two shapes drifting is how fields silently stop round-tripping.
 *
 * Statement vs record: the immutable customer statement lives in
 * `inquiries.stops` (server-side, never rewritten) and in `customerStops`
 * (display). `booking_stops` is the WORKING journey record this builder
 * feeds — latest persisted quote wins, exactly like band/flags.
 *
 * Ends reconstruct from `locationLabel ?? searchText` (mirrors
 * CustomerInfoBlock's display fallback) — NEVER the bare label: an airport
 * booking's terminal end has a null label by design (guaranteed gazetteer
 * miss), and a bare-label build would silently DROP that end on persist.
 * `address` stays the place label (the precedent match key + route joins);
 * the captured street rides `addressLine`, the resolved town rides `town`.
 *
 * Middles: Helen's captured rows when her billable count > 0, else the
 * customer statement passes through — a persist replace must never wipe
 * what normalise wrote just because Helen didn't engage capture.
 */
export function journeyStops(j: JobState): JourneyStopWire[] {
  const stops: JourneyStopWire[] = [];
  // Golf location ids are ROUTES, not towns — never write a route label into
  // the resolved-town slot.
  const townOf = (id: string | null, label: string | null, carry: string | null): string | null =>
    id !== null && j.service !== 'golf' ? label : carry;
  /**
   * T608 M6a (R3) — a golf COURSE leg is a TWO-stop journey: the origin hub and
   * the course. It used to persist ONE row whose `address` was the whole
   * composite route label ("Arklow Bay Hotel — → Druids Glen Resort") while that same
   * row's `address_line`/`town`/`eircode` described the CUSTOMER's home — one row
   * naming two different places, which no reader could resolve into ends.
   *
   * Both ends come from `locationLabel`, because the hub name exists NOWHERE
   * else in client state: GolfVariant holds the chosen hub in local `useState`
   * and only `onPickLocation(locationId, label)` ever reaches the job.
   *
   * COURSE LEGS ONLY — `courseLeg`, read off the label by the splitter, is the
   * gate, not an extra. The grid has a second golf leg shape: 3 of its 21 seeded
   * routes are AIRPORT legs, exactly one per hub, whose route NAME does not begin
   * with the arrow ('Dublin Airport ↔ Arklow Bay Hotel', 'Dublin Airport →
   * Druids Glen Resort', 'Dublin Airport → Woodenbridge Hotel'). Measure that set with
   * the splitter's own predicate — a name that does not start with the arrow —
   * never by searching for the ↔ glyph, which finds only 1 of the 3. Splitting an
   * airport leg would be wrong three ways:
   *   1. it has no course. R3 makes the hub and the COURSE first-class stops; an
   *      airport leg's second half is an airport-and-hotel route NAME. The schema
   *      already agrees — `golf_courses_public` (core/migrations.ts v6) filters on
   *      the same arrow prefix precisely to keep these rows out of the course list.
   *   2. it would assert a direction the rate contradicts. Splitting
   *      "Woodenbridge Hotel — Dublin Airport → Woodenbridge Hotel" makes the
   *      HOTEL the pickup, while the rate's own name says the journey starts at
   *      Dublin Airport. The record would carry a positive claim it cannot support.
   *   3. it reaches a CUSTOMER. The quote SMS/email and the deposit-received SMS
   *      read these stop addresses, so the pair renders as "Woodenbridge Hotel to
   *      Dublin Airport to Woodenbridge Hotel" — the hotel named twice in one
   *      clause. Pinned in test/client/journey-stops.spec.ts through the real
   *      composer, not asserted about.
   *
   * So an airport leg takes the generic single-end path below and keeps its
   * pre-M6a row byte for byte: `address` is the whole composite label (via
   * `pickupEndPlace`), and the street/town/Eircode stay the customer's captured
   * values. Same degradation for a golf job with no route label at all, or a
   * legacy/hand-typed label carrying no hub boundary — which is what keeps
   * LONE_STOP_ROLE.golf reachable and correct for the one-stop rows already in
   * the database.
   *
   * The predicate itself lives in `golfCourseEnds` (store/selectors.ts), beside
   * pickupEndPlace/dropoffEndPlace, because the CAPTURE UI has to ask the very
   * same question: DriverDetails renders no drop-off field on a course leg
   * precisely because this builder is about to null those columns. One function,
   * so the two can never drift into inviting a capture that is then discarded.
   */
  /**
   * T608 M6c (v20) — every row records the role this builder already knows.
   *
   * Both ends are optional here too, so this list can be [pickup], [dropoff] or
   * [middle] alone and all three land at position 0 — which is why the role is
   * stated at each push and never derived from the index server-side. A manual
   * service with only a VENUE emits exactly one row from the block at the end of
   * this function, and it carries 'destination', which is what makes the
   * persisted wedding unambiguous without the venue-matching heuristic.
   */
  /**
   * T906 S1 — the STATED ends win over the composite.
   *
   * `golfCourseEnds` reads the ends back out of `locationLabel`, i.e. out of
   * the rate row, which is direction-blind: it names the leg the way Owen's
   * price sheet writes it. Once the picker states a place at each end, a
   * REVERSED on-list pair (course to hotel — an explicit promise of the
   * approved design, and a real journey: a society flying home) would otherwise
   * persist backwards and TELL THE CUSTOMER they are collected at the far end.
   * Naming a pickup the customer is not at is precisely the defect board 881
   * exists to close, so the stated ends outrank the derived ones.
   *
   * The composite path stays as the fallback, unchanged, for every job written
   * before this shipped and for any whose ends could not be recovered.
   */
  const statedEnds = golfStatedEnds(j);
  const golfEnds = statedEnds ?? golfCourseEnds(j);
  const pickupPlace = golfEnds?.origin ?? pickupEndPlace(j);
  if (pickupPlace && pickupPlace.trim()) {
    stops.push({
      address: pickupPlace,
      role: 'pickup',
      addressLine: j.pickupAddressLine.trim() || null,
      eircode: j.pickupEircode.trim() || null,
      // T515 an ARRIVALS pickup is a TERMINAL, which has no town — the priced
      // town belongs to the address end. Load-bearing, not cosmetic: locationId
      // IS set on arrivals (it prices the job), so townOf would otherwise stamp
      // the customer's home town onto the airport row. Mirrors the departures
      // terminal end, whose town is null via the null-toLocationId carry.
      town: j.pickupTerminal ? null : townOf(j.locationId, j.locationLabel, j.pickupTown),
    });
  }
  const middles: JourneyStopWire[] =
    j.options.extraPickups > 0
      ? j.operatorStops
          .slice(0, j.options.extraPickups)
          .filter((s) => s.address.trim() !== '')
          .map((s) => ({ address: s.address, eircode: s.eircode.trim() || null, role: 'middle' as const }))
      : j.customerStops.map((s) => ({ address: s.address, eircode: s.eircode, role: 'middle' as const }));
  stops.push(...middles);
  // T608 M6a — on a COURSE leg the course is the golf journey's drop-off end, and
  // it carries NO street data: `address_line`/`eircode`/`town` describe where the
  // CUSTOMER was collected, which is stops[0] above (that street, town and Eircode
  // are exactly what they were before this milestone — only stops[0].address
  // changed, from the composite label to the hub). Pushed explicitly rather than folded
  // into the generic drop-off block so the course row cannot pick up a
  // drop-off-end capture field, and so that block stays byte-unchanged.
  if (golfEnds) {
    stops.push({
      address: golfEnds.destination, addressLine: null, eircode: null, town: null,
      role: 'destination',
    });
    return stops;
  }
  // T540 (516) — through the shared selector, so a hand-priced job's VENUE
  // becomes its drop-off end. Without it a wedding that now emits a pickup row
  // persists ONE stop, and both deposit alerts read a single stop as the
  // DESTINATION (`dropoff: (last ?? first)?.address`) — the customer's own
  // street would reach them as where the bus is going. Also restores the
  // "middles present => both ends present" invariant for manual services.
  let dropoffPlace = dropoffEndPlace(j);
  // AIRPORT (Captain rework 1): a walk-in airport journey has no drop-off
  // picker — the destination is the service itself. Emit it explicitly:
  // without this row, [pickup, middles...] persists end-less and the reopen
  // first/last mapping consumes the LAST MIDDLE as the drop-off end (phantom
  // Drop-off Eircode, emptied stop rows). Invariant this preserves: middles
  // present ⇒ both ends present. Inquiry journeys carry the real terminal in
  // the search text and never reach this fallback.
  //
  // T2090 - direction-aware. On an ARRIVAL the airport is already the pickup
  // (`pickupTerminal`, which `setAirportEnd` now captures on the operator path
  // rather than leaving to the lead prefill alone), so its destination is the
  // address end - the priced town, else what `arrivalAddressEndFallback` can
  // still name - never the airport a second time; and when it can name nothing
  // the row is REFUSED (null trims out below) rather than invented. The same
  // function as `airportDropoffFallback` below, so the record and the team alert
  // name the same end, or no end, for the same job (the divergence
  // docs/test-gaps.md filed at M6b; Captain rework 1 closed the empty-string arm).
  if (!(dropoffPlace && dropoffPlace.trim()) && j.service === 'airport' && stops.length > 0) {
    dropoffPlace = j.pickupTerminal ? arrivalAddressEndFallback(j) : DUBLIN_AIRPORT_LABEL;
  }
  if (dropoffPlace && dropoffPlace.trim()) {
    stops.push({
      address: dropoffPlace,
      role: 'destination',
      addressLine: j.dropoffAddressLine.trim() || null,
      eircode: j.dropoffEircode.trim() || null,
      // T515 on ARRIVALS the ADDRESS end is this one, and its resolved town is
      // the PRICED pair (which lives in the pickup slot) — the exact mirror of
      // what departures already do on stops[0]. Without this the row's town
      // stays the customer's stated carry, so an operator who CORRECTS the
      // priced town has that correction nowhere durable: the reopen fallback
      // re-resolves the customer's town and silently reverts her decision on
      // money, with the Location screen skipped so nothing shows it.
      town: j.pickupTerminal
        ? townOf(j.locationId, j.locationLabel, j.dropoffTown)
        : townOf(j.toLocationId, j.toLocationLabel, j.dropoffTown),
    });
  }
  return stops;
}

/**
 * Lazy-create the walk-in booking for the current job (callers gate on the
 * name — the shared mechanics live here so Accept and Park cannot drift).
 *
 * Returns the bookingId, `'email-conflict'` when the server refused the capture
 * because the typed address belongs to a different existing customer (T660 M5 /
 * P0-12 — a 409, and NOTHING was written), or null on any other failure.
 *
 * ⛔ THE EMAIL ON THIS BODY IS `j.operatorEmail` AND NEVER `j.customerEmail`.
 * The latter is prefilled by `sourceFromInquiry` from the PUBLIC form, so
 * sending it would launder unauthenticated input through the authed capture door
 * into `customers.email` — the one write T549 M8 exists to deny, and the exact
 * edit test/client/capture-email-laundering.spec.ts exists to red. `operatorEmail`
 * has no writer but Helen's own keystrokes.
 */
async function createBookingForJob(
  store: Store,
  j: JobState,
  name: string,
): Promise<number | 'email-conflict' | 'band-count-mismatch' | null> {
  /* ⛔ board 2814 M6 (DD-8): NO ACCOUNT ON THIS DOOR. The nursing tile was the only
     job that carried one; it is gone, and POST /api/bookings refuses an `accountId`
     key with a 400. So the T651 M7 (S21) `phone: null` for an account booking has
     nothing left to decide: every capture from here is a personal booking and
     sends the number Helen typed. The server's own S21 strip went with the key. */
  try {
    // `matched` is DECLARED here rather than discarded (P0-12): it is how the
    // server says the booking attached to a customer record already on file for
    // the typed address. The conflict the server can judge is refused above,
    // before anything is written; this is the residue it cannot (no phone, or a
    // phone nobody owns), and Done states it so the re-attach is never silent.
    const created = await postJson<{
      bookingId: number;
      matched?: 'email' | 'phone' | 'created';
      suggestedDuplicateId?: number;
    }>('/api/bookings', {
      customer: {
        name,
        phone: j.customerPhone.trim() || null,
        // Board 2089 M3 (R-2) — the country the number above is READ UNDER. The
        // door validates it (`isPhoneCountry` or 400) and treats null as "not
        // stated".
        phoneCountry: j.customerPhoneCountry || null,
        // Omitted entirely when blank: the server reads '' as "not stated" and
        // deletes it, so sending the key at all buys nothing and puts an
        // email-shaped field on every capture body for no reason.
        ...(j.operatorEmail.trim() ? { email: j.operatorEmail.trim() } : {}),
      },
      service: j.service,
      passengersBand: j.band,
      // T651 M7 (R21) — DOOR A, deliberately on the line BELOW the band it
      // belongs beside. The band is the PRICING tier and the row's
      // `passengers` is that tier's ceiling; this is the real headcount, and
      // the server writes all three. null = not stated, which the server
      // deletes rather than nulling before the wholesale cast.
      passengersExact: j.passengersExact,
      journeyDate: j.journeyDate,
      journeyTime: j.journeyTime,
      note: j.notes,
      stops: journeyStops(j),
    });
    store.update((s) =>
      patchJob(s, {
        bookingId: created.bookingId,
        suggestedDuplicateId: created.suggestedDuplicateId ?? null,
        customerMatchedBy: created.matched ?? null,
      }),
    );
    return created.bookingId;
  } catch (e) {
    // 409 on THIS route means exactly one thing (the capture handler raises it
    // for nothing else), and it is not a transport failure: the operator has a
    // decision to make and the quote is still on screen.
    if (e instanceof ApiError && e.status === 409) return 'email-conflict';
    // T1871 M7 (ask 805): a CODED 400, so both callers can name the cause. Every
    // other 400 on this door still returns null and reads as a generic failure -
    // this one is deterministic and the operator can fix it on screen.
    if (isPassengerMismatchRefusal(e)) return 'band-count-mismatch';
    return null;
  }
}

export type ParkOutcome =
  | { kind: 'parked'; bookingId: number }
  | { kind: 'name-required' }
  | { kind: 'conflict' }
  /** T660 M5 / P0-12 — same refusal as PersistOutcome's, on the other caller of
   *  createBookingForJob. Distinct from 'conflict', which is the 409 the PARK
   *  route itself raises for an already-quoted booking. */
  | { kind: 'email-conflict' }
  /** T1871 M7 (ask 805) — same refusal as PersistOutcome's, and it reaches this
   *  action by BOTH routes: the shared createBookingForJob for a walk-in, and
   *  the park door itself, which validates the pair exactly as the quote door
   *  does. A park that refuses on one route and not the other would be the same
   *  false green `park.spec.tsx`'s stubbed wire contract already produced. */
  | { kind: 'band-count-mismatch' }
  /** ⭐ board 2871 - PersistOutcome's return refusal, on the park door. */
  | { kind: 'return-invalid'; cause: ReturnLegCause }
  | { kind: 'error' };

/**
 * T409 park: save the mid-quote capture for later WITHOUT recording a quote.
 * Extends the name-to-save model — same name-gate + lazy-create as
 * persistCurrentQuote (the invariant lives at the write, not the button). No
 * quotePending guard on purpose: a park never persists a price, so there is
 * no stale band/price pair to protect against. The server pins the rest: no
 * quote_versions row, no status change, 409 on an already-quoted booking
 * (core/park-booking.ts).
 */
export async function parkCurrentQuote(
  store: Store,
  opts: { noteDraft?: string | null } = {},
): Promise<ParkOutcome> {
  const j = store.getState().job;
  let bookingId = j.bookingId;
  // ⭐ board 2871 - the persist action's pre-create check, on the park door.
  const returnKeys = returnLegKeys(j);
  const returnCause = returnLegCauseFor(j, returnKeys);
  if (returnCause !== null) return { kind: 'return-invalid', cause: returnCause };
  if (bookingId === null) {
    const name = j.customerName.trim();
    if (!name) return { kind: 'name-required' };
    const created = await createBookingForJob(store, j, name);
    if (created === 'email-conflict') return { kind: 'email-conflict' };
    if (created === 'band-count-mismatch') return { kind: 'band-count-mismatch' };
    if (created === null) return { kind: 'error' };
    bookingId = created;
  }
  // Rework 2: the screen's typed Note travels with the park (it previously
  // vanished — the field was wired to the price-recording action only). A
  // typed draft wins; otherwise the job's existing capture note stands.
  const noteDraft = opts.noteDraft != null && opts.noteDraft.trim() !== '' ? opts.noteDraft.trim() : null;
  const note = noteDraft ?? j.notes;
  try {
    await postJson(`/api/bookings/${bookingId}/park`, {
      service: j.service,
      band: j.band,
      // T651 M7 (R21) — DOOR C, on the line below the band for the same reason
      // Door A puts it there: one control captures both, and the park wire
      // carried the band alone. The reachable loss it closes: park an unpriced
      // job (Quote.tsx's canPark stays true for anything not yet quoted),
      // re-open it, type or correct the headcount, Save for later again - the
      // number vanished with no error and no banner, against a file whose own
      // invariant is "park fidelity ≡ re-open fidelity".
      //
      // ⛔ SENT UNCONDITIONALLY AND NULLABLE, exactly as the quote body sends
      // it. `null` is the honest wire for "not stated", and the server's ONE
      // reader (readPassengersExact) turns null/absent/'' into don't-touch, so
      // a blank box cannot blank a stored count. Spreading it in only when
      // non-null would make the two doors two contracts.
      passengersExact: j.passengersExact,
      options: j.options,
      venue: j.venue,
      note,
      ...returnKeys,
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return { kind: 'conflict' };
    if (isPassengerMismatchRefusal(e)) return { kind: 'band-count-mismatch' };
    const refusedReturn = returnRefusalOf(e);
    if (refusedReturn !== null) return { kind: 'return-invalid', cause: refusedReturn };
    return { kind: 'error' };
  }
  store.update((s) => patchJob(s, { bookingId, doneOutcome: 'parked' }));
  return { kind: 'parked', bookingId };
}

/** §7.3 deposit-link states. The keyless copy KEEPS the phrase the net
 *  asserts verbatim: 'Deposit links are not switched on'.
 *
 *  T660 M4: the sentence no longer says "for this demo". An unconfigured
 *  transport is a PRODUCTION fact Helen lives in at launch (the SMS vendor is
 *  blocked, the email transport is unbuilt), so "demo" would be false on the
 *  screen this milestone exists to make honest. It also stops short of "here",
 *  the word the off-channel hints use, on purpose: 'is not switched on here'
 *  is a pinned substring (send-ui.spec.tsx's getAllByText length-2 hint count,
 *  and the e2e text matchers are substring matchers), so reusing it verbatim
 *  in a BANNER would make the banner and the hint indistinguishable. */
export const DEPOSIT_KEYLESS_COPY =
  'Deposit links are not switched on. Nothing has been charged.';
export const DEPOSIT_FAILED_COPY =
  "The payment link didn't send. Nothing has been charged and the quote is safe. " +
  'Try again in a moment, or take the deposit over the phone.';

export async function sendDepositLink(store: Store): Promise<void> {
  const j = store.getState().job;
  const bookingId = j.bookingId;
  if (bookingId === null) return;
  store.update((s) => clearBookingError(s, bookingId));
  try {
    const res = await postJson<{ url: string; amountCents: number }>(
      `/api/bookings/${bookingId}/deposit-link`,
      {},
    );
    store.update((s) =>
      patchJob(s, {
        depositSent: true,
        depositUrl: res.url,
        depositAmountCents: res.amountCents,
        paymentStatus: 'deposit_sent',
      }),
    );
  } catch (e) {
    const copy =
      e instanceof ApiError && e.status === 503 ? DEPOSIT_KEYLESS_COPY : DEPOSIT_FAILED_COPY;
    store.update((s) => setBookingError(s, bookingId, copy));
  }
}

/**
 * Poll the payment state (deposit_paid detection rides the server's all-rows
 * refresh). Swallowing a TRANSIENT failure is honest: the chip just doesn't
 * advance and the next poll retries.
 *
 * Swallowing an AUTH failure is not. This runs every 4 seconds, so a cookie that
 * has gone stale — which the 24-char passcode floor guarantees will happen on the
 * next production deploy, to any tab left open on Done — turns the poll into 15
 * failed auth attempts a MINUTE from the operator's own IP. That trips the auth
 * throttle in ~80 seconds and then refuses Helen's CORRECT passcode with a 429.
 * A poll that cannot succeed must stop, not hammer.
 */
export type PollOutcome = 'ok' | 'retry' | 'stop';

export async function refreshPayment(store: Store): Promise<PollOutcome> {
  const j = store.getState().job;
  if (j.bookingId === null) return 'stop';
  try {
    const data = await getJson<{ payment: { status: string } | null }>(
      `/api/bookings/${j.bookingId}/payment`,
    );
    if (data.payment) {
      store.update((s) => patchJob(s, { paymentStatus: data.payment!.status }));
    }
    return 'ok';
  } catch (e) {
    // 401: the session is gone — retrying only burns the throttle.
    // 429: already throttled — retrying is what dug the hole deeper.
    if (e instanceof ApiError && (e.status === 401 || e.status === 429)) return 'stop';
    return 'retry'; // transient — keep the current state and try again
  }
}

// ---------------------------------------------------------------------------
// Inbox → calculator (M4)
// ---------------------------------------------------------------------------

/** The kiosk inquiry row as served by GET /api/inquiries (subset consumed). */
export type ApiInquiry = {
  inquiry_id: number;
  status: string;
  service: string;
  passengers_band: string | null;
  /**
   * ⭐ T1871 M5 (P0-4) — the customer's typed headcount. ALREADY ON THE WIRE:
   * `passengers_exact` is in `INQUIRY_COLUMNS` (core/data/inquiries-repo.ts),
   * parsed there, typed on `InquiryRow`, and `handleGetInquiries` returns the
   * rows whole. This declaration is the only thing that was missing — no server
   * change and no new endpoint.
   *
   * OPTIONAL because four specs build FULL `ApiInquiry` literals and a required
   * field would red every one at compile time; the server always sends the key.
   * ⛔ `null` means NOT CAPTURED (legacy rows, and every nursing-home crossing,
   * which collects no count by decision) — never "unverified", never a lesser
   * lead. Read it with `!= null`; `Number(null) === 0`.
   */
  passengers_exact?: number | null;
  journey_date: string | null;
  journey_time: string | null;
  name: string;
  phone_e164: string | null;
  phone_raw: string;
  email: string | null;
  pickup: string | null;
  dropoff: string | null;
  customer_eircode: string | null;
  course: string | null;
  venue: string | null;
  meet_greet: boolean;
  return_trip: boolean;
  accessible_vehicle: boolean;
  recurring: boolean;
  contact_preference: string | null;
  notes: string | null;
  /** Customer-stated extra stops (stop1..4) — display-only on every kiosk
   *  surface; NEVER a source for options.extraPickups (billable count is
   *  Helen's decision — §5.6b phantom-charge risk). */
  stops: { address: string; eircode?: string | null }[];
  pickup_eircode: string | null;
  dropoff_eircode: string | null;
  extra: Record<string, unknown>;
  /**
   * ⭐ board 2343 — the booking "Quote this" made, or null before it. ALREADY ON
   * THE WIRE (`INQUIRY_COLUMNS`); declared so the card can tell the triage route
   * which booking it showed, which is what stops a stale card ending a job its
   * operator was never warned about. OPTIONAL for the reason `passengers_exact`
   * is: hand-built fixtures predate it, and absent reads as null.
   */
  booking_id?: number | null;
};

/** Named-key reader over inquiries.extra (the extra.flight precedent):
 *  returns a trimmed non-empty string or null. */
function extraString(inq: ApiInquiry, key: string): string | null {
  const v = inq.extra?.[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * The customer's typed STREET, recovered from the composed journey end.
 *
 * The public form collects street + town as separate inputs and its submit
 * handler composes "street, town" into ONE field. Nothing then wrote the
 * street into the driver-details capture slots, so every inquiry-sourced job
 * opened that block blank and Helen retyped what the lead already gave.
 *
 * The town is stripped from the TAIL against the separately-carried
 * `extra.town` — a suffix match on a known value, NOT the comma-parse the
 * composed pickup forbids (that breaks at 2+ interior commas). When the tail
 * is not that town (a pre-T384 row with no extra.town, or hand-edited text)
 * the composed value passes through whole: less tidy, never lossy.
 *
 * Why strip at all: BookingDetail's driver line joins address_line WITH the
 * town column, so a town left inside address_line prints twice on the one
 * surface built for the driver. Stripping also lands the same shape a walk-in
 * produces, which is what the persisted record already pins.
 */
function streetOf(composed: string | null, town: string | null): string | null {
  const text = composed?.trim() ?? '';
  if (text === '') return null;
  const t = town?.trim() ?? '';
  if (t === '') return text;
  const suffix = `, ${t}`;
  // Case-insensitive: the site composes both parts from the same inputs so an
  // exact match always holds today, but a hand-edited row must not silently
  // fall through to the untidy branch.
  if (text.slice(-suffix.length).toLowerCase() !== suffix.toLowerCase()) return text;
  const street = text.slice(0, -suffix.length).trim();
  // Load-bearing, not defensive padding: a customer who leaves the street blank
  // composes to a BARE ", Greystones", which strips to nothing. Returning ''
  // there would persist an empty address line over what they actually
  // submitted. (A length precheck above would be the redundant one — slice(-n)
  // on a shorter string can never equal the longer suffix. This guard is the
  // one that does the work; lead-context.spec pins it.)
  return street === '' ? text : street;
}

/**
 * T486 — seed the driver-details address slots from what the customer typed.
 *
 * Direction mirrors the town carry and the routing key: a from-airport journey
 * carries the customer's address on the DROP-OFF end, everything else on the
 * pickup end.
 *
 * T608 M6b (R5) — the TERMINAL end now carries its designation ('T1'/'T2')
 * instead of null, because this function is what decides whether the terminal
 * `address_line` that `buildStops` writes at intake SURVIVES the first quote:
 * `journeyStops` writes `j.pickupAddressLine`/`j.dropoffAddressLine` straight
 * back to the row, so leaving the terminal end null here silently wiped the
 * designation the moment Helen priced the lead. `terminalAddressLine` is shared
 * with `buildStops` so the two writers cannot record different strings — nor
 * disagree about when recording one is UNSAFE (the two refusals live in that
 * function's docstring: never equal to the row's own place label, never on an
 * unrecognised direction).
 *
 * The pre-M6b reason for null was that duplicating the place label into
 * address_line would print the terminal twice on BookingDetail's driver line.
 * That reason is honoured, not dropped: the designation is deliberately the
 * SHORT form, so the line reads "Dublin Airport Terminal 1: T1" — the label
 * once, plus the fact a reader can key on. 'not-sure' still yields null (there
 * is no terminal to record).
 *
 * `direction` carries `extra.direction` VERBATIM rather than the boolean this
 * used to take: "not from-airport" and "no direction at all" are different
 * facts, and a designation may only be recorded when the direction is known
 * (a direction-less airport submission would otherwise land it on the
 * customer's own address row). `fromAirport` below is byte-identical to the
 * expression the one caller used to pass in.
 *
 * Both new fields are REQUIRED, deliberately: an optional `terminal` lets a
 * future call site silently restore the pre-M6b wipe, which is the exact
 * data-loss class this milestone exists to close.
 *
 * Pure by design: the strip and the direction rule are both reachable from a
 * unit test without a store or a fetch.
 */
export function inquiryAddressLines(args: {
  pickup: string | null;
  dropoff: string | null;
  town: string | null;
  /** `inquiries.extra.direction` verbatim (null on a pre-T384 or non-airport lead). */
  direction: string | null;
  /** `inquiries.extra.terminal` verbatim, service-gated by the caller. */
  terminal: string | null;
}): { pickupAddressLine: string | null; dropoffAddressLine: string | null } {
  const fromAirport = args.direction === 'from-airport';
  const line = streetOf(fromAirport ? args.dropoff : args.pickup, args.town);
  // The terminal end is whichever end the address is not, so the place label
  // this designation must not equal is that end's.
  const terminal = terminalAddressLine({
    token: args.terminal,
    direction: args.direction,
    place: fromAirport ? args.pickup : args.dropoff,
  });
  return {
    pickupAddressLine: fromAirport ? terminal : line,
    dropoffAddressLine: fromAirport ? line : terminal,
  };
}

/**
 * The venue the customer actually named. When they pick "Other / not listed"
 * the select's own literal label lands in `venue` and the typed name lands in
 * `extra.venueOther` — which had no reader, so every surface showed Helen the
 * word "Other" instead of the wedding venue.
 *
 * Reading `extra.venueOther` whenever it is present is safe without matching
 * the "Other" sentinel: the form DISABLES that input unless the select is on
 * "Other" (InquiryForm.astro), and a disabled input does not submit — so its
 * presence IS the signal. Mirrors the extra.flight precedent below.
 */
export function inquiryVenue(inq: ApiInquiry): string | null {
  const typed = inq.extra['venueOther'];
  if (typeof typed === 'string' && typed.trim() !== '') return typed.trim();
  return inq.venue;
}

/**
 * T907 S2a — the golf COLLECTION PLACE the customer named: which of our priced
 * places they set off from. This is NOT their address; the pickup/town/eircode
 * trio still carries that and still drives the driver's plane.
 *
 * Same shape as inquiryVenue above, with one hardening it does not have, and
 * the difference is deliberate. `collectionPlace` holds either a real place name
 * or the literal sentinel 'Other'; the typed name rides in
 * `extra.collectionPlaceOther`. The form disables that input unless the select
 * is on "Other", so its presence is normally the signal — but /api/inquiry is
 * PUBLIC and unauthenticated, `InquirySchema` carries no cross-field refinement,
 * and the per-service required set is enforced client-side only. So a direct
 * POST of `{collectionPlace: 'Other'}` persists, and returning the sentinel here
 * would print the word "Other" to Helen as the place we are collecting from —
 * the exact "told to be somewhere they are not" failure this field exists to
 * end. A sentinel with nothing behind it is not a place: return null.
 */
export function inquiryCollectionPlace(inq: ApiInquiry): string | null {
  const picked = extraString(inq, 'collectionPlace');
  // ⛔ THE SELECT IS AUTHORITATIVE, and the order matters. inquiryVenue reads its
  // typed companion FIRST, which is safe there because a venue is display text.
  // Here it is not: a place that IS on the list is half a rate key, so a typed
  // value must never be able to displace it. The browser cannot produce that
  // shape (the input is disabled unless the sentinel is set), but /api/inquiry is
  // public and unauthenticated and InquirySchema has no cross-field refinement,
  // so { collectionPlace: 'Arklow Bay Hotel', collectionPlaceOther: '...' } is
  // reachable by a direct POST. Reading the companion first would silently
  // discard the priced place in favour of arbitrary text.
  if (picked !== 'Other') return picked;
  // Sentinel: the typed name is the answer, and a sentinel with nothing behind
  // it is NOT a place — returning it would print the word "Other" to Helen as
  // the collection point, which is the failure this whole field exists to end.
  return extraString(inq, 'collectionPlaceOther');
}

/**
 * T485 — will this lead's town FAIL to reach the price book, so Helen must
 * quote it by hand? Drives the inbox's MANUAL QUOTE flag.
 *
 * Synchronous and pure (no fetch in a render path): it asks the curated
 * gazetteer, not the database. That makes the flag PESSIMISTIC **about the town
 * claim it makes** — a town captured into `locations` at runtime but absent
 * from the gazetteer resolves for the resolver while still showing the flag
 * here. Over-warning is the safe direction on that axis.
 *
 * It is NOT a general "this lead needs a hand quote" signal, and does not claim
 * to be: a lead with no town at all is unflagged (below) even though
 * quoteThisInquiry falls back to resolving the free-text `pickup`, which may
 * also miss. Scope is deliberately the town.
 *
 * `pricingMode` gates it to services where the TOWN actually drives the price —
 * the same condition quoteThisInquiry uses before resolving a location at all.
 * Weddings are hand-priced by definition and golf prices off hub tiles, so
 * flagging those is noise, not signal.
 *
 * A lead carrying NO town makes no claim either way and is not flagged: it
 * cannot mis-price (nothing was matched), and flagging every town-less record
 * would bury the real signal.
 */
export function inquiryNeedsManualTown(
  inq: ApiInquiry,
  pricingMode: string | null,
): boolean {
  if (pricingMode === 'manual' || inq.service === 'golf') return false;
  const town = extraString(inq, 'town');
  if (town === null) return false;
  return !isDeterministic(matchTown(town, GAZETTEER));
}

/** Load the truthful inbox (new + normalized-but-unquoted). THROWS on
 *  failure — a failed load renders error copy, never an empty inbox
 *  (silent-empty would mislead; R13 class). Refreshes the badge (§7.5).
 *  ⭐ board 2812 M3 (Q246, SC-13): THROUGH `refreshInboxCount`, never from this read
 *  alone. The badge is the inbox PLUS the Unsent pile's total, so writing this read's
 *  length undercounted every saved, unsent quote (measured on the M3 seat: the rail
 *  read 3 beside a To do whose Need you now read 12). */
export async function loadInbox(store: Store): Promise<ApiInquiry[]> {
  const data = await getJson<{ inquiries: ApiInquiry[] }>('/api/inquiries?status=inbox');
  void refreshInboxCount(store);
  return data.inquiries;
}

/**
 * DETERMINISTIC gazetteer resolution: returns a hit IFF exactly ONE row matches
 * the text exactly (case-insensitive); anything else pre-types the search
 * instead. The "unique confident hit or null" contract is what keeps the
 * never-auto-pick invariant true (state.ts pickupSearchText).
 *
 * T485 adds a SECOND deterministic pass, and only where the first returned null
 * — i.e. exactly where this used to be a dead end. It normalizes (case +
 * diacritics + punctuation/whitespace) against the curated gazetteer and, on a
 * decisive hit, re-asks the API for the CANONICAL name so the real DB row (its
 * true location_id / in_grid) is what lands. The gazetteer only decides which
 * name to ask for; the database stays the source of truth for the row.
 *
 * Why this pass exists: the DB search is ILIKE over the raw label, so a
 * fada-less 'Dun Laoghaire' matched NOTHING while its priced row sat there.
 *
 * DELIBERATELY NOT FUZZY. Only matchTown's deterministic tiers (exact /
 * normalized / declared alias) may auto-pick — they are equality, not
 * inference, and no two curated towns share a normalized form. A fuzzy hit is a
 * GUESS and must never set a price; it is offered to Helen as a labelled
 * suggestion in LocationSearch, beside the customer's verbatim text.
 */
export async function resolveLocationExact(
  text: string | null,
): Promise<{ id: string; label: string } | null> {
  if (!text || !text.trim()) return null;
  const q = text.trim();
  const hit = await exactRow(q);
  if (hit) return hit;

  const m = matchTown(q, GAZETTEER);
  if (!isDeterministic(m) || !('name' in m)) return null;
  // Guard the round-trip: an exact pass already failed for this text, so a
  // canonical equal to it would only repeat that miss.
  if (m.name.toLowerCase() === q.toLowerCase()) return null;
  return exactRow(m.name);
}

/** One /api/locations round-trip, keeping only a UNIQUE exact label match.
 *  The endpoint matches by ILIKE containment, so a query can return several
 *  rows; uniqueness is what makes the result safe to auto-pick. */
async function exactRow(q: string): Promise<{ id: string; label: string } | null> {
  const rows = await getOr<{ id: string; label: string }[]>(
    `/api/locations?q=${encodeURIComponent(q)}`,
    [],
  );
  const exact = rows.filter((r) => r.label.toLowerCase() === q.toLowerCase());
  return exact.length === 1 ? (exact[0] ?? null) : null;
}

/**
 * "Quote this" / "Resume quote": normalise the inquiry (idempotent — an
 * already-normalized inquiry returns its existing booking), resolve
 * locations exact-match-only, land EVERY inquiry field in the store, then
 * navigate to the furthest-ready screen (and re-price if it's add-ons).
 * Returns false when normalise fails (the inbox shows honest copy).
 */
export async function quoteThisInquiry(
  store: Store,
  registry: Registry,
  inq: ApiInquiry,
): Promise<boolean> {
  let norm: { bookingId: number; suggestedDuplicateId?: number; alreadyNormalized?: boolean };
  try {
    norm = await postJson(`/api/inquiries/${inq.inquiry_id}/normalise`, {});
  } catch {
    return false;
  }

  // T409 resume fidelity: an ALREADY-normalized inquiry has a booking row that
  // may carry Helen's saved (parked) edits — band, add-on flags, venue, note.
  // Re-reading only the inquiry would silently resurrect the customer's
  // ORIGINAL values over her saved work. Overlay the booking's saved fields;
  // the customerFlags/customerBand baselines still come from the INQUIRY
  // below (patchJob cannot touch them), so the red "Edited" badges keep
  // comparing against what the customer actually stated. Best-effort: a
  // failed detail read falls back to the plain inquiry prefill.
  let savedOverlay: Partial<Pick<JobState, 'band' | 'options' | 'venue' | 'notes'>> | null = null;
  if (norm.alreadyNormalized === true) {
    try {
      const detail = await getBookingDetailFull(norm.bookingId);
      const bk = detail.booking;
      savedOverlay = {
        options: {
          meetAndGreet: bk.meet_greet,
          extraPickups: bk.extra_pickups,
          returnTrip: bk.return_trip,
          lateNight: bk.late_night,
          trailer: bk.trailer,
          accessibleVehicle: bk.accessible_vehicle,
        },
        // Null booking fields keep the inquiry's value: normalise seeds the
        // note from the inquiry but never the venue, so a null here means
        // "nothing saved", not "cleared".
        ...(bk.passengers_band != null ? { band: bk.passengers_band } : {}),
        ...(bk.venue != null ? { venue: bk.venue } : {}),
        ...(bk.note != null ? { notes: bk.note } : {}),
      };
    } catch {
      /* transient — resume still works from the inquiry alone */
    }
  }

  const descriptor = serviceById(registry, inq.service);
  const mode = descriptor?.pricingMode ?? 'grid';

  // T384 extra.town is the ADDRESS end's town whichever the direction (the
  // from-airport pickup is the terminal string — a guaranteed gazetteer
  // miss), so it seeds the matcher when present. Absence = pre-T384 or
  // non-airport row: the legacy pickup-text resolve stays byte-identical.
  // Never comma-parse the composed pickup (breaks at 2+ interior commas).
  const townSeed = extraString(inq, 'town');
  // The routing key rides the ADDRESS end's Eircode (direction-aware — the
  // T384 form posts the customer's Eircode on whichever end is the address),
  // falling back to the customer's own Eircode. Suggestion-grade area hint
  // only — never a resolver.
  const fromAirport = extraString(inq, 'direction') === 'from-airport';
  const addressEndEircode = fromAirport ? inq.dropoff_eircode : inq.pickup_eircode;
  const routingKey = routingKeyOf(addressEndEircode ?? inq.customer_eircode);
  let location: { id: string; label: string } | null = null;
  let toLocation: { id: string; label: string } | null = null;
  if (mode !== 'manual' && inq.service !== 'golf') {
    location = await resolveLocationExact(townSeed ?? inq.pickup);
    if (mode === 'p2p') toLocation = await resolveLocationExact(inq.dropoff);
  }

  const options: Partial<QuoteOptions> = {
    meetAndGreet: inq.meet_greet,
    returnTrip: inq.return_trip,
    accessibleVehicle: inq.accessible_vehicle,
  };

  const prefill: InquiryPrefill = {
    inquiryId: inq.inquiry_id,
    bookingId: norm.bookingId,
    service: inq.service,
    band: inq.passengers_band,
    // T1871 M5 (P0-4): straight through to the EXISTING "Passengers (exact)"
    // box on Quote. `?? null` keeps "absent" and "0" apart, and that is still
    // the rule — but NOT for the reason this comment used to give.
    // ⛔ CORRECTED (T1871 M5 review): the column read here is
    // `inquiries.passengers_exact`, and v41 ships it with NO CHECK AT ALL,
    // DELIBERATELY — a firing CHECK on this table echoes the offending row into
    // the error DETAIL, the server log and Sentry, and a row here carries name,
    // phone, email, pickup, dropoff and three eircodes. The `> 0` CHECK is
    // v17's, on `bookings.passengers_exact`: same column name, OPERATOR-written
    // behind a login, different threat model, different answer.
    // ⭐ SO THE REFUSAL THAT MAKES 0 IMPOSSIBLE HERE IS THE APPLICATION'S, at
    // the trust boundary (`readPassengersExact`, shared/passengers.ts), where a
    // non-positive count is rejected by name without echoing the row. What a
    // truthiness fallback would break is downstream of that: it would fold a
    // legitimate absent count into the same shape as a value, and NULL means
    // NOT CAPTURED on every pre-M3 row and every nursing crossing.
    passengersExact: inq.passengers_exact ?? null,
    name: inq.name,
    phone: inq.phone_e164 ?? inq.phone_raw,
    /**
     * BOARD 2089 M3 — the country the PUBLIC form recorded (M1 writes it to
     * `inquiries.extra.phoneCountry`). It matters most on the line ABOVE: a
     * REFUSED number has no `phone_e164`, so the kiosk shows `phone_raw` — the
     * visitor's `07…` verbatim — and without this key Helen's screen would read
     * it under Ireland and say the wrong thing about why it will not send.
     * ⛔ GUARDED WITH `isPhoneCountry`, not passed through. The value arrives out
     * of a JSON column and lands on a `<select>`; a code the metadata does not
     * know would leave the control showing its first option while the state said
     * something else. Unknown reads as "not stated", which is the Ireland
     * default — the same degradation the public intake itself makes.
     */
    customerPhoneCountry: (() => {
      const stated = extraString(inq, 'phoneCountry');
      return isPhoneCountry(stated) ? stated : null;
    })(),
    email: inq.email,
    eircode: inq.customer_eircode,
    contactPreference: inq.contact_preference,
    flightNumber: extraString(inq, 'flight'),
    // Board 944 item 2. ⭐ board 2871 (Captain's ruling, C1): the re-open path
    // carries these too now (`reopenBooking` passes the detail route's
    // returnDate/returnTime/returnPassengers to sourceFromBooking), so the Return
    // row and the Return date default survive a re-open.
    returnPassengers: extraString(inq, 'returnPassengers'),
    returnDate: extraString(inq, 'returnDate'),
    returnTime: extraString(inq, 'returnTime'),
    recurring: inq.recurring,
    journeyDate: inq.journey_date,
    journeyTime: inq.journey_time,
    venue: inquiryVenue(inq),
    notes: inq.notes,
    course: inq.course,
    collectionPlace: inquiryCollectionPlace(inq),
    options,
    suggestedDuplicateId: norm.suggestedDuplicateId ?? null,
    location,
    toLocation,
    pickupText: inq.pickup,
    dropoffText: inq.dropoff,
    // T400: the statement keeps its stated eircodes (previously dropped here —
    // the one line that broke the whole round-trip).
    stops: (inq.stops ?? [])
      .filter((s) => s.address.trim() !== '')
      .map((s) => ({ address: s.address, eircode: s.eircode ?? null })),
    pickupEircode: inq.pickup_eircode,
    dropoffEircode: inq.dropoff_eircode,
    // T486: the customer's typed street, direction-mapped and de-towned, into
    // the capture slots that opened blank on every lead until now.
    // T608 M6b — the terminal token rides along, service-gated like
    // `pickupTerminal` below (that one is gated on the direction as well; here
    // the direction goes IN and `terminalAddressLine` applies it), so the
    // designation buildStops wrote at intake is re-written by the first quote
    // instead of wiped.
    ...inquiryAddressLines({
      pickup: inq.pickup,
      dropoff: inq.dropoff,
      town: townSeed,
      direction: extraString(inq, 'direction'),
      terminal: inq.service === 'airport' ? extraString(inq, 'terminal') : null,
    }),
    // The town CARRY, direction-mapped like the routing key above — so a first
    // persist of an end that stays off-gazetteer preserves the town normalise
    // wrote instead of wiping it (two-writer symmetry; code-review WARN 2).
    pickupTown: fromAirport ? null : townSeed,
    dropoffTown: fromAirport ? townSeed : null,
    // T515 the terminal, kept OFF the priced pair. `location` above still
    // resolves the address end's town (pricing + readiness byte-identical) —
    // this is purely which place the PICKUP END shows and persists. Gated on
    // the service too, so a stray `direction` extra on a non-airport lead
    // cannot invent a terminal.
    pickupTerminal: inq.service === 'airport' && fromAirport ? inq.pickup : null,
    townSeed,
    routingKey,
  };

  // T877 — the golf enquiry BUILDS the quote: the customer already named both
  // ends (collection place since T907 S2a, course since Q16), so Helen should
  // not re-pick them. Two guards, and BOTH are load-bearing:
  //
  // (a) ONLY on a resolved pair. `golfReady` (state.ts) is satisfied by the two
  //     ends being NON-NULL, not by their being PRICED — so setting them blind
  //     routes to `addons` and SKIPS the picker. Production's golf rate names
  //     are still drifted (M8a was never seeded; boards 876/638), so a blind
  //     prefill would strand Helen on add-ons holding an UNPRICED job with no
  //     picker left to fix it. Reading the pair out of the registry also keeps
  //     the standing rule that a money key is READ, never composed from two
  //     names (rates-repo listGolfPairs) — and a listed priced row is equality,
  //     not a guess, so this does not "auto-pick" in the sense state.ts forbids.
  //
  // (b) FIRST QUOTE ONLY. `savedOverlay` above restores band/options/venue/notes
  //     and deliberately NOT the golf ends or locationId, so on a RESUME Helen
  //     may already have picked a DIFFERENT pair. Re-prefilling the customer's
  //     stated pair would re-price her job and skip the one screen that would
  //     have shown her — a wrong price, not a missing one. The booking re-open
  //     path guards exactly this shape; see the note above `sourceFromBooking`.
  const golfPick = inq.service === 'golf' ? inquiryCollectionPlace(inq) : null;
  const golfDest = inq.service === 'golf' ? inq.course : null;
  const golfHit =
    norm.alreadyNormalized === true
      ? null
      : resolveGolfPair(registry.options.golfPairs ?? [], golfPick, golfDest);

  let target = 'location' as ReturnType<typeof furthestReadyScreen>;
  store.update((s) => {
    let prefilled = sourceFromInquiry(s, prefill);
    // Lands AFTER the inquiry prefill (so the immutable baselines are set) and
    // BEFORE the overlay, which must keep the last word on a resumed job.
    if (golfHit !== null) {
      prefilled = patchJob(prefilled, {
        golfPickupPlace: golfPick,
        golfDestinationPlace: golfDest,
        locationId: golfHit.locationId,
        locationLabel: golfHit.locationLabel,
      });
    }
    // The overlay lands AFTER the inquiry prefill set the immutable baselines,
    // and BEFORE the readiness routing (a saved band must count as ready).
    if (savedOverlay) prefilled = patchJob(prefilled, savedOverlay);
    target = furthestReadyScreen(prefilled.job, mode, bands(registry));
    return navigate(prefilled, target);
  });
  if (target === 'addons' || target === 'quote') void fetchQuote(store);
  // The card flips to "quoting…" server-side; re-derive the badge truth.
  void refreshInboxCount(store);
  return true;
}

/** board 2374 — what a person must do about the invoice of a job that has just
 *  been ended (the cancel sweep's `balanceAsk`). Queried in specs through these
 *  constants, never a literal, so the locked-names gate does not collect them.
 *  ⚠️ `failed` NAMES NO CAUSE, on purpose (security-review Low, round 1): it is
 *  also what an invoice already PAID at Stripe answers while our row still says
 *  `open`, where a refund is owed rather than a payment, so the copy routes a
 *  person without telling them which. */
export const CANCEL_INVOICE_NOT_CANCELLED_COPY =
  'Its invoice could not be cancelled at Stripe. Tell Alex before anyone contacts the customer.';
export const CANCEL_INVOICE_ALREADY_PAID_COPY =
  'The customer had already paid its invoice, so a refund is owed. Tell Alex.';

/**
 * board 2374 — the sentence a person must act on for a cancel's `balanceAsk`,
 * or null when there is nothing to say. Lives in the action layer so the
 * booking screen's cancel reader can reuse it.
 * ⚠️ ABSENT IS NOT "VOIDED": the server leaves the key off both when there was
 * no invoice and when its sweep threw, so absence reads as "nothing to say",
 * never as a reassurance. `voided`, `closed_unsent`, `link_deactivated` and any word
 * this screen has no copy for are null too (fail quiet, as `readBookingOutcome`).
 */
export function cancelInvoiceNote(balanceAsk: unknown): string | null {
  if (balanceAsk === 'failed') return CANCEL_INVOICE_NOT_CANCELLED_COPY;
  if (balanceAsk === 'paid_on_cancel') return CANCEL_INVOICE_ALREADY_PAID_COPY;
  return null;
}

/**
 * board 2374 — the outcome of `setInquiryStatus`.
 *   - `false`: not done.
 *   - `true`: done, with nothing a person must act on.
 *   - `{ invoiceNote }`: done, AND the ended job's invoice needs a person.
 * ⛔ TWO SUCCESS SHAPES: test success with `!== false`, never `=== true`,
 * which would miss the object.
 */
export type InquiryStatusResult = boolean | { invoiceNote: string };

/**
 * Dismiss with undo (R14): status → dismissed; undo re-posts 'new'.
 * ⭐ board 2343 — `bookingId` is the booking the CARD showed (null before "Quote
 * this"). The server refuses when the row no longer matches it, and a dismiss of
 * a card with a booking ends that job; so a success means the card was right.
 * ⭐ board 2374 — the 200 body's `balanceAsk` is read (a narrowing read, never a
 * cast), so an invoice Stripe would not void, or one already paid, reaches the
 * screen instead of ending the job in silence.
 */
export async function setInquiryStatus(
  id: number,
  status: 'dismissed' | 'new',
  bookingId: number | null,
): Promise<InquiryStatusResult> {
  let body: unknown;
  try {
    body = await postJson<unknown>(`/api/inquiries/${id}/status`, { status, bookingId });
  } catch {
    return false;
  }
  const balanceAsk =
    typeof body === 'object' && body !== null ? (body as { balanceAsk?: unknown }).balanceAsk : undefined;
  const invoiceNote = cancelInvoiceNote(balanceAsk);
  return invoiceNote === null ? true : { invoiceNote };
}

// ---------------------------------------------------------------------------
// Send UI (M6, D17b/c)
// ---------------------------------------------------------------------------

/**
 * Refresh the server-of-record recipients (phone AND email) + latest send state
 * (SEC-6/SEC-7) from the booking detail.
 *
 * Swallow-to-noop, and `serverPhoneKnown` is what says which of the two states
 * that leaves. An earlier revision of this note claimed the send section shows
 * its "honest no-number copy when the phone stays unknown" - FALSE, and falsified
 * by a bug in the same diff that wrote it. UNKNOWN is not ABSENT: on a failed or
 * still-in-flight read the flag stays false and the section shows its LOADING
 * copy, with no recipient named and no send affordance (SendQuote::targetFor
 * gates every recipient on it). The no-number copy is the KNOWN-absent case
 * only - a read that landed and carried no contact detail.
 *
 * T660 M4 (plan step 4) adds the THIRD state, and it is the one this note used
 * to hide behind "swallow-to-noop". `serverPhoneKnown` stays false on a FAILED
 * read exactly as it does on an in-flight one, there is no retry on this path,
 * and nothing re-runs it for the rest of the job - so Helen sat looking at
 * "Getting the customer's saved contact details..." indefinitely, with no send,
 * no copy affordance and no explanation. `serverDetailFailed` is what tells the
 * two apart on screen. Set in the catch (so the loading copy stops lying) and
 * CLEARED on every success, so a read that later lands retires the message
 * rather than leaving a stale failure standing over good data.
 *
 * The catch repeats the job-boundary guard for the same reason the success path
 * has one: a slow REJECTION must not paint a failure onto a different booking's
 * screen.
 */
export async function refreshBookingDetail(store: Store): Promise<void> {
  const bookingId = store.getState().job.bookingId;
  if (bookingId === null) return;
  try {
    // ⛔ THE SHARED WIRE TYPE, NOT A LOCAL ONE (T1011 M2 gate finding). This
    // read used to declare its own inline shape for the SAME endpoint, with
    // `customer` non-nullable. That duplicate was not a style problem: it hid
    // migration v33's `bookings.customer_id` relaxation from the compiler here,
    // so `detail.customer.phone_e164` below type-checked, threw on the first
    // ACCOUNT booking, and the catch turned a perfectly readable detail into a
    // permanent "contact details couldn't be loaded" with nothing to retry it.
    // `getBookingDetailFull` is the one declared reader of GET /api/bookings/:id
    // and carries `ApiBookingDetail` — so the next nullability change is a
    // compile error at THIS call site rather than a runtime throw in Helen's face.
    const detail = await getBookingDetailFull(bookingId);
    // Job-boundary guard (M6 gate RISK): a slow response must never write
    // one booking's recipient/ledger state into a DIFFERENT job's display.
    if (store.getState().job.bookingId !== bookingId) return;
    // T549 M7/M8: the email the SERVER would actually send to, resolved by the
    // SERVER's own helper rather than a second expression that merely looks
    // like it - the stored customer record first, this booking's own inquiry
    // address second. Both halves come from THIS response, so both are
    // server-of-record; what stays banned is the value in client state, which
    // `sourceFromInquiry` writes straight from the public form and which no
    // send path consults. (M7 read `customer.email` alone because the send path
    // did too; M8 taught the send path the fallback, so mirroring it here is
    // what keeps the screen honest. Diverging in either direction is a real
    // defect: naming a recipient the server will not use, or telling Helen
    // there is no email while the send would have gone through.) It absorbs the
    // absent-field case too: every older detail fixture omits both fields, and
    // absent must read as "no email", never as `undefined`.
    const recipient = resolveRecipientEmail(detail.customer?.email ?? null, detail.inquiry_email);
    store.update((s) =>
      patchJob(s, {
        // ⛔ `?? null` is KNOWN-ABSENT, not unknown. An ACCOUNT booking has no
        // customer row at all (v33), and `serverPhoneKnown: true` beside it is
        // what says so: the read LANDED and there is no number. Unknown is the
        // pre-read state (`serverPhoneKnown: false`), which is a different
        // sentence on screen; a throw here would have produced a THIRD one -
        // the failure copy - for a response that was entirely well-formed.
        serverPhone: detail.customer?.phone_e164 ?? null,
        serverPhoneKnown: true,
        // Cleared on success: a read that lands must retire an earlier failure
        // message rather than leave it standing over good data.
        serverDetailFailed: false,
        // `fromInquiry` is the provenance the send surface states on screen, so
        // the label can never disagree with the address beside it.
        customerEmail: recipient.email,
        customerEmailFromInquiry: recipient.fromInquiry,
        latestSend: detail.latest_send
          ? {
              channel: detail.latest_send.channel,
              deliveryStatus: detail.latest_send.delivery_status,
              // T660 M4 (P0-7). `?? null` absorbs the absent field: an older
              // server (or a hand-built fixture) must read as "not recorded",
              // never as `undefined` reaching the sentence.
              target: detail.latest_send.target ?? null,
            }
          : null,
      }),
    );
  } catch {
    // NOT "transient" - that word was the whole defect. Nothing retries this,
    // so the flag below is the only thing that stops the loading copy claiming
    // a read is still coming for the rest of the job.
    if (store.getState().job.bookingId !== bookingId) return;
    store.update((s) => patchJob(s, { serverDetailFailed: true }));
  }
}

/**
 * A send failure carries its KIND, not a string the caller re-reads (T549 M1).
 * 'not-configured' is the demo/config state — quiet, nothing to retry;
 * 'failed' is everything a retry or a phone call could still fix. SendQuote
 * used to recover this distinction by searching its OWN copy for the substring
 * 'not switched on', which turned a copy edit into a silent behaviour change.
 */
export type SendFailureKind = 'not-configured' | 'failed';
export type SendQuoteOutcome =
  | { ok: true }
  | { ok: false; kind: SendFailureKind; message: string };

/* T660 M4 — these four keyless sentences dropped "in this demo" (see
 * DEPOSIT_KEYLESS_COPY above for the full why, the substring constraint they
 * keep, and why they do NOT reuse the hints' word "here"). Each stays
 * distinguishable from the others, and from the off-channel hints, by its
 * SUBJECT clause: 'Text messages are' is not a substring of the singular hint
 * 'Text message is not switched on here...', which is what lets the e2e net
 * assert the banner on a screen that also carries the hint. The reassuring
 * second half of every sentence is untouched. */
export const SEND_KEYLESS_COPY =
  'Text messages are not switched on. Nothing was sent to the customer.';
export const SEND_EMAIL_KEYLESS_COPY =
  'Email is not switched on. Nothing was sent to the customer.';
export const SEND_STRIPE_KEYLESS_COPY =
  'Deposit links are not switched on, so nothing was sent. Try sending without the deposit link.';
/**
 * T879 — the key was REJECTED, which is permanent until a human changes it.
 *
 * ⛔ THE ONE THING THIS SENTENCE MUST NOT SAY IS "try again in a moment". Every
 * other failure on this path is transient and that instruction is honest for them.
 * This one fails identically on every booking, for every service, until someone
 * edits a configuration value - so "try again" is an instruction that can never
 * terminate, and on 2026-08-07 it was given roughly a dozen times across four
 * bookings while the deposit path was completely dead.
 *
 * It names no status code, no provider text and no key: SEC-5 keeps operator copy
 * to the action's own constants. It does tell Helen the two things she can act on
 * (take it by phone; this needs reporting) and one thing that saves her time
 * (retrying is pointless), which is the whole difference between this and the
 * transient sentence.
 *
 * Distinct from SEND_STRIPE_KEYLESS_COPY by SUBJECT: "not switched on" is a kiosk
 * with no key at all - a routine configuration the e2e lane runs in - whereas this
 * is a kiosk holding a key that Stripe will not accept. Same remedy for Helen,
 * completely different remedy for whoever holds the dashboard.
 */
export const SEND_STRIPE_AUTH_COPY =
  'Card payments are not working, so no payment link could be created. ' +
  'Nothing has been charged and the quote is safe. ' +
  'Trying again will not help until this is fixed, so take the deposit over the phone and report it.';
export const SEND_FAILED_COPY =
  "The message didn't send. Nothing reached the customer. Try again in a moment, or call them.";
export const SEND_LIMIT_COPY =
  'The send limit for today was reached. Try again tomorrow, or call the customer.';
export const SEND_NO_PHONE_COPY =
  'This booking has no saved mobile number, so the quote cannot be sent.';
export const SEND_NO_EMAIL_COPY =
  'This booking has no saved email address, so the quote cannot be sent.';
export const SEND_NO_QUOTE_COPY =
  'This booking has no quote yet, so nothing was sent. Price it first, then send.';
export const SEND_NO_AMOUNT_COPY =
  'This quote has no amount on it yet, so nothing was sent.';
export const SEND_DEPOSIT_PAID_COPY =
  'This booking already has a paid deposit, so nothing was sent.';
/** T549 M7: the picker asked for a channel this system cannot drive. Quiet, not
 *  retryable - re-posting the same request refuses identically - so it reads as
 *  a "not available here" state rather than "try again in a moment". */
export const SEND_UNSUPPORTED_CHANNEL_COPY =
  'That way of sending is not available here. Nothing was sent to the customer.';
/**
 * T1447 M5 - the booking is billed to an account, so it takes no card deposit.
 *
 * ⛔ IT NAMES THE REMEDY, because without one this reads as a fault. Every other
 * 'failed' sentence here describes something that went wrong; this one describes
 * the system working, so it has to say what Helen does instead or she will retry
 * a request that can never succeed. It also states that nothing was charged,
 * which is the fact she needs before she rings the home.
 *
 * No amount and no account name: this string is shown wherever a send or a link
 * was refused, and the routes that emit it deliberately refuse BEFORE reading a
 * quote, so there is no figure to be right about.
 */
export const SEND_ACCOUNT_BOOKING_COPY =
  'This booking is billed to an account, so it does not take a card deposit. ' +
  'Nothing has been charged and the booking is safe. ' +
  'The account is invoiced monthly, so add it to the invoice instead.';
/** ⭐ board 2814 M4 (DD-5, S8) - `cash_to_driver`: the run is Cash, paid to the
 *  driver on the day. Permanent unless the payer is changed on the booking. */
export const SEND_CASH_TO_DRIVER_COPY =
  'This trip is Cash, paid to the driver on the day, so it takes no deposit and no payment link. ' +
  'Nothing has been charged. If the home pays for it, change who pays on the booking.';
/** ⭐ board 2814 M7 (DD-7) - `taxi_passed`: the run was passed to a taxi firm. */
export const SEND_TAXI_PASSED_COPY =
  'This run was passed to a taxi, so it takes no deposit and no payment link. Nothing has been charged.';
/**
 * T1002 M4 - the refund was recorded and Stripe did not confirm it.
 *
 * ⛔ RE-EXPORTED FROM src/shared, NEVER RESTATED HERE. This screen and the 502
 * body server/app.ts sends are the same sentence, and they were NOT: the server
 * said "check the payment in Stripe before trying again" while this file said
 * "do not try again", on the one route where a retry is a second real refund.
 * Two copies is what made that possible, so there is now one. See the constant's
 * own docblock in shared/send-schema.ts for the rule it carries. Re-exported so
 * the screens that already read their copy off this module keep one import.
 */
export { REFUND_OUTCOME_UNKNOWN_COPY };
/**
 * board 2089 M2 - the sender cannot reach this number's country, so nothing was
 * posted to any transport.
 *
 * ⛔ RE-EXPORTED FROM src/shared FOR THE SAME REASON AS THE LINE ABOVE, and it
 * is the reason worth repeating: the route's own 409 body carries this exact
 * sentence, so a second copy here would be two strings that must agree about
 * what Helen should do next. Alex's wording, verbatim; the wording is his and
 * not a gate author's.
 */
export { SMS_UNREACHABLE_COUNTRY_COPY };

/** board 2089 rework 1 - re-exported for the same reason as the line above. */
export { SMS_UNREADABLE_NUMBER_COPY };

/**
 * The server's refusal code -> what Helen reads. Exhaustive over
 * SEND_ERROR_CODES by type, so a new refusal reason cannot ship without copy.
 * Four DISTINCT 409 reasons now read as four distinct sentences; before this
 * map, three of the four told her the booking had no mobile number.
 */
const SEND_ERROR_COPY: Readonly<Record<SendErrorCode, { kind: SendFailureKind; message: string }>> = {
  no_quote: { kind: 'failed', message: SEND_NO_QUOTE_COPY },
  no_amount: { kind: 'failed', message: SEND_NO_AMOUNT_COPY },
  no_phone: { kind: 'failed', message: SEND_NO_PHONE_COPY },
  no_email: { kind: 'failed', message: SEND_NO_EMAIL_COPY },
  deposit_paid: { kind: 'failed', message: SEND_DEPOSIT_PAID_COPY },
  sms_unconfigured: { kind: 'not-configured', message: SEND_KEYLESS_COPY },
  email_unconfigured: { kind: 'not-configured', message: SEND_EMAIL_KEYLESS_COPY },
  stripe_unconfigured: { kind: 'not-configured', message: SEND_STRIPE_KEYLESS_COPY },
  // 'failed', NOT 'not-configured'. The kiosk IS configured - it holds a key and
  // was told to use it - so the not-configured kind (which reads as a deliberate
  // switch-off, and which the keyless e2e lane depends on) would misreport a
  // broken deployment as an intentional one.
  stripe_auth_failed: { kind: 'failed', message: SEND_STRIPE_AUTH_COPY },
  unsupported_channel: { kind: 'not-configured', message: SEND_UNSUPPORTED_CHANNEL_COPY },
  // T1002 M4. 'failed' rather than 'not-configured' for the reason
  // stripe_auth_failed gives above - nothing is switched off here - but note the
  // kind is the weaker half of this entry: the MESSAGE is what makes it
  // actionable, because the generic failure copy for this kind says "try again",
  // which is the one instruction that can cost the customer a second refund.
  refund_outcome_unknown: { kind: 'failed', message: REFUND_OUTCOME_UNKNOWN_COPY },
  // T1447 M5. 'failed' rather than 'not-configured' for the reason
  // stripe_auth_failed gives above - nothing is switched off, this deployment is
  // working exactly as ruled - and, like refund_outcome_unknown, the kind is the
  // weaker half of the entry: the generic copy for this kind says "try again",
  // and a retry here is guaranteed to refuse identically, so the MESSAGE is what
  // makes it actionable.
  account_booking: { kind: 'failed', message: SEND_ACCOUNT_BOOKING_COPY },
  // ⭐ board 2814 M4. 'failed' for account_booking's reason: permanent, never "try again".
  cash_to_driver: { kind: 'failed', message: SEND_CASH_TO_DRIVER_COPY },
  // ⭐ board 2814 M7 (DD-7). 'failed' for the same reason: lifted only by Undo.
  taxi_passed: { kind: 'failed', message: SEND_TAXI_PASSED_COPY },
  // board 2089 M2. 'failed' rather than 'not-configured' for the reason
  // stripe_auth_failed gives above - nothing is switched off, the transport is
  // configured and working - and, like account_booking, the kind is the weaker
  // half of the entry: the generic copy for this kind says "try again", and a
  // retry to an unsupported country refuses identically every time, so the
  // MESSAGE is what makes it actionable.
  sms_unreachable_country: { kind: 'failed', message: SMS_UNREACHABLE_COUNTRY_COPY },
  // board 2089 rework 1. 'failed' for the reason sms_unreachable_country gives
  // above. The MESSAGE is the whole difference: this number is fixed by
  // re-keying it with its country, and "send the link by email" would leave it
  // on the record exactly as broken as it is.
  sms_unreadable_number: { kind: 'failed', message: SMS_UNREADABLE_NUMBER_COPY },
};

/** Read the refusal code off an untrusted response body (never rendered raw). */
function sendErrorCodeOf(body: unknown): SendErrorCode | null {
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  const code = (body as { code: unknown }).code;
  return isSendErrorCode(code) ? code : null;
}

/**
 * The bare send call, shared by the Done surface and the booking detail.
 *
 * SEC-6 is UNCHANGED by the channel field (T549 M7): the body still carries
 * DECISION FLAGS ONLY. `channel` picks WHICH transport, never WHO it reaches -
 * the server resolves the recipient from the stored customer record on both
 * channels and ignores any body-supplied target. No phone, address or amount
 * has ever been on this wire and none is added here.
 */
export async function sendQuoteRequest(
  bookingId: number,
  includeDeposit: boolean,
  channel: Channel,
): Promise<SendQuoteOutcome> {
  try {
    await postJson(`/api/bookings/${bookingId}/send-quote`, { includeDeposit, channel });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError) {
      const code = sendErrorCodeOf(e.body);
      if (code) return { ok: false, ...SEND_ERROR_COPY[code] };
      // No code on the body: fall back on the HTTP STATUS ONLY. Sniffing the
      // server's sentence is deliberately NOT retained (T549 M1) — there is no
      // version skew to protect against, because `npm start` rebuilds the
      // client bundle from the same tree the server runs, so a coded server
      // and this client always ship together. A codeless send-path error is a
      // route that has not been given a code, and guessing at its text is what
      // produced the wrong-copy defect in the first place.
      if (e.status === 503) return { ok: false, kind: 'not-configured', message: SEND_KEYLESS_COPY };
      if (e.status === 429) return { ok: false, kind: 'failed', message: SEND_LIMIT_COPY };
    }
    return { ok: false, kind: 'failed', message: SEND_FAILED_COPY };
  }
}

/**
 * POST the customer quote-send for the CURRENT job (Done surface).
 *
 * The channel is PER-SEND request state, chosen on the send surface and passed
 * straight through. It is deliberately never written to the store or to the
 * booking: `contactPreference` is what the CUSTOMER asked for and stays the
 * durable record of that, and the send_ledger row records what actually went
 * out. An override that persisted would quietly rewrite one of the two.
 */
export async function sendQuoteToCustomer(
  store: Store,
  includeDeposit: boolean,
  channel: Channel,
): Promise<SendQuoteOutcome> {
  const j = store.getState().job;
  if (j.bookingId === null) return { ok: false, kind: 'failed', message: SEND_FAILED_COPY };
  const outcome = await sendQuoteRequest(j.bookingId, includeDeposit, channel);
  if (!outcome.ok) return outcome;
  // ⭐ board 2812 M3 (§7.5, Q246): a send takes a saved, unsent quote off the Web
  // badge (the Unsent pile counts in it), so the badge moves now, with no poll tick.
  void refreshInboxCount(store);
  // Render the TRUE ledger state, never an optimistic 'sent' (SEC-7).
  await refreshBookingDetail(store);
  if (includeDeposit) {
    // A with-deposit send MINTED a payment link server-side: mark the
    // deposit sent so the standalone button hides (no accidental second
    // link) and the paid-poll starts (code-review M6 item 3).
    store.update((s) => patchJob(s, { depositSent: true, paymentStatus: s.job.paymentStatus ?? 'deposit_sent' }));
  }
  return { ok: true };
}

// ---------------------------------------------------------------------------
// board 2106 M5 — ASKING A BOOKING FOR ITS BALANCE (the client half of M4's
// `POST /api/bookings/:id/balance-ask`)
// ---------------------------------------------------------------------------

/**
 * board 2106 M5 — the per-booking and per-day ATTEMPT ceilings, which the route
 * answers 429 for and which carry NO `code` (server/app.ts's two `err(429, …)`
 * calls pass two arguments). Read through the STATUS, exactly as
 * `sendQuoteRequest` reads its own 429 below.
 *
 * ⛔ IT NAMES THE PHONE, because a retry cannot clear it: the bucket is a whole
 * day. Every other 'try again' sentence in this map describes something a second
 * press could fix, and this one does not.
 */
export const BALANCE_ASK_LIMIT_COPY =
  'The balance-ask limit for today was reached. Try again tomorrow, or call the customer.';
/**
 * board 2106 M5 — the 404 the ASK route gained in Wave A: an operator's slip on
 * the booking number, answered before the audit write so it cannot read as a
 * broken deployment (server/app.ts states that reasoning in full).
 *
 * ⛔ IT DOES NOT SAY "TRY AGAIN": re-posting the same id refuses identically.
 * The booking this screen is open on is gone or was never there, and the only
 * thing that helps is re-opening it from the list.
 */
export const BALANCE_ASK_NOT_FOUND_COPY =
  'That booking could not be found. Nothing was asked. Open it again from the bookings list.';
/** The last resort: a transport failure, or a status this client has no arm for.
 *  Says nothing about WHY, because it does not know (SEC-5). */
export const BALANCE_ASK_FAILED_COPY =
  'Asking for the balance failed. Nothing was asked. Try again in a moment.';

/**
 * board 2106 M5 — THE KIOSK'S PUBLIC SITE URL IS NOT FIT TO SEND A PAYER TO.
 *
 * ⛔ IT NEVER NAMES OR PRINTS THE VALUE, and neither does the 503 body it is
 * keyed from: the route's own sentence ends "Its value is deliberately not
 * shown." A misconfigured redirect target is a deployment fact, and echoing it
 * onto an operator's screen would put a URL nobody chose in front of Helen and
 * into any screenshot she sends.
 *
 * ⛔ THE REMEDY IT NAMES IS THE PHONE, NOT A RETRY AND NOT A FIX. Nothing Helen
 * can do on this screen changes the deployment's `PUBLIC_SITE_URL`, so a
 * sentence inviting her to try again would be an instruction to fail twice. It
 * is 'failed' rather than 'not-configured' for `stripe_auth_failed`'s reason one
 * screen up: this deployment is BROKEN, not deliberately switched off, and the
 * quiet not-configured tone would report the two as the same thing.
 *
 * ⚠️ MODULE-PRIVATE (board 2106 M5 rework, R12.2). It shipped `export`ed and had
 * no reader outside this file: the component spec grades it through
 * `BALANCE_ASK_COPY`, which IS exported and IS looped. An export with no importer
 * reads as a supported seam and invites one.
 */
const BALANCE_SITE_URL_COPY =
  'This kiosk cannot text a payment link until it is set up properly. ' +
  'Nothing was asked. Take the balance by phone and tell Alex.';
/**
 * board 2106 M5 — THE CHANNEL THE KIOSK POSTED IS NOT ONE THIS ROUTE DRIVES, so
 * this is a defect in the kiosk itself and not a fact about the booking.
 *
 * ⛔ UNREACHABLE FROM A RENDERED CONTROL, and the copy says so rather than
 * inviting a retry. `AskForBalance` only ever posts the value of a two-option
 * picker whose options are `BALANCE_ASK_CHANNELS`, so reaching this sentence
 * means the screen and the route disagree about the vocabulary — which a second
 * press reproduces exactly. "Try again in a moment" would be a lie on the one
 * code in this map that no amount of waiting fixes.
 *
 * ⚠️ MODULE-PRIVATE for its neighbour's reason (rework R12.2).
 */
const BALANCE_UNSUPPORTED_CHANNEL_COPY =
  'The kiosk asked in a way this booking system does not accept. ' +
  'Nothing was asked. Take the balance by phone and tell Alex.';

/** What came back from the ask. `channel` on the OK arm so the caller can state
 *  which rail was used without re-reading the picker it has already reset.
 *  `reread` on a failure the server answered by CHANGING the ask (board 2811 M8:
 *  the `text_not_sent` family), so the caller re-reads the booking. */
export type BalanceAskOutcome =
  | { ok: true; channel: BalanceAskChannel }
  | { ok: false; kind: SendFailureKind; message: string; reread?: true };

/**
 * The balance route's refusal code -> what Helen reads. EXHAUSTIVE over
 * `BalanceAskErrorCode` by type, on `SEND_ERROR_COPY`'s rule and for its reason:
 * a new refusal reason cannot ship without copy, because `tsc` refuses the
 * `Record` until it has one.
 *
 * ⚠️ THE UNION IS LONGER THAN THE PLAN'S LIST, AND THE M5 DRAFT OF THIS NOTE GOT
 * BOTH NUMBERS AND THE ATTRIBUTION WRONG. Corrected, each half re-derived:
 *   · the plan lists EIGHTEEN, not sixteen — sixteen codes get a prose bullet and
 *     the two SMS codes are named in the bullet that follows them
 *     (`docs/plans/ecmb-balance-ask-plan.md`, the `BALANCE_ASK_COPY` block);
 *   · `unsupported_channel` did NOT come from M4's security rework. It shipped in
 *     M4's own feature commit — `git show 159b6f8b -- src/shared/balance-ask-schema.ts`
 *     adds it with eighteen others; the rework (`4ebce5cc`) added exactly one
 *     code, `site_url_unconfigured`. So the plan omits it because the plan
 *     predates M4 ENTIRELY, not because of the rework.
 * ⛔ RE-DERIVE, NEVER TRUST EITHER LIST:
 *   `grep -c "^  '" src/shared/balance-ask-schema.ts`  (the union's size today)
 *
 * ⛔ THE `kind` IS THE BANNER'S TONE AND IT IS NOT DECORATION. `SendQuote.tsx`
 * paints 'not-configured' as `info` and everything else as `error`, and the
 * distinction is "deliberately switched off here" versus "something is wrong":
 * exactly three entries below are the former. `stripe_unconfigured` and
 * `sms_unconfigured` keep the spellings AND the kinds they carry on the send
 * route, so one condition reads as one thing across both surfaces.
 *
 * ⛔ EXPORTED, unlike `SEND_ERROR_COPY`, for one reason: the component spec
 * LOOPS it, so a code added without a rendered sentence reds. A copy of these
 * sentences in the spec would grade the copy against itself.
 */
export const BALANCE_ASK_COPY: Readonly<Record<BalanceAskErrorCode, { kind: SendFailureKind; message: string; reread?: true }>> = {
  // The request itself. See the constant's docblock for why this is 'failed'.
  unsupported_channel: { kind: 'failed', message: BALANCE_UNSUPPORTED_CHANNEL_COPY },
  // Already done. 'failed' rather than an 'ok'-toned note, because the ACT Helen
  // asked for did not happen; the control only offers itself on `none` and
  // `closed`, so reaching this means the screen was stale and she needs to know.
  already_asked: { kind: 'failed', message: 'The balance has already been asked for on this booking.' },
  // No way to reach this customer on the chosen rail. Each names the OTHER rail,
  // because that is the remedy and a retry on this one is not.
  no_email: { kind: 'failed', message: 'This customer has no email address. Ask by text instead.' },
  no_phone: { kind: 'failed', message: 'This customer has no mobile number on file. Ask by email instead.' },
  // ⛔ RE-EXPORTED FROM src/shared, NEVER RESTATED. The route's own 409 body IS
  // these two sentences, so a second copy here would be two strings that must
  // agree about what Helen does next; the send route's map takes them the same
  // way, and the wording is Alex's. Their docblocks carry the rule.
  sms_unreachable_country: { kind: 'failed', message: SMS_UNREACHABLE_COUNTRY_COPY },
  sms_unreadable_number: { kind: 'failed', message: SMS_UNREADABLE_NUMBER_COPY },
  // This deployment cannot do it. The two SWITCHES are 'not-configured' (quiet,
  // nothing to retry); the URL fault is not, for the reason its constant gives.
  stripe_unconfigured: { kind: 'not-configured', message: 'Payments are not set up on this kiosk. Take the balance by phone.' },
  sms_unconfigured: { kind: 'not-configured', message: 'Texting is not set up on this kiosk. Ask by email or by phone.' },
  site_url_unconfigured: { kind: 'failed', message: BALANCE_SITE_URL_COPY },
  // The killswitch: a POSTURE, so 'not-configured' is the honest tone. Nothing is
  // broken and the deposit is untouched; the balance is collected by hand.
  balance_invoicing_disabled: { kind: 'not-configured', message: 'Asking for the balance is switched off on this kiosk. Take it by phone.' },
  // The booking owes no consumer balance. Every one of these is PERMANENT for
  // this booking as it stands, so none of them says "try again"; the kind stays
  // 'failed' for `account_booking`'s reason on the send route — the generic copy
  // for 'not-configured' reads as a switch, and none of these is one.
  no_paid_payment: { kind: 'failed', message: 'This booking has no paid deposit, so there is no balance to ask for.' },
  ambiguous_payment: { kind: 'failed', message: 'This booking has more than one paid deposit. Check it before asking.' },
  no_quote: { kind: 'failed', message: "This booking's quote has no agreed total, so the balance is unknown." },
  deposit_total_mismatch: { kind: 'failed', message: 'The deposit paid does not match the quote. Check the quote before asking.' },
  nothing_to_invoice: { kind: 'failed', message: 'The deposit covers the whole price. There is no balance to ask for.' },
  account_booking: { kind: 'failed', message: 'This booking is billed to an account, which is invoiced monthly.' },
  cash_to_driver: { kind: 'failed', message: 'This trip is Cash, paid to the driver on the day. There is no balance to ask for.' },
  taxi_passed: { kind: 'failed', message: 'This run was passed to a taxi. There is no balance to ask for.' },
  booking_not_confirmed: { kind: 'failed', message: 'Only a confirmed booking can be asked for its balance.' },
  /**
   * ⛔⛔ board 2106 M5 rework (R4) — THE PRE-FLIP DEPOSIT, AND THE COPY MUST NOT
   * SAY "TRY AGAIN". Nothing about this booking changes by waiting: the deposit
   * was taken before the balance rail existed, so no system ever asked for the
   * balance and Helen herself may have taken it in cash (Q150 item 2).
   *
   * ⛔ IT SENDS HER TO CHECK BEFORE ANYTHING IS SENT, which is the whole point of
   * the refusal — the failure it prevents is BILLING A CUSTOMER TWICE, and the
   * only person who knows is Helen. It names no override, because there is none
   * on this screen and inventing one in copy would be worse than silence.
   * 'failed' rather than 'not-configured': nothing is switched off here.
   */
  paid_before_rail: {
    kind: 'failed',
    message:
      'This deposit was taken before the kiosk could ask for balances, so the balance may already have been collected. Check before anything is sent.',
  },
  // Faults: something WAS attempted and did not complete. Every one but
  // `text_not_sent_link_live` states that NOTHING WAS ASKED, which is the fact
  // Helen needs before she decides whether to press again: the route closes or
  // voids behind itself on each of those.
  stripe_failed: { kind: 'failed', message: 'Could not reach Stripe. Nothing was asked. Try again in a moment.' },
  // `reread`: this fault leaves the ask CHANGED on the server (the row voided),
  // so the screen re-reads the booking rather than trusting what it holds.
  text_not_sent: { kind: 'failed', message: 'The text could not be sent. Nothing was asked. Try again or call the customer.', reread: true },
  /**
   * ⛔⛔ board 2811 M8 (M7 security review, carried): the fourth fault is NOT
   * "nothing was asked". The text failed and its pay link could NOT be switched
   * off, so the text may have reached the customer with a live link, and a
   * phone payment or a second ask beside it could bill them twice. It sends
   * Helen to Alex first, and re-reads so the button goes and the "did not
   * finish, ask Alex" note appears.
   */
  text_not_sent_link_live: {
    kind: 'failed',
    message: 'The text may have reached the customer, and its balance link is still live. Do not ask again or take the balance by phone until Alex has checked it.',
    reread: true,
  },
  audit_failed: { kind: 'failed', message: 'Nothing was asked. Try again in a moment.' },
};

/**
 * Read the refusal code off an untrusted response body, through the SHARED
 * boundary guard rather than a local `includes` — `sendErrorCodeOf`'s shape, and
 * `isBalanceAskErrorCode` was written for exactly this call.
 */
function balanceAskErrorCodeOf(body: unknown): BalanceAskErrorCode | null {
  if (typeof body !== 'object' || body === null || !('code' in body)) return null;
  const code = (body as { code: unknown }).code;
  return isBalanceAskErrorCode(code) ? code : null;
}

/**
 * Ask this booking for its balance, on one rail.
 *
 * ⛔ THE BODY IS `{ channel }` AND NOTHING ELSE, which is the route's own
 * contract restated where it is composed: the recipient is resolved server-side
 * from the stored customer record and the amount from the persisted quote, so a
 * body that could name either could redirect a payment demand or ask a customer
 * for a figure nobody agreed. No phone, address, email or amount goes on this
 * wire, and the spec asserts the posted body key for key.
 *
 * ⛔ THE SERVER'S SENTENCE IS NEVER SNIFFED. The code is what is keyed on, and
 * the two status fallbacks below are the only codeless arms:
 *   · 429 — the throttle, which passes no code at all (both `err(429, …)` calls
 *     are two-argument). `sendQuoteRequest` reads its own 429 the same way.
 *   · 404 — Wave A's booking-not-found, likewise codeless.
 * There is no 503 fallback, and its absence is deliberate rather than an
 * omission: every 503 this route can send carries a code
 * (`sms_unconfigured`, `stripe_unconfigured`, `site_url_unconfigured`), so a
 * status-keyed guess would only ever overwrite a better answer.
 */
export async function askForBalanceRequest(
  bookingId: number,
  channel: BalanceAskChannel,
): Promise<BalanceAskOutcome> {
  try {
    await postJson(`/api/bookings/${bookingId}/balance-ask`, { channel });
    return { ok: true, channel };
  } catch (e) {
    if (e instanceof ApiError) {
      const code = balanceAskErrorCodeOf(e.body);
      if (code) return { ok: false, ...BALANCE_ASK_COPY[code] };
      if (e.status === 429) return { ok: false, kind: 'failed', message: BALANCE_ASK_LIMIT_COPY };
      if (e.status === 404) return { ok: false, kind: 'failed', message: BALANCE_ASK_NOT_FOUND_COPY };
    }
    return { ok: false, kind: 'failed', message: BALANCE_ASK_FAILED_COPY };
  }
}

// ---------------------------------------------------------------------------
// board 2249 M5 — "INVOICE THIS JOB" (the client half of M4's three routes:
// `POST …/job-invoice/preview`, `POST …/job-invoice`, `GET …/job-invoice/:rowId/pdf`)
// ---------------------------------------------------------------------------

/**
 * ⭐ EVERY CODE THE THREE JOB-INVOICE ROUTES CAN ANSWER WITH: the core's
 * `JobInvoiceRefusalCode` (server/app.ts's `JOB_INVOICE_REFUSAL` keys) plus the
 * four the routes add themselves (`unknown_field`, `no_document`, `document_void`,
 * `pdf_unavailable`). `job_invoiced` is NOT here: it is the DEPOSIT surfaces' code,
 * never one of these routes'.
 *
 * ⛔ DECLARED HERE, NOT IMPORTED, because the client imports `src/shared` and never
 * `src/core` or `src/server`. `test/client/job-invoice-ui.spec.tsx` pins this list
 * EQUAL to the server's in both directions, so a code added to the core without a
 * sentence here reds there, and `JOB_INVOICE_COPY` below is a `Record` over it, so
 * `tsc` refuses a code with no sentence.
 */
export const JOB_INVOICE_CODES = [
  'disabled',
  'stripe_unavailable',
  'invalid_bill_to',
  'not_found',
  'account_booking',
  'cash_to_driver',
  'taxi_passed',
  'not_eligible_status',
  'refunded',
  'no_job_ref',
  'no_journey_date',
  'ambiguous_payment',
  'no_quote',
  'requoted_after_deposit',
  'deposit_total_mismatch',
  'paid_before_rail',
  'in_progress',
  'stale_balance_draft',
  'paid_meanwhile',
  'replace_failed',
  'stripe_failed',
  'already_receipted',
  'deposit_link_live',
  'issue_limit',
  'not_switched_on',
  'no_customer',
  'document_mismatch',
  'refund_check_failed',
  'bill_to_customer_conflict',
  'unknown_field',
  'no_document',
  'document_void',
  'pdf_unavailable',
] as const;
export type JobInvoiceCode = (typeof JOB_INVOICE_CODES)[number];

/**
 * The code → what Helen reads. ⛔ NEVER THE SERVER'S SENTENCE (SEC-5): the routes
 * send plain prose beside every code, and it is not rendered; this map is keyed on
 * the code alone, `BALANCE_ASK_COPY`'s rule. `kind` is the banner's tone:
 * `not-configured` for a deliberate switch-off, `failed` for everything else.
 *
 * ⚠️ `already_receipted`'s entry is the UNDATED fallback. The dated sentence is
 * composed from the refusal's own `issuedOn` field (a Dublin civil date) by
 * `alreadyReceiptedCopy`, never lifted from the server's message.
 * ⚠️ `invalid_bill_to` is shown on the FIELD it names (`jobInvoiceFieldError`); this
 * entry is the banner line for a field this form does not have.
 * ⚠️ The PDF route's own codes (`no_document`, `document_void`, `pdf_unavailable`)
 * are unreachable from this client today: the PDF is a plain download link, so a
 * refusal there is the browser's failed download, not a banner. They are here
 * because the plan asks for every code the routes return (M5 step 2), so a later
 * fetch-based download needs no new copy.
 * simplified: the PDF is a link, not a fetch; upgrade trigger: a PDF refusal seen
 * in practice (a void or an unfetchable document).
 */
export const JOB_INVOICE_COPY: Readonly<Record<JobInvoiceCode, { kind: SendFailureKind; message: string }>> = {
  disabled: { kind: 'not-configured', message: 'Invoicing jobs is switched off on this kiosk. Nothing was sent.' },
  stripe_unavailable: { kind: 'not-configured', message: 'Payments are not set up on this kiosk, so it cannot make invoices. Nothing was sent.' },
  invalid_bill_to: { kind: 'failed', message: 'Check who the invoice is for and where it goes. Nothing was sent.' },
  not_found: { kind: 'failed', message: 'That booking could not be found. Nothing was sent. Open it again from the bookings list.' },
  account_booking: { kind: 'failed', message: 'This booking is billed to an account, so it goes on the monthly account invoice. Nothing was sent.' },
  cash_to_driver: { kind: 'failed', message: 'This trip is Cash, paid to the driver on the day, so it is not invoiced. Nothing was sent.' },
  taxi_passed: { kind: 'failed', message: 'This run was passed to a taxi, so it is not invoiced. Nothing was sent.' },
  not_eligible_status: { kind: 'failed', message: 'Only a quoted, confirmed or travelled job can be invoiced. Nothing was sent.' },
  refunded: { kind: 'failed', message: 'This job has a refund on record, so it cannot be invoiced here. Ask Alex.' },
  no_job_ref: { kind: 'failed', message: 'This booking has no job reference, so an invoice cannot be made for it. Ask Alex.' },
  no_journey_date: { kind: 'failed', message: 'This booking has no journey date, so the invoice cannot be dated. Nothing was sent.' },
  ambiguous_payment: { kind: 'failed', message: 'This booking has more than one paid deposit. Ask Alex before invoicing it.' },
  no_quote: { kind: 'failed', message: 'This booking has no agreed price yet, so there is nothing to invoice.' },
  requoted_after_deposit: { kind: 'failed', message: 'This booking was re-quoted after the deposit was paid. Ask Alex before invoicing it.' },
  deposit_total_mismatch: { kind: 'failed', message: 'The money paid does not match the agreed price. Ask Alex before invoicing it.' },
  paid_before_rail: {
    kind: 'failed',
    message: 'This deposit was taken before the kiosk made invoices, so the rest may already have been paid by hand. Check before anything is sent.',
  },
  in_progress: { kind: 'failed', message: 'An invoice for this job is being made right now. Wait a minute, then open the booking again.' },
  stale_balance_draft: { kind: 'failed', message: 'An earlier balance invoice for this job did not finish and may still go out. Ask Alex before invoicing this job.' },
  paid_meanwhile: { kind: 'failed', message: 'A payment came in while this was being made. Nothing new was sent. Press Check it again.' },
  replace_failed: { kind: 'failed', message: 'The earlier invoice or deposit link could not be cancelled, so nothing new was sent. Try again, or ask Alex.' },
  stripe_failed: { kind: 'failed', message: 'Stripe did not finish the invoice, so nothing new was sent. Press Check it again.' },
  already_receipted: { kind: 'failed', message: 'A paid receipt was already issued for this job. Ask Alex if it needs to be made out again.' },
  deposit_link_live: { kind: 'failed', message: 'A deposit link was sent while you were doing this. Nothing was sent. Press the same button again.' },
  issue_limit: { kind: 'failed', message: 'This job has had the most invoices allowed in one day. Nothing was sent. Try again tomorrow, or ask Alex.' },
  not_switched_on: { kind: 'not-configured', message: 'Invoice this job is not switched on yet. Nothing was sent. Ask Alex.' },
  no_customer: { kind: 'failed', message: 'This booking has no customer record, so it cannot be invoiced here. Nothing was sent.' },
  document_mismatch: {
    kind: 'failed',
    message: 'Stripe made the invoice differently from what you checked, so it was not sent. Ask Alex.',
  },
  refund_check_failed: {
    kind: 'failed',
    message: 'Stripe could not tell the kiosk whether any of this job was refunded, so no receipt was made. Try again in a moment, or ask Alex.',
  },
  bill_to_customer_conflict: {
    kind: 'failed',
    message: 'The invoice details saved at Stripe for this job belong to another record, so nothing was sent. Ask Alex.',
  },
  unknown_field: { kind: 'failed', message: 'The kiosk sent something this form does not take. Nothing was sent. Tell Alex.' },
  no_document: { kind: 'failed', message: 'That invoice is not on this job. Open the booking again.' },
  document_void: { kind: 'failed', message: 'That invoice was cancelled. Download the newer one.' },
  pdf_unavailable: { kind: 'failed', message: 'The invoice PDF could not be fetched just now. Try again in a moment.' },
};

/**
 * ⛔ Rework 1 B4: the press cancelled the old invoice at Stripe and then did not
 * make the new one (the refusal's `oldInvoiceCancelled`). This replaces the code's
 * own sentence, which may say "nothing was sent" about a job whose invoice is now
 * gone. It does not say whether the job has an invoice now: another press may have
 * made one, so the instruction is to look first.
 */
export const JOB_INVOICE_OLD_CANCELLED_COPY =
  'The old invoice was cancelled, but a new one was not made. Open the booking again and check it before trying again.';

/** A transport failure or an uncoded answer on the PREVIEW. The preview writes
 *  nothing and calls no Stripe, so "nothing was sent" is true by construction. */
export const JOB_INVOICE_PREVIEW_FAILED_COPY = 'Checking the invoice failed. Nothing was sent. Try again in a moment.';
/**
 * A transport failure or an uncoded answer on the ISSUE.
 * ⛔ IT DOES NOT SAY "NOTHING WAS SENT": a dropped connection can lose the answer
 * to a press the server finished, so the honest instruction is to look first.
 */
export const JOB_INVOICE_ISSUE_FAILED_COPY =
  'The kiosk could not tell whether the invoice was made. Open the booking again and check before trying again.';

/** `already_receipted`, dated from the refusal's own `issuedOn` ('YYYY-MM-DD',
 *  untrusted): a malformed value falls back to the undated sentence. */
export function alreadyReceiptedCopy(issuedOn: unknown): string {
  const day = typeof issuedOn === 'string' ? civilDateLabel(issuedOn, null) : '';
  return day === ''
    ? JOB_INVOICE_COPY.already_receipted.message
    : `A paid receipt was already issued for this job on ${day}. Ask Alex if it needs to be made out again.`;
}

/**
 * board 2281: the one line the booking detail shows when the server refuses every
 * press (`jobInvoice.standing`) and the remedy is Alex's. Keyed on the code, never
 * on the server's prose. A code absent here (`no_quote`, a paid receipt, a status
 * the screen already explains) shows no line.
 */
export const JOB_INVOICE_STANDING_COPY: Readonly<Partial<Record<JobInvoiceCode, string>>> = {
  refunded: 'No invoice can be made here: this job has a refund on record. Ask Alex.',
  requoted_after_deposit: 'No invoice can be made here: the job was re-quoted after the deposit was paid. Ask Alex.',
  deposit_total_mismatch: 'No invoice can be made here: the money paid does not match the agreed price. Ask Alex.',
  ambiguous_payment: 'No invoice can be made here: this booking has more than one paid deposit. Ask Alex.',
  no_job_ref: 'No invoice can be made here: this booking has no job reference. Ask Alex.',
  paid_before_rail: 'No invoice can be made here: the deposit was taken before the kiosk made invoices. Ask Alex.',
};

/** `jobInvoice.standing` (untrusted) → its line, or null. `Object.hasOwn` first, so a
 *  prototype key (`constructor`) reads as no line. */
export function jobInvoiceStandingCopy(standing: unknown): string | null {
  if (typeof standing !== 'string' || !Object.hasOwn(JOB_INVOICE_STANDING_COPY, standing)) return null;
  return JOB_INVOICE_STANDING_COPY[standing as JobInvoiceCode] ?? null;
}

/** The three fields of the form. The core names seven bill-to fields; the five
 *  address parts all land on the one "Address (optional)" box. */
export type JobInvoiceFormField = 'billTo' | 'address' | 'email';

/** `invalid_bill_to`'s `field` → which box, or null for a name this form lacks. */
function formFieldOf(field: unknown): JobInvoiceFormField | null {
  if (field === 'name') return 'billTo';
  if (field === 'addressLine1' || field === 'addressLine2' || field === 'town' || field === 'county' || field === 'eircode') {
    return 'address';
  }
  if (field === 'recipientEmail') return 'email';
  return null;
}

/** What each box says when the server refuses it. Keyed on the field, never on the
 *  server's reason, which is prose. */
export const JOB_INVOICE_FIELD_COPY: Readonly<Record<JobInvoiceFormField, string>> = {
  billTo: 'Type the name to put on the invoice, on one line, and keep it short.',
  address: 'Check the address. Keep each line short, with no hidden characters.',
  email: 'That does not look like an email address. Correct it, or leave the box empty.',
};

/** The request body, exactly the route's pinned key-set (M4 H2). */
export type JobInvoiceBody = {
  billToName: string;
  address?: { line1: string; line2?: string; town?: string; county?: string; eircode?: string };
  recipientEmail?: string;
};

/** The most address lines the one box can map onto the five address parts. */
export const JOB_INVOICE_ADDRESS_MAX_LINES = 5;

/**
 * Build the body from the three boxes, or say which box is wrong.
 *
 * ⛔ THE ADDRESS BOX IS SPLIT BY LINE, IN ORDER: line 1, line 2, town, county,
 * Eircode. Blank lines are dropped. More than five lines is refused here, because
 * there is nowhere to put a sixth without changing what was typed.
 * simplified: positional mapping, no per-part boxes; upgrade trigger: an A1 VAT
 * ruling that makes the address a legal field with named parts.
 */
export function jobInvoiceBody(
  billTo: string,
  address: string,
  email: string,
): { ok: true; body: JobInvoiceBody } | { ok: false; field: JobInvoiceFormField; message: string } {
  const body: JobInvoiceBody = { billToName: billTo };
  const lines = address.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== '');
  if (lines.length > JOB_INVOICE_ADDRESS_MAX_LINES) {
    return { ok: false, field: 'address', message: 'Use five lines or fewer for the address.' };
  }
  const [line1, line2, town, county, eircode] = lines;
  if (line1 !== undefined) {
    body.address = { line1 };
    if (line2 !== undefined) body.address.line2 = line2;
    if (town !== undefined) body.address.town = town;
    if (county !== undefined) body.address.county = county;
    if (eircode !== undefined) body.address.eircode = eircode;
  }
  if (email.trim() !== '') body.recipientEmail = email.trim();
  return { ok: true, body };
}

/** The preview as the route sends it. ⛔ It carries no email and no Stripe id. */
export type ApiJobInvoicePreview = {
  action: 'new' | 'replace' | 'receipt';
  /** board 2281 (UX7): the live document a replace cancels, as our row id, its kind,
   *  its channel and the Dublin civil date it was made ('YYYY-MM-DD', untrusted).
   *  Optional: fixtures predate it, and absent reads as the generic warning. */
  replaces?: { documentId: number; kind: string; channel: string; madeOn: string } | null;
  lines: { description: string; amountCents: number }[];
  totalCents: number;
  billTo: {
    name: string;
    address: { line1: string; line2: string | null; town: string | null; county: string | null; eircode: string | null } | null;
  };
  recipient: { summary: 'booking email' | 'the address you entered' | 'nobody, PDF only'; isOverride: boolean };
  nothingRecordedAsPaid: boolean;
};

/** The issue as the route sends it (`IssuedJobInvoice`). */
export type ApiIssuedJobInvoice = {
  action: 'new' | 'replace' | 'receipt';
  documentId: number;
  kind: 'job' | 'receipt';
  sent: boolean;
  recipient: 'booking email' | 'the address you entered' | 'nobody, PDF only';
};

export type JobInvoiceRefusal = {
  ok: false;
  code: JobInvoiceCode | null;
  kind: SendFailureKind;
  message: string;
  /** Set only on `invalid_bill_to` that names one of this form's boxes. */
  field: JobInvoiceFormField | null;
  /** Rework 1 B4: this press cancelled the old invoice before it was refused. */
  oldInvoiceCancelled: boolean;
};

function isJobInvoiceCode(v: unknown): v is JobInvoiceCode {
  return typeof v === 'string' && (JOB_INVOICE_CODES as readonly string[]).includes(v);
}

/** An `ApiError` (or anything else) → Helen's words. The body is untrusted: only
 *  `code`, `field` and `issuedOn` are read, and none of them is rendered raw. */
function jobInvoiceRefusalOf(e: unknown, fallback: string): JobInvoiceRefusal {
  const body = e instanceof ApiError && typeof e.body === 'object' && e.body !== null ? (e.body as Record<string, unknown>) : {};
  const code = isJobInvoiceCode(body['code']) ? body['code'] : null;
  if (code === null) return { ok: false, code: null, kind: 'failed', message: fallback, field: null, oldInvoiceCancelled: false };
  const field = code === 'invalid_bill_to' ? formFieldOf(body['field']) : null;
  const oldInvoiceCancelled = body['oldInvoiceCancelled'] === true;
  const message = oldInvoiceCancelled
    ? JOB_INVOICE_OLD_CANCELLED_COPY
    : code === 'already_receipted'
      ? alreadyReceiptedCopy(body['issuedOn'])
      : field !== null
        ? JOB_INVOICE_FIELD_COPY[field]
        : JOB_INVOICE_COPY[code].message;
  return { ok: false, code, kind: oldInvoiceCancelled ? 'failed' : JOB_INVOICE_COPY[code].kind, message, field, oldInvoiceCancelled };
}

/** What pressing "Yes, send it" would do. Writes nothing, calls no Stripe. */
export async function previewJobInvoiceRequest(
  bookingId: number,
  body: JobInvoiceBody,
): Promise<{ ok: true; preview: ApiJobInvoicePreview } | JobInvoiceRefusal> {
  try {
    const res = await postJson<{ preview: ApiJobInvoicePreview }>(`/api/bookings/${bookingId}/job-invoice/preview`, body);
    return { ok: true, preview: res.preview };
  } catch (e) {
    return jobInvoiceRefusalOf(e, JOB_INVOICE_PREVIEW_FAILED_COPY);
  }
}

/** Make (and, when there is a recipient, send) the whole-job document. */
export async function issueJobInvoiceRequest(
  bookingId: number,
  body: JobInvoiceBody,
): Promise<{ ok: true; issued: ApiIssuedJobInvoice } | JobInvoiceRefusal> {
  try {
    const res = await postJson<{ issued: ApiIssuedJobInvoice }>(`/api/bookings/${bookingId}/job-invoice`, body);
    return { ok: true, issued: res.issued };
  } catch (e) {
    return jobInvoiceRefusalOf(e, JOB_INVOICE_ISSUE_FAILED_COPY);
  }
}

/** The authenticated PDF route for one of this booking's documents. ⛔ Always this
 *  server's own path: the PDF is streamed through it, never a Stripe URL (H12). */
export function jobInvoicePdfHref(bookingId: number, documentId: number): string {
  return `/api/bookings/${String(bookingId)}/job-invoice/${String(documentId)}/pdf`;
}

/**
 * ⭐ Rework 2 (delta 8): a whole-job History row IN WORDS. The stored `detail` stays
 * ids and booleans (`job invoice #7; sent=false; recipient_override=false`, written
 * by core/job-invoice.ts, append-only); this reads that exact grammar and nothing
 * looser, and returns null for anything else, which the row then shows verbatim.
 * `sent=false` reads "not emailed", which is true of both ways it happens (no email
 * on the booking, or Stripe not taking the send).
 */
const JOB_HISTORY_FLAGS = '; sent=(true|false); recipient_override=(true|false)$';
const JOB_HISTORY_GRAMMAR: Readonly<Record<string, RegExp>> = {
  job_invoice_issued: new RegExp(`^job invoice #(\\d+)${JOB_HISTORY_FLAGS}`),
  job_invoice_replaced: new RegExp(`^replaced (balance|job) invoice #(\\d+) with job invoice #(\\d+)${JOB_HISTORY_FLAGS}`),
  job_receipt_issued: new RegExp(`^receipt #(\\d+)${JOB_HISTORY_FLAGS}`),
};
export function jobInvoiceHistoryWords(kind: string, detail: string): string | null {
  const grammar = JOB_HISTORY_GRAMMAR[kind];
  const m = grammar === undefined ? null : grammar.exec(detail);
  if (m === null) return null;
  const [sent, override] = m.slice(-2);
  const how =
    sent === 'false'
      ? 'not emailed'
      : override === 'true'
        ? 'emailed to the address typed in'
        : 'emailed to the email on this booking';
  if (kind === 'job_invoice_replaced') {
    const old = m[1] === 'balance' ? `the balance invoice #${m[2]!}` : `invoice #${m[2]!}`;
    return `invoice #${m[3]!} replaces ${old}, ${how}`;
  }
  return `${kind === 'job_receipt_issued' ? 'receipt' : 'invoice'} #${m[1]!}, ${how}`;
}

/**
 * ⭐ board 2811 M7 fix pass (ux Low): WHY A BALANCE ASK WAS WITHDRAWN, IN WORDS, at
 * RENDER time only. The stored `balance_ask_withdrawn` detail stays the bare cause
 * token (append-only; ids and tokens, H12). A CLOSED table over every cause the
 * code writes: the reconcile's `BalanceAskReconcileCause`, the scheduler's
 * `FireTimeVoidCause` and the send path's `mint_failed` / `send_failed`;
 * `test/client/booking-detail-balance.spec.tsx` reads those three declarations out
 * of the source and reds when a cause has no words here. An unknown token returns
 * null and the row shows it as stored.
 * ⭐ board 2811 M8 (ux Med): `balance_ask_moved`'s detail, the reconcile's
 * `YYYY-MM-DD HH:MM to YYYY-MM-DD HH:MM` (two Dublin date-times), reads in words.
 * ⭐ board 2811 rework part 2c (Captain items 8 and 15): in the balance sentences'
 * own shape and year rule (`civilDayAtTime`), never the comma shape
 * (`civilDateLabel`) `dublinDayAtTime` keeps out of copy. The stored detail is
 * unchanged, so every existing row renders; any other shape returns null.
 */
export const BALANCE_ASK_WITHDRAWN_CAUSE_WORDS: Readonly<Record<string, string>> = {
  post_attach: 'the booking changed while it was being asked',
  requote: 'the booking was re-quoted',
  account_crossing: 'the booking moved to an account',
  account_resave: 'the booking is billed to an account',
  sweep: 'the booking changed, so the automatic check withdrew it',
  reschedule: 'the date was changed and the booking could no longer be asked',
  no_journey_date: 'the booking has no journey date',
  journey_started: 'the journey had already started',
  fire_time_check: 'the booking could no longer be asked when it was due',
  refunded: 'the deposit was refunded',
  mint_failed: 'the payment link could not be made',
  send_failed: 'the text could not be sent',
};
export function balanceAskHistoryWords(kind: string, detail: string): string | null {
  if (kind === 'balance_ask_moved') {
    const m = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}) to (\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})$/.exec(detail);
    if (m === null) return null;
    const from = civilDayAtTime(m[1]!, m[2]!);
    const to = civilDayAtTime(m[3]!, m[4]!);
    return from === '' || to === '' ? null : `${from} to ${to}`;
  }
  if (kind !== 'balance_ask_withdrawn') return null;
  return Object.hasOwn(BALANCE_ASK_WITHDRAWN_CAUSE_WORDS, detail) ? BALANCE_ASK_WITHDRAWN_CAUSE_WORDS[detail]! : null;
}

// ---------------------------------------------------------------------------
// T524 — the manual copy route (Helen pastes into WhatsApp herself)
// ---------------------------------------------------------------------------

export const COPY_FAILED_COPY =
  'Getting the message failed. Try again in a moment, or read the details off the screen.';
export const COPY_LIMIT_COPY =
  'The copy limit for today was reached. Try again tomorrow, or call the customer.';
/** The Done surface only: a re-price is in flight, so the persisted total the
 *  server would compose from may differ from the one on Helen's screen. */
export const COPY_REPRICING_COPY =
  'The price is still updating. Copy the message again in a moment.';
export const COPY_CLIPBOARD_COPY =
  'Could not reach the clipboard. Read the details off the screen instead.';
/** Shown ONLY when the copied text carries a live payment link (H-12). */
export const COPY_PAYMENT_LINK_CAUTION =
  'That text has a live payment link and personal details in it. Check you are pasting into the right chat.';

export type CopyQuoteOutcome =
  | { ok: true; message: string; depositIncluded: boolean }
  | { ok: false; message: string };

/**
 * Ask the server for the customer-facing quote message, so Helen can paste it.
 *
 * Shaped like `previewTeamMessage` (a discriminated union carrying operator copy
 * rather than a status code, SEC-5) but the hazard is genuinely different, and
 * the difference is worth stating because it changes what the caller must guard.
 * `previewTeamMessage` composes from CLIENT state, so a re-price in flight can
 * pair a new band with the previous band's price. This composes SERVER-side from
 * the persisted quote row, so it can only ever return a price that was really
 * quoted - the wrong price is unreachable here.
 *
 * The in-flight guard on the Done surface therefore earns its place for a
 * different reason, and it is a real one: Helen must not copy a message whose
 * total differs from the one on the screen in front of her. Persisted-but-stale
 * is still a number she did not mean to send.
 *
 * The guard lives in the CALLER, not here: Done.tsx holds no store, so App.tsx
 * supplies an already-guarded callback (see its `<Done>` render).
 */
export async function copyQuoteMessage(bookingId: number): Promise<CopyQuoteOutcome> {
  try {
    const res = await postJson<{ message: string; depositIncluded: boolean }>(
      `/api/bookings/${bookingId}/copy-quote-message`,
      {},
    );
    return { ok: true, message: res.message, depositIncluded: res.depositIncluded === true };
  } catch (e) {
    if (e instanceof ApiError) {
      // The route reuses SEND_ERROR_CODES for its three 409s, so their operator
      // copy is already written and already exhaustive under `tsc`. Reusing the
      // map rather than writing three more sentences also keeps the two routes
      // saying the same thing about the same booking - "no quote yet" must not
      // read one way on Send and another on Copy.
      const code = sendErrorCodeOf(e.body);
      if (code) return { ok: false, message: SEND_ERROR_COPY[code].message };
      if (e.status === 429) return { ok: false, message: COPY_LIMIT_COPY };
    }
    return { ok: false, message: COPY_FAILED_COPY };
  }
}

// ---------------------------------------------------------------------------
// T660 M3 - the PAYMENT LINK copy row (P0-3/P0-4/P0-5)
// ---------------------------------------------------------------------------

/**
 * The copy for a refusal that has no more specific reading.
 *
 * NOT `DEPOSIT_FAILED_COPY`, which is the same shape and one word wrong: it
 * opens "The payment link didn't send", and this action never sends anything -
 * that is the whole of P0-3. A sentence implying a send was attempted is exactly
 * the false claim the copy route's own labels were rewritten to avoid.
 */
export const COPY_LINK_FAILED_COPY =
  'The payment link could not be created. Nothing has been charged and the quote is safe. ' +
  'Try again in a moment, or take the deposit over the phone.';

/**
 * T879 — THE CLIPBOARD FAILED, which is a DIFFERENT EVENT from the server refusing.
 *
 * ⛔ WHY THIS CONSTANT EXISTS, measured rather than imagined. Until now one sentence
 * covered both halves of this action: the server refusing to mint, and the clipboard
 * write failing after a successful mint. Its wording - "could not be COPIED" -
 * described only the second. On 2026-08-07 the live kiosk's Stripe key was rejected
 * on every booking, and the screen said the link "could not be copied", so the
 * investigation began at the clipboard and at browser permissions while the actual
 * fault was a 401 from Stripe. One sentence for two opposite causes, worded for the
 * rarer one, is what cost that morning.
 *
 * The split is by WHICH HALF FAILED, and each half now says what it did:
 *   - server refused  -> COPY_LINK_FAILED_COPY   ("could not be CREATED")
 *   - clipboard wrote -> this constant           ("could not be COPIED")
 *   - key rejected    -> SEND_STRIPE_AUTH_COPY   (permanent; do NOT retry)
 *
 * "The link was created" is load-bearing and not reassurance: it tells Helen the
 * mint SUCCEEDED, so pressing the button again is a retry of the clipboard alone
 * and cannot mint a second link or sweep the one that now exists.
 */
export const COPY_LINK_CLIPBOARD_FAILED_COPY =
  'The link was created but could not be copied to the clipboard. ' +
  'Nothing has been charged and the quote is safe. ' +
  'Try the button again, or take the deposit over the phone.';
/**
 * The UNCODED 409s, which share one remedy and it is the OPPOSITE of "try again".
 *
 * M1's route refuses two states without a code: the quote was re-priced since the
 * link was minted (the newest row's amount no longer matches), and there is no
 * reusable live link at all. Both are decided from rows, both are permanent until
 * something changes, and in both the only thing that helps is issuing a new link
 * through the send path - the one path allowed to mint-and-sweep. Telling Helen
 * to "try again in a moment" here would put a wrong instruction on the money
 * path, which is the defect SEND_ERROR_COPY was written to end.
 */
export const COPY_LINK_STALE_COPY =
  'There is no live payment link to copy for this price. Send the quote with the deposit link to issue a new one.';
/**
 * H-12 for the BARE link, and deliberately not COPY_PAYMENT_LINK_CAUTION.
 *
 * That sentence says the copied text has "a live payment link and personal
 * details in it", which is true of the composed message and false here: this
 * clipboard write is a URL and nothing else. Same hazard (the link is multi-use
 * and never expires, so a wrong-chat paste cannot be undone), same remedy, one
 * fewer claim.
 */
export const COPY_LINK_CAUTION =
  'That is a live payment link. Check you are pasting into the right chat.';

/**
 * The URL, handed back to the caller and NEVER to the store.
 *
 * `copyQuoteMessage`'s shape, with the message replaced by the url: the action
 * fetches, the COMPONENT writes the clipboard. That split is what lets
 * CopyAffordances' unmount guard apply - an action that wrote the clipboard
 * itself could not be skipped once the component was gone, and the OS clipboard
 * would end up holding another customer's payable URL with nothing on screen
 * having said so.
 *
 * `reused` is deliberately dropped from M1's response: nothing on this screen
 * says whether the link is fresh, and a value the UI does not render is a value
 * that can go stale silently. P0-4 is a SERVER property, pinned server-side
 * (test/server/app.spec.ts, POST /api/bookings/:id/payment-link); the client's
 * whole contribution to it is calling THAT route rather than the re-minting one.
 */
export type CopyLinkOutcome =
  | { ok: true; url: string }
  | { ok: false; message: string };

/**
 * Ask the server for this booking's live deposit URL so Helen can paste it.
 *
 * ⛔ THE ROUTE IS THE POINT. `/payment-link` (T660 M1) reuses an existing live
 * link and refuses rather than falling through to mint; `/deposit-link` mints a
 * new one and sweeps the old, which would kill a link the customer may already
 * be holding. That second route still exists and still has its contract-lock
 * tests - it must never become this action's target. THAT is P0-4 on the client
 * side, and payment-link-copy.spec.tsx pins it by asserting the path.
 *
 * THE 503 IS READ BEFORE THE CODE, and the order is load-bearing. M1's route
 * sends `stripe_unconfigured` with its 503, and that code is a member of
 * SEND_ERROR_CODES, so a code-first lookup would render
 * SEND_STRIPE_KEYLESS_COPY: "...so nothing was sent. Try sending without the
 * deposit link." Both halves are wrong here - nothing was being sent, and there
 * is no send to retry without. A keyless kiosk is a routine configuration (it is
 * the one the e2e lane runs in), so this is the branch Helen meets most, and it
 * gets the phrase the net asserts verbatim.
 */
export async function copyPaymentLink(bookingId: number): Promise<CopyLinkOutcome> {
  try {
    const res = await postJson<{ url: string; amountCents: number; reused: boolean }>(
      `/api/bookings/${bookingId}/payment-link`,
      {},
    );
    return { ok: true, url: res.url };
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 503) return { ok: false, message: DEPOSIT_KEYLESS_COPY };
      if (e.status === 429) return { ok: false, message: COPY_LIMIT_COPY };
      // The CODED 409s (no_quote / no_amount / deposit_paid) reuse the send
      // path's sentences for the same reason copyQuoteMessage does: "this
      // booking already has a paid deposit" must not read one way on Send and
      // another here.
      const code = sendErrorCodeOf(e.body);
      if (code) return { ok: false, message: SEND_ERROR_COPY[code].message };
      if (e.status === 409) return { ok: false, message: COPY_LINK_STALE_COPY };
    }
    return { ok: false, message: COPY_LINK_FAILED_COPY };
  }
}

/** §7.3 quiet info copy — em-dash-free; the paired whatsapp-preview edit
 *  asserts this wording. */
export const WA_NOT_CONFIGURED_COPY =
  "WhatsApp isn't set up on this device. Messages can't be sent from here.";
export const ALERT_FAILED_COPY =
  "The alert didn't send. Try again in a moment.";

/**
 * ⭐ T1949 (board ask 824) — the compose door's refusal, in Helen's words.
 *
 * The same idiom as `BAND_COUNT_MISMATCH_COPY` (the save doors' copy in
 * Quote.tsx) and a DIFFERENT sentence, because a different thing happened and a
 * different act fixes it. The save doors say nothing was saved; here nothing was
 * COMPOSED, and the fix is the band or a save — ⛔ never "correct the number of
 * passengers", which changes nothing this route reads and would re-derive the
 * band further from the saved one.
 *
 * ⚠️ IT QUOTES NEITHER VALUE, matching the server's own rule: she has both
 * numbers in front of her; what she does not have is the fact that they
 * disagree. ⚠️ ASCII HYPHENS, no band literal, no euro literal - check-copy.sh
 * scans all of src/client for all three.
 */
export const TEAM_ALERT_BAND_COUNT_MISMATCH_COPY =
  'The passenger band and the number of passengers do not match, so no alert was composed. ' +
  'Correct the band, or save the booking, then preview again.';

/** Build the /api/notify/preview payload from the job (the server prefers
 *  the durable booking row when bookingId is present — bug-a two-layer).
 *
 *  Same in-flight guard as the save path, and for the same reason: this payload
 *  pairs `paxBand: j.band` (synchronous) with `quotedTotalCents:
 *  j.finalTotalCents` (only on resolve), so previewing mid-re-price would put a
 *  NEW band next to the PREVIOUS band's price into a WhatsApp message to Helen
 *  and Owen. A wrong price read off a phone is the same harm as a wrong price
 *  in the database. */
export async function previewTeamMessage(
  store: Store,
  customerName: string,
  customerPhone: string,
): Promise<
  { ok: true; message: string } | { ok: false; message: string; blocked?: true }
> {
  const state = store.getState();
  if (quotePending(state)) {
    return { ok: false, message: 'The price is still updating. Preview again in a moment.' };
  }
  const j = state.job;
  try {
    const res = await getPreview(j, customerName, customerPhone);
    return { ok: true, message: res.message };
  } catch (e) {
    /**
     * ⭐ T1949 (board ask 824) — THE NAMED REFUSAL SURVIVES THE CATCH. This was
     * a bare `catch {}`, so ask 805's code was swallowed and every failure came
     * out as one generic sentence; the screen could not tell "the server
     * refused to compose this" from "the network blinked".
     *
     * ⛔ `blocked` IS WHAT HIDES SEND, and it is scoped to this refusal ON
     * PURPOSE. TeamAlert discards the composed message when it is set, because
     * a message already in the box would otherwise stay sendable after the
     * server refused to build a new one. A transient failure must NOT do that -
     * it would throw away Helen's edits for a blink. The general
     * stale-message-after-any-failure case is filed in docs/test-gaps.md; it is
     * a real defect and it is not what 824 ruled, so it is not widened here.
     *
     * Off the CODE, never the sentence (SEC-5), through the same predicate the
     * three save doors' callers use.
     */
    if (isPassengerMismatchRefusal(e)) {
      return { ok: false, blocked: true, message: TEAM_ALERT_BAND_COUNT_MISMATCH_COPY };
    }
    return { ok: false, message: 'Building the preview failed. Check the name and phone, then try again.' };
  }
}

/**
 * T608 M6b (R5) — the team/driver alert's drop-off end, with the airport
 * fallback `journeyStops` has always had and this payload never did.
 *
 * Deliberately NOT `dropoffEndPlace`: see the long note at the call site. The
 * first expression is byte-identical to the pre-M6b one, so every non-airport
 * service is inert BY CONSTRUCTION rather than by guard analysis. Emptiness is
 * tested the same way `journeyStops` tests it (trim, not just null), because a
 * cleared search box holds '' and '' renders as a blank line just as null does.
 *
 * `pickupTerminal` decides the direction. It is set from `inq.pickup` on an
 * arrivals LEAD (actions.ts, quoteThisInquiry), by the T515 arrivals reopen and,
 * since T2090, by `setAirportEnd` when Helen presses "Coming from the airport"
 * or a terminal on the Location screen's airport tab. Before T2090 it was a
 * PROXY: the screen had no direction control, so every kiosk walk-in was null
 * and a walk-in that was really an ARRIVALS job got the airport named as its
 * DESTINATION here (measured through formatDriverMessage: '📍 Kilcoole /
 * 🏁 Dublin Airport' for a job collecting from the airport). What remains is a
 * DEFAULT, not a proxy: a walk-in with no press is a departure until she says
 * otherwise, and an arrivals lead whose optional pickup text was omitted still
 * seeds null (the residual docs/test-gaps.md
 * `airport-fallback-arrivals-divergence` keeps).
 *
 * On an arrival the drop-off is the ADDRESS end - the priced town, else what
 * `arrivalAddressEndFallback` can still name, else nothing (no finish line) -
 * never the airport a second time. The same function as the `journeyStops`
 * fallback, so the record and this alert name the same end, or no end, for the
 * same job; the pins are in test/client/wedding-pickup-alert.spec.ts.
 */
function airportDropoffFallback(j: JobState): string | null {
  // T906 S1 — GOLF REACHES ITS DROP-OFF HERE AND NOWHERE ELSE ON THIS PAYLOAD.
  // This is the ONE end that does NOT go through `dropoffEndPlace`, so it does
  // not come along for free with the selector change, and leaving it out is a
  // live regression rather than a cosmetic gap: once the picker states a pickup,
  // `pickupEndDisplay`'s golf branch stops firing (its `end === j.locationLabel`
  // conjunct goes false), so 📍 narrows from the whole composite to the pickup
  // place — and with 🏁 still empty, `driver-message.ts` omits the drop-off line
  // entirely and the COURSE would vanish from Helen and Owen's WhatsApp.
  if (j.golfDestinationPlace !== null) return j.golfDestinationPlace;
  const end = j.toLocationLabel ?? j.dropoffSearchText;
  if (end !== null && end.trim() !== '') return end;
  return j.service === 'airport' ? (j.pickupTerminal ? arrivalAddressEndFallback(j) : DUBLIN_AIRPORT_LABEL) : null;
}

function getPreview(
  j: JobState,
  customerName: string,
  customerPhone: string,
): Promise<{ message: string }> {
  return postJson<{ message: string }>('/api/notify/preview', {
    booking: {
      ...(j.bookingId !== null ? { bookingId: j.bookingId } : {}),
      service: j.service ?? '',
      // T515 the server takes this VERBATIM (handleNotifyPreview) — the durable
      // booking row only backs venue + the add-on flags, so this is the only
      // thing standing between an arrivals lead and a team alert naming the
      // customer's home town as the pickup.
      //
      // T660 M6 (SC-7, R1) — the wrap is now `pickupEndDisplay`, the shared
      // selector that renders the BARE hub boundary as the arrow on a PICKED
      // golf route and leaves everything else on the pre-M6 rendering. This
      // payload is an OPERATOR-display path, not a persist or match one:
      // handleNotifyPreview reads these keys, hands them to formatDriverMessage
      // and returns the composed text (no write, no lookup), and that text is the
      // WhatsApp Helen and Owen read. So the 3 airport legs stop reaching the
      // team alert em-dashed — while a typed pickup carrying a lone dash still
      // reaches the driver byte for byte (R1: the unconditional wider collapse
      // rewrote those too).
      //
      // T608 M6a (ux-review) — formatRouteLabel, the same wrap the two other
      // DISPLAY readers of `pickupEndPlace` already apply (store/selectors'
      // journeySummary at selectors.ts, and CustomerInfoBlock). Those two plus
      // this one are every `pickupEndPlace` reader that RENDERS; measured with
      // `grep -rn pickupEndPlace src --include='*.ts' --include='*.tsx'`, not
      // recalled — and all three now share the ONE gated selector rather than
      // three copies of the wrap. (Quote.tsx calls `displayRouteLabel` on a rate
      // LINE-ITEM label, and BookingDetail.tsx picks between `displayRouteLabel`
      // and `formatRouteLabel` PER STOP, collapsing only an address that
      // re-derives `latest_quote_location_id` — neither reads `pickupEndPlace`.
      // Both moved off the bare `formatRouteLabel`
      // this line used to name in T660 M6.) This was the ONE unwrapped display
      // reader, and
      // "verbatim" is exactly why it mattered: a golf COURSE leg's label is
      // stored with the em dash AND the arrow, so Helen and Owen's WhatsApp read
      // "📍 Arklow Bay Hotel — → Druids Glen Resort" on every course-leg job.
      // formatDriverMessage renders this string as-is and no reader keys on it,
      // so the wrap belongs here at the READ site — the same place every other
      // display consumer does it — not in the message formatter.
      //
      // The FOURTH reader, `journeyStops` (line ~502), is deliberately left
      // UNWRAPPED and must stay that way: its value is written to
      // `booking_stops.address`, where the stored separator is load-bearing (the
      // `golf_courses_public` view filters on it and `listGolfHubs` splits on it).
      // Wrapping a PERSIST site would rewrite stored bytes; wrapping a display
      // site only changes what Helen reads.
      pickup: pickupEndDisplay(j) ?? '',
      // T540 (516) — deliberately NOT dropoffEndPlace, despite looking like the
      // same idiom as journeyStops/journeySummary. It is a different contract:
      // this payload carries `venue` on its OWN key below, and formatDriverMessage
      // renders it as its own line, "never folded into pickup/drop-off". Routing
      // the venue in here too would print it on the drop-off line AND the venue
      // line of the same team alert — the blemish class Vlad flagged in Round 18.
      // The wedding pickup still reaches the driver: it rides pickupEndPlace above.
      //
      // The DISPLAY wrap is applied to both ends all the same, byte-identical to
      // CustomerInfoBlock's pair (which composes this same expression): the
      // divergence above is about which VALUE the drop-off carries, never about
      // how a route label renders. `formatRouteLabel` and not the wider collapse,
      // for the same reason CustomerInfoBlock's and journeySummary's drop-offs
      // use it — a golf route label lands on the PICKUP end (`locationLabel`), so
      // this end only ever holds a place or typed text, and collapsing every
      // boundary here could only ever corrupt one (T660 M6 R1).
      //
      // T608 M6b (R5) — the airport fallback, and ONLY the airport fallback.
      // For a kiosk WALK-IN airport departure both terms above are null (there is
      // no drop-off picker on the airport screen — the destination IS the
      // service), so this key posted '' and formatDriverMessage omits the 🏁 line
      // entirely: the driver was told a pickup and nothing at all about the
      // airport. `journeyStops` has had a NEAR-twin of this fallback for the
      // PERSIST path since the Captain rework, and the alert never got it, so the
      // record and the message disagreed about the same journey.
      //
      // Until T2090 it was a NEAR-twin: `journeyStops` had no `pickupTerminal`
      // conjunct, so on an arrivals job with no address the record persisted
      // ['Dublin Airport Terminal 2', 'Dublin Airport'] while this key stayed ''
      // (measured, M6b). Both now read the SAME direction-keyed expression: on an
      // arrival the drop-off is the priced town, on a departure the airport, so
      // the record and the alert cannot name different ends for one job.
      //
      // Scoped two ways, both load-bearing. `service === 'airport'` keeps a
      // wedding posting an EMPTY drop-off so its venue prints once, on its own
      // line. And the `pickupTerminal` branch keeps the airport off the drop-off
      // of every job whose pickup end IS the airport: there pickupEndPlace ranks
      // the terminal first, so filling the drop-off with it too would print the
      // airport as both ends of one journey. That case is reachable, not
      // theoretical - the public form's address block is optional, so an
      // arrivals lead with no address stores pickup='Dublin Airport Terminal 2'
      // and dropoff=null; it now posts the priced town, or '' when there is none.
      dropoff: formatRouteLabel(airportDropoffFallback(j)) ?? '',
      paxBand: j.band ?? '',
      customerName,
      customerPhone,
      ...(j.venue ? { venue: j.venue } : {}),
      meetAndGreet: j.options.meetAndGreet,
      extraPickups: j.options.extraPickups,
      returnTrip: j.options.returnTrip,
      lateNight: j.options.lateNight,
      trailer: j.options.trailer,
      accessibleVehicle: j.options.accessibleVehicle,
      ...(j.journeyDate ? { dateISO: j.journeyDate } : {}),
      ...(j.journeyTime ? { pickupTime: j.journeyTime } : {}),
      // ⭐ T1717 M6 (DD5). The return INSTANT, so the preview Helen edits and
      // the WhatsApp the driver receives carry the same line.
      // ⛔ THE SERVER PREFERS ITS OWN DURABLE READ over this key, exactly as it
      // does for `venue` and the six add-on flags: client state is the
      // unreliable half. This is the fallback for a job with no bookingId yet.
      ...(j.returnAt ? { returnAt: j.returnAt } : {}),
      ...(j.finalTotalCents !== null ? { quotedTotalCents: j.finalTotalCents } : {}),
      ...(j.notes ? { notes: j.notes } : {}),
    },
  });
}

/**
 * Send the (edited) team alert. 503 = the quiet not-configured card.
 *
 * The route fans out to every recipient and reports each one INDEPENDENTLY:
 * `sendMessage` never rejects, so a 200 means "the request was handled", NOT
 * "everyone got it". This used to discard the `results` array and render any
 * 200 as a clean success, so an alert that reached Owen but not Helen looked
 * identical to one that reached both. That is the silent-failure gap Vlad hit
 * (X2): the send appears to work and nobody learns otherwise.
 *
 * A partial failure is now a FAILURE. Told "2 of 3 failed", Helen can pick up
 * the phone; told nothing, she assumes the team knows.
 */
export type TeamAlertOutcome =
  | { ok: true }
  | { ok: false; notConfigured: boolean; failed?: number; total?: number };

export async function sendTeamAlert(body: string): Promise<TeamAlertOutcome> {
  try {
    const res = await postJson<{ results?: { to: string; ok: boolean }[] }>(
      '/api/notify/send',
      { body },
    );
    const results = res?.results ?? [];
    const failed = results.filter((r) => !r.ok).length;
    if (failed > 0) return { ok: false, notConfigured: false, failed, total: results.length };
    return { ok: true };
  } catch (e) {
    return { ok: false, notConfigured: e instanceof ApiError && e.status === 503 };
  }
}

// ---------------------------------------------------------------------------
// Bookings + admin (M7)
// ---------------------------------------------------------------------------

export type ApiBookingRow = {
  booking_id: number;
  /** ⛔ NULL FOR AN ACCOUNT BOOKING (migration v33 made `bookings.customer_id`
   *  nullable and `listBookings` LEFT JOINs). Mirrors the server's
   *  `BookingListRow`; see `bookingRowLabel` in screens/Bookings.tsx for the one
   *  place the display fallback is decided. */
  customer_name: string | null;
  customer_phone: string | null;
  booking_type: string;
  status: string;
  journey_date: string | null;
  passengers_band: string | null;
  /**
   * ⭐ T1871 M6 - THE EXACT HEADCOUNT beside the band, mirroring the server's
   * `BookingListRow`. ⛔ NULL IS "NOT CAPTURED" and is the majority answer (every
   * pre-M3 row, every nursing-home crossing); the row renders the band alone and
   * NEVER a `0`. Nothing may sort, filter or badge on whether this exists.
   */
  passengers_exact: number | null;
  latest_quote_cents: number | null;
  /** Server truth: the latest quote's RAW service ('nursing-home' even where
   *  booking_type folds to 'local-other'). null = no quote / no string service. */
  latest_quote_service: string | null;
  payment_status: string | null;
  /** T1130 — what happened to this booking's money, which `payment_status`
   *  cannot say (a paid deposit stays `deposit_paid` after it is refunded).
   *  Hand-mirrors the server's `BookingRefundState`
   *  (core/data/payment-refunds-repo.ts), which owns the ladder that derives it;
   *  there is no shared list type, so the union is repeated verbatim. */
  refund_state: 'none' | 'unconfirmed' | 'refunded' | 'part_refunded' | 'failed';
  /** ⭐⭐ board 2106 M6 delta 2 — WHERE THE BALANCE STANDS, which neither
   *  `payment_status` nor `refund_state` can say: both describe the DEPOSIT.
   *  ⛔ `'none'` is "nobody has asked", NOT "nothing is owed", and `'closed'` is a
   *  voided ask — the row renders NO chip for either, which is silence about the
   *  balance rather than a claim about it.
   *  ⭐ board 2811 M7: the server's `BalanceAskState` itself
   *  (src/shared/balance-ask-schema.ts), imported rather than hand-mirrored: M7
   *  added three members, and a repeated union is how the two halves drift. */
  balance_state: BalanceAskState;
  /** ⭐ board 2811 M4 (R-4, H9): the journey has started and the balance is not
   *  paid (server: `isBalanceUnpaidAtDeparture`). OPTIONAL, because every
   *  hand-built row fixture predates it: absent reads as false. */
  balance_unpaid_at_departure?: boolean;
  /** ⭐ board 2811 M4. Captain, board 2811, 2026-10-06: a stale scheduled email
   *  must be flagged to Helen before it sends (server: `isBalanceEmailStale`).
   *  A boolean, never the Stripe id. OPTIONAL for the reason above. */
  balance_email_stale?: boolean;
  /** ⭐ board 2811 M7: the balance TEXT failed / is late (server: the ask
   *  ladder's `failed` / `late`, gated as the detail's `balance` block is).
   *  Booleans only. OPTIONAL for the reason above: absent reads as false. */
  balance_text_failed?: boolean;
  balance_text_late?: boolean;
  /** ⭐ board 2811 R3 (item 2): the balance text's send never finished (the ask
   *  ladder's `failed` with `unfinished`); then `balance_text_failed` is false.
   *  OPTIONAL for the reason above: absent reads as false. */
  balance_text_unfinished?: boolean;
  /** ISO creation timestamp (T409) — drives the saved-age line on 'new' rows. */
  created_at: string | null;
  /**
   * ⭐⭐ T1746 M4 (B5, DD6) - THE HOME'S NAME, from the booking's own account
   * snapshot by join on the server. `null` on a consumer booking.
   *
   * ⛔ REQUIRED, LIKE EVERY OTHER KEY ON THIS TYPE, and that is what makes the
   * five hand-built row fixtures state it. The server always sends it; a fixture
   * that omitted it would silently exercise a row shape the route cannot
   * produce, which is how a display fallback gets graded against a fiction.
   */
  account_name: string | null;
  /**
   * ⭐⭐ T1746 M4 (B5, DD6, S8) - THE RESIDENT'S DISPLAY NAME, RESOLVED BY THE
   * ROUTE AT RENDER FROM THE PORTAL AND STORED IN NO KIOSK TABLE.
   *
   * ⛔ `null` IS TWO FACTS THE SCREEN MUST TREAT ALIKE: this booking is not a
   * portal crossing, or the seam could not be asked. Both mean "no name to
   * show" - never a placeholder, never a blank where a person should be.
   */
  resident_display_name: string | null;
  /** ⭐ T1746 M4 (B5) - the booking's own opaque reference (T1683 M3), as
   *  stored. NULL on any booking written before M3. */
  job_ref: string | null;
  /**
   * ⭐ board 2814 M4 (DD-5) - the run is Cash, paid to the driver on the day, so
   * the list marks it. ⚠️ THE ONE OPTIONAL KEY ON THIS TYPE, deliberately: absent
   * reads as "not cash", which is the true answer for every hand-built fixture,
   * so making it required would add a line to each and grade nothing new.
   */
  cash_to_driver?: boolean;
  /** ⭐ board 2814 M7 (DD-7) - passed to a taxi firm, so the list marks it. Optional
   *  for `cash_to_driver`'s reason; a boolean, never the note. */
  taxi_passed?: boolean;
};

/** Bookings list read. THROWS — a failed search must render error copy,
 *  never an empty "no results" lie. ⛔ board 2812 M4 (SR1): it takes no search
 *  term, because a term never rides a URL (the server refuses `q` with a 400);
 *  Find posts its term in a body (`listBookingsFiltered`). */
export async function listBookings(status: string | null): Promise<ApiBookingRow[]> {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  const qs = params.toString();
  const data = await getJson<{ bookings: ApiBookingRow[] }>(`/api/bookings${qs ? `?${qs}` : ''}`);
  return data.bookings;
}

/**
 * ⭐⭐ Board 2812 M1 (plan step 1) - ONE ROW OF THE FILTERED BOOKINGS LIST and of
 * every To do pile: today's row plus the server-derived fields. Hand-mirrors the
 * served shape (its key set is pinned in test/server/app.spec.ts). ⛔ It carries
 * no account id, no home key and no request id.
 */
export type ApiFilteredBookingRow = Omit<ApiBookingRow, 'cash_to_driver' | 'taxi_passed'> & {
  cash_to_driver: boolean;
  journey_time: string | null;
  return_at: string | null;
  accessible_vehicle: boolean;
  pickup: string | null;
  destination: string | null;
  money_kind: 'web' | 'account' | 'cash';
  /** The latest quote send, copies included (shared/quote-age.ts); null = never sent. */
  quote_sent_at: string | null;
  /** The channel of the latest send that carries the quote; null when only copied. */
  quote_sent_channel: string | null;
  /** ⭐ Alex's ruling Q273(c): every send of the latest quote FAILED (so it is unsent);
   *  the channel of the newest failed attempt, else null. The channel only. */
  quote_send_failed: 'sms' | 'email' | null;
  can_mark_travelled: boolean;
  can_not_go_ahead: boolean;
  /** From today and Tomorrow (board 3021): the out leg, or the back leg from `return_at`.
   *  Absent on History. */
  leg?: 'out' | 'back';
  /** ⭐ board 2812 M3 (A-14): History lists this quote as No reply (the server's rule). */
  no_reply: boolean;
  /** ⭐ board 2812 M3 fix round 2 (UX-9): History only, the day the job ended (the
   *  server's History sort), which the screen's day headings follow. */
  history_day?: string | null;
  /** ⭐ board 2814 M7 (DD-7): passed to a taxi; the row's NOTE line (Q243), never a chip. */
  taxi_passed: boolean;
};

/** From today's and Tomorrow's waiting request (board 3021), narrowed to what the row
 *  renders (plan step 4). `primary` is the server's `needsHelenNow` primary (board 3020),
 *  the one table the row's chip reads (`requestActionChip`). */
export type ApiWaitingRequest = Pick<
  PortalRequest,
  'requestId' | 'pickupAt' | 'homeName' | 'residentDisplayName' | 'destinationName' | 'returnTrip' | 'returnAt'
  | 'accessibleVehicle' | 'pricedAmountCents' | 'status'
> & { leadAt: string | null; askState?: string | null; askKind?: string | null; primary: HelenPrimary | null };

export type BookingsFilter = {
  who?: 'all' | 'web' | 'homes';
  when?: 'from-today' | 'tomorrow' | 'history';
  /** A group key from `listHomeGroups` (a short code or `a<id>`). */
  home?: string;
  q?: string;
};

/**
 * ⭐ Board 2812 M1 - the filtered bookings read. THROWS, like `listBookings`.
 * ⛔⛔ board 2812 M4 (Captain's ruling, SR1) - A FIND TERM NEVER RIDES A URL. Find
 * carries resident names and phone numbers, which an edge or access log could
 * keep, so a read WITH a term posts `{ who, when, home, q }` as JSON to
 * `POST /api/bookings/find` (the same read on the server); a read without one stays
 * a GET of `who`, `when` and `home` alone. Graded in `test/client/find-term-off-url.spec.tsx`.
 */
export async function listBookingsFiltered(f: BookingsFilter): Promise<{
  rows: ApiFilteredBookingRow[];
  truncated: boolean;
  /** Present only when the resident names could not be read. */
  residentSearch?: 'unavailable';
  waiting?: ApiWaitingRequest[] | null;
  waitingRead?: 'ok' | 'failed';
}> {
  const who = f.who ?? 'all';
  const when = f.when ?? 'from-today';
  const term = f.q?.trim() ?? '';
  if (term !== '') return postJson('/api/bookings/find', { who, when, ...(f.home ? { home: f.home } : {}), q: term });
  const params = new URLSearchParams();
  params.set('who', who);
  params.set('when', when);
  if (f.home) params.set('home', f.home);
  return getJson(`/api/bookings?${params.toString()}`);
}

/** ⭐ Board 2812 M3 - one To do pile, at most 50 rows (or `limit`) and its TRUE total. */
export type ApiTodoPile = { rows: ApiFilteredBookingRow[]; total: number };
export type ApiTodoPiles = Partial<Record<'unsent' | 'wait' | 'soon' | 'outcome', ApiTodoPile>>;

/**
 * ⭐ Board 2812 M3 - the To do piles (M1's `GET /api/todo-piles`). THROWS, like
 * every list read here: a failed read is a notice, never an empty pile. `pile` with
 * `limit` reads that one pile in full (Show all, up to 500).
 */
export async function listTodoPiles(q: {
  channel: 'web' | 'homes';
  home?: string;
  pile?: 'unsent' | 'wait' | 'soon' | 'outcome';
  limit?: number;
}): Promise<ApiTodoPiles> {
  const params = new URLSearchParams();
  params.set('channel', q.channel);
  if (q.home) params.set('home', q.home);
  if (q.pile) params.set('pile', q.pile);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  return getJson(`/api/todo-piles?${params.toString()}`);
}

/** ⭐ Board 2812 M1 - one home on the Nursing homes column (keyed per Q-A). */
export type ApiHomeGroup = {
  key: string;
  name: string;
  town: string | null;
  /** null = not known (the portal, crossings or settlements could not be read). */
  needYouNow: number | null;
  comingUp: number;
  needAnOutcome: number;
};

/** ⭐ Board 2812 M1 - the homes, grouped on the server. THROWS. */
export async function listHomeGroups(): Promise<{
  groups: ApiHomeGroup[];
  notLinked: { needYouNow: number | null };
  portalRead: 'ok' | 'failed';
}> {
  return getJson('/api/home-groups');
}

/**
 * ⭐⭐ board 2814 M5 (plan DD-9) - A TRIP A HOME PHONED IN, as the Bookings tab's form
 * sends it to `POST /api/nursing/phone-ins`. `destination` is a listed destination's
 * id (a number) or the address Helen typed (a string). `amountCents` null means the
 * home's agreed price, which the SERVER reads; the screen's figure is advisory.
 * `phoneInKey` is made once when the form opens, so a double tap books one trip.
 */
export type PhoneInBody = {
  accountId: number;
  destination: number | string;
  journeyDate: string;
  pickupTime: string;
  returnTrip: boolean;
  returnTime: string | null;
  passengersBand: string;
  wheelchair: boolean;
  payerKind: 'hse' | 'house' | 'self';
  amountCents: number | null;
  travellerRef: string | null;
  phoneInKey: string;
};

/** What the route answers: `booked`, or `already` for a key that booked the same
 *  trip before. `totalCents` is the figure the home is billed (wave-1 security S3),
 *  null when an older server sends none. */
export type PhoneInBooked = {
  outcome: 'booked' | 'already';
  booking: { bookingId: number; jobRef: string | null; journeyDate: string | null; totalCents: number | null };
};

/** The route's coded refusals, mirrored by hand (the server module pulls in `pg`);
 *  `test/server/app.spec.ts` pins the two lists equal, both ways. */
export type PhoneInRefusalCode =
  | 'phone_in_home_invalid'
  | 'phone_in_destination_invalid'
  | 'phone_in_date_invalid'
  | 'phone_in_time_invalid'
  | 'phone_in_return_invalid'
  | 'phone_in_band_invalid'
  | 'phone_in_wheelchair_invalid'
  | 'phone_in_payer_invalid'
  | 'phone_in_amount_invalid'
  | 'phone_in_traveller_invalid'
  | 'phone_in_key_invalid'
  | 'phone_in_key_reused'
  | 'phone_in_date_passed'
  | 'phone_in_home_unavailable'
  | 'phone_in_destination_unavailable'
  | 'phone_in_cash_listed'
  | 'phone_in_price_needed';

/** ⛔ The words are keyed on the CODE, never the server's sentence (SEC-5). Each
 *  says the trip was not booked, so a refusal never reads as a half success. */
export const PHONE_IN_REFUSAL_COPY: Readonly<Record<PhoneInRefusalCode, string>> = {
  phone_in_home_invalid: 'Choose the home. The trip was not booked.',
  phone_in_destination_invalid: 'Choose a destination, or type the drop-off address. The trip was not booked.',
  phone_in_date_invalid: 'Choose the day of the trip. The trip was not booked.',
  phone_in_time_invalid: 'Type the pickup time, like 09:30. The trip was not booked.',
  phone_in_return_invalid: 'A return needs a return time after the pickup time. The trip was not booked.',
  phone_in_band_invalid: 'Choose how many are travelling. The trip was not booked.',
  phone_in_wheelchair_invalid: 'Say whether a wheelchair user is travelling. The trip was not booked.',
  phone_in_payer_invalid: 'Choose who pays. The trip was not booked.',
  phone_in_amount_invalid: 'That price cannot be recorded. Type it in euro, like 120 or 120.50.',
  phone_in_traveller_invalid: 'The traveller reference is too long or unreadable. Keep it short, like a room number.',
  phone_in_key_invalid: 'This form is out of date. Close it and add the trip again.',
  // The words with the reference are composed in `bookPhoneIn` below; this is the
  // fallback for an answer that carries none.
  phone_in_key_reused: 'This trip was already booked with other details, so nothing new was made. Open the booking to change it.',
  phone_in_date_passed: 'This trip’s date has passed. It was not booked.',
  phone_in_home_unavailable: 'That home is not an active nursing home account any more, so the trip was not booked.',
  phone_in_destination_unavailable: 'That destination is no longer on the list, so the trip was not booked. Choose another.',
  phone_in_cash_listed: 'Cash is only for a typed address. A listed destination is billed to the home.',
  phone_in_price_needed: 'There is no agreed price for this trip, so type the price. It was not booked.',
};

function isPhoneInRefusalCode(v: unknown): v is PhoneInRefusalCode {
  return typeof v === 'string' && Object.hasOwn(PHONE_IN_REFUSAL_COPY, v);
}

/** Book the phoned-in trip, or say in words why not. A key that booked a DIFFERENT
 *  trip answers with that booking, so the form can offer "Open the booking". */
export async function bookPhoneIn(
  body: PhoneInBody,
): Promise<{ ok: true; value: PhoneInBooked } | { ok: false; message: string; openBookingId?: number; code?: PhoneInRefusalCode }> {
  try {
    const res = await postJson<PhoneInBooked>('/api/nursing/phone-ins', body);
    const total = res.booking.totalCents;
    return {
      ok: true,
      value: {
        outcome: res.outcome === 'already' ? 'already' : 'booked',
        booking: { ...res.booking, totalCents: typeof total === 'number' ? total : null },
      },
    };
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    if (status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    if (status === 409 && code === 'phone_in_key_reused') {
      const held = (e as ApiError).body as { booking?: { bookingId?: unknown; jobRef?: unknown } } | null;
      const id = held?.booking?.bookingId;
      const ref = held?.booking?.jobRef;
      if (typeof id === 'number' && Number.isSafeInteger(id) && id > 0) {
        const name = typeof ref === 'string' && ref !== '' ? ref : `#${String(id)}`;
        return {
          ok: false,
          message: `This trip was already booked as ${name} with other details, so nothing new was made. Open the booking to change it.`,
          openBookingId: id,
        };
      }
    }
    // The code rides out too, so the form can put the caret on the field it names.
    if ((status === 400 || status === 409) && isPhoneInRefusalCode(code)) {
      return { ok: false, message: PHONE_IN_REFUSAL_COPY[code], code };
    }
    // ⛔ NOT "nothing was booked": a lost answer may follow a booking that landed.
    // The form keeps its key, so a second press answers `already` rather than
    // booking twice, and these words can say so.
    return { ok: false, message: 'Something went wrong booking that trip. Press again: it will not be booked twice.' };
  }
}

export type ApiBookingDetail = {
  booking: {
    booking_id: number;
    booking_type: string;
    status: string;
    passengers_band: string | null;
    journey_date: string | null;
    journey_time: string | null;
    note: string | null;
    venue: string | null;
    meet_greet: boolean;
    return_trip: boolean;
    accessible_vehicle: boolean;
    extra_pickups: number;
    late_night: boolean;
    trailer: boolean;
    /** T484 — stated contact preference; already served by getBooking's SELECT,
     *  previously just undeclared here. May be a RETIRED value on an old row. */
    contact_preference: string | null;
    /** ⭐ T1683 M3 (A1) — the one opaque job reference. OPTIONAL on the wire for
     *  the same reason `role` is on the stops: the server always sends the key
     *  now, but every hand-built detail fixture predates it, and an absent field
     *  must read as "no reference", never `undefined` on screen. NULL = a booking
     *  written before M3, which will never have one. */
    job_ref?: string | null;
  };
  /** ⛔ NULL FOR AN ACCOUNT BOOKING (migration v33). Mirrors the server's
   *  BookingDetail.customer: an explicit absence, never a placeholder object
   *  whose id would be 0 and whose name would be the empty string. */
  customer: { name: string; phone_e164: string | null; email: string | null } | null;
  stops: {
    seq: number; address: string; address_line: string | null; eircode: string | null; town: string | null;
    /** v20's recorded journey role, so the ONE reader can prefer the fact over
     *  its pre-v20 inference on the RE-OPEN path too (this is the fourth
     *  resolveJourneyEnds consumer). OPTIONAL on the wire type for the same
     *  reason as `inquiry_email` below: the server always sends it, but every
     *  hand-built detail fixture predates it, and an absent field must read as
     *  "not recorded", never `undefined`. NULL = a stop written before v20. */
    role?: string | null;
  }[];
  /** Flight number from the latest linked inquiry's extra (read-only join —
   *  flight is never persisted onto bookings). null = none stated. */
  flight: string | null;
  /**
   * ⭐⭐ T2181 (board 2181) — THE REST OF THE JOURNEY THE CUSTOMER TYPED, off the
   * SAME read-only join `flight` comes from, and at the top level for the reason
   * `returnAt` states below.
   *
   * OPTIONAL, like `returnAt` and `role`: the server always sends these keys now,
   * but every hand-built detail fixture predates them, and an absent field must
   * read as "not stated", never `undefined` on screen.
   *
   * ⛔ `terminal` is a STORED TOKEN (`AIRPORT_TERMINALS`), not wording — render it
   * through `terminalDesignation`, never raw. The server has already gated it on
   * the inquiry being an airport job, so a non-null value here means the customer
   * answered; null means non-airport OR never answered, which read the same on
   * screen: no row.
   */
  returnDate?: string | null;
  returnTime?: string | null;
  returnPassengers?: string | null;
  terminal?: string | null;
  /**
   * ⭐ T1717 M6 (DD5) — `bookings.return_at` as an ISO instant, or null.
   *
   * ⛔ IT IS NOT ON `booking` ABOVE, AND THAT IS THE SHAPE OF THE DECISION.
   * `booking` mirrors `getBooking`'s ruled 18-column SELECT, which is copied
   * into ~19 pg-mem fixtures; this value comes from its own one-column read that
   * the DETAIL ROUTE bolts on, so it sits at the top level beside `flight`,
   * which is another read-only join the booking row does not carry.
   *
   * OPTIONAL on the wire type: the server always sends the key now, but every
   * hand-built detail fixture predates it, and an absent field must read as "no
   * return time", never `undefined` on screen.
   */
  returnAt?: string | null;
  /**
   * ⭐⭐ T1871 M6 — THE EXACT HEADCOUNT, from its own one-column read.
   *
   * ⛔ IT IS NOT ON `booking` ABOVE, FOR THE REASON `returnAt` STATES ONE LINE UP
   * AND AGAINST THE LETTER OF THE PLAN'S STEP 2. `booking` mirrors `getBooking`'s
   * ruled 18-column SELECT — `test/server/app.spec.ts`'s `BOOKING_KEYS` arm
   * asserts that key for key, and the same milestone's step 1 forbids widening
   * that SELECT. Both cannot hold at once; this key sits beside `returnAt`,
   * `portalRequest` and `identity`, the three facts already served this way.
   *
   * OPTIONAL on the wire type for the reason those three state: the server always
   * sends the key now, but every hand-built detail fixture predates it, and an
   * absent field must read as "not captured", never `undefined` on screen.
   *
   * ⛔ NULL IS NOT ZERO AND NOT A LESSER BOOKING.
   */
  passengersExact?: number | null;
  /**
   * ⭐⭐ T1717 M6 (DD6, P0-11) — WHAT THE **HOME** SAID about the return, from a
   * dedicated read of the portal-sourced `inquiries` row.
   *
   * ⛔ NULL MEANS **NO PORTAL ANSWER EXISTS** (a web enquiry, a walk-in), NOT
   * "the home said one way". Three states, three values — a boolean here would
   * make every ordinary booking look like a nursing home that asked for a
   * one-way run, and the screen would announce an override against an answer
   * nobody gave.
   *
   * ⛔ AND IT IS NOT `booking.return_trip`. That column is the OPERATOR's
   * current value: `updateBookingFlags` overwrites it on every quote save. This
   * one is written once by the crossing and never updated, which is what lets
   * the baseline survive Helen's first override.
   *
   * ⭐⭐ T2088 M7 (D5) — `returnAtState` JOINS IT, AND NO SIXTH TOP-LEVEL KEY
   * ARRIVES WITH IT. The marker comes off the SAME dedicated read (one more
   * column on a query the route already runs, never a second round trip), so the
   * response's key set is byte-identical and only this VALUE's shape moved. ⛔ The
   * three-answers property above is untouched: `null` still means no portal row,
   * and a portal row with no marker answers `'unset'`, never null.
   */
  /**
   * ⭐⭐ board 2637 (plan D2) — `appointmentAt` / `appointmentTime` JOIN IT, on
   * the same terms as the marker: the same one read, no new top-level key. The
   * crossing now writes the COLLECTION into `journey_time`, so the appointment
   * rides here and renders small under When. OPTIONAL for a server that predates
   * it; `null` or absent means "no appointment to show".
   */
  portalRequest?: {
    returnTrip: boolean;
    returnAtState?: 'unset' | 'to-confirm';
    appointmentAt?: string | null;
    appointmentTime?: string | null;
  } | null;
  /**
   * ⭐⭐ T1746 M4 (B5, DD6) - WHO THIS JOB IS FOR when there is no customer
   * record: the home from the booking's own account snapshot, and the resident
   * from the portal request, both resolved BY THE ROUTE.
   *
   * ⛔ IT ARRIVES IN THIS PAYLOAD AND IS NEVER FETCHED HERE. The screen's own
   * spec pins that it re-resolves NOTHING (no price book, no seam); a client
   * fetch for the name would break that and put the portal on the render path
   * of a screen that must open when the seam is down.
   *
   * ⛔ AND NO RESIDENT NAME IS STORED ANYWHERE IN THE KIOSK (S8) - this key is a
   * render-time join, which is the whole of DD6.
   *
   * OPTIONAL on the wire type for the reason `returnAt` and `portalRequest`
   * above state: the server always sends it now, but every hand-built detail
   * fixture predates it, and an absent field must read as "nothing to say about
   * who this is for", never `undefined` on screen.
   */
  identity?: { accountName: string | null; residentDisplayName: string | null };
  /**
   * ⭐⭐ board 2106 M5 — WHAT HAS HAPPENED ABOUT THIS BOOKING'S BALANCE.
   *
   * ⛔⛔ ABSENT IS NOT A STATE, AND THE DIFFERENCE IS THE WHOLE KEY. The server
   * OMITS this key entirely unless the booking is confirmed, deposit-paid and
   * not account-billed (server/app.ts's `askable`, which restates the ask
   * route's own preconditions) — so `undefined` means "a balance ask is not a
   * thing that can happen here" and must render NO CONTROL AND NO SENTENCE,
   * while `state: 'none'` means "askable, and nobody has asked yet" and renders
   * the button. A `??` default to `{ state: 'none', … }` would collapse the two
   * and offer Helen a control the server would refuse.
   *
   * ⚠️ THE FIELD IS `askedAt`, NOT `at` (the plan says `at`). It is the repo
   * reader's own field name (`BalanceAskSummary` in
   * core/data/balance-invoices-repo.ts), and the route spreads that object
   * through unrenamed, so this type mirrors the wire and not the plan.
   *
   * ⚠️ `channel` IS NULL ONLY ON `none`; `askedAt` IS NOT — AND THE M5 DRAFT OF
   * THIS LINE LICENSED A CONSEQUENCE FROM THE WRONG HALF. It said both were null
   * only on `none`, "which is why the screen may render a date without a
   * fallback". `getBalanceAskSummary` writes `askedAt: null` on ANY state whose
   * `created_at` it cannot parse, so a fallback is REQUIRED on every dated arm —
   * which the screen does write (`askedAt ?? ''`). The reader's own interface
   * carries the rule and the reason the NaN guard stays.
   *
   * ⛔ THE STATE UNION IS IMPORTED, NOT RESTATED (rework R12.3). It was spelled
   * out here and again in `core/data/balance-invoices-repo.ts` with nothing
   * pinning the two equal.
   *
   * OPTIONAL for the reason `returnAt`, `portalRequest` and `identity` state
   * above, PLUS the stronger one here: absence is a meaning, not a gap.
   */
  balance?: {
    state: BalanceAskState;
    channel: string | null;
    askedAt: string | null;
    /** board 2249 rework 2 (bug B): the newest ask is a whole-job document, so
     *  `channel` says nothing about what was sent. Optional: fixtures predate it. */
    wholeJob?: boolean;
    /** ⭐ board 2811 M4 (H8): ISO, when an EMAIL ask is (or was) sent, its
     *  `finalize_at`. ⭐ M7: on a TEXT row, when the text is (or was) due.
     *  Optional: fixtures predate it. */
    sendsAt?: string | null;
    /** ⭐ board 2811 M7: ISO, when a TEXT ask went (text `asked` only). */
    sentAt?: string | null;
    /** ⭐ board 2811 M7 (D-17): it went more than `BALANCE_TEXT_LATE_AFTER_MS` after it was due. */
    sentLate?: boolean;
    /** ⭐ board 2811 M7: ISO, when a TEXT ask failed (`failed` only). */
    failedAt?: string | null;
    /** ⭐ board 2811 M7: which failure, on `failed` only (the schema's closed
     *  vocabulary); it decides the copy and whether the ask is offered again. */
    textFailure?: BalanceTextFailure | null;
    /**
     * ⛔⛔ board 2106 M5 rework 3 — THE TWO MONEY FIGURES THE BLOCK MAY STATE, AND
     * THEY ARE THE **ROUTE'S**, NOT ANYTHING ON THIS PAYLOAD.
     *
     * `depositPaidCents` is the amount of the ONE `deposit_paid` row — not
     * `payment.amount_cents`, which is the newest payment row of ANY status and
     * on a superseded-link booking is an UNPAID figure. `agreedTotalCents` is
     * that deposit's OWN quote's total — not `latest_quote_cents`, which moves on
     * a re-price and would pair one quote's deposit with another quote's total.
     * Both are derived server-side the way `validateBalanceAsk` derives them, so
     * the sentence on screen and the sum demanded cannot disagree.
     *
     * ⛔ EITHER MAY BE NULL AND THE BLOCK THEN SAYS NOTHING ABOUT MONEY: no quote
     * on the deposit, no figure on that quote, or TWO paid deposits — the shape
     * the ask itself refuses (`ambiguous_payment`) rather than guessing which
     * deposit a balance completes. Half a sentence about money is worse than none.
     *
     * OPTIONAL on the wire type for the reason every bolted-on key above states:
     * the server always sends them now, but every hand-built fixture predates
     * them, and `undefined` must read as "no figure" — which is why the screen
     * normalises with `?? null` rather than testing `!== null` on the raw field.
     */
    depositPaidCents?: number | null;
    agreedTotalCents?: number | null;
    /** ⭐ board 2811 R3 (Captain's money ruling): what Helen's ask will request,
     *  the server's `balanceAskCents` (the agreed total less what was actually
     *  paid, net of succeeded refunds). Absent when the ask would refuse on the
     *  money, and then the screen states no figure. */
    askCents?: number;
    /** ⭐ board 2811 R3: on an `asked` balance row only, the sum it asked for
     *  (server: `BalanceAskSummary.askedCents`). Absent otherwise. */
    askedCents?: number;
    /** board 2811 rework (item 5; Captain's ruling on the item 5 deviation):
     *  present only when the screen says there is no automatic balance ask
     *  because of a refund (server: `automaticAskRefundedOnScreen`), carrying the
     *  refund state the words follow. Absent on every other booking. */
    automaticAskRefunded?: 'refunded' | 'part_refunded' | 'failed' | 'unconfirmed';
    /** ⭐ board 2811 rework part 2c (Captain item 4): on a `due` or `late` text
     *  only, this kiosk knows nothing here will send it (the killswitch is off, or
     *  no text sender runs in the process). Absent otherwise. */
    automaticTextsOff?: true;
    /** ⭐ board 2811 rework part 2c (Captain item 13): on a `failed` text only, the
     *  provider refused the number itself. Absent otherwise. */
    numberRejected?: true;
  };
  /**
   * ⭐ board 2249 M4/M5 — WHAT THE "INVOICE THIS JOB" CONTROL NEEDS (server/app.ts
   * builds it beside `askable`). `offered`: a consumer booking in a status the issue
   * accepts. `lastDocument`: the newest whole-job document of any status, as ids.
   * `defaults.billToName` / `defaults.billToAddress`: the form's starting values
   * (board 2281): the live job document's own Helen-typed bill-to, else the
   * traveller's name and no address. ⛔ Never the pickup or drop-off, no email, no URL.
   * `standing`: the standing refusal's code (never its prose), or null.
   *
   * OPTIONAL on the wire type for the reason every bolted-on key above states: the
   * server always sends it now, but hand-built fixtures predate it, and an absent
   * key must read as "not offered, nothing to show", never as a control.
   */
  jobInvoice?: {
    /** Rework 1 B1: the dark-launch switch. False (or absent), nothing renders. */
    switchedOn: boolean;
    offered: boolean;
    /** Rework 2 (delta 6): a press would cancel a live invoice, so the action is a
     *  deliberate one behind a disclosure. Optional: fixtures predate it. */
    replaces?: boolean;
    lastDocument: { rowId: number; kind: string; status: string; issuedAt: string } | null;
    /** Rework 2 (delta 7): `bookingHasEmail` is whether there is one, never the
     *  address. Optional: fixtures predate it, and absent reads as the old words.
     *  `billToAddress` (board 2281): newline-joined; optional, absent reads as empty.
     *  `lastRecipientWasTyped` (board 2281, security Low 1): the live job document
     *  went to an email typed by hand; WHETHER, never the address. Absent reads false. */
    defaults: { billToName: string; billToAddress?: string; bookingHasEmail?: boolean; lastRecipientWasTyped?: boolean };
    /** board 2281: why the server refuses every press, as a code, or null.
     *  Optional: fixtures predate it, and absent reads as no line. */
    standing?: string | null;
  };
  /**
   * ⛔⛔ board 2106 M6 delta 3 — THE REFUND FIGURES, AND THEY ARE **TOP-LEVEL**,
   * NOT ON `balance`, because the cancel control reads them and it is reachable on
   * bookings that are not askable (a cancelled or unconfirmed one), where `balance`
   * is absent from the payload entirely.
   *
   * `depositPaidCents` is the ONE `deposit_paid` row's amount — the same figure
   * `balance.depositPaidCents` carries, from the same `deposit` oracle in the same
   * route block, duplicated only because their gating differs. ⚠️ A spec pins the
   * two EQUAL whenever both are present, so the duplication cannot drift.
   *
   * `depositRefundedCents` is `consumedRefundCents` over that deposit's refund
   * rows — `pending` and `requires_action` included, not only `succeeded`, because
   * money already spoken for is out of reach. It is the SAME bound
   * core/refund-booking.ts enforces server-side, so the ceiling on screen and the
   * ceiling in the transaction are one derivation.
   *
   * ⛔ BOTH NULL WHEN THERE IS NO SINGLE PAID DEPOSIT (zero or two), the shape the
   * refund path refuses rather than guessing at.
   *
   * OPTIONAL on the wire for the reason the block above states: the server always
   * sends them now, but every hand-built fixture predates them, so `undefined`
   * must read as "no figure".
   */
  depositPaidCents?: number | null;
  depositRefundedCents?: number | null;
  /**
   * ⭐ board 2343 — the server's ADVISORY answer to "may this job be ended as not
   * going ahead" (a web enquiry's job, new or quoted, nothing paid). True puts the
   * plain "Not going ahead" action where the "Cancel this run" disclosure sits;
   * the cancel route re-checks under its lock. OPTIONAL: absent reads as false,
   * which is today's screen.
   */
  notGoingAhead?: boolean;
  /**
   * ⭐ board 2814 M4 (plan DD-5) - the run is Cash, paid to the driver on the day
   * (`bookings.payer_kind = 'self'`). The screen states the fact, offers no deposit
   * toggle and no payment link, and offers "Change who pays". OPTIONAL: absent
   * reads false, which is every hand-built fixture that predates it.
   */
  cashToDriver?: boolean;
  /**
   * ⭐ board 2814 M7 (plan DD-7, S16) - the run was passed to a taxi firm: when, and
   * Helen's optional note, or null. ⛔ THIS IS THE ONE PAYLOAD THAT CARRIES THE NOTE;
   * every list carries a boolean. OPTIONAL: absent reads as not passed.
   */
  taxi?: { at: string; note: string | null } | null;
  /**
   * ⭐ board 2814 M6 (DD-8) - `accountBooking`: the row's own `account_id` is set,
   * so the screen offers "Change the price". `calculatorAllowed`: the server's
   * answer to "may this booking open the calculator" (false for an account, a
   * portal-linked or a nursing booking). ⛔ "Re-open in calculator" renders ONLY on
   * `calculatorAllowed === true`: absent hides it (fail closed). OPTIONAL on the
   * wire type because hand-built fixtures predate both.
   */
  accountBooking?: boolean;
  calculatorAllowed?: boolean;
  /**
   * ⭐ board 2812 M1/M3 - which kind of money this booking takes, by the list rows'
   * own rule (`moneyKindOf`): `web` (a deposit link and a payment link), `account`
   * (invoiced at month end: NO deposit stage) or `cash` (paid to the driver). The
   * detail offers the deposit link and "Payment link only" on `web` only, so an
   * account run never offers what the server refuses (`account_booking`).
   * OPTIONAL on the wire type: the server always sends it; hand-built fixtures
   * predate it and read as today's web screen.
   */
  moneyKind?: 'web' | 'account' | 'cash';
  /** ⭐ fix round 1 (ux F5): the latest figure was typed by Helen (`pricing_context.basis`
   *  'operator'), so the nursing caption says "set by hand". Absent reads false. */
  quoteSetByHand?: boolean;
  /**
   * ⭐ board 2811 M4 - THE TWO BALANCE FLAGS, TOP-LEVEL and UNGATED by the
   * `balance` key's askable test (a re-quoted booking has no `balance` key and is
   * exactly the case the first is for). `balance_unpaid_at_departure`: the journey
   * has started and the balance is not paid. `balance_email_stale` (Captain, board
   * 2811, 2026-10-06: a stale scheduled email must be flagged to Helen before it
   * sends): a scheduled balance email at Stripe on a booking that is no longer
   * askable; `balance_email_stale_sends_at` is when it goes (ISO), null unless
   * stale. Booleans and one instant, never an id or an amount. OPTIONAL for the
   * reason every bolted-on key above states: absent reads as no flag.
   */
  balance_unpaid_at_departure?: boolean;
  balance_email_stale?: boolean;
  balance_email_stale_sends_at?: string | null;
  /**
   * ⭐ board 2788 M1 - the server's ADVISORY answer to "may this booking's date and
   * time be changed". `why` is a refusal CODE the screen words itself (SEC-5);
   * `invoiceNumber` names the issued account invoice when that is the reason. The
   * reschedule route re-checks under its lock. OPTIONAL: absent renders nothing,
   * which is the screen before this milestone.
   */
  reschedule?: { allowed: boolean; why?: RescheduleRefusal; invoiceNumber?: string; linked: boolean };
  /** T549 M8 — the address supplied on THIS booking's own inquiry, kept
   *  distinct from `customer.email` (the stored customer record's address) all
   *  the way to the screen, so Helen can see which one she is writing to. The
   *  send path uses `customer.email` first and this second. OPTIONAL on the
   *  wire type: the server always sends it, but every hand-built detail fixture
   *  predates it, and an absent field must read as "none", never `undefined`. */
  inquiry_email?: string | null;
  latest_quote_cents: number | null;
  latest_quote_note: string | null;
  /** Server truth: the latest quote's RAW service ('nursing-home' even where
   *  booking_type folds to 'local-other'). The invoice affordance + re-open key
   *  identity on THIS, not a client-only capture. null = no quote / no service. */
  latest_quote_service: string | null;
  /** T608 M6a — the RATE KEY the persisted quote was priced against, out of the
   *  same request_json blob as the service above. The re-open path restores THIS
   *  rather than recomposing a key from the stop labels (slugify collapses the
   *  `__` route boundary, and a stop's text is operator-editable). OPTIONAL on the
   *  wire type: the server always sends it, but every hand-built detail fixture
   *  predates it, and an absent field must read as "no key" — which degrades to
   *  the location picker — never as `undefined` reaching a priced input. */
  latest_quote_location_id?: string | null;
  /** T549 M4 — how the PERSISTED quote was priced ('priced' | 'estimated' |
   *  'manual'), so the provenance survives the session it was made in. Optional
   *  on the wire type deliberately: the server always sends it, but every
   *  hand-built test/detail fixture predates it and an absent value reads the
   *  same as an old row with no status (no chip), never as a false 'priced'. */
  latest_quote_pricing_status?: string | null;
  /** paid_at: ISO string once the deposit-paid transition landed (T408);
   *  null while awaiting. */
  payment: { status: string; amount_cents: number; currency: string; paid_at: string | null } | null;
  /** `target` (T660 M4, P0-7) is WHERE the message actually went - a phone on
   *  sms, an address on email. OPTIONAL on the wire type for the same reason
   *  `inquiry_email` and `latest_quote_pricing_status` are: the server always
   *  sends it, but every hand-built detail fixture in the suite predates it, and
   *  an absent field must read as "not recorded" (the sentence simply omits the
   *  recipient) rather than printing `undefined` at Helen. */
  latest_send: {
    send_id: number; channel: string; delivery_status: string;
    target?: string | null; created_at: string;
  } | null;
};

export async function getBookingDetailFull(bookingId: number): Promise<ApiBookingDetail> {
  return getJson<ApiBookingDetail>(`/api/bookings/${bookingId}`);
}

/**
 * One thing that happened to a booking (T608 M4, R6d) — the wire shape of
 * GET /api/bookings/:id/timeline, a read-time union of booking_events +
 * send_ledger + payments.
 *
 * FLAT, with per-source nulls, mirroring the server's BookingTimelineEntry.
 * Every field here is one the server explicitly SELECTs: the customer message
 * body, the send target and the Stripe ref are excluded by narrow readers on the
 * server and must never be added to this type either.
 */
export type ApiBookingTimelineEntry = {
  id: string;
  source: string;
  kind: string;
  at: string;
  detail: string | null;
  actor: string | null;
  /** ⭐ board 2811 rework part 2c: 'system' renders as "Automatic"; anything else
   *  (and absent, as on every fixture that predates it) keeps the quoted name. */
  actor_kind?: string | null;
  channel: string | null;
  delivery_status: string | null;
  /** 'delivered' | 'sent' | 'failed' | 'unknown' — from the ONE shared classifier the
   *  send UI reads (shared/send-schema.ts sendOutcome), computed server-side so
   *  the timeline and the send surface cannot describe one row differently. */
  outcome: string | null;
  error_code: string | null;
  amount_cents: number | null;
  currency: string | null;
};

/**
 * The timeline read. NEVER REJECTS (`getOr`) — deliberately the opposite idiom to
 * getBookingDetailFull above.
 *
 * This is a SECOND request beside the detail, and the booking record must
 * survive its failure: blanking the whole booking screen (the journey, the
 * quote, the send affordance) over a history outage would turn a cosmetic
 * problem into an operational one on the screen Helen quotes from. `getOr`
 * keeps that STRUCTURAL — the promise cannot reject, so no caller can blank the
 * detail by forgetting a `.catch`.
 *
 * `null` means COULD NOT LOAD, distinct from `[]` meaning nothing has happened
 * yet (the live ux-review's finding 2: the two used to collapse into one empty
 * list, so a failed fetch rendered as a silent claim that the booking had no
 * history). Same null-is-failure idiom the payment re-read on this screen
 * already uses. A 2xx body of an unexpected SHAPE is a load failure too — the
 * one thing it is not is evidence that nothing happened.
 */
export async function getBookingTimeline(
  bookingId: number,
): Promise<BookingHistoryRead | null> {
  const data = await getOr<{ timeline?: ApiBookingTimelineEntry[]; lifecycle?: unknown } | null>(
    `/api/bookings/${bookingId}/timeline`,
    null,
  );
  if (data == null || !Array.isArray(data.timeline)) return null;
  return { entries: data.timeline, lifecycle: readLifecycle(data.lifecycle) };
}

/**
 * What the history read returns — the timeline entries AND the server-decided
 * lifecycle spine, from the one request that already carries both.
 *
 * ⛔ THE TWO DEGRADE INDEPENDENTLY, deliberately. A `null` OUTER value still
 * means "the history could not be loaded" and blanks neither the booking record
 * nor the spine's own panel; a `null` `lifecycle` inside a loaded read means
 * "there is no spine to show", which is what a CANCELLED run legitimately
 * returns. Folding the second into the first would let a cancelled booking read
 * as a failed request.
 */
export type BookingHistoryRead = {
  entries: ApiBookingTimelineEntry[];
  lifecycle: ApiLifecycleStage[] | null;
};

/** One stage on the wire. Structurally `Stage` from `@ecmb/ui`, restated as the
 *  WIRE type because these three fields arrive as unknown JSON and are narrowed
 *  by `readLifecycle` before anything renders them. */
export type ApiLifecycleStage = { id: string; label: string; state: StageState };

/**
 * ⛔ NARROW THE WIRE VALUE BEFORE IT REACHES THE COMPONENT — the same discipline
 * `bookingTimelineKindLabel` applies to a timeline `kind`, and for a sharper
 * reason here. `Lifecycle.tsx` builds its modifier class by INTERPOLATING the
 * state (`lifecycle-stage--${s.state}`) and reads its screen-reader wording out
 * of `STAGE_STATE_TEXT[s.state]`. An unrecognised state would therefore render
 * a class no stylesheet defines AND an empty visually-hidden span — a stage
 * with no visible state and no announced one, which is the one outcome the P0
 * copy rule exists to prevent.
 *
 * ⇒ ANY malformed member drops the WHOLE spine rather than rendering a partial
 * one. A spine missing a stage would silently re-state Option A on Helen's
 * screen, and a spine is only meaningful as the complete six.
 */
function readLifecycle(v: unknown): ApiLifecycleStage[] | null {
  if (!Array.isArray(v)) return null;
  const out: ApiLifecycleStage[] = [];
  for (const raw of v) {
    if (raw === null || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    if (typeof s['id'] !== 'string' || typeof s['label'] !== 'string') return null;
    const state = s['state'];
    if (typeof state !== 'string' || !Object.hasOwn(STAGE_STATE_TEXT, state)) return null;
    out.push({ id: s['id'], label: s['label'], state: state as StageState });
  }
  return out.length > 0 ? out : null;
}

/**
 * ⭐ "The run came back" — T1033 M7 part 2. THE CALLER `markBookingTravelled`
 * spent part 1 without, which left `Travelled` inert in the middle of the spine
 * ADR4-D11 puts on both surfaces.
 *
 * ⚠️ `postJson`, NOT a hand-rolled fetch: the route calls `requiresJsonBody`,
 * so a request without `content-type: application/json` 415s before anything
 * is read.
 *
 * The outcome is a closed union rather than a thrown error, because three of the
 * five are things Helen must be TOLD rather than failures to retry:
 *   - 'marked'    : written now.
 *   - 'already'   : it was already recorded. NOT an error — the run IS marked
 *                   travelled, which is what was asked. Saying "failed" would
 *                   invite a second attempt at something already true.
 *   - 'not-confirmed' : 409 coded `run_not_confirmed` (board 2377). The run is
 *                   not confirmed yet, so it cannot be marked.
 *   - 'taxi-passed' : 409 coded `run_taxi_passed` (board 2814 M7). The run was
 *                   passed to a taxi firm, so it did not travel with us.
 *   - 'cancelled' : an UNCODED 409. The run is cancelled, so it did not travel.
 *   - 'error'     : anything else, including a stale 404 and a 409 carrying a
 *                   code this build does not know (security-review L4): telling
 *                   Helen a run is cancelled on a refusal nobody mapped would
 *                   be a false fact, where retryable copy is merely vague.
 */
export type MarkTravelledOutcome = 'marked' | 'already' | 'not-confirmed' | 'taxi-passed' | 'cancelled' | 'error';

/**
 * ⛔ A HAND-WRITTEN MIRROR of `TravelledRefusalCode` in `server/app.ts`, for the
 * reason the price code above is one: that module pulls in `pg`. Pinned more
 * tightly than the older mirrors: `test/server/app.spec.ts` asserts the two
 * unions are the SAME TYPE, so a member added or dropped on either side fails `tsc`.
 */
export type TravelledRefusalCode = 'run_not_confirmed' | 'run_taxi_passed';
const RUN_NOT_CONFIRMED: TravelledRefusalCode = 'run_not_confirmed';
/** ⭐ board 2814 M7 (DD-7): the run was passed to a taxi firm. */
const RUN_TAXI_PASSED: TravelledRefusalCode = 'run_taxi_passed';

/** board 2377 — the `not-confirmed` banner. Queried in specs through this
 *  constant, never a literal. */
export const MARK_TRAVELLED_NOT_CONFIRMED_COPY =
  'This run is not confirmed yet, so it cannot be marked as travelled.';

export async function markRunTravelled(bookingId: number): Promise<MarkTravelledOutcome> {
  try {
    const res = await postJson<{ changed?: boolean }>(
      `/api/bookings/${bookingId}/travelled`,
      {},
    );
    return res.changed === true ? 'marked' : 'already';
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      // Off the CODE, never the sentence (SEC-5). An uncoded 409 is the
      // cancelled refusal, which keeps its body unchanged on the server; an
      // unknown code is NOT assumed to be one.
      const body = e.body;
      const code = typeof body === 'object' && body !== null && 'code' in body
        ? (body as { code: unknown }).code
        : undefined;
      if (code === undefined) return 'cancelled';
      if (code === RUN_TAXI_PASSED) return 'taxi-passed';
      return code === RUN_NOT_CONFIRMED ? 'not-confirmed' : 'error';
    }
    return 'error';
  }
}

/**
 * THE SECOND DOOR, client half (T660 M5 / P0-11): correct the stored
 * `customers.email` for this booking's customer.
 *
 * Deliberately invoked — it fires only when an operator types a replacement on
 * the booking record screen and presses Save. It is not, and must never become,
 * something a capture triggers: that separation IS the narrowed ruling (see
 * CORRECTION_STATEMENTS in core/data/customers-repo.ts).
 *
 * The outcome is a closed union rather than a thrown error, because three of the
 * four cases are things Helen must be TOLD, not failures to retry:
 *   - 'corrected'  : written. `resendPrompt` is R7 — the quote had already gone
 *                    by email to the address just replaced, so a live payable
 *                    deposit link is in the wrong inbox and the record alone is
 *                    not the whole remedy.
 *   - 'unchanged'  : the stored address already was this one (case-folded). No
 *                    write, no audit row, and saying "saved" would be a lie.
 *   - 'conflict'   : 409 — on file for a DIFFERENT customer, nothing written.
 *                    The copy for this NAMES NO PARTY (SEC-5); see the screen.
 *   - 'invalid'    : 400 — the address is not a valid one, nothing written.
 *   - 'error'      : anything else. TRANSIENT, "try again in a moment" copy.
 *
 * ⚠️ 'invalid' EXISTS BECAUSE THIS COMMENT USED TO BE WRONG. It said a 400 was
 * "a 400 the field should have prevented" and folded it into 'error', whose copy
 * says "try again in a moment". The live ux-review reproduced a 400 by typing a
 * malformed address: `<input type="email">` outside a <form> runs NO native
 * validation, so nothing prevented it, and the operator was told to retry an act
 * that fails identically every time. A permanent refusal must not be reported as
 * a transient fault.
 *
 * MAPPED FROM THE STATUS, not from the body (SEC-5, and api.ts's header): the
 * screen owns the sentence. The route's only other 400 is a non-integer booking
 * id, which is this client's own value and never operator-typed, so on this
 * route 400 means exactly one thing to Helen. A client-side format check was the
 * alternative and is the weaker one: it would duplicate the server's zod rule in
 * the browser, and the two disagreeing would put us straight back here.
 */
export type CorrectEmailOutcome =
  | { kind: 'corrected'; resendPrompt: boolean }
  | { kind: 'unchanged' }
  | { kind: 'conflict' }
  | { kind: 'invalid' }
  | { kind: 'error' };

export async function correctCustomerEmail(
  bookingId: number,
  email: string,
): Promise<CorrectEmailOutcome> {
  try {
    const res = await postJson<{ changed?: boolean; resendPrompt?: boolean }>(
      `/api/bookings/${bookingId}/customer-email`,
      { email },
    );
    return res.changed === true
      ? { kind: 'corrected', resendPrompt: res.resendPrompt === true }
      : { kind: 'unchanged' };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) return { kind: 'conflict' };
    if (e instanceof ApiError && e.status === 400) return { kind: 'invalid' };
    return { kind: 'error' };
  }
}

/**
 * ⭐ board 2788 M1 (R-1) - "Change date and time", client half. The
 * `correctCustomerEmail` shape: a closed outcome union, never a throw, because
 * most outcomes are things Helen must be TOLD rather than failures to retry.
 *   - 'changed'   : written; `notices` are the follow-ups the server read after the commit.
 *   - 'unchanged' : the booking already had this date and time. Nothing written.
 *   - 'refused'   : a coded 409. The screen words the CODE (SEC-5); the only server
 *                   values it shows are the invoice NUMBER and the stated return.
 *   - 'invalid'   : 400, a date, time or reason the server will not take.
 *   - 'error'     : anything else, including a 409 with a code this build does not know.
 */
export type RescheduleOutcome =
  | ({
      kind: 'changed';
      notices: { tellCustomer: boolean; replaceInvoice: boolean; tellTeam: boolean } & BalanceMoveNotices;
      /** ⭐ board 2871 - only the return moved; the outbound is unchanged. */
      returnOnly?: true;
    } & LinkedRescheduleFacts)
  /** ⭐ board 2811 M8: a re-press carries the balance facts too, present only when
   *  one is set, so a retry that fails again is not silent. */
  | ({ kind: 'unchanged'; notices?: BalanceMoveNotices } & LinkedRescheduleFacts)
  /** ⭐ Board 2788 M4 - a linked change the home's request refused, before anything was
   *  written. ⭐ M6 (step 0, DD-12): a refusal on the request's RETURN carries the
   *  return's Dublin day and time, so the screen can name it. */
  | { kind: 'portal_refused'; code: LinkedRescheduleRefusal; returnDay?: string | null; returnTime?: string | null }
  | {
      kind: 'refused';
      code: RescheduleRefusal;
      invoiceNumber?: string;
      returnDay?: string | null;
      returnTime?: string | null;
    }
  | { kind: 'invalid' }
  | { kind: 'error' };

/**
 * ⭐ board 2811 M8 - the balance fact the server read after the move; at most one
 * is true. `balanceAskNotMoved` (A1 item 1): the ask had gone out, or goes out
 * within the hour, so it kept the old date. `balanceAskMoveFailed` (security L3):
 * the move failed; the screen sends Helen to Alex (a later save still retries it).
 */
export type BalanceMoveNotices = { balanceAskNotMoved: boolean; balanceAskMoveFailed: boolean };

/**
 * ⭐ Board 2788 M4/M6 - what a PORTAL-LINKED change also says. Every key is absent
 * for a kiosk-only booking:
 *   · `portal`    - what became of the home's request (DD-18);
 *   · `pickup`    - the collection the booking now leads with, which the portal
 *                   derived (DD-3), as a Dublin day and `HH:MM` (M6 step 3);
 *   · `askClosed` - the change closed the home's open ask (DD-8, M6 step 0).
 */
export type LinkedRescheduleFacts = {
  portal?: LinkedPortalOutcome;
  pickup?: { day: string; time: string };
  askClosed?: true;
};

export async function rescheduleBooking(
  bookingId: number,
  /** ⭐ board 2871: `returnDay`/`returnTime`, both or neither, for a kiosk-only
   *  booking with its return on; sent only when the Return time is filled. */
  input: { day: string; time: string; reason: RescheduleReason; returnDay?: string; returnTime?: string },
  /** ⭐ Board 2788 M6 (step 0; DD-1, S22): the home's request revision this screen
   *  rendered, for a portal-linked booking. A stale one is refused before anything
   *  is written. null sends none (a kiosk-only booking, or a request not read). */
  revision: number | null = null,
): Promise<RescheduleOutcome> {
  try {
    const res = await postJson<{
      changed?: boolean;
      notices?: { tellCustomer?: boolean; replaceInvoice?: boolean; tellTeam?: boolean; balanceAskNotMoved?: boolean; balanceAskMoveFailed?: boolean };
      portal?: unknown;
      pickup?: unknown;
      askClosed?: unknown;
      returnOnly?: unknown;
    }>(`/api/bookings/${bookingId}/reschedule`, revision === null ? input : { ...input, revision });
    // ⭐ Board 2788 M4: a linked booking's answer carries the request's outcome.
    const portal = isLinkedPortalOutcome(res.portal) ? res.portal : undefined;
    const p = (typeof res.pickup === 'object' && res.pickup !== null ? res.pickup : {}) as Record<string, unknown>;
    const pickup = typeof p['day'] === 'string' && typeof p['time'] === 'string'
      ? { day: p['day'], time: p['time'] }
      : undefined;
    const facts: LinkedRescheduleFacts = {
      ...(portal === undefined ? {} : { portal }),
      ...(pickup === undefined ? {} : { pickup }),
      ...(res.askClosed === true ? { askClosed: true as const } : {}),
    };
    const balance: BalanceMoveNotices = {
      balanceAskNotMoved: res.notices?.balanceAskNotMoved === true,
      balanceAskMoveFailed: res.notices?.balanceAskMoveFailed === true,
    };
    if (res.changed !== true) {
      return balance.balanceAskNotMoved || balance.balanceAskMoveFailed
        ? { kind: 'unchanged', notices: balance, ...facts }
        : { kind: 'unchanged', ...facts };
    }
    return {
      kind: 'changed',
      notices: {
        tellCustomer: res.notices?.tellCustomer === true,
        replaceInvoice: res.notices?.replaceInvoice === true,
        tellTeam: res.notices?.tellTeam === true,
        ...balance,
      },
      ...(res.returnOnly === true ? { returnOnly: true as const } : {}),
      ...facts,
    };
  } catch (e) {
    if (e instanceof ApiError && e.status === 400) return { kind: 'invalid' };
    if (e instanceof ApiError && e.status === 409) {
      const body = (typeof e.body === 'object' && e.body !== null ? e.body : {}) as Record<string, unknown>;
      const code = body['code'];
      const text = (k: string): string | null => (typeof body[k] === 'string' ? body[k] : null);
      if (isLinkedRescheduleRefusal(code)) {
        return code === 'portal_refused' && text('returnTime') !== null
          ? { kind: 'portal_refused', code, returnDay: text('returnDay'), returnTime: text('returnTime') }
          : { kind: 'portal_refused', code };
      }
      if (!isRescheduleRefusal(code)) return { kind: 'error' };
      const invoiceNumber = typeof body['invoiceNumber'] === 'string' ? body['invoiceNumber'] : undefined;
      return {
        kind: 'refused',
        code,
        ...(invoiceNumber === undefined ? {} : { invoiceNumber }),
        ...(code === 'after_return' ? { returnDay: text('returnDay'), returnTime: text('returnTime') } : {}),
      };
    }
    return { kind: 'error' };
  }
}

/**
 * T1002 M7 — "Cancel this run", client half, WITH the optional refund.
 *
 * WHAT BECAME OF THE MONEY, as its own report. `POST /api/bookings/:id/cancel`
 * answers 200 in every refund arm - including the two failures - because by the
 * time the money is decided the cancel has already COMMITTED, so a 4xx would
 * tell Helen nothing happened when the run really is gone. The enclosing `kind`
 * says what became of the RUN; this says what became of the MONEY, and the
 * screen has to be able to say both in one sentence.
 *
 * ⛔ NO `code`, DELIBERATELY, and this is the one place it could have leaked.
 * The server distinguishes seven refusal reasons (five from the refund engine
 * plus `stripe_unconfigured` and `rate_limited`); Helen's remedy for every one
 * of them is identical - the run is cancelled, no money went back, issue it by
 * hand if it is owed - and the codes are internal vocabulary that SEC-5 keeps
 * off the screen. The distinctions are not lost: each one is already on the
 * durable channel (reportCriticalAnomaly) where the person who can act on the
 * difference will see it. simplified: one refusal sentence rather than seven,
 * upgrade trigger = a refusal whose OPERATOR remedy actually differs.
 */
export type CancelRefundReport =
  | { outcome: 'refunded'; amountCents: number }
  | { outcome: 'retained' }
  | { outcome: 'refused' }
  | { outcome: 'unknown' };

/**
 * ⛔ 'unknown' IS NOT 'refused', and collapsing them would be the worst
 * available bug on this path. Refused means no money moved and we know it.
 * Unknown means a refund row exists, may or may not have reached the provider,
 * and a human has to look - which is why its copy is the shared
 * REFUND_OUTCOME_UNKNOWN_COPY and not a "try again".
 *
 * UNRECOGNISED SHAPES READ AS 'unknown', never as a refusal. A server newer than
 * this bundle could add an outcome; assuming no money moved on an outcome we
 * cannot read is the direction that ends in a second real refund.
 *
 * ⛔ ABSENT AND UNREADABLE ARE DIFFERENT ANSWERS, and reading them as one
 * contradicted the rule directly above (code-review R7). `null` is returned for
 * ABSENCE ONLY - the key is not on the response, which is the server's positive
 * record that no refund arm ran (the idempotent repeat, and every cancel with no
 * `refundCents` on the wire). Anything PRESENT that this bundle cannot parse - a
 * string, a number, an outcome added by a newer server - is 'unknown', because
 * something was reported and we cannot say it was nothing. Only reachable under
 * client/server version skew, which is exactly when a guess is worst.
 */
function readRefundReport(v: unknown): CancelRefundReport | null {
  if (v === undefined) return null;
  if (v === null || typeof v !== 'object') return { outcome: 'unknown' };
  const o = v as Record<string, unknown>;
  const amount = o['amountCents'];
  switch (o['outcome']) {
    case 'refunded':
      // The SERVER's figure, not the operator's typed one: what actually went
      // back is what the banner must say.
      return typeof amount === 'number' && Number.isFinite(amount)
        ? { outcome: 'refunded', amountCents: amount }
        : { outcome: 'unknown' };
    case 'retained': return { outcome: 'retained' };
    case 'refused': return { outcome: 'refused' };
    default: return { outcome: 'unknown' };
  }
}

/**
 * ⛔ TERMINAL, AND THERE IS NO UNDO. `setBookingStatus`'s UPDATE ends
 * `AND status NOT IN ('cancelled','completed')`, so a cancel is irreversible
 * through application code, and a refund that has reached the provider cannot be
 * recalled either. Every caller must put a confirm step in front of this.
 *
 * The outcomes:
 *   - 'cancelled'         : the run is cancelled BY THIS CALL. `refund` is null
 *                           when no refund was asked for (an unpaid booking).
 *   - 'already-cancelled' : the server's idempotent arm (`changed:false`). No
 *                           second refund was issued, and the ABSENCE of a
 *                           refund block is the server's record of that.
 *   - 'refused'           : 409. The run is UNCHANGED - it is completed, or
 *                           paid with no refund asked for.
 *   - 'refused-invoiced'  : 409 T1011 M6. The run is UNCHANGED because it is on
 *                           an account invoice that has already been issued. A
 *                           SEPARATE kind rather than a flag on 'refused',
 *                           because it is the one refusal with a DIFFERENT
 *                           remedy: void that document, then cancel. The generic
 *                           sentence ("it may already be finished") is false
 *                           here, and a false explanation on a terminal action
 *                           is worse than a vague one.
 *   - 'error'             : anything else, including a 404 and a 400. Transient
 *                           copy at the screen.
 *
 * ⚠️ THE COPY COMES FROM THE STATUS, NEVER THE SERVER BODY (SEC-5, api.ts's
 * header). The 409 sentences are ours and are actionable, but rendering them
 * would be a second exception to that rule for no gain: the screen only offers
 * this control on a run it believes is cancellable, so a 409 is a state
 * disagreement, and "it may already be finished" covers every member of the set.
 */
/**
 * ⭐⭐ T2088 REWORK 1 — WHAT THE CANCEL DID TO THE CROSSED PORTAL REQUEST.
 *
 * ⛔ `null` IS "THIS BOOKING NEVER CROSSED FROM THE PORTAL", which is every public
 * booking, and it is the reason this is not a member of the union: absence is the
 * ordinary case and modelling it as a word would invite a screen to render it.
 * ⛔ ONLY TWO OF THE FOUR HAVE WORDS ON A SCREEN. `cancelled` and `already-ended`
 * are both "the home knows", and saying so on every ordinary cancel is the kind of
 * chatter Owen and Helen bounced off a previous tool for. `unreachable` and
 * `failed` are the ones where the run is cancelled and the NURSE STILL READS
 * "The taxi is booked." — which is the defect the live drive found.
 */
export type PortalRequestSweepOutcome = 'cancelled' | 'already-ended' | 'unreachable' | 'failed';

/** ⛔ A NARROWING READ, NOT A CAST — `readBookingOutcome`'s rule, for the same
 *  reason: an older kiosk build or a proxy must read as "nothing to say" rather
 *  than render a word this screen has no copy for. */
function readPortalRequestSweep(v: unknown): PortalRequestSweepOutcome | null {
  return v === 'cancelled' || v === 'already-ended' || v === 'unreachable' || v === 'failed'
    ? v
    : null;
}

export type CancelBookingOutcome =
  | { kind: 'cancelled'; refund: CancelRefundReport | null; portalRequest: PortalRequestSweepOutcome | null }
  | { kind: 'already-cancelled' }
  | { kind: 'refused' }
  | { kind: 'refused-invoiced'; invoiceNumber: string }
  // ⭐ board 2814 M4 (Captain ruling (c)) — the trip ran, so its run stays on the
  // month's bill; nothing changed. Only booking detail's acknowledged confirm may
  // cancel it (`travelledAcknowledged`).
  | { kind: 'refused-travelled' }
  // board 2343 — "Not going ahead" was refused: the job is past the point it may
  // be dropped (a payment, or no longer new/quoted). Nothing changed.
  | { kind: 'refused-not-going-ahead' }
  | { kind: 'error' };

/**
 * ⭐ T2088 REWORK 1 — the run is cancelled and the HOME WAS NOT TOLD.
 *
 * ⛔ IT SAYS WHAT IS STILL TRUE ON THE NURSE'S SCREEN, not that "a sync failed":
 * she is looking at "The taxi is booked." for a bus nobody is sending, and the
 * only thing that closes that today is a phone call. ⚠️ It does NOT say "try
 * again" on the unreachable arm, because nothing a kiosk operator can do changes
 * a seam that is switched off.
 */
export const CANCEL_PORTAL_NOT_TOLD_COPY =
  'The run is cancelled, but the home has NOT been told: the nurse still sees this trip as booked. '
  + 'Ring the home, and tell the office the portal link needs looking at.';
/** T2088 REWORK 1 — the transient half of the same fact, where a retry is worth it. */
export const CANCEL_PORTAL_RETRY_COPY =
  'The run is cancelled, but the home has NOT been told: the nurse still sees this trip as booked. '
  + 'Open the request on the Requests tab and end it there, or ring the home.';

/**
 * @param refundCents  omitted for a booking with no paid deposit (unchanged
 *   pre-M7 behaviour: a paid booking then 409s, which is the opt-in safety of
 *   the server option). 0 means "cancel and keep the whole deposit".
 */
export async function cancelBookingRun(
  bookingId: number,
  refundCents?: number,
  /** board 2343 — 'not-going-ahead' posts `{notGoingAhead: true}`, the tighter
   *  cancel; never combined with a refund (the server 400s that). */
  mode?: 'not-going-ahead',
  /** ⭐ board 2814 M4 (Captain ruling (c)) — booking detail ONLY, and only from a
   *  cancel confirm that said the trip ran and that cancelling takes the run off
   *  the month's bill. The Requests row never sends it, so a stale screen or an
   *  old bundle is refused (`run_travelled`). */
  opts: { travelledAcknowledged?: true } = {},
): Promise<CancelBookingOutcome> {
  // ⚠️ postJson, NOT a hand-rolled fetch. This route calls `requiresJsonBody`
  // (it is the first /api/bookings/:id/* route to), so a request without
  // `content-type: application/json` 415s before anything is read.
  try {
    const res = await postJson<{ changed?: boolean; refund?: unknown; portalRequest?: unknown }>(
      `/api/bookings/${bookingId}/cancel`,
      {
        ...(mode === 'not-going-ahead' ? { notGoingAhead: true } : refundCents === undefined ? {} : { refundCents }),
        ...(opts.travelledAcknowledged === true ? { travelledAcknowledged: true } : {}),
      },
    );
    if (res.changed !== true) return { kind: 'already-cancelled' };
    const report = readRefundReport(res.refund);
    // ⛔ ASKED FOR MONEY TO MOVE AND GOT NO REPORT = 'unknown', never null.
    // The server cannot produce that pairing today, but "we asked for a refund
    // and cannot say what happened" must never render as "no refund was asked
    // for" - that reading sends nobody to look.
    return {
      kind: 'cancelled',
      refund: report ?? (refundCents !== undefined && refundCents > 0 ? { outcome: 'unknown' } : null),
      // ⚠️ ON THE `cancelled` ARM ONLY, AND THAT IS NOT AN OVERSIGHT. The server
      // runs sweep 3 only for a cancel that reached the sweeps at all; the 409 arms
      // below never got that far, and `already-cancelled` is a repeat press whose
      // FIRST press already reported whatever there was to report.
      portalRequest: readPortalRequestSweep(res.portalRequest),
    };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      // ⭐ board 2814 M4 — keyed on the CODE, first, as the server decides it first.
      if ((e.body as { code?: unknown } | null)?.code === 'run_travelled') return { kind: 'refused-travelled' };
      // T1011 M6 — the ONE 409 with its own remedy. Read as a keyed code, never
      // rendered: the sentence the screen shows is ours, and the only thing that
      // travels is the invoice NUMBER we allocated.
      const invoicedOn = (e.body as { invoicedOn?: unknown } | null)?.invoicedOn;
      if (typeof invoicedOn === 'string' && invoicedOn !== '') {
        return { kind: 'refused-invoiced', invoiceNumber: invoicedOn };
      }
      // board 2343 — keyed on the PRESENCE of the code, never its wording.
      if (typeof (e.body as { notGoingAhead?: unknown } | null)?.notGoingAhead === 'string') {
        return { kind: 'refused-not-going-ahead' };
      }
      return { kind: 'refused' };
    }
    return { kind: 'error' };
  }
}

/** T1002 M7 — the run is cancelled and the deposit was kept in full. */
export const CANCEL_RETAINED_COPY =
  'The run is cancelled and the whole deposit was kept. Nothing has been sent back.';
/**
 * T1002 M7 — the run is cancelled and the refund was REFUSED, which is the one
 * thing we can say for certain: no money moved. Distinct from
 * REFUND_OUTCOME_UNKNOWN_COPY, which is the case where it may have.
 */
export const CANCEL_REFUND_REFUSED_COPY =
  'The run is cancelled, but the refund did not go through, so no money has been sent back. '
  + 'Issue it by hand if it is owed.';
/** T1002 M7 — the idempotent arm: somebody (or a second tap) got here first. */
export const CANCEL_ALREADY_COPY =
  'This run was already cancelled, so nothing changed and no refund was issued now.';
/** T1002 M7 — the 409. The RUN did not move, which is the half that matters. */
export const CANCEL_REFUSED_COPY =
  'This run cannot be cancelled, so nothing changed. It may already be finished.';
/** board 2343 rework 1 — "Not going ahead" landed. It restates what the confirm
 *  promised, in the same words (job, Cancelled, deposit link, nothing sent),
 *  rather than the generic cancel sentence. */
export const NOT_GOING_AHEAD_DONE_COPY =
  'The job moved to Cancelled. Any deposit link stopped working, and nothing was sent to the customer.';
/** board 2343 — "Not going ahead" refused. The run is still on, and the
 *  disclosure below is the way to end it (with a refund, if one is owed). */
export const NOT_GOING_AHEAD_REFUSED_COPY =
  'This job cannot be marked not going ahead any more, so nothing changed. It may have a payment on it. '
  + 'Use Cancel this run below instead.';
/** T1002 M7 — transient. Nothing changed, and retrying is worth a try. */
export const CANCEL_FAILED_COPY =
  'Cancelling this run did not work. Nothing changed. Try again in a moment.';

/**
 * T1011 M6 — the run is on an issued account invoice.
 *
 * ⛔ A FUNCTION, NOT A CONSTANT, BECAUSE THE NUMBER IS THE ACTIONABLE PART. The
 * remedy lives on ONE document and the operator has to find it; "an invoice"
 * would send her through a list.
 *
 * ⚠️ IT NAMES THE REMEDY IN THE ORDER IT HAS TO BE DONE, and it does not say
 * "try again": retrying returns the same refusal for ever until the invoice is
 * withdrawn. It also does NOT promise the run is off the bill, because it is not:
 * refusing is precisely what keeps the ledger and the issued document agreeing.
 *
 * ⭐ T1447 M6 — AND THE REMEDY IS NOW SOMEWHERE. This sentence said "Void that
 * invoice first" while `voidIssuedAccountInvoice` had no route at all, so it
 * prescribed a step no screen could perform. It names the tab now.
 */
export function cancelInvoicedCopy(invoiceNumber: string): string {
  return `This run is on invoice ${invoiceNumber}, which has already been issued, so it cannot be cancelled here `
    + 'and nothing has changed. Withdraw that invoice on the Invoices tab first, then cancel the run and issue the month again.';
}

/* ⛔ `fetchBookingSeries` (the nursing schedule read-back for a re-open) WAS HERE and
   went at board 2814 M6 (DD-8): "Re-open in calculator" is hidden on every nursing
   booking (`calculatorAllowed`), so nothing re-opens a schedule. The server route
   GET /api/bookings/:id/series stays. `openNursingInvoice` left at board 2764 M6. */

/**
 * 'Re-open in calculator' (net-locked): source the job from the DURABLE
 * booking row (R15 A3 — real persisted flags, never hardcoded false),
 * resolve locations exact-match-only, land on the furthest-ready screen.
 */
export async function reopenBooking(
  store: Store,
  registry: Registry,
  detail: ApiBookingDetail,
): Promise<void> {
  const b = detail.booking;
  // T414 F4 (FB2): SERVER TRUTH decides the service. request_json persisted the
  // raw unfolded service (serviceToBookingType folds 'nursing-home' to
  // 'local-other' on the row), so the latest quote's service wins.
  // ⛔ board 2814 M6 (DD-8): the nursing part of this re-open (the schedule read
  // and `job.nursingHome`) is gone. The screen offers Re-open only when the server
  // says `calculatorAllowed`, which is false for every nursing booking.
  const service = detail.latest_quote_service ?? b.booking_type;
  const descriptor = serviceById(registry, service);
  // Unknown booking types (Phase-2 e.g. recurring_contract) land on the
  // manual surface — never a grid screen or an engine POST for a service
  // the engine doesn't know.
  const mode = descriptor?.pricingMode ?? 'manual';

  // T549 M4 — the FOURTH reader of a lone stop's role, on the SAME rule as the
  // three message producers (shared/journey-ends.ts) rather than a local
  // convention. It used to map stops[0] to the pickup unconditionally, so an
  // ORDINARY wedding — one stop, and that stop IS the venue — re-opened with the
  // venue sitting in the pickup field (and its eircode/town in the pickup
  // capture fields), after which the next persist wrote the venue into BOTH
  // ends. Helen's screen and the customer's text then disagreed about one row.
  //
  // The whole rule is shared, not just the venue arm. This reader holds the
  // UNFOLDED service ('nursing-home'), while the row's booking_type has already
  // folded that to 'local-other' (serviceToBookingType) — and the rule gives
  // both the same answer (a lone stop is the pickup Helen typed), so the fold
  // is no longer a reason to keep a second reader. The nursing-home pin in
  // wedding-pickup-alert.spec.ts holds that.
  //
  // AIRPORT is the one arm this changes: a lone airport stop is the terminal
  // (the form composes both ends and journeyStops appends the airport whenever
  // a pickup exists, so a lone row means the pickup half is missing). Putting it
  // in the pickup field re-persisted it as ['Dublin Airport T2', 'Dublin
  // Airport'] via that same fallback; as the drop-off end it round-trips to the
  // one stop it came from.
  const ends = resolveJourneyEnds({ booking_type: service, venue: b.venue }, detail.stops);
  const pickupStop = ends.pickup;
  const dropoffStop = ends.dropoff;
  // T540 (516) — a stop whose place label IS its street was written by the
  // manual pickup fallback (pickupEndPlace writes the typed street to BOTH
  // columns). Carrying it back into pickupSearchText would FREEZE it: that term
  // outranks the address line, so Helen's correction on a re-opened wedding
  // would update address_line while `address` -- the team alert's pickup, the
  // customer SMS end, the precedent key -- kept the original street forever.
  // Exactly the half-landed-edit class this task exists to close. Leaving it
  // null lets the editable field own the end, mirroring how dropoffEndPlace
  // ranks the editable venue above the persisted carry.
  // Keyed on the two columns being EQUAL, never on the service: a private-tour
  // or nursing-home row whose label is a real place ('Bray', an account name)
  // differs from its street, so those round-trip exactly as before.
  const pickupLabelIsStreet =
    pickupStop !== null &&
    pickupStop.address_line !== null &&
    pickupStop.address === pickupStop.address_line;
  const pickupText = pickupLabelIsStreet ? null : pickupStop?.address ?? null;
  const dropoffText = dropoffStop?.address ?? null;
  // Middle stops survive the re-open RICH (T400: this path used to drop the
  // stored eircodes even though the server returned them) — mirrors the
  // first=pickup / last=dropoff assumption the lines above already make.
  const middleStops = detail.stops.length > 2
    ? detail.stops.slice(1, -1).map((s) => ({ address: s.address, eircode: s.eircode ?? null }))
    : [];
  let location: { id: string; label: string } | null = null;
  let toLocation: { id: string; label: string } | null = null;
  let pickupTerminal: string | null = null;
  if (mode !== 'manual' && service !== 'golf') {
    location = await resolveLocationExact(pickupText);
    if (mode === 'p2p') toLocation = await resolveLocationExact(dropoffText);
    // T515 ARRIVALS round-trip. Once the pickup row correctly holds a TERMINAL
    // it can never resolve, so without this the re-opened job has no locationId
    // and furthestReadyScreen walls Helen on a location search pre-typed with a
    // terminal that matches nothing — trading one P0 for another.
    //
    // Key on the durable `town` COLUMN, never dropoffStop.address: that address
    // is the composed "street, town" ("5 Seafront Cottages, Kilcoole") and
    // resolveLocationExact needs a unique EXACT label (isDeterministic excludes
    // fuzzy), so keying on it returns null in exactly the case this exists for.
    //
    // The guard is what keeps it arrivals-only, and it takes TWO facts about the
    // pickup end, not one. CORRECTION (T608 M6c security review): this comment
    // used to claim the town clause blocked departures "ALONE … independent of
    // the first", which was FALSE the moment it was written as
    // `pickupStop?.town == null` — that reads true both when a pickup end exists
    // carrying no town (a terminal, the case meant) and when there is NO PICKUP
    // END AT ALL (`undefined == null`). So the precondition is stated explicitly:
    // a pickup end must EXIST, and *its* town must be null. Departures are then
    // blocked because their pickup row IS the address and carries a town.
    //
    // Not hypothetical, and it is M6c that made it reachable: `resolveJourneyEnds`
    // now prefers `booking_stops.role`, and a lone row recorded 'destination' —
    // what buildStops writes for an unauthenticated `/api/inquiry` POST carrying
    // direction + terminal + dropoff and NO pickup — truthfully resolves to
    // `{pickup: null}`. Pre-v20 the town heuristic mis-read that row as the pickup,
    // which is the only reason the vacuous clause never fired. Measured cost when
    // it did: the submitter's structured town became the grid RATE KEY, the
    // re-persist wrote ['Arklow', '4 Church Rd, Greystones'] (the airport gone,
    // a pickup end the record never claimed), and Helen landed on 'addons' —
    // past the Location screen that would have shown her the choice. Pinned in
    // test/client/admin-screens.spec.tsx, with the arrivals arm as its control.
    //
    // `pickupTerminal = pickupText` needs no second null-check of its own: it can
    // only be assigned inside this branch, and the branch now requires the pickup
    // row that `pickupText` is read from.
    //
    // `service === 'airport'` is belt-and-braces: the else already chains off
    // the p2p branch, so grid-minus-golf is airport alone. Stated explicitly so
    // the guard survives a refactor that unchains it — without it, a p2p
    // local-other reopen would price a hand-quoted off-gazetteer pickup against
    // the DROP-OFF's town.
    else if (
      service === 'airport' && location === null
      && pickupStop !== null && pickupStop.town == null
      && dropoffStop?.town != null
    ) {
      const addressEnd = await resolveLocationExact(dropoffStop.town);
      if (addressEnd !== null) {
        location = addressEnd;
        pickupTerminal = pickupText;
      }
    }
  }
  /**
   * T608 M6a (R3) — the GOLF re-price, and the reason golf stays OUT of the
   * label-resolution block above.
   *
   * A golf rate is keyed by ROUTE (`<hub-slug>__<course-slug>`), not by a place,
   * so `resolveLocationExact` can never find it and a re-opened golf job used to
   * land unpriced on the hub picker with the whole route sitting in a search box
   * that matches nothing. The key that priced it is already persisted, in the
   * quote's own `request_json` — so restore THAT, exactly, and never recompose a
   * money key from two operator-editable stop labels.
   *
   * `locationLabel` is a DISPLAY value and IS recomposed from the two ends: it is
   * the inverse of the split `journeyStops` does, which makes reopen →
   * journeyStops a fixed point (the same round-trip the arrivals reopen pins).
   * It must be restored: `journeyStops`' golf arm reads it, and this repo has a
   * recorded defect of exactly the shape a null label would cause.
   *
   * `courseLeg: true` is PROVEN by the KEY MATCH, not inferred from the stop text.
   * Both key writers compose the id the same way — `${slugify(hubName)}__${slugify(routeName)}`
   * (shared/pricing-flatten.ts for the seeded grid, core/data/audit-repo.ts's
   * `entryKeys` for an operator-added course) — and `slugify` maps every
   * non-alphanumeric run to '-', so it strips the course prefix's arrow:
   * `slugify('→ Druids Glen Resort') === slugify('Druids Glen Resort')`. A COURSE leg's two
   * ends therefore re-derive the persisted key byte for byte, and a pair that
   * re-derives it demonstrably IS the hub and the course the rate names. Nothing
   * about the rate is guessed, so the recomposed display label cannot name a place
   * the rate does not.
   *
   * `slugify` is a PREDICATE here and nothing more. The restored money key stays
   * `detail.latest_quote_location_id` verbatim — never a recomposition. slugify is
   * lossy (case, punctuation and the arrow all collapse), so a recomposed key could
   * name a DIFFERENT rate; it is only ever safe to ask "does this pair derive the
   * key we already hold?".
   *
   * A TEXT heuristic was NOT enough, measured rather than theorised. The two
   * `splitRouteLabel(...) === null` clauses prove only that neither end is ITSELF a
   * composite; they cannot prove the pair belongs to the rate. `persistQuote`
   * replaces stops only when the request carries a non-empty list, so a golf
   * booking can still hold two plain INTAKE stops — a customer street plus an
   * unrelated hotel — alongside a valid key. That pair passed the text-only gate,
   * and the restore then minted "12 Main St, Bray — → Druids Glen Hotel": a
   * fabricated course-form label naming places no rate names, which reaches a
   * CUSTOMER through the quote SMS/email composer and the team WhatsApp pickup
   * line — while `journeyStops` split it back into two rows and DISCARDED the
   * second stop's real address_line/eircode/town on the next persist. The key
   * match refuses that record, so it lands on the hub picker with both stops
   * carried, exactly as a golf booking with no quote version does. Pinned in
   * test/client/admin-screens.spec.tsx ('two ordinary places that are NOT the
   * rate's pair').
   *
   * The composite clauses stay, and are STRICTLY additive to the key match: they
   * prove neither end carries a boundary, so the recomposed display label
   * round-trips through exactly one. Load-bearing beyond the arrow, which is why
   * they are checks rather than comments: golf allows `extraPickups`, so an
   * airport-leg job with a middle stop persists [composite, middle] — recomposing
   * from those would mint a TWO-boundary label that `splitRouteLabel` cannot take
   * back apart, and the next persist would collapse the two rows into one mangled
   * address. Refusing it keeps that record byte-exact. (`journeyStops` splits
   * COURSE LEGS ONLY, so an AIRPORT leg's record is a single row whose one address
   * is the whole composite label — it splits, so it is refused here and lands on
   * the hub picker exactly as it did before M6a.)
   * simplified: no leg-shape column (nothing left needs one — an airport leg is
   * never recomposed), upgrade trigger = a reader that keys on the recomposed label.
   *
   * `containsHubBoundary` is the BYTE test and it is the one that actually closes
   * the hole (T608 M6a security review, advisory 1). `splitRouteLabel(x) === null`
   * looked like "x carries no boundary" and is not: the splitter also returns null
   * when a boundary IS present but a half is empty, so a trailing-separator end
   * slipped through. Measured through the real modules: pickupText
   * 'Arklow Bay Hotel — ' splits to null AND slugifies to 'arklow-bay-hotel', so
   * every other clause passed and the restore minted
   * 'Arklow Bay Hotel —  — → Druids Glen Resort' — two boundaries; `journeyStops` then
   * re-split it, read `courseLeg: false`, took the single-end path, and the next
   * persist COLLAPSED the two-row record into one row holding that fabricated
   * label, losing the course row. Reachable only by an authenticated operator
   * writing the stop address through the API (`parseStops` takes any 200-char
   * string), never through the kiosk UI, and with no privilege gain — but it is
   * the fabricated-label + lost-row class this milestone exists to close.
   *
   * The two `splitRouteLabel(...) === null` clauses are kept BENEATH it, and they
   * are subsumed: no-boundary implies split-null, so dropping one of them alone
   * cannot change the outcome and no test can red on it. Kept deliberately, on the
   * `service === 'golf'` precedent above — they state the SEMANTIC refusal ("neither
   * end is itself a pair") in the vocabulary the rest of this docstring uses, so a
   * future narrowing of `containsHubBoundary` cannot silently reopen the hole.
   *
   * Clause inventory, so nobody spends a round mutation-proving the wrong one.
   * MEASURED (each drop reds exactly one pin in test/client/admin-screens.spec.tsx):
   * `!containsHubBoundary(pickupText)` -> the trailing-separator pin;
   * `!containsHubBoundary(dropoffText)` -> its drop-off mirror; the key-match
   * conjunct -> the not-the-rate-pair pin.
   * SUBSUMED (nothing can red them, see above): the two split-null clauses.
   * NARROWING ONLY: `latest_quote_location_id != null && !== ''` cannot change the
   * runtime outcome — the key-match conjunct compares against a string that always
   * contains '__', so a null/empty id already fails it — it is there so TypeScript
   * narrows the `id:` assignment below to `string`.
   * STRUCTURAL: `service === 'golf'` and the two `!== null` end checks are not
   * mutable in isolation — dropping an end check does not even typecheck, because
   * the boundary predicate and `slugify` both take a `string`.
   *
   * A booking with no quote version, a legacy one-stop golf row (no drop-off end),
   * a boundary-bearing end, or a pair that does not derive the persisted key has
   * nothing restorable and degrades honestly to the hub picker — with its stops
   * carried in the search texts, so a re-persist still reproduces the record
   * rather than wiping it.
   */
  const golfRoute =
    service === 'golf' && pickupText !== null && dropoffText !== null
      && !containsHubBoundary(pickupText) && !containsHubBoundary(dropoffText)
      && splitRouteLabel(pickupText) === null && splitRouteLabel(dropoffText) === null
      && detail.latest_quote_location_id != null && detail.latest_quote_location_id !== ''
      && `${slugify(pickupText)}__${slugify(dropoffText)}` === detail.latest_quote_location_id
      ? {
          id: detail.latest_quote_location_id,
          label: composeRouteLabel({ origin: pickupText, destination: dropoffText, courseLeg: true }),
        }
      : null;
  if (golfRoute !== null) location = golfRoute;
  /**
   * T906 S1 — recover the price for the pairs the byte-match above cannot.
   *
   * That guard re-derives `slugify(pickup)__slugify(dropoff)` and compares it to
   * the persisted key, so it only ever fires for a FORWARD course leg. It misses
   * all 3 airport legs even forward (their key is hub + a whole route name, not
   * a place pair) and every reversed pair. Before S1 that was harmless: golf was
   * single-ended, so a miss WALLED the job at the Location screen and Helen
   * re-picked. Now both ends are recovered below, which makes the job READY — so
   * without this the same job would sail past the only screen that can fix it
   * and re-price as MANUAL, silently losing a grid price it already had.
   *
   * Assigns the local `location` and nothing else: no store write, and no
   * `fetchQuote` (this path already dispatches one). Safe by construction — the
   * only other assignment to `location` sits inside a `service !== 'golf'`
   * block, so it is still null here for golf.
   */
  /**
   * T906 S1 — the two stated ends, recovered from the stop rows journeyStops
   * wrote.
   *
   * ⛔ BOTH ENDS MUST BE PLAIN PLACES, and these are the SAME boundary clauses
   * the golfRoute gate above applies, for the same reason. A stop address is
   * free text on any booking: an AIRPORT leg persists ONE row whose address is
   * the whole composite label, and golf allows every add-on, so such a job with
   * an extra pickup persists [composite, middle]. Reading those two as "ends"
   * would state a composite as the collection place and promote a MIDDLE stop to
   * the destination — and because the destination row is written with null
   * street columns, the middle stop's Eircode would be silently dropped on the
   * next save. That is the data loss the M6a pins in
   * test/client/admin-screens.spec.ts exist to catch, and it is why this cannot
   * simply take `pickupText`/`dropoffText`.
   *
   * A record that fails these clauses keeps its pre-S1 behaviour exactly: no
   * ends, so `journeyStops` falls back to the composite path and Helen lands on
   * the picker.
   */
  const golfEndsRestorable =
    service === 'golf' &&
    pickupText !== null && dropoffText !== null &&
    !containsHubBoundary(pickupText) && !containsHubBoundary(dropoffText) &&
    splitRouteLabel(pickupText) === null && splitRouteLabel(dropoffText) === null &&
    // ⛔ AND THE DROP-OFF ROW MUST LOOK LIKE ONE THIS WRITER PRODUCES: no street
    // data. The stated-ends push nulls `addressLine`/`eircode`/`town` on the
    // destination, which is right for every place the picker can produce (a
    // registry place or a typed place name — never a street), but it would
    // DESTROY those three columns on a record that legitimately carries them.
    // Restoring ends here is only safe where re-persisting them is lossless, so
    // the shape is checked rather than assumed. Anything else keeps its pre-S1
    // path and round-trips byte-for-byte.
    (dropoffStop?.address_line ?? null) === null &&
    (dropoffStop?.eircode ?? null) === null &&
    (dropoffStop?.town ?? null) === null;

  /**
   * ⛔ THE PRICE RESTORE AND THE ENDS RESTORE ARE ONE DECISION, NOT TWO.
   *
   * Output-gate finding, and both mixed corners were real defects when these
   * were independent:
   *
   *  - Resolving a pair WITHOUT restorable ends left `journeyStops` reading the
   *    ends back out of the restored rate row, which is direction-blind. A
   *    reversed record came back REVERSED — the customer told they are collected
   *    at the far end — with the pickup end's Eircode re-stamped onto the other
   *    place and the drop-off Eircode destroyed. So the resolver runs ONLY when
   *    the ends are restorable.
   *
   *  - Restoring ends WITHOUT a price made the job ready while the booking still
   *    named a real grid key nothing had resolved. Helen sailed past the one
   *    screen that could recover it and the job silently re-priced as a hand
   *    quote. So the ends count as restored ONLY when either a price came back
   *    or there was never a persisted key to lose.
   *
   * Stated as two named booleans rather than folded into the conditions above,
   * because the whole defect was that these two facts drifted apart.
   */
  if (golfRoute === null && golfEndsRestorable) {
    const hit = resolveGolfPair(registry.options.golfPairs ?? [], pickupText, dropoffText);
    if (hit !== null) location = { id: hit.locationId, label: hit.locationLabel };
  }
  const hasPersistedRateKey =
    detail.latest_quote_location_id != null && detail.latest_quote_location_id !== '';
  const golfEndsRestored = golfEndsRestorable && (location !== null || !hasPersistedRateKey);
  const golfPickup = golfEndsRestored ? pickupText : null;
  const golfDestination = golfEndsRestored ? dropoffText : null;

  let target = 'quote' as ReturnType<typeof furthestReadyScreen>;
  store.update((s) => {
    const fresh = sourceFromBooking(s, {
      bookingId: b.booking_id,
      service,
      band: b.passengers_band,
      // ⛔ v33 — an ACCOUNT booking has no customer to prefill from. Empty
      // strings, not a fabricated name: the capture screens already treat '' as
      // "not stated" and refuse to send on it.
      name: detail.customer?.name ?? '',
      phone: detail.customer?.phone_e164 ?? '',
      journeyDate: b.journey_date,
      journeyTime: b.journey_time,
      venue: b.venue,
      notes: b.note,
      contactPreference: b.contact_preference,
      options: {
        meetAndGreet: b.meet_greet,
        extraPickups: b.extra_pickups,
        returnTrip: b.return_trip,
        lateNight: b.late_night,
        trailer: b.trailer,
        accessibleVehicle: b.accessible_vehicle,
      },
      stops: middleStops,
      flight: detail.flight ?? null,
      // ⭐ T1717 M6 (DD5) — WHEN the job comes back, so the Quote screen's
      // "From the customer" card shows the nurse's own time rather than the
      // site lane's free text (which a portal job has none of).
      returnAt: detail.returnAt ?? null,
      // ⭐ board 2871 (C1) — the customer's stated return (site form), which the
      // detail route already serves: the Quote card and Return date default read it.
      returnDate: detail.returnDate ?? null,
      returnTime: detail.returnTime ?? null,
      returnPassengers: detail.returnPassengers ?? null,
      // ⭐⭐ T1717 M6 (DD6) — the HOME's answer, which becomes the immutable
      // baseline instead of `b.return_trip`. Without this line the baseline is
      // the value Helen last SAVED, so re-opening a job she has already
      // overridden would show no override at all.
      homeReturnTrip: detail.portalRequest?.returnTrip ?? null,
      taxiPassed: detail.taxi != null,
      // ⭐⭐ T2088 M7 (D5) — the HOME's marker, off the SAME `portalRequest`
      // value the line above reads. `?? 'unset'` covers a non-portal booking and
      // an older server alike: absence is silence, never a promise.
      returnAtState: detail.portalRequest?.returnAtState ?? 'unset',
      // ⭐⭐ T1871 M6 (ux-review) — THE STORED HEADCOUNT COMES BACK. The detail
      // route already serves it (its own one-column read); this line is the only
      // thing that was missing, and without it a re-opened over-band job showed a
      // blank count box AND lost the over-band mismatch banner, which is derived
      // from the in-memory count. The band is NOT re-derived from it — see the
      // fact-not-an-act note on sourceFromBooking.
      passengersExact: detail.passengersExact ?? null,
    });
    const recipient = resolveRecipientEmail(detail.customer?.email ?? null, detail.inquiry_email);
    const withLocs = patchJob(fresh, {
      // Same helper as refreshBookingDetail and the send path itself (T549 M8) -
      // a reopened booking must not show a different recipient, or a different
      // provenance for it, from a freshly-saved one.
      customerEmail: recipient.email,
      customerEmailFromInquiry: recipient.fromInquiry,
      previousQuoteCents: detail.latest_quote_cents,
      /**
       * ⛔⛔ T1949 REWORK — HYDRATE THE HAND-PRICE SIGNAL, OR ASK 823 HAS NO
       * TRIGGER ON THE PATH THAT MATTERS MOST.
       *
       * `wasHandQuoted` and `finalTotalCents` are CLIENT-TRANSIENT: the only
       * writer is `persistCurrentQuote`, and `sourceFromBooking` above starts
       * from `EMPTY_JOB`, so a re-opened booking arrived with the signal at
       * false/null however it was priced. Measured on a served kiosk: a
       * 25-passenger job hand-priced at €800 and saved, re-opened from Bookings,
       * count corrected to 10 — the band re-derived to `8-12` and NOTHING
       * cleared, no banner appeared, the agreed-price box still read €800 beside
       * the new band, and Record agreed price re-saved €800 silently. That is
       * the harm ask 823 exists to prevent, in its worst form, on the flow Helen
       * actually uses.
       *
       * ⭐ THE WITNESS IS A DISJUNCTION, AND THE SECOND HALF IS THE ONE THAT
       * MATTERS — MEASURED, NOT REASONED. `latest_quote_pricing_status ===
       * 'manual'` is what the server recorded about HOW the quote was priced
       * (T549 M4), and it survives the session, which is the property the
       * transient flag lacks. ⛔ BUT IT IS NULL ON EXACTLY THE CASE THIS FIX
       * EXISTS FOR: driven on a served kiosk, a 25-passenger over-band job
       * hand-priced at €800 saved with `final_total_cents` 80000,
       * `computed_total_cents` NULL and `pricing_status` **NULL** — so a witness
       * of `'manual'` alone re-derived the band and announced nothing, which is
       * the very BLOCK this rework answers. (T1871 recorded the same thing:
       * an over-band hand price saves with `pricing_status` NULL.)
       *
       * ⛔ SO THE SECOND HALF: a persisted band that does not canonicalise is
       * OVER-BAND, and an over-band job is hand-priced BY CONSTRUCTION — the
       * engine refuses to price it (`pricing-spec.md`), which is why the
       * hand-price surface is the only one that renders for it. `toCanonicalBand`
       * rather than a literal `'16+'`, the same guard `fetchQuote`'s suppression
       * uses, so the two agree about what over-band means.
       *
       * The amount must be non-null in both arms: an announcement with no figure
       * to name is not what was ruled.
       *
       * ⚠️ The detail route carries NO computed total, so "final present with no
       * engine total" — the other shape one might reach for — is not derivable on
       * this wire. This disjunction is what the served payload actually supports.
       *
       * ⛔ IT CANNOT CHANGE WHAT A REOPEN SAVES. `persistCurrentQuote` builds
       * its body from `opts.finalTotalCents` (the screen's box), never from
       * `job.finalTotalCents`, so this restores a SIGNAL and not a saved figure.
       * It also does not move `handPriced`, which `Quote.tsx` derives from the
       * quote's own status and `isManual`, never from this flag.
       *
       * ⚠️ BOUNDED, AND THE BOUND IS THE ALREADY-FILED RESIDUAL: `reopenBooking`
       * dispatches a `fetchQuote`, and on a band the engine CAN price that POST
       * returns through `applyQuoteResult`, whose `wasHandQuoted: result.
       * wasHandQuoted ?? false` wipes this again. On an OVER-BAND job — the
       * hand-priced case, and the measured repro — the suppression branch carries
       * it instead, so the signal survives and the clear announces. The
       * priced-band arm is the second silent clear filed in `docs/test-gaps.md`;
       * it is a wider rule than asks 823/824 answered and is boarded for Alex,
       * so it is deliberately NOT fixed here.
       *
       * ⛔ SO DO NOT READ THIS AS COVERING AN OVERRIDE. An Override on a band the
       * engine CAN price persists `pricing_status: 'priced'` (deriveProvenance
       * returns the ENGINE's status; the override is recorded only in
       * `pricing_context`), so NEITHER arm of the witness above matches it and
       * nothing here hydrates it — see the 2026-09-09 T1949 residual on the
       * second silent clear in `docs/test-gaps.md`, which board 1957 owns.
       */
      ...(detail.latest_quote_cents != null &&
      (detail.latest_quote_pricing_status === 'manual' ||
        toCanonicalBand(detail.booking.passengers_band ?? '') === null)
        ? { wasHandQuoted: true, finalTotalCents: detail.latest_quote_cents }
        : {}),
      locationId: location?.id ?? null,
      locationLabel: location?.label ?? null,
      toLocationId: toLocation?.id ?? null,
      toLocationLabel: toLocation?.label ?? null,
      pickupSearchText: location ? null : pickupText,
      // T608 M6a: on a restored GOLF route the recomposed label owns BOTH ends
      // (journeyStops splits it back into the pair), so the drop-off carry has to
      // clear with the pickup one. Left set, `dropoffEndPlace` would return the
      // course as a SECOND drop-off and journeySummary would render the course
      // twice — "hub → course to course" on the Quote screen.
      dropoffSearchText:
        toLocation !== null || golfRoute !== null || golfEndsRestored ? null : dropoffText,
      // T906 S1 — the stated ends come back from the two persisted stop rows,
      // which is where journeyStops put them. This is what lets a re-opened golf
      // job show Helen the same journey it saved (and, with the pair resolver
      // above, the same PRICE) instead of degrading to a picker.
      // Guarded on the service so every other job keeps them null.
      golfPickupPlace: golfPickup,
      golfDestinationPlace: golfDestination,
      pickupTerminal,
      // T400 — the per-end capture round-trip: what v16 stored comes back into
      // the capture fields, so a re-opened persist can never impoverish the row.
      pickupEircode: pickupStop?.eircode ?? '',
      pickupAddressLine: pickupStop?.address_line ?? '',
      pickupTown: pickupStop?.town ?? null,
      dropoffEircode: dropoffStop?.eircode ?? '',
      dropoffAddressLine: dropoffStop?.address_line ?? '',
      dropoffTown: dropoffStop?.town ?? null,
    });
    target = furthestReadyScreen(withLocs.job, mode, bands(registry));
    return navigate(withLocs, target);
  });
  if (descriptor !== null && (target === 'addons' || target === 'quote')) void fetchQuote(store);
}

export type ApiRateRow = {
  service: string;
  hub: string;
  locationId: string;
  label: string;
  band: string;
  priceCents: number | null;
  edited: boolean;
  /** T373 M6 — an operator-invented rate, and whether a human has ratified it. */
  operatorAdded: boolean;
  ratified: boolean;
};

export async function listRates(): Promise<ApiRateRow[]> {
  return getJson<ApiRateRow[]>('/api/rates');
}

/** Inline rate edit — the reason note is REQUIRED (audit parity). */
export async function saveRate(
  row: { service: string; locationId: string; band: string },
  newCents: number,
  note: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await postJson('/api/rate', { service: row.service, locationId: row.locationId, band: row.band, newCents, note });
    return { ok: true };
  } catch {
    return { ok: false, message: "Saving the price didn't work. Try again in a moment." };
  }
}

/** Ratify an operator-added rate — a human vouches for it (T373 M6). The note is
 *  REQUIRED (audit parity). 409 = already ratified (idempotent, friendly copy). */
export async function ratifyRate(
  row: { service: string; locationId: string; band: string },
  note: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await postJson('/api/rate/ratify', { service: row.service, locationId: row.locationId, band: row.band, note });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      return { ok: false, message: 'That price is already confirmed.' };
    }
    return { ok: false, message: "Confirming the price didn't work. Try again in a moment." };
  }
}

// ---------------------------------------------------------------------------
// T387 M5 — the Price Book: generalized add-entry + the union price history
// ---------------------------------------------------------------------------

export type PriceEntryKind = 'town' | 'route' | 'venue';

/** Add a standing price entry (town / route / venue) — POST /api/rate-entry.
 *  bandPrices carries integer CENTS (callers convert euros with Math.round,
 *  the saveRate convention). Lands provisional gold until ratified. */
export async function addRateEntry(entry: {
  kind: PriceEntryKind;
  service: string;
  name: string;
  hubName?: string;
  bandPrices: Record<string, number>;
  note: string;
}): Promise<
  | { ok: true; searchable: boolean | null }
  | { ok: false; status: number; message: string }
> {
  try {
    // T608 M8b (R8) — `searchable` is the server's measured answer to "can the
    // kiosk pickup search ever offer this place?", present for kind 'town' only
    // (the other kinds are read straight off `rates` and need no locations row).
    // null = not applicable, never "unknown": an older server that omits the
    // field also reads as null, and the caller says nothing rather than guessing.
    const res = await postJson<{ searchable?: boolean }>('/api/rate-entry', entry);
    return { ok: true, searchable: typeof res.searchable === 'boolean' ? res.searchable : null };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      return { ok: false, status: 409, message: 'Already on the price list. Edit its prices in the list instead.' };
    }
    return { ok: false, status: e instanceof ApiError ? e.status : 0, message: "Adding it didn't work. Check the fields and try again." };
  }
}

/** Set ONE unpriced band cell (T399 item 2) — POST /api/rate-cell. cents is
 *  integer CENTS (the caller converts euros with Math.round, the saveRate
 *  convention). Lands a single provisional gold row. 409 = that cell is already
 *  priced (edit it in the list instead). */
export async function setRateCell(entry: {
  kind: PriceEntryKind;
  service: string;
  name: string;
  hubName?: string;
  band: string;
  cents: number;
  note: string;
}): Promise<{ ok: true } | { ok: false; status: number; message: string }> {
  try {
    await postJson('/api/rate-cell', entry);
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      return { ok: false, status: 409, message: 'That band already has a price. Edit it in the list instead.' };
    }
    return { ok: false, status: e instanceof ApiError ? e.status : 0, message: "Setting the price didn't work. Check the amount and try again." };
  }
}

export type ApiPriceAuditEntry = {
  source: 'rate' | 'meta';
  auditId: number;
  service: string | null;
  locationId: string | null;
  band: string | null;
  metaKey: string | null;
  oldCents: number | null;
  newCents: number | null;
  actor: string;
  note: string;
  at: string;
  sourceBookingId: number | null;
};

/** The who/when/why/old-to-new trail for one rate cell or one add-on key —
 *  GET /api/price-audit (the union reader). */
export async function listPriceAudit(
  filter: { service: string; locationId: string; band: string } | { metaKey: string },
): Promise<ApiPriceAuditEntry[]> {
  const params = new URLSearchParams(filter as Record<string, string>);
  const res = await getJson<{ entries: ApiPriceAuditEntry[] }>(`/api/price-audit?${params.toString()}`);
  return res.entries;
}

export type ApiRateConflict = {
  rateId: number;
  service: string;
  band: string;
  label: string;
  storedPriceCents: number | null;
  kind: 'conflict' | 'file-removed';
  filePriceCents?: number | null;
};

/** Reconcile read — THROWS on !ok (the R13 HIGH: silent-empty renders as
 *  "all up to date" and hides real conflicts from Helen). */
export async function listRateConflicts(): Promise<ApiRateConflict[]> {
  return getJson<ApiRateConflict[]>('/api/rate-changes');
}

export async function resolveRateConflict(
  rateId: number,
  action: 'keep' | 'accept',
): Promise<boolean> {
  try {
    await postJson(`/api/rate-changes/${rateId}/${action}`, {});
    return true;
  } catch {
    return false;
  }
}

export type ApiFlaggedQuote = {
  feedback_id: number;
  created_at: string;
  created_by: string | null;
  computed_total_cents: number | null;
  note: string | null;
  request: unknown;
};

/** Flagged quotes ONLY (kind=flag) — manual/override/accept rows never
 *  pollute the triage list (R14). */
export async function listFlaggedQuotes(): Promise<ApiFlaggedQuote[]> {
  return getJson<ApiFlaggedQuote[]>('/api/feedback?status=open&kind=flag');
}

/** Confirming a flag REQUIRES the correct price (the server's feedback
 *  model: a confirmed flag feeds the eval corpus with expectedTotalCents). */
export async function setFlagStatus(
  feedbackId: number,
  status: 'confirmed' | 'rejected',
  expectedTotalCents?: number,
): Promise<boolean> {
  try {
    await postJson(`/api/feedback/${feedbackId}/status`, {
      status,
      ...(expectedTotalCents != null ? { expectedTotalCents } : {}),
    });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// T387 M6 — the "Needs a decision" queue, promote, suggest, add-on prices
// ---------------------------------------------------------------------------

export type ApiQueueProvisional = {
  kind: 'provisional'; service: string; locationId: string; label: string;
  cells: { band: string; cents: number | null }[];
};
export type ApiQueueMiss = {
  kind: 'miss'; placeSlug: string; place: string; service: string | null;
  count: number; lastSeen: string;
};
export type ApiQueueEvidence = {
  kind: 'venue-evidence'; venue: string; service: string;
  /** Only bands whose evidence is an each-way base (T897); others honestly absent. */
  bands: Record<string, number>; count: number;
  /** Of `count`, how many carried a return leg or a priced add-on and so set no base. */
  excludedCount: number;
  lastSeen: string;
  /** The booking that supplied a price, or null when every quote was excluded. */
  sourceBookingId: number | null;
};
export type ApiQueueSuggestion = {
  kind: 'suggestion'; feedbackId: number; bookingId: number | null;
  place: string | null; service: string | null; note: string | null;
  createdBy: string | null; createdAt: string;
  /** Source-booking pre-fill fallback: the band the flagged booking was quoted
   *  at → its agreed cents. Used by the promote form only when no venue-evidence
   *  twin exists (an engine/estimate-priced booking contributes no evidence). */
  bandCents: Record<string, number>;
};
export type ApiQueue = {
  provisional: ApiQueueProvisional[];
  misses: ApiQueueMiss[];
  evidence: ApiQueueEvidence[];
  suggestions: ApiQueueSuggestion[];
};

export async function listRatesQueue(): Promise<ApiQueue> {
  return getJson<ApiQueue>('/api/rates-queue');
}

/** Promote evidence into a provisional standing rate — POST /api/promote.
 *  Same body shape as addRateEntry plus provenance + the source card to
 *  resolve in the same transaction. */
export async function promoteEntry(entry: {
  kind: PriceEntryKind;
  service: string;
  name: string;
  hubName?: string;
  bandPrices: Record<string, number>;
  note: string;
  sourceBookingId?: number;
  source?: { type: 'suggestion'; feedbackId: number } | { type: 'miss'; placeSlug: string };
}): Promise<
  | { ok: true; searchable: boolean | null }
  | { ok: false; status: number; message: string }
> {
  try {
    // `searchable` as in addRateEntry above. A town promote DOES write a
    // locations row, but always a coordless one, so this is false there too.
    const res = await postJson<{ searchable?: boolean }>('/api/promote', entry);
    return { ok: true, searchable: typeof res.searchable === 'boolean' ? res.searchable : null };
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) {
      return { ok: false, status: 409, message: 'Already on the price list. Edit its prices in the list instead.' };
    }
    return { ok: false, status: e instanceof ApiError ? e.status : 0, message: "Saving it didn't work. Check the fields and try again." };
  }
}

/** "Not now" on a miss aggregate. */
export async function dismissMiss(placeSlug: string): Promise<boolean> {
  try {
    await postJson('/api/miss-dismiss', { placeSlug });
    return true;
  } catch {
    return false;
  }
}

/** "Not now" on a Done-screen suggestion (rides the existing feedback
 *  status endpoint — a rejected suggestion leaves the queue). */
export async function dismissSuggestion(feedbackId: number): Promise<boolean> {
  try {
    await postJson(`/api/feedback/${feedbackId}/status`, { status: 'rejected' });
    return true;
  } catch {
    return false;
  }
}

/** The Done-screen one-tap flag: a QUEUE input, never a rates write. */
export async function suggestForPriceList(
  context: { service: string; venue?: string; place?: string },
  bookingId: number,
): Promise<boolean> {
  try {
    await postJson('/api/feedback', { request: context, kind: 'suggestion', bookingId });
    return true;
  } catch {
    return false;
  }
}

/** Audited add-on price change (AD1) — POST /api/meta-price, integer cents. */
export async function setMetaPrice(
  key: string,
  cents: number,
  note: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    await postJson('/api/meta-price', { key, cents, note });
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      return { ok: false, message: 'That price is not set up on this system yet. Reseed before editing add-on prices.' };
    }
    return { ok: false, message: "Saving the price didn't work. Check the amount and try again." };
  }
}

// ---------------------------------------------------------------------------
// T651 M2 — the accounts spine: the four read loaders
//
// Four reads on SCREEN ENTRY, not four fields on the login-time registry. The
// registry is fetched once by doLogin, so an account somebody else created
// today would be invisible until this operator signed out and back in.
//
// ⛔ THE DEGRADE POSTURE IS THE CALLER'S, and it is stated at the call site
// (screens/Admin.tsx's Promise.all) exactly as listRatesQueue's is: all four of
// these are wrapped in `.catch(...)` there and degrade to an empty list.
// They are deliberately NOT load-bearing the way listRates is — an unwrapped
// accounts read would red the whole price book on an accounts failure, which
// inverts the reason the account book has a quiet entry of its own.
//
// ⛔ ALL FOUR ANSWER `BoundedList<T>` — `{ rows, truncated }` — NOT a bare array
// (T651 M2 hardening). Every one of these routes truncates at a server-side
// LIMIT, and until now it did so silently: the operator would see a short book
// with nothing saying it was short. `truncated` is threaded through
// screens/Admin.tsx into AccountBook, which shows one line on the tab whose list
// was cut. ⛔ It is NOT pagination and must not become one: there is no cursor
// and no page control, because the requirement is only that the cut stops being
// invisible. The TYPE is imported (never `bound` itself), so nothing from the
// server side reaches the browser bundle.
//
// ⚠️ THE READS ARE ALL FOUR SETTLED; THE WRITES ARE NOT, AND THAT IS DELIBERATE.
// The three milestones that fill the account book's child views each need their
// own create/edit/deactivate call, and none of them exists yet because none of
// their SERVER routes does either — a client action posting to a route nobody
// has written is the dead code this build refuses elsewhere. What matters is
// that adding one is an APPEND of a new named export, never an edit to a shared
// declaration: the hazard this file was frozen against is the positional tuple
// in screens/Admin.tsx, and that is closed above. Prefer a per-area module
// (lib/<area>-actions.ts) if three of them would otherwise land here at once.
// ---------------------------------------------------------------------------

export type ApiAccount = {
  accountId: number;
  name: string;
  addressLine: string | null;
  town: string | null;
  county: string | null;
  eircode: string | null;
  /** A reporting label. Never a join key, and never the bill-to. */
  groupName: string | null;
  /** v35 - one of the four organisation kinds, or null for NEVER CLASSIFIED,
   *  which is a different answer from 'other' (somebody looked, and it is none
   *  of the three). ⛔ Never compared to a service id: that is a journey-level
   *  fact and this is an organisation-level one. */
  kind: string | null;
  /** v35 - the organisation's number as typed. Never normalised, never a match
   *  key: the customer record holds the one that identifies a person. */
  phone: string | null;
  /** v35 - who to ask for at the organisation. ⚠️ Personal data. */
  contactName: string | null;
  /** v35 - the customer's VAT number. ⛔ Stored, never rendered on an invoice. */
  vatNumber: string | null;
  /** The default bill-to, or null where this account bills itself. */
  billToId: number | null;
  active: boolean;
  /** ⭐ v40 (T1719 M5) — the letters that go on this account's job references,
   *  or null. NULL IS AN ORDINARY STATE, not a gap: a name that yielded fewer
   *  than two initials, a derived collision the registry withheld, and an
   *  operator clearing the box all persist as null and the row records which
   *  NOWHERE — so the screen names both causes and claims neither. */
  shortCode: string | null;
  /** ⭐ DD4 — a code may change only until the first reference has been issued
   *  under it. Computed on the SERVER from `next_job_seq > 1`.
   *  ⛔ THE COUNTER ITSELF DOES NOT CROSS THE WIRE: it is a job count for a
   *  named home. The predicate is all a screen needs. */
  shortCodeLocked: boolean;
  /** ⭐ T1719 M5 step 9 — the stored code is one the short-code denylist would
   *  refuse today, or is outside the stored shape. The denylist is consulted
   *  when a code is CHOSEN, never when a reference is MINTED (DD4 forbids that),
   *  so this read-back is the only thing that can surface a code that predates a
   *  widening or arrived by hand-run SQL. */
  shortCodeRefused: boolean;
  // ⛔ NO `billingEmail` HERE, DELIBERATELY. It lives on whichever row the
  // bill-to resolver names, so the list read cannot carry it without either
  // lying about the parent-billed case or pushing a payer's address to three
  // consumers that have no use for it. GET /api/billing-email is the reader.
};

export type ApiDestination = {
  destinationId: number;
  name: string;
  active: boolean;
};

export type ApiAccountPrice = {
  accountPriceId: number;
  accountId: number;
  accountName: string;
  destinationId: number;
  destinationName: string;
  /** null = on their list, not yet priced. Different from "not on their list". */
  priceCents: number | null;
  sourcePriceCents: number | null;
  edited: boolean;
};

export type ApiBillingParty = {
  billingPartyId: number;
  name: string;
  active: boolean;
};

export async function listAccounts(): Promise<BoundedList<ApiAccount>> {
  return getJson<BoundedList<ApiAccount>>('/api/accounts');
}

export async function listDestinations(): Promise<BoundedList<ApiDestination>> {
  return getJson<BoundedList<ApiDestination>>('/api/destinations');
}

/** ⛔ The WHOLE book, for DISPLAY. It is not a price lookup: searching these rows
 *  for one account's destination is the client-side bypass of D-RESOLVE, pinned
 *  in test/core/account-prices-resolve.spec.ts.
 *  ⚡ THE REDIRECT THIS COMMENT USED TO MAKE NOW HAS SOMEWHERE TO GO. It said
 *  "ask `/api/account-prices?accountId=&destinationId=` for one decided price"
 *  while no client function asked anything of the kind — a correct instruction
 *  with no implementation, which reads at a glance like the call below IS the
 *  lookup. `resolveCapturedPrice` under this is that call. */
export async function listAccountPrices(): Promise<BoundedList<ApiAccountPrice>> {
  return getJson<BoundedList<ApiAccountPrice>>('/api/account-prices');
}

/**
 * T651 M8 (R12/R13) — ONE decided price for ONE (account, destination) pair,
 * BY KEY. The single-price arm of the same route `listAccountPrices` above
 * refuses to be searched for.
 *
 * ⛔ THE DESTINATION GOES OVER AS THE CAPTURE'S OWN TEXT and the SERVER does the
 * name join (`findDestinationIdByName`, the same one `readAccountPriceDelta`
 * uses at persist time). Resolving the id here instead — read `/api/
 * destinations`, match a name, ask by id — would be a second implementation of
 * the join, and its failure mode is silent: a case difference misses on a client
 * `===` where the server's `lower(name)` hits, so Helen is shown NO captured
 * price for a run whose recorded delta is measured against one.
 *
 * ⛔ THROWS on a failed read rather than degrading to "no price" (so NOT
 * `getOr`, the idiom `loadPrecedent` next door uses). "Could not check" and
 * "there is no agreed price" are different sentences to an operator mid-call
 * and the screen says which; a swallowed failure would state the second while
 * meaning the first, on the money field.
 *
 * ⛔ THE ANSWER IS FOR DISPLAY. It never travels back on the persist body — the
 * server re-reads the figure itself (M7's S5 pin). What Helen types is what is
 * recorded, whether or not she keeps the number this puts in front of her.
 */
export type CapturedPriceResolution =
  | { kind: 'missing' }
  | { kind: 'unpriced'; accountPriceId: number }
  | { kind: 'priced'; accountPriceId: number; priceCents: number; edited: boolean };

export async function resolveCapturedPrice(
  accountId: number,
  destination: string,
): Promise<CapturedPriceResolution> {
  const q = new URLSearchParams({ accountId: String(accountId), destination });
  return getJson<CapturedPriceResolution>(`/api/account-prices?${q.toString()}`);
}

/**
 * T1746 M5: TWO CLIENT HELPERS WERE RETIRED HERE, WITH THE PICKER THEY SERVED.
 * One listed the destinations a single account was priced to; the other asked
 * for one price BY DESTINATION ID, with the row type that carried the pair.
 * Both existed only for the destination picker on the portal-requests panel.
 * M5 removed that picker: the kiosk derives the request's home from the portal
 * and prefills the agreed price from the offer, so nothing asks by destination
 * id any more. Deleted rather than left in place because a client helper with
 * no caller reads as a supported route to the next person who needs a price,
 * and NAMED NOWHERE in this tree on purpose - a retired identifier left in
 * prose is the thing the next grep finds and believes still exists.
 *
 * ⛔ THE ROUTE `/api/account-prices` IS NOT ORPHANED BY THIS. Its other two
 * client callers stay above: `listAccountPrices` (the whole book, for display)
 * and `resolveCapturedPrice` (one price by capture TEXT, through D-RESOLVE).
 * Only the by-id arm lost its browser caller; the server route is untouched.
 */
export async function listBillingParties(): Promise<BoundedList<ApiBillingParty>> {
  return getJson<BoundedList<ApiBillingParty>>('/api/billing-parties');
}

// ---------------------------------------------------------------------------
// T788 M3 — the portal request inbox
// ---------------------------------------------------------------------------

/**
 * One nursing-home request as the KIOSK sees it.
 *
 * ⛔ THIS TYPE IS NOT THE PORTAL'S ROW, AND THAT IS THE POINT. The upstream row
 * carries THIRTY fields including a resident's room number, their equipment
 * and free-text clinical notes - Article 9 data. The kiosk server projects it
 * down to these TWENTY BEFORE it is serialised (core/portal-ops-projection.ts).
 * (KEPT below, re-derived 2026-10-01 at board 2707 M1; it said NINETEEN until
 * then. UPSTREAM's two `sed` forms in portal-ops-projection.ts's header printed
 * 26 + 3 -> 29 that day and 27 + 3 -> 30 at board 2707 M3, which added
 * `seenAt` upstream; it reaches no kiosk keep-set.)
 * ⚠️ RE-DERIVED AT T2088 M7 (26 on `RequestListItem` + 3 `OpsRequestListItem`
 * adds). It read "TWENTY-EIGHT / EIGHTEEN" from T1868 M6 until then, and the
 * upstream half was already stale by one when T2088 M2 added
 * `portal.requests.return_at_state` and did not touch this sentence - the exact
 * rot the paragraph below warns about, happening again while the warning stood.
 * ⚠️ THE COUNTS MOVE, AND THIS SENTENCE HAD ALREADY GONE STALE ONCE - it said
 * "twenty-four" and "fourteen" in the same breath as the warning below, which is
 * the failure the warning exists to prevent. History: T1683 M3 added `jobRef`
 * (12 -> 13 kept, 22 -> 23 upstream), M6 added `kioskDestinationId`
 * (13 -> 14 kept, 23 -> 24 upstream), M7 added `returnTrip`
 * (14 -> 15 kept, 24 -> 25 upstream), T1717 M6 added `returnAt`
 * (15 -> 16 kept, 25 -> 27 upstream - v11 added `next_of_kin_name` as well as
 * `return_at`, and the name is DROPPED here by DD9), T1730 added
 * `accessibleVehicle` (16 -> 17 kept, 27 -> 28 upstream - the ONE bit the
 * portal derives from the nurse's equipment tick; the list stays dropped),
 * T1868 M5 added `pickupAt` (17 -> 18 kept, 28 -> 30 upstream - v12 added
 * `pickup_at` AND `pickup_offset_minutes_used`, and the OFFSET is DROPPED here
 * by M5's own ruling; see `PortalRequestSummary.pickupAt` for why).
 * ⛔⛔ T1868 M6 MOVED THE UPSTREAM HALF **DOWN**, WHICH NO ENTRY ABOVE DOES, and
 * that is why it is called out rather than appended: it removed
 * `nextOfKinName` and `nextOfKinPhone` from `RequestListItem` (30 -> 28
 * upstream; the KEPT count is unchanged at 18, because neither was ever kept).
 * T2088 M2 added `return_at_state` upstream (28 -> 29) and did NOT edit this
 * sentence; T2088 M7 crosses it (18 -> 19 kept). Board 2707 M1 keeps
 * `createdAt`, already upstream (19 -> 20 kept; upstream unchanged at 29).
 * ⚠️ SO THE HISTORY LINE IS NOT MONOTONIC and cannot be read as one. Re-derive.
 * ⚠️ THE UPSTREAM HALF MOVES INDEPENDENTLY. Re-derive it rather than
 * incrementing: `RequestListItem` + the `OpsRequestListItem` adds.
 * ⛔ DERIVE, NEVER COPY, and here is the derivation both halves came from:
 * KEPT   = `PORTAL_REQUEST_KEEP_KEYS.length` (core/portal-ops-projection.ts),
 *          which this type must mirror field-for-field.
 * UPSTREAM = `RequestListItem` (27 - it said 23 until T1730 re-counted the
 *          type's own fields, 25 until T1868 M5 added `pickupAt` and
 *          `pickupOffsetMinutesUsed` (27), back to 25 at T1868 M6, which
 *          removed `nextOfKinName` and `nextOfKinPhone`, 26 from T2088 M2,
 *          which added `returnAtState`, and 27 since board 2707 M3 added
 *          `seenAt`; the command below printed 27 on 2026-10-01) + the 3
 *          `OpsRequestListItem` adds
 *          (`homeName`, `pricedAmountCents`, `accessibleVehicle`) in
 *          portal/src/core/requests-repo.ts.
 * ⛔ AND HERE IS THE COMMAND, so the next author does not have to hand-count:
 *   sed -n '/^export type RequestListItem = {/,/^};/p' \
 *     ../portal/src/core/requests-repo.ts | grep -cE '^  [a-zA-Z]+\??:'
 * ⚠️ A COUNT GREP MUST COVER DIGITS **AND** SPELLED WORDS - the same trap is
 * recorded in portal-ops-projection.ts's header, where a numerals-only sweep of
 * `Ops.tsx` concluded the file "carries no count word at all".
 * This is a HAND-WRITTEN MIRROR of `PortalRequestSummary`, so it must be edited
 * in the same change as the projection.
 * ⭐ AND IT NOW HAS A CHECK, WHICH IT DID NOT BEFORE THE M7 REVIEW (G). This
 * block used to end "the compiler will not tell you, because nothing imports one
 * from the other" - true at the time, and the reason the counts above rotted.
 * `test/core/portal-ops-projection.spec.ts` ("the seam type, the client mirror
 * and the keep-set are ONE key set") now holds a `Record<keyof T, true>` literal
 * for BOTH types and compares them to `PORTAL_REQUEST_KEEP_KEYS`: a key added to
 * either type and not to the literal fails `tsc --noEmit` with a missing
 * property, and a key that reaches only one of the three reds at runtime.
 * ⚠️ THE CHECK GRADES THE KEY SET, NOT THE COUNT PROSE. The sentences above are
 * still hand-maintained and can still go stale - re-derive them, never copy.
 *
 * ⛔ DO NOT "helpfully" widen this by adding a field the screen might want. The
 * kiosk's feedback widget photographs rendered DOM text verbatim into a JPEG,
 * so a field that reaches this type reaches a capture that can leave the
 * building. Widening is a server-side decision with a server-side spec, never a
 * client edit.
 * ⚠️ CORRECTED 2026-08-14 (T1076, board 900): this said the capture lands "into
 * `feedback_submissions`", i.e. "a kiosk table". It does not — the kiosk sink is
 * a LOCAL DOWNLOAD (client/feedback/widget.ts:759-786); that table is written
 * only on the SITE path. The prohibition is unchanged.
 */
/** ⛔ AN OBJECT, NOT A STRING. The portal column is JSONB. `time` is the only
 *  place a recurring request's time of day exists — its row has no
 *  `journeyDate` at all (the table's one_off_xor_pattern CHECK). */
export type PortalSchedulePattern = {
  frequency: string;
  weekday: number | null;
  intervalWeeks: number | null;
  dayOfMonth: number | null;
  time: string;
};

export type PortalRequest = {
  requestId: number;
  /** ⭐ T1683 M3 (A1). null on every request written before M3; Helen's screen
   *  omits the cell rather than printing a blank label. ⛔ A HAND-WRITTEN MIRROR
   *  of `PortalRequestSummary` (core/portal-ops-projection.ts) - the two move
   *  together, and the seam projection is the authority. */
  jobRef: string | null;
  /**
   * ⭐⭐ T1683 M6 (A3) - THE KIOSK'S OWN DESTINATION ID, so an agreed account
   * price RESOLVES on arrival instead of being typed from memory.
   *
   * ⛔⛔ NOT the portal's `destinationId`, which this type has never carried and
   * must not: two different id spaces in overlapping ranges, so the wrong one
   * resolves a real but WRONG destination's price. ⛔ A HAND-WRITTEN MIRROR of
   * `PortalRequestSummary` (core/portal-ops-projection.ts) - the two move
   * together, and the seam projection is the authority.
   *
   * null on a free-typed destination, on a legacy row predating M4's projection,
   * and on any row from a portal that predates M6. All three fall to the
   * hand-priced path, which is the safe direction - no price is invented.
   */
  kioskDestinationId: number | null;
  /**
   * ⭐ T1683 M7 (A4) - THE NURSE SAID THE JOB COMES BACK, and this screen prices
   * ONE booking at twice the account entry from it. It REPLACED an operator tick
   * that doubled the figure and set nothing.
   *
   * ⭐⭐ IT SAYS **THAT** IT COMES BACK; A SECOND FIELD SAYS **WHEN**, AND THIS
   * PARAGRAPH'S RULING IS REVERSED. Alex, 2026-09-02, via `/scope` of the QA
   * report (question 708 in the lane store), reversed board #1681's deferral;
   * boards #1681 and #1683 M7 record it. Portal schema v11 adds
   * `portal.requests.return_at`, T1717 M5 writes it, and T1717 M6 carries the
   * instant across the seam onto `bookings.return_at` - so consumers of this
   * type DO render a return time from M6, always gated on this boolean.
   * ⭐ `bookings.return_at TIMESTAMPTZ` (`core/migrations.ts` v17), carried here
   * as having zero writers and zero readers, is now the DESTINATION column. The
   * public SITE form's free-text `returnTime` in `inquiries.extra`
   * (`server/inquiry.ts`) that `CustomerInfoBlock.tsx` renders on a web enquiry
   * stays free text on its own lane, with a P2 follow-up to normalise it.
   *
   * ⛔ A HAND-WRITTEN MIRROR of `PortalRequestSummary`
   * (core/portal-ops-projection.ts) - the two move together, and the seam
   * projection is the authority. It is a plain `boolean`, not optional: the
   * projection coerces with `=== true`, so a pre-M7 portal's silence arrives
   * here as `false`, which is the true answer for every row it wrote.
   */
  returnTrip: boolean;
  /**
   * ⭐⭐ T1717 M6 (DD5) - **WHEN** the job comes back: an ISO-8601 instant, or
   * null. The companion the paragraph above promised, and the fourth and last
   * widening of this seam.
   *
   * ⛔ NULL IS THE COMMON CASE AND IS HONEST - a one-way job, a RECURRING
   * request (its return time is a named residual), a request written before
   * T1717 M5, or a portal that predates the key. The row omits the line rather
   * than inventing a time.
   *
   * ⛔ RENDER IT ONLY WITH `returnTrip` TRUE. The flag says THAT, this says
   * WHEN, and an operator who sets the job to one way must stop seeing a return
   * time - without the home's own answer being erased underneath her.
   *
   * ⛔ A HAND-WRITTEN MIRROR of `PortalRequestSummary`
   * (core/portal-ops-projection.ts) - the two move together, and the seam
   * projection is the authority. The projection coerces this with a strict
   * ISO-instant test, so anything that arrives here is already canonical.
   */
  returnAt: string | null;
  /**
   * ⭐⭐ T2088 M7 (plan D5; ADR-0006) - **WHY** THERE IS NO INSTANT, beside a
   * figure this screen has already doubled.
   *
   * ⛔ `'to-confirm'` IS THE ONLY VALUE THAT RENDERS, AND IT RENDERS THE
   * PORTAL'S OWN WORDS. The nurse said the return time is not known yet; the
   * kiosk says `time to confirm`, which is byte-for-byte what her own two lists
   * show her (`portal/src/client/copy.ts`'s `returnToConfirm`), so one job reads
   * the same in both buildings.
   * ⛔ `'unset'` KEEPS TODAY'S SILENCE, and that is a rule rather than a gap: it
   * is the state of a timed return, a one-way job, a RECURRING run whose pattern
   * captures no return time, and every request written before portal v14. None
   * of those is a time anybody promised to ring back with.
   *
   * ⛔ A HAND-WRITTEN MIRROR of `PortalRequestSummary`
   * (core/portal-ops-projection.ts) - the two move together, and the seam
   * projection is the authority. It is NOT optional and NOT nullable: the
   * projection coerces every unknown value to `'unset'`, so absence never
   * reaches this type and a render never has to ask what `null` meant.
   */
  returnAtState: 'unset' | 'to-confirm';
  /**
   * ⭐⭐ T1868 M5 (P0-4) - WHEN THE BUS COLLECTS HER, or null.
   *
   * ⛔ RENDER IT ONLY WHEN IT IS NON-NULL, never as a blank label. Null is the
   * TRUE state of every `collection` run (a collection time IS the pickup, so
   * there is no second one) and of every request written before portal v12,
   * permanently and with no backfill.
   *
   * ⛔ NOT AN ARITHMETIC TO REDO ON THIS SIDE - though the reason changed, and
   * the old wording was stale. It said "IT IS THE NURSE'S ANSWER... the portal
   * form offers her the home's own pickup offset AND LETS HER CHANGE IT, so this
   * is what she left in the box." She cannot change it and has not been able to
   * since 2026-09-13 (board 2073 deleted the editable pair); it is derived from
   * the home's offset, and since board 2188 she SEES it read-only. What survives
   * unchanged is the instruction: it is the value that was STORED. Do NOT re-derive it from `journeyDate` and the home's
   * setting on this side: the offset that produced the prefill is not on this
   * seam, deliberately (`PortalRequestSummary.pickupAt` carries that ruling and
   * the measured DST reason for it).
   *
   * ⛔ A HAND-WRITTEN MIRROR of `PortalRequestSummary`
   * (core/portal-ops-projection.ts) - the two move together, and the seam
   * projection is the authority. The projection coerces this with a strict
   * ISO-instant test, so anything that arrives here is already canonical.
   */
  pickupAt: string | null;
  /**
   * ⭐⭐ T1730 (Alex, 2026-09-03, q731; ADR2-D15) - DOES THE BUS NEED TO TAKE A
   * WHEELCHAIR. ONE BIT the portal derived from the nurse's equipment tick;
   * the list never reaches this type. Rendered on the row as the kiosk's own
   * word, "Accessible vehicle", so it reads the same here as on Inbox and
   * BookingDetail after the crossing. A HAND-WRITTEN MIRROR of
   * `PortalRequestSummary` (core/portal-ops-projection.ts), which carries the
   * Article 9 justification; the projection coerces it `=== true`, so what
   * arrives here is already a boolean.
   */
  accessibleVehicle: boolean;
  homeName: string;
  nurseDisplayName: string;
  residentDisplayName: string;
  destinationName: string;
  payerKind: string;
  /** ISO instant — carries the TIME, not just the date (TIMESTAMPTZ upstream). */
  journeyDate: string | null;
  schedulePattern: PortalSchedulePattern | null;
  timeMeaning: string | null;
  status: string;
  /** ⭐ board 2707 (M1, R-2) - when the home asked, an ISO instant or null.
   *  Rendered in the OPEN row's facts table as `Requested` (Dublin time), never
   *  ordered by. A HAND-WRITTEN MIRROR of `PortalRequestSummary.createdAt`. */
  createdAt: string | null;
  waitingNotes: string;
  pricedAmountCents: number | null;
  /**
   * ⭐ Board 2788 M4 (DD-1, DD-16, S21, S22) - THE CHANGE AND THE ASK. A
   * HAND-WRITTEN MIRROR of `PortalRequestSummary` (core/portal-ops-projection.ts),
   * key for key and type for type; the projection is the authority and decides
   * every coercion. `revision` and `askSeq` are posted back by every act on the
   * row (S22, S10); the rest is M6's to render.
   */
  revision: number | null;
  homeChanged: boolean;
  askKind: 'change' | 'cancel' | null;
  askState: 'none' | 'open' | 'accepted' | 'declined' | 'closed';
  askSeq: number | null;
  askJourneyDate: string | null;
  askPickupAt: string | null;
  askReturnAt: string | null;
  askDeclineReason: 'no_bus' | 'too_close' | 'please_ring' | null;
  history: Array<{
    kind: 'time-changed' | 'change-asked' | 'cancel-asked' | 'ask-declined' | 'cancelled' | 'declined';
    fromAt: string | null;
    toAt: string | null;
    at: string;
    by: 'home' | 'ops';
  }>;
};

/**
 * ⭐⭐ T1746 M1 (B4) - WHAT ONE REQUEST ALREADY MADE IN THIS KIOSK.
 *
 * ⛔ NOT A KEY ON `PortalRequest`, and that is the same rule the type above
 * states three times: `PortalRequest` is a HAND-WRITTEN MIRROR of the seam
 * projection, whose key set is pinned byte for byte. This is a kiosk-side fact
 * about the same request, so it arrives beside the row, keyed by request id.
 *
 * ⛔ `bookingId: null` IS REACHABLE: the crossing lands the inquiry and creates
 * the booking in two transactions, so a reference collision leaves a request
 * that HAS crossed with nothing to open.
 */
export type PortalCrossing = {
  bookingId: number | null;
  /** Raw `bookings.status`. The operator word comes from the registry at
   *  render, never from a label invented on the wire. */
  status: string | null;
  jobRef: string | null;
  /**
   * T1746 M5 (step 9) - the FROZEN figure: the booking's latest quote in cents,
   * or null when it has none. REQUIRED, mirroring `PortalCrossingSummary` in
   * core/data/inquiries-repo.ts key for key.
   *
   * It is the KIOSK's number, not the portal's: once a request has crossed, the
   * money fact is the quote, and the portal's price is history. Render it
   * through `euro()` like every other figure on this screen.
   */
  latestQuoteCents: number | null;
  /**
   * ⭐ Board 2788 M4 (DD-17) - the booking's own day (`YYYY-MM-DD`) and raw time,
   * and whether it carries a `date_changed` event, so M6 can say "the booking
   * says X but the home's request says Y" under DD-17's condition. Mirrors
   * `PortalCrossingSummary` key for key.
   */
  bookingJourneyDate: string | null;
  bookingJourneyTime: string | null;
  dateChanged: boolean;
  /**
   * ⭐ Board 2814 M2 (DD-11 row 12) - the booking carries a `travelled` event (the
   * run happened; travel is an event, never a status). Mirrors
   * `PortalCrossingSummary`. ⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06): A
   * TRAVELLED RUN STAYS ON THE MONTH'S BILL FROM EVERY REQUESTS-SCREEN CONTROL.
   * The server refuses it on all three (Cancel, accepting a cancel ask, End the
   * run); booking detail's acknowledged cancel is the only correction path.
   */
  travelled: boolean;
  /**
   * ⭐ board 2814 M7 (DD-7, DD-10) - the booking was passed to a taxi firm, so the
   * booking line marks it. KIOSK-LOCAL (`PortalCrossingSummary`), never on
   * `PortalRequest`. OPTIONAL: absent reads as not passed.
   */
  taxiPassed?: boolean;
};

// ⭐ Board 2812 M1 - `canEndRunHere`, `requestLeadDayTime`, `dublinWallClockOf` and
// `bookingRequestMismatch` moved to `shared/needs-helen-now.ts` with `needsHelenNow`;
// re-exported from the import block at the top of this file.

/**
 * ⭐⭐ Board 2788 M7 (step 0, the M6 wave's security Medium; DD-14, DD-17) - WHICH
 * WAY THE MISMATCH LINE STEERS, by the side that moved last, NEVER by `homeChanged`.
 * DD-14 clears `homeChanged` on Helen's next status write, so a Changed Priced row
 * that Helen re-priced (DD-2) read as "the booking is right" and was steered to
 * "Send to the home again", which pushes the OLD booking time onto the home's
 * request. The rule, both screens (Requests row and Booking detail):
 *   · FIRST the request's NEWEST `time-changed` history entry, because both sides
 *     write their changes there: `by: 'home'` means the request moved last and the
 *     booking is behind, so `change` (change the booking to the home's time);
 *     `by: 'ops'` means Helen moved it last and the request is behind, so `send`
 *     ("Send to the home again");
 *   · ONLY when there is no `time-changed` entry at all, the booking's
 *     `date_changed` event decides: present, so Helen moved the booking and the
 *     request never moved, `send`; absent, so the request moved (its revision is
 *     above 0, DD-17's other half), `change`.
 * ⛔ `dateChanged` IS A FOREVER FLAG (any `date_changed` event, ever), so it must
 * never outrank the history. The shape the M7 review wave traced: a half-crossed
 * Priced row whose booking Helen moved to the home's time after DD-1's refusal
 * carries the event, and a home change made after that is the newer fact; steering
 * on the flag said "Send to the home again" and one Save put the booking's older
 * time over the home's newer change.
 * ⚠️ When the newest entry is the home's but Helen moved the booking later and the
 * portal never took it, this steers to `change` too: the conservative side, since
 * it never writes over the home's request (Helen sees both times on the line).
 * Only meaningful where `bookingRequestMismatch` answered a line.
 */
export function mismatchSteer(
  r: Pick<PortalRequest, 'history'>,
  c: Pick<PortalCrossing, 'dateChanged'>,
): 'change' | 'send' {
  let newest: { at: number; by: string } | null = null;
  for (const h of Array.isArray(r.history) ? r.history : []) {
    if (h.kind !== 'time-changed') continue;
    const at = Date.parse(h.at);
    if (Number.isNaN(at)) continue;
    if (newest === null || at > newest.at) newest = { at, by: h.by };
  }
  if (newest !== null) return newest.by === 'ops' ? 'send' : 'change';
  return c.dateChanged === true ? 'send' : 'change';
}

// ---------------------------------------------------------------------------
// ⭐⭐ Board 2814 M2 (plan DD-11) - WHERE EVERY REQUEST LIVES. ⭐ Board 2812 M1 moved
// `needsHelenNow` and its pure closure to `shared/needs-helen-now.ts` (the server
// counts a home's requests by the same rule); every moved name is re-exported here.
// ---------------------------------------------------------------------------

/**
 * ⭐ Board 2814 M4 (DD-11 row 14) - HELEN'S HAND SETTLEMENT OF ONE REQUEST, as the
 * Requests list serves it beside the crossings (keyed by request id). Mirrors
 * `PortalRequestSettlement` in core/data/portal-request-settlements-repo.ts.
 */
export type PortalSettlement = { requestId: number; reason: SettleReason; settledAt: string };

/** ⭐ Board 2814 M4 - the two settle reasons, in Helen's words (the quiet controls'
 *  labels and the Done row's line). */
export const SETTLE_REASON_WORDS: Readonly<Record<SettleReason, string>> = {
  booked_another_way: 'Booked another way',
  not_going_ahead: 'Not going ahead',
};

// ---------------------------------------------------------------------------
// ⭐⭐ Board 2788 M6 (steps 2 and 3) - THE HOME'S ASK AND ITS HISTORY, IN WORDS.
// ⭐ Board 2814 M2: moved here from the Requests screen, unchanged, because booking
// detail answers the same ask and shows the same history (DD-11 item 4); one copy
// of each sentence for both screens.
// ---------------------------------------------------------------------------

const ASK_DUBLIN = { timeZone: 'Europe/Dublin' } as const;

/** "10:30" in Dublin, or null. */
function clockOf(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString('en-IE', { ...ASK_DUBLIN, hour: '2-digit', minute: '2-digit' });
}

/** "Tue 10 Nov" in Dublin, or null. */
function dayWordsOf(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString('en-IE', { ...ASK_DUBLIN, weekday: 'short', day: 'numeric', month: 'short' }).replace(/,/g, '');
}

/**
 * "08:15 to 09:45", or with the day on both ends when the two instants fall on
 * different Dublin days ("Tue 10 Nov 08:15 to Thu 12 Nov 09:45"), so a change of
 * day is never read as a change of time. The portal card's own shape (DD-11).
 */
function spanWords(from: string | null | undefined, to: string | null | undefined): string | null {
  const a = clockOf(from);
  const b = clockOf(to);
  if (a === null || b === null) return null;
  if (dublinCivilDate(new Date(from as string)) === dublinCivilDate(new Date(to as string))) return `${a} to ${b}`;
  return `${dayWordsOf(from) ?? ''} ${a} to ${dayWordsOf(to) ?? ''} ${b}`.trim();
}

/** The request's own kind of time (DD-11), as a word. */
function meaningWord(r: Pick<PortalRequest, 'timeMeaning'>): 'appointment' | 'collection' | 'time' {
  return r.timeMeaning === 'appointment' || r.timeMeaning === 'collection' ? r.timeMeaning : 'time';
}

/**
 * ⭐ THE ASK, IN WORDS (DD-11): "The home asked to move the collection from 08:15
 * to 09:45", or for an appointment "… the appointment from 10:00 to 11:30
 * (collection 09:00 to 10:30)", so no line mixes an appointment with a pickup
 * unlabelled; or "The home asked to cancel this trip". null when nothing is asked.
 */
export function askLineOf(r: PortalRequest): string | null {
  if (r.askState !== 'open') return null;
  if (r.askKind === 'cancel') {
    return r.journeyDate === null ? 'The home asked to stop this repeating trip.' : 'The home asked to cancel this trip.';
  }
  if (r.askKind !== 'change') return null;
  const span = spanWords(r.journeyDate, r.askJourneyDate);
  if (span === null) return 'The home asked to change the time.';
  const pickup = r.timeMeaning === 'appointment' ? spanWords(r.pickupAt, r.askPickupAt) : null;
  return `The home asked to move the ${meaningWord(r)} from ${span}${pickup === null ? '' : ` (collection ${pickup})`}.`;
}

/**
 * ⭐ board 2812 M4 (step 0 (a), round 2's `round2.py:241`) - THE ASK DECLINE'S QUESTION,
 * shown above "Yes, decline it" / "Keep it" wherever the ask's Decline confirms in place
 * (a To do card and the booking detail): what stays, and what the home sees. The time is
 * the trip's own (the ask's "from" time, `askLineOf`'s), in Dublin. One sentence for both
 * screens.
 */
export function askDeclineQuestion(r: PortalRequest): string {
  if (r.askKind === 'cancel') {
    return r.journeyDate === null
      ? 'Decline the stop? The repeating trip carries on and the home sees Declined.'
      : 'Decline the cancel? The trip goes ahead and the home sees Declined.';
  }
  const at = clockOf(r.journeyDate);
  return at === null
    ? 'Decline the change? The trip stays as it is and the home sees Declined.'
    : `Decline the change? The trip stays at ${at} and the home sees Declined.`;
}

/** DD-5's closed list, worded for Helen. Flagged for Vicky's sitting. */
export const DECLINE_REASON_WORDS: ReadonlyArray<{ value: 'no_bus' | 'too_close' | 'please_ring'; label: string }> = [
  { value: 'no_bus', label: 'We have no bus free at that time' },
  { value: 'too_close', label: 'It is too close to the trip to change it here' },
  { value: 'please_ring', label: 'Please ring us so we can sort it out' },
];

/**
 * ⭐ ONE HISTORY LINE, labelled by `time_meaning` (DD-11): "Collection 08:15 to
 * 09:45, asked by the home, Tue 6 Oct". The times are the request's OWN stored
 * instants, never a re-derived pickup (DD-11). null for a kind this build does not
 * know, which renders nothing rather than a guess.
 */
export function historyLineOf(r: PortalRequest, h: PortalRequest['history'][number]): string | null {
  const what = meaningWord(r);
  const label = `${what.charAt(0).toUpperCase()}${what.slice(1)}`;
  const by = h.by === 'home' ? 'the home' : 'us';
  const at = dayWordsOf(h.at) ?? '';
  switch (h.kind) {
    case 'time-changed': {
      const span = spanWords(h.fromAt, h.toAt);
      return span === null ? null : `${label} ${span}, changed by ${by}, ${at}`;
    }
    case 'change-asked': {
      const span = spanWords(h.fromAt, h.toAt);
      return span === null ? null : `${label} ${span}, asked by ${by}, ${at}`;
    }
    case 'cancel-asked':
      return `${r.journeyDate === null ? 'Stop' : 'Cancel'} asked by ${by}, ${at}`;
    case 'ask-declined':
      return `Ask turned down by ${by}, ${at}`;
    case 'cancelled':
      return `Cancelled by ${by}, ${at}`;
    case 'declined':
      return `Declined by ${by}, ${at}`;
    default:
      return null;
  }
}

/** Every history line a request carries, in the portal's order, unknown kinds dropped. */
export function historyLinesOf(r: PortalRequest): string[] {
  return (Array.isArray(r.history) ? r.history : [])
    .map((h) => historyLineOf(r, h))
    .filter((t): t is string => t !== null);
}

/**
 * ⭐⭐ Board 2814 M4 (Captain ruling (c), 2026-10-06) - ONE ANSWER ABOUT A RUN THAT
 * HAS TRAVELLED, THE SAME SENTENCE IN EVERY PLACE, then the next step for where
 * Helen is: on the Requests screen the booking is where it can be taken off; on
 * booking detail (its ask block) the run's own cancel is below.
 */
export const RUN_TRAVELLED_LINE = 'The trip ran, so its run stays on the month\u2019s bill.';
export type RunTravelledWhere = 'requests' | 'booking';
export function runTravelledText(where: RunTravelledWhere): string {
  return `${RUN_TRAVELLED_LINE} ${where === 'booking' ? 'To take it off, cancel the run below.' : 'To take it off, cancel it on the booking.'}`;
}

/**
 * ⛔ THE ONE PLACE LEG B's OUTCOME BECOMES WORDS (T2088 M8), after `said` (the
 * request's own "Home: Cancelled." head). The status route's cancel and a cancel
 * ask's accept read it, so the two cannot tell two stories about one run.
 * ⛔ THE SERVER'S SENTENCE IS NEVER RENDERED (SEC-5); `invoicedOn` is a CODE and
 * an invoice number we allocated. Every arm names the booking and, where there is
 * one, the remedy.
 */
export function portalRunNotice(
  said: string,
  outcome: PortalRequestBookingOutcome | null,
  /** ⭐ Board 2814 M4: where Helen is, so a travelled run's next step names the
   *  control on THIS screen. The Requests row by default. */
  where: RunTravelledWhere = 'requests',
): { kind: 'ok' | 'error'; text: string } {
  if (outcome === null) return { kind: 'ok', text: said };
  switch (outcome.kind) {
    case 'cancelled':
      return { kind: 'ok', text: `${said} The linked run is cancelled too.` };
    case 'refused-travelled':
      // ⭐ Board 2814 M4 (Captain ruling (c)): the request closed, the run did not.
      return { kind: 'error', text: `${said} ${runTravelledText(where)}` };
    case 'refused-invoiced':
      return {
        kind: 'error',
        text: `${said} The linked run is STILL ON, because its booking is on invoice ${outcome.invoicedOn}, which has already been issued. Void that invoice on the Invoices tab, end the run, then re-issue it.`,
      };
    case 'refused':
      // ⛔ IT DOES NOT SAY "STILL ON" (T2088 M8 review wave, the named gate's M4).
      // ⭐ Board 2814 M4: a travelled run has its own arm above now, so the reachable
      // causes here are a PAID DEPOSIT and a `completed` status (no writer today).
      return {
        kind: 'error',
        text: `${said} The linked run was NOT ended. Open the booking and deal with it there: a paid deposit needs a refund decision.`,
      };
    case 'missing':
      return {
        kind: 'error',
        text: `${said} The linked booking could not be found, so nothing was cancelled. Tell Alex.`,
      };
    case 'unknown':
      return {
        kind: 'error',
        text: `${said} Whether a run is linked to it could not be read, so it may still be on. Check the booking.`,
      };
    default:
      return {
        kind: 'error',
        text: `${said} The linked run is STILL ON. Open the booking and cancel it there.`,
      };
  }
}

/**
 * ⭐⭐ WHAT BECAME OF HELEN'S ANSWER, IN WORDS (2788 M4's outcome union, DD-18). Every
 * failure is an error naming what to do next; nothing is a quiet success. A cancel
 * ask's run outcome is said in `portalRunNotice`'s words.
 * ⭐ Board 2814 M2: one function for the Requests row AND booking detail.
 */
export function askNotice(
  r: Pick<PortalRequest, 'homeName' | 'askKind'>,
  decision: 'accept' | 'decline',
  outcome: PortalAskOutcome,
  where: RunTravelledWhere = 'requests',
): { kind: 'ok' | 'error'; text: string } {
  const home = r.homeName;
  switch (outcome.kind) {
    case 'refused':
    case 'error':
      return { kind: 'error', text: outcome.text };
    case 'request_changed':
      return {
        kind: 'error',
        text: `${home}: the home changed this request in between, so it was not answered. Look at it again on the reloaded list.`,
      };
    case 'not_reached':
      return {
        kind: 'error',
        text: outcome.booking?.changed === true
          ? `${home}: the booking moved, but the home's request was not reached. Press again: the booking will not move twice.`
          : `${home}: the home's request was not reached, so nothing changed there. Press again.`,
      };
    case 'unknown':
      return {
        kind: 'error',
        text: `${home}: the home's portal did not answer, so it may or may not have taken it. The list is reloaded: if it still shows the ask, press again.`,
      };
    case 'declined':
      return { kind: 'ok', text: `${home}: the ask is declined. The home sees why.` };
    case 'changed':
    case 'already':
      if (decision === 'decline') return { kind: 'ok', text: `${home}: the ask was already declined.` };
      if (outcome.run !== undefined || r.askKind === 'cancel') return portalRunNotice(`${home}: Cancelled.`, outcome.run ?? null, where);
      return {
        kind: 'ok',
        text: outcome.kind === 'already'
          ? `${home}: the change was already accepted.`
          : outcome.booking === undefined
            ? `${home}: the change is accepted.`
            : `${home}: the change is accepted, and booking ${String(outcome.booking.bookingId)} moved with it.`,
      };
  }
}

/**
 * ⭐⭐ T1746 M3 (Captain's M2 gate item 2a) - THREE ANSWERS, AND `null` IS NOT
 * `{}`.
 *
 *   Record  - the kiosk read its own database. Every request not in it has NOT
 *             crossed, and the row offers its ordinary controls.
 *   `null`  - THE LOCAL READ FAILED. Nothing is known about ANY row, so the
 *             screen must claim nothing: no "Make it a booking", no booking
 *             control, no landed-but-unbooked line, one neutral sentence
 *             instead. This is the honest reading of a degraded route.
 *   `{}`    - a server that predates M1 sent no key at all (see below). "Nothing
 *             has crossed yet" is the right reading THERE, because that server
 *             never lands crossings this map could describe.
 *
 * ⛔ COLLAPSING `null` INTO `{}` IS THE DEFECT M3 FIXED, in one line: it turns
 * "the link could not be read" into the positive claim "nothing has crossed",
 * which restores a crossing control on a request that already crossed and hides
 * the hand-key exit on one that landed without a booking.
 */
export async function listPortalRequests(opts: { notes?: boolean } = {}): Promise<{
  requests: PortalRequest[];
  crossings: Record<number, PortalCrossing> | null;
  /**
   * ⭐⭐ board 2637 (plan D4; ADR-0001 §8.3 AMENDMENT) — THE NURSE'S NOTE, BESIDE
   * THE ROWS AND KEYED BY REQUEST ID, never a key on `PortalRequest`. Asked for
   * ONLY by the Requests screen's own load (`{ notes: true }`); the 30-second
   * badge poll above asks without it and the server sends none.
   * ⛔ A DISTINCT NAME ON PURPOSE: the screen already has a `notes` state for
   * Helen's "Note for the home", which is posted as `waitingNotes` and reaches
   * drivers through the booking. `{}` when not asked, or from an older server.
   */
  nurseNotes: Record<number, string>;
  /**
   * ⭐ Board 2814 M4 (DD-11 row 14) - Helen's hand settlements, keyed by request id,
   * read by the server in the same request as the crossings. `null` is THIS server
   * saying the read failed (the link-unknown case); an absent key is an older
   * server, read as "nothing settled".
   */
  settlements: Record<number, PortalSettlement> | null;
}> {
  // ⛔ getJson, NOT getOr. `getOr` would turn "the portal link is down" into an
  // empty list, and an empty inbox is a MEANING here - it says there is nothing
  // waiting. Helen cannot tell that apart from a broken seam, and the whole
  // point of this screen is that she acts on what it shows her.
  const body = await getJson<{
    requests: PortalRequest[];
    crossings?: Record<number, PortalCrossing> | null;
    settlements?: Record<number, PortalSettlement> | null;
    nurseNotes?: Record<number, string>;
  }>(opts.notes === true ? '/api/portal/requests?notes=1' : '/api/portal/requests');
  // ⚠️ THE KEY'S ABSENCE AND ITS `null` ARE DIFFERENT ANSWERS, and `??` would
  // fold them together. A server mid-deploy, or a stub that predates M1, sends
  // no `crossings` key at all: "nothing has crossed yet" is the safe reading
  // there, and the row keeps its ordinary controls. An explicit `null` is THIS
  // server saying the local read failed, and it is carried through unchanged.
  return {
    requests: body.requests,
    crossings: body.crossings === undefined ? {} : body.crossings,
    settlements: body.settlements === undefined ? {} : body.settlements,
    nurseNotes: opts.notes === true ? (body.nurseNotes ?? {}) : {},
  };
}

/**
 * ⭐ Board 2812 M1 (plan step 3) - ONE HOME'S REQUESTS, filtered on the server by
 * its group key and returned through the same projection (no row gains a key).
 * The same shape and the same null-versus-absent reading as `listPortalRequests`.
 */
export async function listHomeRequests(homeKey: string, opts: { notes?: boolean } = {}): Promise<{
  requests: PortalRequest[];
  crossings: Record<number, PortalCrossing> | null;
  nurseNotes: Record<number, string>;
  settlements: Record<number, PortalSettlement> | null;
}> {
  const params = new URLSearchParams({ home: homeKey });
  if (opts.notes === true) params.set('notes', '1');
  const body = await getJson<{
    requests: PortalRequest[];
    crossings?: Record<number, PortalCrossing> | null;
    settlements?: Record<number, PortalSettlement> | null;
    nurseNotes?: Record<number, string>;
  }>(`/api/portal/requests?${params.toString()}`);
  return {
    requests: body.requests,
    crossings: body.crossings === undefined ? {} : body.crossings,
    settlements: body.settlements === undefined ? {} : body.settlements,
    nurseNotes: opts.notes === true ? (body.nurseNotes ?? {}) : {},
  };
}

/**
 * ⭐⭐ Board 2814 M4 (DD-11 row 14, step 5b) - HELEN SETTLES AN OLD UNBOOKED ROW BY
 * HAND, or undoes it. Nothing reaches the portal: the home's card is unchanged.
 * ⛔ The copy is keyed on the status and the code, never the server's sentence.
 */
export const SETTLE_NOT_ALLOWED_COPY =
  'This request is no longer waiting to be booked, so it was not settled. Reload the list to see where it is now.';

export async function settlePortalRequest(requestId: number, reason: SettleReason): Promise<AdminResult<'settled' | 'already'>> {
  try {
    const body = await postJson<{ outcome?: unknown }>(`/api/portal/requests/${String(requestId)}/settle`, { reason });
    return { ok: true, value: body.outcome === 'already' ? 'already' : 'settled' };
  } catch (e: unknown) {
    if (e instanceof ApiError && e.status === 409) {
      const code = (e.body as { code?: unknown } | null)?.code;
      if (code === 'settle_not_allowed') return { ok: false, message: SETTLE_NOT_ALLOWED_COPY };
    }
    return { ok: false, message: portalFailureCopy(e, 'settle that request') };
  }
}

export async function undoPortalRequestSettlement(requestId: number): Promise<AdminResult<'undone' | 'already'>> {
  try {
    const body = await postJson<{ outcome?: unknown }>(`/api/portal/requests/${String(requestId)}/settle/undo`, {});
    return { ok: true, value: body.outcome === 'already' ? 'already' : 'undone' };
  } catch (e: unknown) {
    return { ok: false, message: portalFailureCopy(e, 'undo that') };
  }
}

/**
 * Friendly copy from the STATUS, never the server body (SEC-5), mirroring
 * `adminFailure` above.
 *
 * ⚠️ 503 IS THE CONFIGURATION CASE, NOT A SESSION PROBLEM. The kiosk route maps
 * a portal 401 to 503 precisely so this layer never sees a 401 it would read as
 * "signed out" - see the mapping note in server/app.ts. So 503 here means the
 * portal link is not set up or its passcode is wrong: a server fault Helen
 * cannot fix and must not be asked to.
 */
function portalFailureCopy(e: unknown, verb: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  // ⭐ Board 2788 M4 (DD-1): the row was stale; keyed on the CODE (SEC-5).
  if (status === 409 && statusRefusalCodeOf(e) === 'request_changed') return STALE_ROW_COPY;
  // ⭐ Board 2814 M1 (DD-3): Confirm makes the booking now, through its own route.
  if (status === 409 && statusRefusalCodeOf(e) === 'confirm_needs_booking') return CONFIRM_NEEDS_BOOKING_COPY;
  // ⭐ Board 2814 M8R1 (Q287): the run travelled, so the request stays open.
  if (status === 409 && statusRefusalCodeOf(e) === 'request_run_travelled') return TRAVELLED_REQUEST_COPY;
  // ⭐ Board 2788 M6 (step 0): a Confirm on a half-crossed row whose booking is on
  // the old time, in the crossing's own words.
  const stale = staleBookingCopy(e);
  if (stale !== null) return stale;
  if (status === 503) {
    return 'The portal link is not set up on this server. Nothing you did caused this - tell Alex.';
  }
  if (status === 502) return 'The portal did not answer. Try again in a moment.';
  if (status === 409) return 'That request has already moved on. Reload to see where it is now.';
  if (status === 404) return 'That request no longer exists. Reload the list.';
  if (status === 400) return 'Check the price and the note, then try again.';
  return `Could not ${verb}. Try again in a moment.`;
}

/**
 * ⭐⭐ T2088 M8 — WHAT LEG B DID TO THE LINKED BOOKING, IF ANYTHING.
 *
 * `POST /api/portal/requests/:id/status` gained an OPTIONAL `booking` key when
 * the status is `cancelled` and the request has crossed: Helen's kiosk cancel
 * now takes the run with it. The key is ABSENT when no booking was involved,
 * which is the whole reason this type has no `'none'` member — absence is the
 * ordinary case and modelling it as a value would invite a caller to render it.
 *
 * ⛔ IT IS A CODE, NEVER A SENTENCE (SEC-5). Every arm's words are authored on
 * this side; the only server value carried is `invoicedOn`, an invoice NUMBER
 * this system allocated — the same key, spelled the same way, that
 * `POST /api/bookings/:id/cancel` returns on its 409, so one vocabulary serves
 * both legs.
 * ⚠️ `unknown` IS NOT "no booking". It means the crossings read FAILED, so the
 * server cannot say whether a run is still live. Rendering it as success is the
 * exact lie R-4 exists to remove.
 */
export type PortalRequestBookingOutcome =
  | { kind: 'cancelled'; bookingId: number; changed: boolean; balanceAsk?: string }
  | { kind: 'refused-invoiced'; bookingId: number; invoicedOn: string }
  /** ⭐ Board 2814 M4 (Captain ruling (c)) - the run travelled, so it stays on the
   *  month's bill; the request side closed. Terminal: a replay meets the same. */
  | { kind: 'refused-travelled'; bookingId: number }
  | { kind: 'refused'; bookingId: number }
  | { kind: 'missing'; bookingId: number }
  | { kind: 'failed'; bookingId: number }
  | { kind: 'unknown' };

/**
 * ⛔ A NARROWING READ, NOT A CAST. The wire is untrusted even from our own
 * server: a body shaped differently (an older kiosk build, a proxy, a truncated
 * response) must read as "nothing to say" rather than crash the screen or, worse,
 * render `undefined` at Helen. Unrecognised `kind`s are dropped for the same
 * reason — fail quiet on a key the screen has no words for, never fail loud at
 * the operator for a server it cannot fix.
 */
function readBookingOutcome(body: unknown): PortalRequestBookingOutcome | null {
  if (typeof body !== 'object' || body === null) return null;
  const raw = (body as { booking?: unknown }).booking;
  if (typeof raw !== 'object' || raw === null) return null;
  const kind = (raw as { kind?: unknown }).kind;
  if (typeof kind !== 'string') return null;
  if (kind === 'unknown') return { kind: 'unknown' };

  // ⛔⛔ EVERY OTHER ARM CARRIES A BOOKING ID, so a body without one is not that
  // arm — it is a body this screen has no words for. Checked rather than cast.
  const bookingId = (raw as { bookingId?: unknown }).bookingId;
  if (typeof bookingId !== 'number' || !Number.isFinite(bookingId)) return null;

  if (kind === 'cancelled') {
    const changed = (raw as { changed?: unknown }).changed;
    const balanceAsk = (raw as { balanceAsk?: unknown }).balanceAsk;
    return {
      kind: 'cancelled',
      bookingId,
      changed: changed === true,
      ...(typeof balanceAsk === 'string' && balanceAsk !== '' ? { balanceAsk } : {}),
    };
  }
  if (kind === 'refused-invoiced') {
    // ⛔⛔ THE STRICTNESS THAT WAS MISSING, AND THE SIBLING MAPPER ALREADY HAD IT
    // (M8 review wave, output-gate R-1 / the named gate's L6, CONFIRMED by a
    // running probe). Narrowing on `kind` alone and casting rendered
    // "its booking is on invoice **undefined**, which has already been issued"
    // straight at Helen. `cancelBookingRun` above degrades to a plain refusal
    // unless `invoicedOn` is a non-empty string; this docblock CLAIMED the two
    // legs "cannot drift apart", and on strictness they already had.
    // ⚠️ The shipped server cannot emit that shape (`invoice_number` is NOT NULL),
    // so this is defence against a FUTURE producer, not a live defect — which is
    // exactly when a narrowing read is cheap to add and impossible to retrofit
    // once a screen has been rendering nonsense for a month.
    const invoicedOn = (raw as { invoicedOn?: unknown }).invoicedOn;
    if (typeof invoicedOn !== 'string' || invoicedOn === '') return { kind: 'refused', bookingId };
    return { kind: 'refused-invoiced', bookingId, invoicedOn };
  }
  if (kind === 'refused' || kind === 'refused-travelled' || kind === 'missing' || kind === 'failed') return { kind, bookingId };
  // An unrecognised kind is a body this screen has no words for: say nothing
  // rather than render a key it cannot name.
  return null;
}

export async function setPortalRequestStatus(
  requestId: number,
  status: string,
  opts: { waitingNotes?: string; pricedAmountCents?: number; revision?: number | null } = {},
): Promise<AdminResult<PortalRequestBookingOutcome | null>> {
  try {
    const body = await postJson<unknown>(`/api/portal/requests/${String(requestId)}/status`, {
      status,
      ...(opts.waitingNotes !== undefined ? { waitingNotes: opts.waitingNotes } : {}),
      ...(opts.pricedAmountCents !== undefined ? { pricedAmountCents: opts.pricedAmountCents } : {}),
      // ⭐ Board 2788 M4 (DD-1, S22): the revision the row RENDERED, when it has one.
      ...(typeof opts.revision === 'number' ? { revision: opts.revision } : {}),
    });
    return { ok: true, value: readBookingOutcome(body) };
  } catch (e: unknown) {
    return { ok: false, message: portalFailureCopy(e, 'update that request') };
  }
}

/**
 * ⭐ board 2707 M3 (DD4, ruling RB) - HELEN OPENED THIS REQUEST, so the care home
 * can be told it was seen. ⛔ FIRE-AND-FORGET: it returns nothing to await and
 * swallows every failure, because a Seen that cannot be recorded must never
 * reach her screen or hold up the row she just opened. The caller decides WHEN
 * (an explicit open, once per row per mount); this decides nothing.
 */
export function markPortalRequestSeen(requestId: number): void {
  void postJson<unknown>(`/api/portal/requests/${String(requestId)}/seen`, {}).catch(() => undefined);
}

/** ⭐ Board 2788 M4 (DD-1) - the stale-row sentence, neutral on purpose: the
 *  change may be Helen's own earlier write. */
const STALE_ROW_COPY = 'This request has changed since you opened the list. Reload to see it.';

/** ⭐ Board 2788 M4 - the CODE on a seam refusal (SEC-5), or null. */
function seamCodeOf(e: unknown): string | null {
  const body = e instanceof ApiError ? e.body : null;
  if (typeof body !== 'object' || body === null) return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/**
 * ⭐ Board 2814 M1 (plan DD-3, S1) - THE STATUS ROUTE'S OWN CODES. ⛔ A
 * HAND-WRITTEN MIRROR of `PortalStatusRefusalCode` in `server/app.ts` (that
 * module pulls in `pg`); `test/server/portal-proxy-routes.spec.ts` asserts the two
 * are mutually assignable, so either side gaining a member fails the build.
 */
export type PortalStatusRefusalCode = 'request_changed' | 'confirm_needs_booking' | 'request_run_travelled';

/** The status route's code on a refusal, narrowed to the closed set, or null. */
function statusRefusalCodeOf(e: unknown): PortalStatusRefusalCode | null {
  const code = seamCodeOf(e);
  return code === 'request_changed' || code === 'confirm_needs_booking' || code === 'request_run_travelled'
    ? code
    : null;
}

/** ⭐ Board 2814 M1 (DD-3) - the status route refused a confirm with no booking. */
const CONFIRM_NEEDS_BOOKING_COPY = 'Confirm now makes the booking. Reload the list and press Confirm on the row.';

/**
 * ⭐⭐ Board 2814 M8R1 (Captain's ruling on Q287, 2026-10-07) - A RUN THAT HAS
 * TRAVELLED CANNOT HAVE ITS REQUEST CLOSED FROM THE KIOSK. The server refuses Cancel,
 * Decline and accepting the home's ask on it (409 `request_run_travelled`, before any
 * write); these are the words, Captain's own, said before the press on a row whose
 * run is known to have travelled and after a refused one.
 */
export const TRAVELLED_REQUEST_COPY =
  "The trip ran, so it stays booked and on the month's bill. Decline the home's ask and tell them why.";

/**
 * ⭐⭐ Board 2788 M4 (plan step 6, DD-18) - WHAT BECAME OF HELEN'S ANSWER TO THE
 * HOME'S ASK, as a closed set the screen words itself (M6 renders it):
 *   - 'changed' / 'declined' / 'already' : it landed (a re-press after a lost
 *     answer is `already`, never a refusal);
 *   - 'not_reached' : the portal did not take it (provably unchanged there);
 *   - 'unknown'     : the portal may or may not have taken it;
 *   - 'request_changed' : the home changed the request in between;
 *   - 'refused'     : refused before anything was written, with the CODE and
 *                     the screen's own words for it.
 * `booking` says whether the crossed booking moved (an accept of a change ask),
 * so "not reached" can say the booking already did. ⛔ Every failure arm tells
 * Helen to press again, and the screen reloads after `not_reached` and `unknown`.
 */
export type PortalAskOutcome =
  | ({ kind: 'changed' | 'declined' | 'already' } & PortalAskBooking)
  | ({ kind: 'not_reached' | 'unknown' | 'request_changed' } & PortalAskBooking)
  | { kind: 'refused'; code: string; text: string }
  | { kind: 'error'; text: string };

/**
 * ⭐ Board 2788 M6 (step 0, the M4 wave's plan-alignment Medium) - WHAT HAPPENED TO
 * THE BOOKING, which is a different thing on each kind of ask, so it is two keys:
 *   · `booking` - a CHANGE ask's crossed booking, moved (`changed`) or already there;
 *   · `run`     - a CANCEL ask's leg B, in `PortalRequestBookingOutcome`'s words: a
 *                 refused, failed, missing or unknown booking cancel is said as
 *                 such, never folded into "changed". The status route's cancel and
 *                 this one read ONE mapper (`readBookingOutcome`).
 * Either may be absent (no crossed booking, or a body this build has no words for).
 */
export type PortalAskBooking = {
  booking?: { bookingId: number; changed: boolean };
  run?: PortalRequestBookingOutcome;
};

const ASK_REFUSAL_COPY: Record<string, string> = {
  request_changed: STALE_ROW_COPY,
  ask_not_open: "The home's ask has already been answered or replaced. Reload to see it.",
  ask_expired: 'The time the home asked for has passed, so it cannot be accepted. Decline it, and ring the home.',
  not_linked: "This request's booking is not linked to it any more. Reload the list.",
  no_collection: "The home's portal gave no collection time for that change, so nothing was changed. Ring the home.",
  // ⭐ Board 2814 M8R1 (Q287): the run travelled, so the ask cannot be accepted.
  request_run_travelled: TRAVELLED_REQUEST_COPY,
};

/**
 * ⛔ A NARROWING READ. A booking key that carries a `kind` is leg B's answer to a
 * cancel-ask accept and goes through `readBookingOutcome`, the status route's own
 * mapper; one without is a change ask's moved booking. Before M6 every body was
 * read as the second shape, so a REFUSED run read as "changed: false" and the
 * screen said nothing about a bus that was still on.
 */
function readAskBooking(body: unknown): PortalAskBooking {
  const raw = typeof body === 'object' && body !== null ? (body as { booking?: unknown }).booking : undefined;
  if (typeof raw !== 'object' || raw === null) return {};
  if (Object.hasOwn(raw, 'kind')) {
    const run = readBookingOutcome(body);
    return run === null ? {} : { run };
  }
  const b = raw as { bookingId?: unknown; changed?: unknown };
  return typeof b.bookingId === 'number' ? { booking: { bookingId: b.bookingId, changed: b.changed === true } } : {};
}

async function postAskDecision(path: string, payload: Record<string, unknown>): Promise<PortalAskOutcome> {
  try {
    const body = await postJson<{ outcome?: unknown }>(path, payload);
    const outcome = body.outcome;
    const about = readAskBooking(body);
    if (outcome === 'changed' || outcome === 'declined' || outcome === 'already') {
      return { kind: outcome, ...about };
    }
    if (outcome === 'not_reached' || outcome === 'unknown' || outcome === 'request_changed') {
      return { kind: outcome, ...about };
    }
    return { kind: 'error', text: 'The answer from the server was not understood. Reload to see where the request is now.' };
  } catch (e: unknown) {
    const code = seamCodeOf(e);
    if (e instanceof ApiError && e.status === 409 && code !== null) {
      const text = ASK_REFUSAL_COPY[code] ??
        (isRescheduleRefusal(code) ? 'The booking cannot be moved to that time. Open the booking to see why.' : null);
      if (text !== null) return { kind: 'refused', code, text };
    }
    return { kind: 'error', text: portalFailureCopy(e, 'answer the home') };
  }
}

/** ⭐ Board 2788 M4 - Accept: `{askSeq, revision}` as the row RENDERED them. */
export function acceptPortalAsk(requestId: number, input: { askSeq: number; revision: number }): Promise<PortalAskOutcome> {
  return postAskDecision(`/api/portal/requests/${String(requestId)}/ask/accept`, {
    askSeq: input.askSeq,
    revision: input.revision,
  });
}

/** ⭐ Board 2788 M4 - Decline, with one of the three closed reasons (DD-5). */
export function declinePortalAsk(
  requestId: number,
  input: { askSeq: number; revision: number; reason: 'no_bus' | 'too_close' | 'please_ring' },
): Promise<PortalAskOutcome> {
  return postAskDecision(`/api/portal/requests/${String(requestId)}/ask/decline`, {
    askSeq: input.askSeq,
    revision: input.revision,
    reason: input.reason,
  });
}

/**
 * T1447 M7 — TURNING A NURSING-HOME REQUEST INTO A KIOSK BOOKING (E6).
 *
 * ⛔ THE BROWSER SENDS NO HOME AND NO `accountId` (T1746 M5, B7). It used to
 * send the operator's `homeId` pick; the server now derives the home from the
 * REQUEST'S OWN KEY and refuses a body `homeId` that disagrees with it
 * (`crossing_home_mismatch`). ⭐ THAT IS STRICTLY STRONGER THAN THE PICK IT
 * REPLACES: a pick can be wrong, and #1720 S4 recorded exactly that defect, so
 * nobody can pick the wrong home if nobody picks.
 *
 * ⛔ WHAT DID NOT CHANGE, AND MUST NOT. The account is still the WRITE-TIME
 * SNAPSHOT taken off that home row once, at creation, and nothing may later
 * re-resolve it by joining the home to its account at read time - that link is
 * reversible, so a read-time join would let one re-link silently re-bill
 * journeys that have already travelled.
 */
export type PortalCrossingOutcome = {
  bookingId: number;
  /** `false` = this request had already been crossed and the SAME booking came
   *  back. A successful no-op, not an error. */
  created: boolean;
  /**
   * What is known about the PORTAL side afterwards, and it is deliberately four
   * values rather than a boolean. The booking is written first, in the kiosk;
   * marking the request confirmed is a second write to ANOTHER SERVICE, and
   * "a transport failure is not a failed write".
   * ⭐ Board 2814 M1 (plan DD-1.5) - `home_cancelled`: the mark came back refused
   * and a fresh read showed the home had cancelled inside the window. The
   * booking was made and stays live; nothing was cancelled automatically.
   */
  requestMarked: 'confirmed' | 'already' | 'unchanged' | 'unknown' | 'request_changed' | 'home_cancelled';
};

/** ⭐ Board 2814 M1 (plan DD-4) - one same-day booking the duplicate guard found:
 *  its id and reference, and nothing else. */
/** ⭐ board 2814 M4 fix round 1: `likely` marks the cash half's likely twins (DD-4);
 *  absent (an older server) reads as not likely. */
export type CrossingDuplicate = { bookingId: number; jobRef: string | null; likely?: boolean };

/**
 * ⭐ Board 2814 M1 - what one Confirm press came back with. The failure arm keeps
 * `AdminResult`'s shape and ADDS `duplicates` when the refusal is DD-4's, so the
 * confirm step can list them and offer "Book it anyway" in place.
 */
export type PortalCrossingResult =
  | { ok: true; value: PortalCrossingOutcome }
  | { ok: false; message: string; duplicates?: CrossingDuplicate[] };

const REQUEST_MARKED_VALUES: ReadonlySet<string> = new Set([
  'confirmed', 'already', 'unchanged', 'unknown', 'request_changed', 'home_cancelled',
]);

/**
 * ⛔ A NARROWING READ, NOT A CAST (the `readBookingOutcome` rule). An
 * unrecognised `requestMarked` reads as `unknown`, the one answer that tells
 * Helen the truth when the screen cannot: the booking exists, the request may not
 * be confirmed, press again. A body with no usable booking id is not a success.
 */
function readCrossingOutcome(body: unknown): PortalCrossingOutcome | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as { bookingId?: unknown; created?: unknown; requestMarked?: unknown };
  if (typeof b.bookingId !== 'number' || !Number.isFinite(b.bookingId)) return null;
  const marked = typeof b.requestMarked === 'string' && REQUEST_MARKED_VALUES.has(b.requestMarked)
    ? (b.requestMarked as PortalCrossingOutcome['requestMarked'])
    : 'unknown';
  return { bookingId: b.bookingId, created: b.created === true, requestMarked: marked };
}

/** The DD-4 refusal's `matches`, narrowed: ids and references only. */
function readDuplicates(e: unknown): CrossingDuplicate[] | null {
  const body = e instanceof ApiError ? e.body : null;
  if (typeof body !== 'object' || body === null) return null;
  const raw = (body as { matches?: unknown }).matches;
  if (!Array.isArray(raw)) return null;
  const out: CrossingDuplicate[] = [];
  for (const m of raw as unknown[]) {
    if (typeof m !== 'object' || m === null) continue;
    const id = (m as { bookingId?: unknown }).bookingId;
    const ref = (m as { jobRef?: unknown }).jobRef;
    const likely = (m as { likely?: unknown }).likely;
    if (typeof id !== 'number' || !Number.isFinite(id)) continue;
    out.push({
      bookingId: id,
      jobRef: typeof ref === 'string' && ref !== '' ? ref : null,
      ...(likely === true ? { likely: true } : {}),
    });
  }
  return out.length > 0 ? out : null;
}

/**
 * ⭐ Board 2814 M1 (DD-4) - the duplicate sentence, every match named.
 * ⭐ M2 (the M1 wave's code-review Low): ONE copy, `shared/crossing-duplicate.ts`,
 * which the crossing route's 409 words itself with too.
 */
export function maybeDuplicateText(matches: readonly CrossingDuplicate[]): string {
  return maybeDuplicateSentence(matches);
}

/**
 * The reasons POST /api/portal/requests/:id/booking answers 409.
 * ⚠️ NO COUNT IS WRITTEN HERE ANY MORE (board 2814 M1): the prose counted
 * ELEVEN after board 2788 M4 had already made it thirteen. The union below is
 * the count; derive it, never read it here:
 * `sed -n '/^export type CrossingRefusalCode =/,/;$/p' quote-helper/src/client/lib/actions.ts | grep -c "^  | '"`.
 * ⚠️ It said "four" until T1447 M7's review added `crossing_account_missing`,
 * "five" until the T1683 M3 output gate added `crossing_reference_conflict`,
 * "six" until T1746 M2 added the two PRICE refusals below, eight until
 * T1746 M5 added the two HOME refusals with the derived home, and ten until
 * board 2707 M6 added `crossing_ref_account_mismatch`.
 *
 * ⛔ A HAND-WRITTEN MIRROR OF `CrossingRefusalCode` IN `server/app.ts`, for the
 * same reason `PushRefusalCode` is mirrored above: that module imports `pg` and
 * cannot be reached from the browser bundle.
 * The mirror-rot arm asserts the two unions are mutually assignable AND names
 * the newest members, so either side gaining a member fails the build rather
 * than silently rotting. It lives in `test/server/portal-proxy-routes.spec.ts`,
 * titled `⭐ the client mirror of CrossingRefusalCode has not rotted`.
 * ⚠️ CITED BY FILE AND TITLE, NEVER BY LINE, and both halves of that have
 * already bitten. The line cited was `test/server/app.spec.ts`, which holds no
 * such arm, so anyone following it to check the mirror found nothing; the
 * corrected citation then carried `:1305`, which the same milestone's edits
 * moved. A title survives an edit above it; a line number does not.
 */
export type CrossingRefusalCode =
  | 'crossing_not_priced'
  | 'crossing_home_unlinked'
  | 'crossing_payer_unknown'
  | 'crossing_account_missing'
  | 'crossing_reference_conflict'
  | 'crossing_price_invalid'
  | 'crossing_price_missing'
  /**
   * ⭐ T1746 M5 (B7) - TWO HOME REFUSALS, ARRIVING WITH THE DERIVED HOME. The
   * kiosk no longer takes the operator's word for which home a request belongs
   * to; it asks the portal for the request's own key.
   *  - `crossing_home_unknown` - the portal did not state a usable home for
   *    this request, so there is nothing to derive. Reload and try again.
   *  - `crossing_home_mismatch` - the browser posted a home that is not the
   *    request's. Closes #1720 S4 kiosk-side: this is the exact shape that used
   *    to succeed and snapshot the wrong account onto a booking.
   */
  | 'crossing_home_unknown'
  | 'crossing_home_mismatch'
  /**
   * ⭐ board 2707 M6 (plan DD2) - the request's readable reference (`LCC 05`) names
   * a different account from the one this crossing would bill: the home was
   * re-linked after the nurse sent it. Nothing was created.
   */
  | 'crossing_ref_account_mismatch'
  /** ⭐ Board 2788 M4 (DD-1, DD-17) - see the server's union for each. */
  | 'crossing_request_changed'
  | 'crossing_booking_stale'
  /** ⭐ Board 2814 M1 (DD-1.5, DD-4) - see the server's union for each. */
  | 'crossing_ask_open'
  | 'crossing_date_passed'
  | 'crossing_maybe_duplicate'
  | 'crossing_booking_cancelled'
  /** ⭐ Board 2814 M4 (plan-review Low 7) - the request was settled by hand. */
  | 'crossing_settled';

/**
 * ⛔ ONE REFUSAL, ONE REMEDY, ONE SENTENCE EACH - collapsing them would
 * point most of the operators at a repair that cannot work. That is the defect
 * the push refusals already paid for once (T1141 F3).
 *
 * ⚠️ THE COUNT IN THIS PROSE IS THE ONLY PART tsc DOES NOT HOLD. The union
 * above and the map below are mutually constrained by `Record<...>`, so a
 * missing member fails the build; the number in the sentence is text, and
 * `test/server/portal-proxy-routes.spec.ts`'s mirror-rot arm is what reads the
 * union against the server's.
 *
 * ⭐ Board 2814 M4: `crossing_payer_self` and its sentence are gone. Cash now
 * crosses onto the home's account like any other request (ADR1-D17 as amended).
 */
const CROSSING_REFUSAL_COPY: Record<CrossingRefusalCode | 'unknown', string> = {
  crossing_not_priced:
    'Put a price on this request first, then turn it into a booking.',
  crossing_home_unlinked:
    'That home is not linked to a billing account yet. Link it on the Nursing homes screen, then come back.',
  crossing_payer_unknown:
    'The portal sent something about this request that this system does not recognise, so nothing was created. Nothing you did caused this - tell Alex.',
  // ⛔ NOT the same sentence as `crossing_home_unlinked`, and the difference is
  // the whole point: that one says "go and link it", which would be WRONG here.
  // The home IS linked - to an account this system does not have - so linking it
  // again on the Nursing homes screen cannot help, and only Alex can reconcile
  // the two databases.
  crossing_account_missing:
    'That home is linked to a billing account this system does not have, so nothing was created. Re-linking it will not help - tell Alex which home it was.',
  /**
   * ⛔⛔ THE ONE SENTENCE ON THIS MAP THAT MUST TALK THE OPERATOR OUT OF
   * RETRYING, and the reason is a genuine asymmetry rather than caution. Two of
   * the three job-reference domains RETRY a collision invisibly, because they
   * are minting and any fresh string will do. The crossing cannot: the nurse is
   * already holding this reference and has read it out, so a replacement would
   * give one job two identities. The reference is therefore FIXED, every press
   * of the button collides on exactly the same row, and an operator who reads
   * this as a transient glitch will press it all afternoon.
   * ⚠️ IT ALSO NEVER PRINTS THE REFERENCE. It does not need to: the `Ref` column
   * on that same row already shows it, so "which request" is answerable off the
   * screen. The reference is the one thing the server may not put in a response,
   * a log line or a Sentry event, because the colliding row belongs to somebody
   * else's journey.
   */
  crossing_reference_conflict:
    'The reference on that request is already in use in this system, so nothing was created. Trying again will not clear it - send Alex the reference from the Ref column on that row.',
  /**
   * ⛔ THE TWO PRICE REFUSALS SAY OPPOSITE THINGS AND MUST NOT BE COLLAPSED
   * (T1746 M2). One is Helen's to fix in ten seconds on the row she is looking
   * at; the other cannot be fixed by pressing anything, because the request
   * crossed before the figure travelled with it and the inquiry row is already
   * in her inbox. One sentence for both would send her to re-save a price that
   * would change nothing.
   */
  crossing_price_invalid:
    'The price on that request did not arrive in a usable form, so nothing was created. Re-save the price on that row, then turn it into a booking.',
  crossing_price_missing:
    'That request crossed before prices travelled with them, so it is in the inbox with no booking. Pressing again will not change that - tell Alex which request it was.',
  /**
   * ⛔ T1746 M5 (B7) - THE TWO HOME SENTENCES, AND NEITHER READS AS THE
   * OPERATOR'S FAULT, because neither is. The home is derived from the request
   * now, so there is no pick for her to have got wrong: one says the portal did
   * not tell us, the other says the screen is out of date. Both name RELOAD as
   * the repair, which is the only thing that can actually change the answer.
   */
  crossing_home_unknown:
    'The portal did not say which home this request belongs to, so nothing was created. Reload the list and try again.',
  crossing_home_mismatch:
    'That request belongs to a different home than the one on your screen, so nothing was created. Reload the list and try again.',
  /**
   * ⭐ board 2707 M6 (plan DD2) - NOT "TRY AGAIN": the reference the nurse holds is
   * fixed, and the home's account has moved since she sent it, so every press
   * refuses the same way. The repair is the calculator and a decline with a note.
   */
  crossing_ref_account_mismatch:
    'This home\u2019s account changed after the request was sent, so nothing was created. Book it with Add a phoned-in trip in the Nursing homes area, then decline this request with a note giving the new reference.',
  // ⭐ Board 2788 M4 (DD-1). The stale booking names itself from the body's data (below).
  crossing_request_changed: 'This request has changed since you opened the list, so nothing was created. Reload to see it.',
  crossing_booking_stale:
    'The home changed this request after the booking was made, so it was not confirmed. Change the booking to the home\u2019s new time first, then press again.',
  // ⭐ Board 2814 M1 (DD-1.5, DD-4). The duplicate and the cancelled booking are
  // worded from the 409's DATA below; these two are the fallbacks for a body
  // that arrives without it.
  crossing_ask_open: 'The home has asked to change or cancel this trip. Answer that first.',
  crossing_date_passed: PAST_LINE,
  crossing_maybe_duplicate:
    'A booking on this day may already be this trip. If it is, there is nothing to do. If it is a different trip, press Book it anyway.',
  crossing_booking_cancelled:
    'The booking for this request was cancelled, so this request cannot be confirmed onto it. Decline or cancel the request instead.',
  crossing_settled:
    'This request was settled by hand, so it was not booked. Undo that on the Done list first if it still needs a booking.',
  unknown:
    'That request has already moved on, or it cannot be turned into a booking. Reload to see where it is now.',
};

function crossingRefusalCodeOf(e: unknown): CrossingRefusalCode | 'unknown' {
  const body = e instanceof ApiError ? e.body : null;
  if (typeof body !== 'object' || body === null || !('code' in body)) return 'unknown';
  const code = (body as { code: unknown }).code;
  return code === 'crossing_not_priced' ||
    code === 'crossing_home_unlinked' || code === 'crossing_payer_unknown' ||
    code === 'crossing_account_missing' || code === 'crossing_reference_conflict' ||
    code === 'crossing_price_invalid' || code === 'crossing_price_missing' ||
    // T1746 M5 (B7) - the two HOME refusals.
    code === 'crossing_home_unknown' || code === 'crossing_home_mismatch' ||
    // board 2707 M6 (DD2) - the reference names another account.
    code === 'crossing_ref_account_mismatch' ||
    // board 2788 M4 (DD-1, DD-17) - a stale row, and a stale booking.
    code === 'crossing_request_changed' || code === 'crossing_booking_stale' ||
    // board 2814 M1 (DD-1.5, DD-4) - an open ask, a past date, a possible twin
    // and a cancelled booking.
    code === 'crossing_ask_open' || code === 'crossing_date_passed' ||
    code === 'crossing_maybe_duplicate' || code === 'crossing_booking_cancelled' ||
    // board 2814 M4 - settled by hand.
    code === 'crossing_settled'
    ? code
    : 'unknown';
}

/** ⛔ THE 409 ARM IS KEYED ON THE CODE, NOT THE SENTENCE (SEC-5): no server
 *  string ever reaches this screen. Every other status reuses the seam's own
 *  map, so a portal outage reads the same here as it does one row over. */
function crossingFailureCopy(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  const stale = staleBookingCopy(e);
  if (stale !== null) return stale;
  // ⭐ Board 2814 M1 (DD-1.5, S3f): the cancelled booking is named from the DATA.
  if (status === 409 && crossingRefusalCodeOf(e) === 'crossing_booking_cancelled') {
    const b = (e as ApiError).body as Record<string, unknown>;
    const id = typeof b['bookingId'] === 'number' ? b['bookingId'] : null;
    const ref = typeof b['jobRef'] === 'string' && b['jobRef'] !== '' ? b['jobRef'] : null;
    if (id !== null || ref !== null) {
      return `Booking ${ref ?? `#${String(id)}`} was cancelled, so this request cannot be confirmed onto it. Decline or cancel the request instead.`;
    }
  }
  // ⭐ Board 2814 M1 (DD-4): every same-day match named, from the DATA.
  if (status === 409 && crossingRefusalCodeOf(e) === 'crossing_maybe_duplicate') {
    const matches = readDuplicates(e);
    if (matches !== null) return maybeDuplicateText(matches);
  }
  if (status === 409) return CROSSING_REFUSAL_COPY[crossingRefusalCodeOf(e)];
  return portalFailureCopy(e, 'turn that request into a booking');
}

/**
 * ⭐ Board 2788 M4 (DD-17): the stale booking is named, with the request's own day
 * and time, from the 409's DATA (never its sentence). ⭐ M6 (step 0) made it ONE
 * function: the status route's Confirm now refuses a half-crossed row with the same
 * code, and the two doors must word it the same way. null when `e` is not that 409
 * or its data is unusable (the caller then falls to its generic sentence).
 */
function staleBookingCopy(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409 || crossingRefusalCodeOf(e) !== 'crossing_booking_stale') return null;
  const b = e.body as Record<string, unknown>;
  const id = typeof b['bookingId'] === 'number' ? b['bookingId'] : null;
  const day = typeof b['day'] === 'string' ? b['day'] : null;
  const time = typeof b['time'] === 'string' ? b['time'] : null;
  if (id === null || day === null) return CROSSING_REFUSAL_COPY.crossing_booking_stale;
  const when = civilDateLabel(day, time) || `${day}${time === null ? '' : ` ${time}`}`;
  return `The home changed this request after the booking was made. Change booking ${String(id)} to ${when} first, then press again.`;
}

/**
 * ⛔⛔ THE BODY CARRIES NO HOME AND NO ACCOUNT - THE ONE THING A READER MUST NOT
 * UNDO (T1746 M5, B7). ⚠️ It said "EXACTLY `{passengersBand}`" until board 2814
 * M1 added the optional note, revision and `bookAnyway`; none of them names a
 * home or an account, and that is the property this block is about.
 *
 * The home is DERIVED SERVER-SIDE from the request's own key
 * (`resolveRequestHome`), so there is no home for the browser to state and no
 * pick for an operator to get wrong. #1720 S4 was exactly that: any home's key
 * could be posted against any request and be frozen onto the booking, and it is
 * closed kiosk-side by this signature no longer having somewhere to put one.
 *
 * ⚠️ THE SERVER STILL ACCEPTS AN OPTIONAL `homeId` and refuses it on a mismatch
 * (`crossing_home_mismatch`), for callers that predate this change. ⛔ DO NOT
 * "helpfully" start sending one again: a body key the server only ever uses to
 * disagree with itself is a 409 waiting for a screen that re-picks a home.
 *
 * ⛔ THE BAND IS THE ONLY ANSWER LEFT THAT IS GENUINELY HELEN'S. The nurse's
 * form carries no passenger count at all, so nothing upstream can supply it.
 */
export async function convertPortalRequestToBooking(
  requestId: number,
  input: {
    passengersBand: string;
    /** ⭐ Board 2814 M1 (DD-2): the note for the home, carried on the confirm
     *  mark. SENT ONLY WHEN NON-EMPTY: an empty note is no note, and the server
     *  refuses a note on a request that is already confirmed. */
    notes?: string;
    /** ⭐ Board 2788 M4 (DD-1, S22): the revision the row RENDERED, when it has one. */
    revision?: number | null;
    /** ⭐ Board 2814 M1 (DD-4): "Book it anyway", past the duplicate guard. Sent
     *  only as `true`; the server refuses any other value. */
    bookAnyway?: boolean;
  },
): Promise<PortalCrossingResult> {
  try {
    const body = await postJson<unknown>(`/api/portal/requests/${String(requestId)}/booking`, {
      passengersBand: input.passengersBand,
      ...(typeof input.notes === 'string' && input.notes !== '' ? { notes: input.notes } : {}),
      ...(typeof input.revision === 'number' ? { revision: input.revision } : {}),
      ...(input.bookAnyway === true ? { bookAnyway: true } : {}),
    });
    const value = readCrossingOutcome(body);
    if (value === null) {
      return { ok: false, message: 'The answer from the server was not understood. Reload to see where the request is now.' };
    }
    return { ok: true, value };
  } catch (e: unknown) {
    const duplicates = e instanceof ApiError && e.status === 409 && crossingRefusalCodeOf(e) === 'crossing_maybe_duplicate'
      ? readDuplicates(e)
      : null;
    return duplicates === null
      ? { ok: false, message: crossingFailureCopy(e) }
      : { ok: false, message: crossingFailureCopy(e), duplicates };
  }
}

/**
 * ⭐⭐ T1746 M5 (B7) - WHAT THIS REQUEST'S HOME HAS AGREED TO PAY FOR WHERE THE
 * BUS IS GOING. AN OFFER, ASKED FOR BY REQUEST ID AND BY NOTHING ELSE.
 *
 * ⛔⛔ THE ONE THING A READER MUST NOT UNDO: THERE IS NO HOME PARAMETER AND NO
 * ACCOUNT PARAMETER, and adding one would put the question this route exists to
 * remove back in the browser. The home comes off the REQUEST, server-side, so a
 * screen cannot price one home's card against another home's request. The route
 * writes nothing (`test/core/portal-home-read-census.spec.ts` text-scans its
 * body to keep that true), which is the whole reason it is allowed to read a
 * home's account key at all: an offer is not an attribution.
 *
 * ⛔ SIX KINDS, ALL ON A 200, AND THE CALLER MUST BRANCH ON `kind` RATHER THAN
 * ON THE NUMBER. `unpriced` (on the home's list, no figure agreed) and `missing`
 * (not on their list at all) are different sentences to an operator, and ZERO is
 * a real agreed price - a destination a home is carried to at no charge. A
 * falsy check on `oneWayCents` reports that as "no price" and invents a charge
 * nobody agreed, which is verbatim the harm `account-prices-repo.ts` names.
 *
 * ⛔⛔ `totalCents` IS THE SERVER'S ARITHMETIC AND THE CLIENT NEVER REDOES IT
 * (S9). The captured figure is a ONE WAY price and the return multiplier is
 * sheet-scope, so nothing in `account_prices` records it; a second copy of the
 * `× 2` in a browser is a second place for it to be forgotten, on a figure that
 * would then undercharge a nursing home by half, silently, on every return run.
 *
 * ⛔ IT THROWS ON A FAILED READ rather than degrading to "no price", the same
 * posture `resolveCapturedPrice` states: "could not check" and "there is no
 * agreed price" are different sentences on a money field, and the panel has its
 * own `failed` state for the first one.
 */
export type AgreedPrice =
  | {
      kind: 'priced';
      oneWayCents: number;
      returnTrip: boolean;
      totalCents: number;
      homeName: string;
      destinationName: string;
    }
  | { kind: 'unpriced'; homeName: string; destinationName: string }
  | { kind: 'missing'; homeName: string; destinationName: string }
  | { kind: 'unlinked'; homeName: string }
  | { kind: 'no-destination'; homeName: string }
  | { kind: 'unknown-home' };

export async function agreedPriceFor(requestId: number): Promise<AgreedPrice> {
  return getJson<AgreedPrice>(`/api/portal/requests/${String(requestId)}/agreed-price`);
}

// ---------------------------------------------------------------------------
// T788 M4 — the nurse access review
// ---------------------------------------------------------------------------

export type PortalAccessNurse = {
  /** ⛔ The row's own key, or `null` when the portal did not state it (T939 M5).
   *  The revoke/restore control addresses by this id, so a `null` row renders
   *  WITHOUT that control rather than disappearing — see the note on the
   *  projection, and the recorded kiosk-first deploy order that makes the null
   *  case reachable. */
  nurseId: number | null;
  displayName: string;
  /** The stable per-nurse key for DISPLAY and the exported sheet. ⚠️ Not the
   *  key the write routes address by — that is `nurseId` above. */
  email: string;
  hasPin: boolean;
  lastActiveAt: string | null;
  deviceCount: number;
  dormant: boolean;
  /** ⛔ Whether she can still sign in, or `null` when the portal did not state
   *  it. The report used to omit deactivated nurses entirely — the one
   *  population an access review exists to show. `null` is not a third access
   *  state: it means the seam does not know, and the screen must say so rather
   *  than assert either value. See portal-ops-projection.ts. */
  active: boolean | null;
};

export type PortalAccessReview = {
  generatedAt: string;
  dormantAfterDays: number;
  /**
   * ⚠️ STRUCTURALLY DUPLICATED from `core/portal-ops-projection.ts`'s
   * `PortalAccessReviewHome`, and deliberately so — this file never imports from
   * `../../core/`, and `PortalAccessNurse` above carries the same cross-reference
   * for the same reason. ⛔ NOTHING COUPLES THE TWO: a field added to the
   * projection and not here is silently dropped at this boundary, which is
   * exactly how an earlier version of this work named the wrong functions.
   *
   * ⛔ T1146 M2 — `homeId` is the JOIN KEY, `number | null`. `null` means the
   * portal predates the field (kiosk deploys FIRST), and the screen must then
   * fall back to the name key and REFUSE to attribute twin-named homes. It is a
   * join key and must NEVER be rendered — the feedback widget captures the DOM
   * verbatim into a JPEG.
   */
  homes: Array<{ homeId: number | null; homeName: string; nurses: PortalAccessNurse[] }>;
};

export async function listPortalAccessReview(): Promise<PortalAccessReview> {
  // getJson, not getOr: an empty roster is a MEANING (nobody has access), and
  // Helen must not read a broken seam as that.
  const body = await getJson<{ report: PortalAccessReview }>('/api/portal/access-review');
  return body.report;
}

// ---------------------------------------------------------------------------
// T939 M5 — the nursing front door: the WRITES, and the two reads they need
// ---------------------------------------------------------------------------

/**
 * ⛔ THESE TYPES ARE THE SERVER'S PROJECTION, NOT THE PORTAL'S ROW, and they
 * are snake_case because that is the shape `core/portal-ops-projection.ts`
 * emits — renaming them here would put two vocabularies for one row in the
 * client and invite a mapper nobody asked for.
 *
 * ⛔ DO NOT WIDEN THEM to make a field render (board 970). The kiosk feedback
 * widget photographs rendered DOM verbatim into a JPEG, so a field that reaches
 * these types can reach a capture that can leave the building. Widening is a
 * server-side decision with a server-side spec.
 * ⚠️ CORRECTED 2026-08-14 (T1076, board 900): "into `feedback_submissions` / a
 * kiosk table" was false; the kiosk sink is a LOCAL DOWNLOAD. See the
 * PortalNurseRow header above. The prohibition is unchanged.
 */
export type PortalHome = {
  home_id: number;
  name: string;
  address_line: string;
  town: string;
  eircode: string;
  active: boolean;
  /** The kiosk account this home bills to, or null while it is unlinked. This
   *  is the join key the accounts spine ruled (P0-9) and the whole reason the
   *  "Not in the portal yet" region can be computed at all. */
  account_id: number | null;
  /** T1868 M4/M5 — minutes before the appointment the nurse's form prefills the
   *  pickup at. `NOT NULL DEFAULT 60` in the schema, so a home the portal has
   *  reported always carries one; nullable HERE only because this type also
   *  describes a payload from a portal deployed before v12. */
  pickup_offset_minutes?: number | null;
  /** T1868 M4/M6 — the number Helen rings about a live job, once next of kin is
   *  gone from the request. An ORGANISATION's number, stored as typed. */
  fallback_phone?: string | null;
};

export type PortalNurse = {
  nurse_id: number;
  home_id: number;
  email: string;
  display_name: string;
  active: boolean;
};

export type PortalNurseEvent = {
  event_id: number;
  nurse_id: number;
  nurse_email: string;
  event: string;
  actor: string;
  created_at: string | null;
};

/**
 * Friendly copy for the FRONT-DOOR routes, keyed on status like every other
 * failure map here (SEC-5: no server body reaches the screen).
 *
 * ⛔ IT IS A SEPARATE MAP FROM `portalFailureCopy` ABOVE, AND THAT IS THE POINT.
 * The kiosk server funnels all nine seam ops through one `portalFailure`, whose
 * 404/409 sentences were written when it served three ops that were all about a
 * REQUEST. Reused here, a duplicate nurse email — the portal's own 409 — would
 * read to Helen as "that request has already moved on": copy about a different
 * object entirely, on the one refusal INITIAL.md names as a success criterion
 * ("a nurse who works at two homes is refused with a clear message rather than
 * silently mis-filed"). Recorded as a low advisory when M4 shipped; M5 is the
 * milestone that makes those refusals reachable by a human, so it is paid here.
 *
 * ⛔ THE 409 NAMES NO HOME, deliberately (R6). The portal's own refusal is
 * "That address is already on a list" precisely so this form cannot become an
 * oracle for which staff work where, over Article 9 subjects. Do not "improve"
 * this sentence by adding the home.
 */
function frontDoorFailureCopy(e: unknown, verb: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 503) {
    return 'The portal link is not set up on this server. Nothing you did caused this - tell Alex.';
  }
  if (status === 502) return 'The portal did not answer. Try again in a moment.';
  if (status === 409) {
    return 'That is already taken: the email address is on a list already, or the account is already linked to another home. A nurse can only be at one home.';
  }
  // ⚠️ NAMES NO NOUN, deliberately. This map serves four ops and a 404 can mean
  // the HOME, the ACCOUNT or the NURSE is gone depending on which. Naming one of
  // them would be wrong on the other two, and every 404 has the same remedy.
  if (status === 404) return 'Something on this screen is out of date. Reload it and try again.';
  // ⚠️ The home arm is real, not decorative: the portal answers 400 (not 404)
  // when a well-formed homeId is not on the portal, because what was not found
  // is a FIELD VALUE the caller supplied rather than the addressed resource.
  if (status === 400) {
    return 'Check the name and the email address. If the home was only just changed, reload the screen.';
  }
  return `Could not ${verb}. Try again in a moment.`;
}

export async function listPortalHomes(): Promise<PortalHome[]> {
  // getJson, not getOr — same reason as the review above: "no homes yet" is a
  // MEANING (the bootstrap case, which this screen exists to resolve) and Helen
  // must not read a broken seam as that.
  const body = await getJson<{ homes: PortalHome[] }>('/api/portal/homes');
  return body.homes;
}

export async function listPortalNurseEvents(): Promise<PortalNurseEvent[]> {
  const body = await getJson<{ events: PortalNurseEvent[] }>('/api/portal/nurse-events');
  return body.events;
}

/** ⭐ T2075 M2 (plan D6) — the account's sign-in type and, for a group
 *  sign-in, its group name. The kiosk route allow-lists the role and requires
 *  the name iff `group`; the screen refuses a blank name before it gets here. */
export type PortalNurseRole = 'group' | 'reception' | 'admin';

export async function createPortalNurse(
  homeId: number,
  email: string,
  displayName: string,
  role: PortalNurseRole,
  groupLabel: string | null,
): Promise<AdminResult<PortalNurse | null>> {
  try {
    const body = await postJson<{ nurse: PortalNurse | null }>('/api/portal/nurses', {
      homeId,
      email,
      displayName,
      role,
      groupLabel,
    });
    return { ok: true, value: body.nurse };
  } catch (e: unknown) {
    return { ok: false, message: frontDoorFailureCopy(e, 'add that nurse') };
  }
}

export async function setPortalNurseActive(
  nurseId: number,
  active: boolean,
): Promise<AdminResult<PortalNurse | null>> {
  try {
    const body = await postJson<{ nurse: PortalNurse | null }>(
      `/api/portal/nurses/${String(nurseId)}/active`,
      { active },
    );
    return { ok: true, value: body.nurse };
  } catch (e: unknown) {
    return {
      ok: false,
      message: frontDoorFailureCopy(e, active ? 'restore that access' : 'remove that access'),
    };
  }
}

/**
 * Bring an existing kiosk account into the portal as a home (P0-9).
 *
 * ⛔ ONLY THE ACCOUNT ID IS SENT. The home's name and address are read from the
 * account row SERVER-SIDE (`handlePortalCreateHome`) rather than accepted from
 * this browser, so a caller cannot create a portal home labelled anything at
 * all against a real account id. Sending them from here would defeat that.
 */
export async function createPortalHome(accountId: number): Promise<AdminResult<PortalHome | null>> {
  try {
    const body = await postJson<{ home: PortalHome | null }>('/api/portal/homes', { accountId });
    return { ok: true, value: body.home };
  } catch (e: unknown) {
    // ⛔ 400 HERE IS ALWAYS ABOUT THE ACCOUNT'S OWN NAME AND ADDRESS, and it is
    // not theoretical — but it has TWO causes, not one, and an earlier version
    // of this arm named only the first and was WRONG for the second.
    //
    //   MISSING — every address column on `accounts` is nullable, and
    //     `handlePortalCreateHome` refuses before crossing the seam when
    //     addressLine / town / eircode is absent. Real: the seeded demo accounts
    //     are the data this runs against.
    //   TOO LONG — the kiosk caps an account ADDRESS field at
    //     MAX_ACCOUNT_FIELD = 200 (accounts-repo.ts) while the portal caps a
    //     home field at HOME_FIELD_MAX_LEN = 120 (request-routes.ts), and the
    //     kiosk's pre-check tests emptiness ONLY. So a 121-200 character
    //     addressLine or town is valid on the kiosk, passes the pre-check, and
    //     is refused upstream.
    //     ⚠️ The NAME is not part of this: MAX_ACCOUNT_NAME is also 120, so it
    //     can never exceed the portal's cap through a normal writer — even
    //     though the pre-check does not look at it. Stated because the obvious
    //     reading of "the pre-check skips name" is that name is a second
    //     reachable cause, and it is not.
    //
    // Telling Helen to "add the address" in that second case is false AND
    // unactionable — the address is there; it is too long. The copy therefore
    // names the field set and both failure directions, which is true of every
    // 400 this route can return. (`Choose the account` is the one 400 that
    // cannot occur: the id comes from a list this screen just read.)
    //
    // ⚠️ The two limits are NOT reconciled here. Doing that means either
    // duplicating the portal's constant in the kiosk or tightening the account
    // validator, and both are decisions about the accounts spine rather than
    // about this screen. Recorded as a deferred item.
    const status = e instanceof ApiError ? e.status : 0;
    if (status === 400) {
      return {
        ok: false,
        message: 'The portal will not take that account: its name or address is either missing or too long. Fix them on the account first, then bring it in.',
      };
    }
    return { ok: false, message: frontDoorFailureCopy(e, 'bring that home in') };
  }
}

/**
 * Set or clear a home's account link — the correction path for a mis-link.
 *
 * ⚠️ `null` MEANS UNLINK and is a real act, not an absent field: roles.sql
 * grants no DELETE on `portal.homes`, so without this a wrong pick would be
 * permanent and the correct home could never take that account.
 */
export async function linkPortalHome(
  homeId: number,
  accountId: number | null,
): Promise<AdminResult<PortalHome | null>> {
  try {
    const body = await postJson<{ home: PortalHome | null }>(
      `/api/portal/homes/${String(homeId)}/link`,
      { accountId },
    );
    return { ok: true, value: body.home };
  } catch (e: unknown) {
    return {
      ok: false,
      message: frontDoorFailureCopy(e, accountId === null ? 'unlink that home' : 'link that home'),
    };
  }
}

/**
 * T1868 M4 — save a home's per-home settings: the pickup offset the nurse's
 * form prefills from, and the fallback phone Helen rings about a live job.
 *
 * ⚠️ Both fields are OPTIONAL and `null` CLEARS the phone, so the caller's keys
 * are forwarded as given — building a full object here with defaults would turn
 * "leave the offset alone" into "set the offset to whatever I defaulted", which
 * is a silent write to a setting nobody touched.
 */
export async function savePortalHomeSettings(
  homeId: number,
  settings: { pickupOffsetMinutes?: number; fallbackPhone?: string | null },
): Promise<AdminResult<PortalHome | null>> {
  try {
    const body = await postJson<{ home: PortalHome | null }>(
      `/api/portal/homes/${String(homeId)}/settings`,
      settings,
    );
    return { ok: true, value: body.home };
  } catch (e: unknown) {
    // ⛔⛔ THE SERVER'S OWN SENTENCE WINS AT 400, and this is the documented
    // exception `ApiError.body`'s header asks for rather than the idiom.
    // THE ARGUMENT: `frontDoorFailureCopy`'s 400 arm answers "Check the name and
    // the email address" — copy written for the nurse and account-link flows,
    // naming two fields this settings block does not have. MEASURED on the live
    // seat: an offset of 0 and an offset of 999 both rendered that sentence,
    // identically, so Helen learned neither what was wrong nor that the offset
    // has a range at all. The portal composes a sentence that names the field
    // and its bound, and the kiosk now forwards it; rendering it is strictly
    // more useful than a map that cannot know which field failed.
    // ⚠️ TRUSTED COPY, NOT USER INPUT: the portal builds these from its own
    // constants and never echoes the caller's value. Every other status still
    // takes the friendly-copy map, which is right for them.
    if (e instanceof ApiError && e.status === 400) {
      const named = (e.body as { error?: unknown } | null)?.error;
      if (typeof named === 'string' && named !== '') return { ok: false, message: named };
    }
    return { ok: false, message: frontDoorFailureCopy(e, "save that home's settings") };
  }
}

// ---------------------------------------------------------------------------
// ⛔ T853 M3's `fetchAccountBillables` (GET /api/account-billables, the kiosk
// statement's server truth) and its wire types WERE HERE and are deleted with
// the statement and the route (board 2764 M6, R-12). The month's money is the
// Invoices tab's roll-up and the ledger's frozen lines; the document is Stripe's.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// T1011 M8 — THE INVOICES TAB: the roll-up, the top-up, and the per-series stop
// ---------------------------------------------------------------------------

/** One payer with billable runs waiting to be assembled into a document. */
export type ApiAssemblyRow = {
  /** An OPAQUE index, minted by the handler. ⛔ NOT the roll-up key: that embeds
   *  the parent's `billing_parties` row id, which nothing on a screen needs. */
  payerId: string;
  payerName: string;
  payerSource: 'billing-party' | 'account';
  anchorAccountId: number;
  /** Sites contributing runs. More than one means a parent pays centrally. */
  siteCount: number;
  runCount: number;
  /** Cents across the PRICED runs only. */
  cents: number;
  /** ⛔ NEVER folded into `cents` as zero — see billables-repo's `priceState`. */
  unpricedCount: number;
  firstRun: string;
  lastRun: string;
  /** ANY live invoice for the month, of any kind. ⚠️ board 2764 M7: after a void
   *  of one kind of the pair this is still true while that kind is issuable, so
   *  the card offers Issue from `kindsToIssue`, never from this. */
  alreadyInvoiced: boolean;
  /** board 2764 M4 (R-8 data) - what each invoice of the pair holds. */
  kinds: Record<ApiInvoiceKind, ApiKindRollUp>;
  /** Runs with no payer kind. Issuing refuses while this is > 0 and names them
   *  (R-5); the card says how many. */
  noPayerKindCount: number;
  /**
   * ⭐ board 2764 M7 - THE SERVER'S ANSWER to "which invoices would Issue create
   * now", from the issue path's own `kindsToIssue` over the known kinds (RULED
   * D3's shapes minus the live kinds). ⛔ Never re-derived here: the card offers
   * Issue and counts its confirm from this, so the screen and the issue cannot
   * disagree about what a press makes.
   */
  kindsToIssue: ApiInvoiceKind[];
  /**
   * ⭐ board 2764 RW1 (R1) - false when the payer has no billing email, which the
   * issue refuses before any number is taken (`no_billing_email`). The card then
   * says so and offers no Issue. A BOOLEAN; the address never crosses the wire.
   * ⚠️ Read as `=== false`: a server that predates the field omits it, and the
   * route's own refusal still stands behind the button.
   */
  hasBillingEmail?: boolean;
  /**
   * ⭐ board 2764 RW1 (R3) - the month's runs that no live invoice carries, each
   * with its payer kind, for the card's correction control. Absent from a server
   * that predates it (read as none).
   */
  uninvoicedRuns?: ApiUninvoicedRun[];
  /**
   * ⭐ board 3059 - PRESENT ONLY FOR A PAYER WHOSE RESIDENT-NAMES SWITCH IS ON.
   * `'on'`: each run carries the `residentLabel` its invoice line will print.
   * `'unavailable'`: the portal could not be read, so no run carries one and the
   * card offers no Issue (the route would refuse it, `resident_names_unavailable`).
   * ABSENT: the switch is off, and the invoice names nobody.
   */
  residentNames?: 'on' | 'unavailable';
};

/** board 3059 - why a run's line reads "resident not recorded" (the server's
 *  `ResidentNotRecordedReason`, mirrored by hand: that module pulls in `pg`). */
export type ApiResidentNotRecordedReason = 'no-portal-request' | 'account-changed' | 'not-found' | 'ref-changed';

/** board 3059 - what an ON payer's invoice line will say about who travelled. */
export type ApiResidentLabel =
  | { kind: 'named'; name: string }
  | { kind: 'not-recorded'; reason: ApiResidentNotRecordedReason };

/** One run of a payer's month that no live invoice carries (board 2764 RW1, R3).
 *  The R-5 refusal's allowlist plus the run's kind (`null` = not set yet). */
export type ApiUninvoicedRun = {
  bookingId: number;
  jobRef: string | null;
  journeyDate: string;
  payerKind: ApiInvoiceKind | null;
  /** ⭐ board 3059 - only on a run of a payer whose `residentNames` is `'on'`. */
  residentLabel?: ApiResidentLabel;
};

/** board 2764 - the two invoices of a nursing home's month (`account_invoices.payer_kind`). */
export type ApiInvoiceKind = 'hse' | 'house';

/** board 2764 M4 - one kind of one payer's month, as the roll-up spells it. */
export type ApiKindRollUp = {
  runCount: number;
  /** Cents across the PRICED runs of this kind only. */
  cents: number;
  unpricedCount: number;
  /** A live invoice covers this kind (its own, or a pre-v44 combined one). */
  live: boolean;
  /** Runs of this kind on NO live invoice although the month has one (L4). */
  lateCount: number;
};

export type ApiPayerInvoice = {
  accountInvoiceId: number;
  invoiceNumber: string;
  year: number;
  /** 1-12, not a 0-based index. */
  month: number;
  status: 'issued' | 'paid' | 'void';
  cents: number;
  lineCount: number;
  /**
   * ⭐ T1447 M6 (v37) — WHO WITHDREW IT AND WHEN, or NULL twice.
   * ⛔ NULL IS NOT "UNKNOWN OPERATOR": never voided, or voided before v37 added
   * the pair. The screen renders NOTHING for a null rather than "withdrawn by
   * nobody". They exist here because an audit column nothing displays is
   * coverage rather than a control — this type IS the reader.
   */
  voidedBy: string | null;
  voidedAt: string | null;
  /**
   * ⭐ T1447 M6 — does STRIPE hold a copy of this document?
   * ⛔ A BOOLEAN, and the server sends no Stripe id at all: the house rule on
   * every invoice response is "never the Stripe object it happened to". What
   * the screen needs is the FACT, because an invoice sent through Stripe carries
   * `auto_advance` + `collection_method=send_invoice`, so recording a BANK
   * payment against it leaves a live payable page and live dunning — which the
   * mark-paid confirm has to say in words.
   */
  atStripe: boolean;
  /**
   * board 2764 M4 (v44), frozen at issue: which invoice of the pair this is, or
   * `'combined'` for a pre-v44 row covering every kind.
   * ⛔ THE ROW'S OWN KIND IS ALL THE SCREEN NEEDS TO SHOW THE PAIR. The frozen
   * companion number is deliberately NOT on this wire: after a void and reissue
   * the surviving invoice's companion still names the VOIDED number (v44's
   * freeze), so the pair is read from the live rows, never from a companion.
   */
  payerKind: ApiInvoiceKind | 'combined';
  /** An HSE invoice for a month with no HSE runs: zero lines, total 0, nothing due. */
  isNil: boolean;
};

export type ApiOutstandingRow = {
  payerId: string;
  payerName: string;
  /** Cents on `issued` documents only. */
  outstandingCents: number;
  invoices: ApiPayerInvoice[];
};

/** A live contract that is billing less than its own pattern implies. ⛔ Carries
 *  NO corrected figure: expanding a pattern into money is the arithmetic that
 *  produced the invoice this whole build exists to make impossible. */
export type ApiSeriesShortfall = {
  seriesId: number;
  accountId: number;
  /**
   * ⛔ HAND-MIRRORED FROM `SeriesShortfall['reason']`, AND IT SILENTLY DIVERGED
   * ONCE (T1719 M4d, review batch D): M4b added `reference-unmintable` to the
   * server union and this copy was not widened, so a reason the server can send
   * was not a reason this type admits — with nothing watching. There is now a
   * PARITY PIN over both unions in `test/core/job-ref-parity.spec.ts`; add a
   * member here in the same change as the server, or it reds.
   */
  reason: 'duplicate-pattern' | 'unrecognised-schedule' | 'off-pattern-anchor'
    | 'no-dated-anchor' | 'reference-unmintable' | 'cash-anchor' | 'missing-runs';
  detail: string;
  expected: number;
  actual: number;
};

export type ApiBillingRollUp = {
  year: number;
  /** 1-12. */
  month: number;
  computedOn: string;
  assembly: { rows: ApiAssemblyRow[]; truncated: boolean };
  outstanding: { rows: ApiOutstandingRow[]; truncated: boolean };
  shortfalls: ApiSeriesShortfall[];
};

/**
 * The whole billing picture, at the grain the ledger bills at.
 *
 * ⛔ `getJson`, NEVER `getOr`. `getOr` swallows every failure into a fallback, and
 * the fallback here would be an empty outstanding table — which reads as "nothing
 * is owed" on the one screen built to answer the opposite question. A failed read
 * and a settled book must stay distinguishable, so this throws and the caller
 * renders an explicit failure.
 *
 * ⛔⛔ THE MONTH IS OPTIONAL, AND `undefined` IS NOT NEUTRAL: it makes the server
 * answer for ITS OWN CURRENT month, which `issueAccountInvoice` then refuses for
 * being unfinished (`PeriodNotEndedError`). This used to be the ONLY call shape,
 * so the queue could only ever show a month nothing could be invoiced for and the
 * issue control was disabled in every state production can reach - true of the
 * function, false of the system, one layer up from the missing button itself.
 *
 * ⛔ AND THE SERVER STILL OWNS THE CLOCK. `month` is what the handler's own
 * comment provides for ("accepted so an operator can look back, and the RESOLVED
 * window travels home"), NOT a licence to compute a month here: the caller
 * derives the key from a PREVIOUS RESPONSE's resolved `{year, month}`, never from
 * `new Date()`, so the same button on two machines in different offsets cannot
 * assemble two different months.
 *
 * @param month 'YYYY-MM' exactly, which is the only form the route parses; it
 *   answers 400 on anything else and never echoes the value back.
 */
export async function fetchBillingRollUp(month?: string, accountId?: number): Promise<ApiBillingRollUp> {
  const params = new URLSearchParams();
  if (month !== undefined) params.set('month', month);
  // ⭐ board 2812 M4: one home's Invoices tab, account-keyed (Q240).
  if (accountId !== undefined) params.set('account', String(accountId));
  const qs = params.toString();
  return getJson<ApiBillingRollUp>(`/api/account-billing-rollup${qs === '' ? '' : `?${qs}`}`);
}

export type ApiGenerationReport = {
  from: string;
  untilExclusive: string;
  seriesConsidered: number;
  created: number;
  withdrawn: number;
  skipped: number;
  /**
   * ⭐⭐ T1719 M4c — THE CONTRACTS THAT COULD NOT BE GIVEN A JOB REFERENCE,
   * ADDED BECAUSE THE SERVER WAS ALREADY SENDING IT AND NOTHING READ IT.
   *
   * ⛔ THAT IS THIS REPO'S NAMED ANTIPATTERN — a durable signal with no reader —
   * and here it had a MEASURED cost rather than a theoretical one: on the D1
   * fixture the route answers `created 0, withdrawn 0, skipped 1`, and the notice
   * this type feeds read "The diary was already up to date. Nothing new was
   * added" for a run that had skipped a home. Before M4b that case was a loud
   * 500; adding the per-plan catch without this reader turned it into a QUIET
   * LIE, which is strictly worse.
   *
   * ⛔ IDS ONLY, and the screen must keep it that way: an account short code
   * IDENTIFIES THE HOME (v40's column comment). The name the operator needs is
   * already on the shortfall card, which resolves it from the account list — so
   * the notice points there rather than carrying a second copy.
   * ⚠️ READ DEFENSIVELY AT THE CALL SITE (`?? []`). `postJson` casts the body
   * unchecked, so a response from a server that predates this field would make
   * `.length` throw inside a click handler.
   * ⛔ OPTIONAL SINCE T1719 M5 STEP 0(d): an older server omits the key entirely,
   * and the reader's `?? []` IS the contract — a required member said the body
   * always carries it, which is the one thing the `??` exists because it does not.
   */
  unmintable?: { seriesId: number; accountId: number | null }[];
};

/**
 * Materialise every missing run inside the horizon — the TOP-UP.
 *
 * ⚠️ IT IS NOT WHAT CLOSES THE UNDER-BILL, and the copy this feeds must not claim
 * it is. Generation floors at today in Dublin, so this can never fill in a month
 * that has already gone; capture is the trigger that runs ahead of the period.
 * This is the lever for the current and future months.
 */
export async function topUpOccurrences(): Promise<AdminResult<ApiGenerationReport>> {
  try {
    return { ok: true, value: await postJson<ApiGenerationReport>('/api/occurrence-generation', {}) };
  } catch (e: unknown) {
    return { ok: false, message: frontDoorFailureCopy(e, 'bring the diary up to date') };
  }
}

/**
 * Stop (or restart) a recurring contract — the writer `booking_series.active`
 * shipped without.
 */
export async function setBookingSeriesActive(
  seriesId: number,
  active: boolean,
): Promise<AdminResult<{ seriesId: number; active: boolean }>> {
  try {
    return {
      ok: true,
      value: await postJson<{ seriesId: number; active: boolean }>(
        `/api/booking-series/${String(seriesId)}/active`,
        { active },
      ),
    };
  } catch (e: unknown) {
    return {
      ok: false,
      message: frontDoorFailureCopy(e, active ? 'restart that contract' : 'stop that contract'),
    };
  }
}

/**
 * ⭐ board 2764 M6 (R-12, RULED D4) - STRIPE'S PDF OF ONE OF OUR ACCOUNT INVOICES,
 * through this kiosk's own route. It REPLACES the "N runs" disclosure
 * (`fetchInvoiceRuns`, an ECMB table of the frozen lines), which was the kiosk's
 * own rendering of an invoice; the document a home receives is Stripe's.
 * ⛔ ALWAYS THIS SERVER'S PATH, keyed by OUR row id, never a Stripe URL: the
 * hosted page and `invoice_pdf` are bearer URLs, and the route streams the bytes
 * (`handleGetAccountInvoicePdf`, server/app.ts). `GET /api/account-invoices/:id`
 * stays as a server-side ledger read with no client reader.
 */
export function accountInvoicePdfHref(accountInvoiceId: number): string {
  return `/api/account-invoices/${String(accountInvoiceId)}/pdf`;
}

/** The pane's words for the route's 409 `not_ready`: a non-nil invoice stays a
 *  Stripe DRAFT until its `automatically_finalizes_at`, which the send sets
 *  `ACCOUNT_INVOICE_FINALIZE_DELAY_MS` (one hour, core/account-invoice.ts) ahead,
 *  and a draft has no PDF yet (M5 probe P6). A state to wait out, not a failure. */
export const ACCOUNT_INVOICE_PDF_NOT_READY_COPY =
  'Stripe is still preparing this invoice; try again in an hour.';
/** Every other refusal or a dropped connection. ⛔ Keyed off the CODE, never the
 *  server's sentence (SEC-5, lib/api.ts). */
export const ACCOUNT_INVOICE_PDF_FAILED_COPY =
  'Stripe\'s invoice could not be fetched just now. Nothing has changed; try again in a moment.';
/**
 * A 401: the session ended. ⭐ board 2764 M7 - ONE sentence for it, shared with
 * `pushFailureCopy`, which already said exactly this; the PDF press used to fold
 * a signed-out answer into "try again in a moment", which no amount of waiting
 * clears (M6 security-review, informational).
 */
export const SIGNED_OUT_COPY = 'You have been signed out. Sign in again, then try once more.';

/**
 * Fetch the PDF and hand it to the browser as a download named by OUR number, or
 * say why not. ⛔ It FETCHES rather than letting the link navigate, because the
 * route answers 409 `not_ready` for the first hour after a send, and a bare
 * navigation would show Helen a JSON error body instead of the sentence above
 * (L9). The download is the `createObjectURL -> a.download` idiom
 * `NursingHomes.tsx`'s roster sheet already uses; the blob URL never leaves this
 * function.
 */
export async function downloadAccountInvoicePdf(
  accountInvoiceId: number,
  invoiceNumber: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  let pdf: Blob;
  try {
    pdf = await getBlob(accountInvoicePdfHref(accountInvoiceId));
  } catch (e: unknown) {
    if (e instanceof ApiError && e.status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    return {
      ok: false,
      message: code === 'not_ready' ? ACCOUNT_INVOICE_PDF_NOT_READY_COPY : ACCOUNT_INVOICE_PDF_FAILED_COPY,
    };
  }
  const url = URL.createObjectURL(pdf);
  const a = document.createElement('a');
  a.href = url;
  a.download = `invoice-${invoiceNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { ok: true };
}

// ---------------------------------------------------------------------------
// T1011 — ISSUING, FROM THE SCREEN
//
// ⛔ THE ROUTE AND THE SERVICE SHIPPED IN M3 AND M4 WITH NO CLIENT CALLER AT
// ALL. `POST /api/account-invoices` was reachable only with curl, so the
// milestone's core deliverable had no reachable UI while 5000-odd tests passed
// against the service and the route directly. This is the wiring, and nothing
// below adds capability the route did not already have.
// ---------------------------------------------------------------------------

/**
 * What ONE issued document reports back: one entry of the issue route's
 * `invoices` (board 2764 M4; M7 moved the client onto it and the route dropped
 * the old top-level copy of the FIRST invoice, which for a house-only month was
 * the nil HSE one and read "EUR 0 across 0 runs").
 *
 * ⛔ TWO OF THESE ARE OUTCOME FIELDS ON A 200, NOT ERRORS, and a screen that
 * renders only the number reports one field of a multi-field outcome. The
 * handler deliberately never 5xxs over a document that WAS issued: the number is
 * spent, the lines are frozen and the runs are marked invoiced the moment
 * `issueAccountInvoice` returns, so Stripe's outcome rides out as
 * `sentToStripe`. An invoice can issue AND fail to send, and "issued but not
 * sent" is a real, actionable state.
 */
export type ApiIssuedInvoice = {
  accountInvoiceId: number;
  invoiceNumber: string;
  payerKind: ApiInvoiceKind;
  /** The HSE invoice of a month with no HSE runs: zero lines, total 0. */
  isNil: boolean;
  lineCount: number;
  totalCents: number;
  /** false = the document exists and nobody has been asked for the money yet. */
  sentToStripe: boolean;
  /** false = it went out quoting a DIFFERENT reference, so an operator chasing a
   *  bank transfer cannot expect to see `invoiceNumber` on it. */
  ourNumberIsTheReference: boolean;
};

/** What ONE issue reports back: every invoice it created, in number order (the
 *  pair HSE first, or the one kind still missing), plus the two properties of
 *  the month that its own lines cannot show. */
export type ApiIssuedMonth = {
  year: number;
  /** 1-12, like every other period on this wire. */
  month: number;
  invoices: ApiIssuedInvoice[];
  linesTruncated: boolean;
  hadUndatedRuns: boolean;
};

/**
 * ⛔ A HAND-WRITTEN MIRROR of `AccountInvoiceIssueRefusalCode` in `server/app.ts`
 * (that module pulls in `pg`). `test/server/account-invoice-routes.spec.ts`
 * asserts the two unions are the SAME TYPE, so either side gaining or losing a
 * member fails `tsc`.
 */
export type AccountInvoiceIssueRefusalCode = 'payer_kind_missing' | 'payer_kind_changed' | 'not_nursing_payer' | 'no_billing_email'
  // ⭐ board 2814 M6 fix round 1 (F1): a run's price changed while the issue was assembled.
  | 'run_price_changed'
  // ⭐ board 3059: the resident-names switch changed since the screen loaded (D3),
  // or it is on and the names could not be read (D4). Nothing was issued.
  | 'resident_names_changed'
  | 'resident_names_unavailable';

/** One run the R-5 refusal names: the booking to set, and what Helen reads. */
export type ApiRunWithoutPayer = { bookingId: number; jobRef: string | null; journeyDate: string };

/**
 * ⭐ board 2764 RW1 (R1) - the `no_billing_email` refusal's words. ⛔ It names the
 * field Helen sets, by the account form's own label ("Billing email"), and says
 * nothing was invoiced: the server refuses before any number is taken. The card
 * says the same thing BEFORE a press (`AccountInvoices.tsx`), so this is only
 * seen when the card was out of date.
 */
export const NO_BILLING_EMAIL_ISSUE_COPY =
  'Nothing was invoiced. This payer has no billing email, so the invoices could not be sent. Set the Billing email on this home\'s account first, then issue again.';

/** board 2764 M7 - the R-5 refusal's words, keyed on its code. */
export const PAYER_KIND_MISSING_COPY =
  'These runs have no payer set. Choose who pays for each, then issue again.';

/**
 * ⭐ board 2764 M7 (R-5 UI) - THE RUNS A `payer_kind_missing` REFUSAL NAMES, read
 * from its own JSON body, or `null` for any other answer.
 *
 * ⛔ SEC-5, ARGUED HERE AS `ApiError`'s HEADER ASKS. The server's SENTENCE is
 * never rendered. What is read is the closed structured allowlist the route
 * builds for this code alone (`handlePostAccountInvoice`: booking id, job ref and
 * civil date, capped at 50, plus the true total), and each field is SHAPE-CHECKED
 * before it can reach the screen: a positive whole booking id, a job ref string
 * or null, a `YYYY-MM-DD` date. A run that fails any check is dropped rather than
 * rendered, and `total` never reads below the runs actually listed.
 */
function runsWithoutPayer(e: unknown): { runs: ApiRunWithoutPayer[]; total: number } | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const body = e.body as { code?: unknown; runs?: unknown; total?: unknown } | null;
  if (body?.code !== ('payer_kind_missing' satisfies AccountInvoiceIssueRefusalCode)) return null;
  const runs: ApiRunWithoutPayer[] = [];
  for (const r of Array.isArray(body.runs) ? body.runs as unknown[] : []) {
    const run = r as { bookingId?: unknown; jobRef?: unknown; journeyDate?: unknown } | null;
    const bookingId = run?.bookingId;
    const rawRef = run?.jobRef ?? null;
    const jobRef = typeof rawRef === 'string' ? rawRef : rawRef === null ? null : undefined;
    const journeyDate = run?.journeyDate;
    if (typeof bookingId !== 'number' || !Number.isSafeInteger(bookingId) || bookingId <= 0) continue;
    if (jobRef === undefined) continue;
    if (typeof journeyDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(journeyDate)) continue;
    runs.push({ bookingId, jobRef, journeyDate });
  }
  const total = typeof body.total === 'number' && Number.isSafeInteger(body.total) ? body.total : 0;
  return { runs, total: Math.max(total, runs.length) };
}

/**
 * Friendly copy for the ISSUE route, keyed on STATUS and never on the server
 * body (SEC-5) - the rule `adminFailure` and `frontDoorFailureCopy` both state.
 *
 * ⛔ IT IS A SEPARATE MAP FROM BOTH, for the reason `frontDoorFailureCopy`'s own
 * header gives about being reused: its 409 is about a duplicate nurse email and
 * would read here as copy about a different object entirely.
 *
 * ⛔ THE REFUSALS, ENUMERATED FROM `handlePostAccountInvoice` RATHER THAN
 * ASSUMED. Nine named service errors reach a response:
 *   404 - AccountNotFoundError.
 *   409 - PeriodNotEndedError, NothingToInvoiceError, BookingsAlreadyInvoicedError,
 *         InvoicePeriodAlreadyIssuedError, BookingNotPricedError, BookingMovedError,
 *         InvoiceLinesTruncatedError, AccountLeftPayerError; and (board 2764 M4)
 *         the three CODED ones, PayerKindMissingError, PayerKindChangedError and
 *         NotNursingPayerError.
 *   400 - a body the route could not read (`accountId`, or a month that is not
 *         YYYY-MM).
 * Anything else is left to THROW into http.ts's sink and arrives as a 500.
 *
 * ⚠️ EIGHT OF THE NINE SHARE ONE STATUS, SO THIS MAP CANNOT TELL THEM APART AND
 * DOES NOT PRETEND TO. `err()` takes an optional machine-readable `code` and
 * this handler passes none, so the only discriminator on the wire is the
 * SENTENCE - and sniffing server prose from the client is a pin that rots the
 * first time somebody rewords it. So the 409 arm says what is true of all eight
 * (each refusal is raised before the number is allocated, or inside the
 * transaction that rolls it back, so nothing was invoiced) and names the causes
 * as POSSIBILITIES rather than asserting one it cannot observe. Per-refusal
 * remedies need a `code` on the route; that is a server change and is recorded
 * as a finding rather than smuggled in here.
 *
 * ⛔ AND THE DEFAULT ARM CLAIMS NOTHING ABOUT THE OUTCOME. `postJson` rejects on
 * a network failure as well as on a status, and a response that never arrived
 * may sit behind a document that WAS issued. "Nothing was invoiced" is observed
 * on 400/404/409, where the server answered; here it would be a fabrication, so
 * the copy sends the operator to look instead.
 *
 * ⭐ board 2764 M7 - THE PAIR'S THREE REFUSALS DO CARRY A `code`
 * (`AccountInvoiceIssueRefusalCode`), so they are keyed AHEAD of the status arm
 * and each says what to do. The server's sentence is still never rendered.
 */
function issueFailureCopy(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
  if (status === 409) {
    const coded: Record<AccountInvoiceIssueRefusalCode, string> = {
      payer_kind_missing: PAYER_KIND_MISSING_COPY,
      payer_kind_changed: 'Nothing was invoiced. Who pays for one of these runs changed while the invoices were being made. Issue again.',
      // board 2814 M6 fix round 1 (F1): "Change the price" committed during the issue.
      run_price_changed: 'Nothing was invoiced. The price of one of these runs changed while the invoices were being made. Issue again.',
      not_nursing_payer: 'Nothing was invoiced. Set Kind to Nursing home on this home\'s account first.',
      // board 2764 RW1 (R1): refused before any number was taken.
      no_billing_email: NO_BILLING_EMAIL_ISSUE_COPY,
      // board 3059 (D3, D4): both refused before any number was taken.
      resident_names_changed: 'Nothing was invoiced. Whether resident names go on this payer\'s invoices changed since this screen was loaded. Reload the screen and check what is in it, then issue again.',
      resident_names_unavailable: 'Nothing was invoiced. The resident names could not be read just now, so no invoice number was used. Try again in a moment.',
    };
    if (typeof code === 'string' && Object.hasOwn(coded, code)) return coded[code as AccountInvoiceIssueRefusalCode];
    return 'Nothing was invoiced. That month may already have an invoice, it may not have ended yet, or one of its runs may not be priced yet or may have moved to another home. Reload the screen and check.';
  }
  if (status === 404) {
    return 'Nothing was invoiced. Something on this screen is out of date. Reload it and try again.';
  }
  if (status === 400) {
    return 'Nothing was invoiced. The screen asked for something the server could not read. Reload it and try again.';
  }
  return 'Could not tell whether that invoice went out. Reload the screen and check before trying again.';
}

/**
 * Raise ONE payer's invoice for ONE month.
 *
 * ⚠️ `accountId` IS ONE SITE AND THE DOCUMENT IS THE WHOLE PAYER'S. The route
 * resolves the payer from the account, so any contributing site anchors the same
 * document; the roll-up hands the screen `anchorAccountId` for exactly this.
 *
 * ⛔ THE MONTH IS SENT EXPLICITLY, AND IT IS THE ONE THE SCREEN IS SHOWING. An
 * omitted `month` is not neutral: the route defaults to the CURRENT month, which
 * the service then refuses (`PeriodNotEndedError`), so the button would target a
 * period nobody asked for. The value comes from the roll-up's own response, so
 * the server still owns the clock and two machines in different offsets cannot
 * issue two different documents.
 *
 * ⛔ `actor` IS NOT A FIELD. The route attributes the document to the session.
 *
 * ⭐ board 2764 M7 - A `payer_kind_missing` REFUSAL ALSO CARRIES THE RUNS TO SET
 * (`runsWithoutPayer`), so the card can offer the two payer buttons beside each.
 */
export async function issueAccountInvoiceForMonth(
  accountId: number,
  month: string,
  /** ⭐ board 3059 (D3): whether the card the operator pressed showed resident
   *  names (its row's `residentNames`). The route refuses `resident_names_changed`
   *  when the payer's live switch differs, so a press never issues the other kind
   *  of invoice from the one previewed. ⛔ SENT ONLY WHEN TRUE: the route reads an
   *  absent key as "expected off", so an OFF payer's issue body is exactly the
   *  `{accountId, month}` it always was. */
  expectResidentNames: boolean,
): Promise<
  | { ok: true; value: ApiIssuedMonth }
  | { ok: false; message: string; missingPayer: { runs: ApiRunWithoutPayer[]; total: number } | null }
> {
  try {
    return {
      ok: true,
      value: await postJson<ApiIssuedMonth>(
        '/api/account-invoices',
        expectResidentNames ? { accountId, month, expectResidentNames: true } : { accountId, month },
      ),
    };
  } catch (e: unknown) {
    return { ok: false, message: issueFailureCopy(e), missingPayer: runsWithoutPayer(e) };
  }
}

/**
 * ⭐ board 2764 M7 (R-5 UI) - SET WHO PAYS FOR ONE ACCOUNT RUN: the HSE / Rehab
 * (`hse`) or the house account (`house`), through the M3 route
 * (`POST /api/bookings/:id/payer-kind`). The Invoices tab calls it from the R-5
 * refusal, one press per run.
 * ⛔ The body is the one closed token; the actor is the session. Copy is keyed
 * on the route's two coded 409s, never its sentence (SEC-5).
 */
export async function setBookingPayerKind(
  bookingId: number,
  payerKind: ApiInvoiceKind,
): Promise<AdminResult<{ changed: boolean }>> {
  try {
    const res = await postJson<{ changed: boolean }>(`/api/bookings/${String(bookingId)}/payer-kind`, { payerKind });
    return { ok: true, value: { changed: res.changed } };
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    if (status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    if (status === 409 && code === 'invoiced') {
      return { ok: false, message: 'Nothing was changed. That run is already on an invoice, so who pays for it is fixed until that invoice is withdrawn.' };
    }
    if (status === 409 && code === 'not_account_booking') {
      return { ok: false, message: 'Nothing was changed. That run is not billed to an account, so it goes on neither invoice.' };
    }
    if (status === 404) return { ok: false, message: 'Nothing was changed. That run is no longer there. Reload the screen.' };
    return { ok: false, message: 'Could not save who pays for that run. Try again in a moment.' };
  }
}

/** ⭐ board 2814 M6 (DD-8) - "Change the price"'s coded refusals, mirrored by hand
 *  (the server module pulls in `pg`); `test/server/app.spec.ts` pins the mirror. */
export type PriceChangeRefusalCode =
  | 'price_change_amount_invalid'
  | 'price_change_not_account'
  | 'price_change_cancelled'
  | 'price_change_taxi_passed'
  | 'price_change_invoiced';

/** ⛔ The words are keyed on the CODE, never the server's sentence (SEC-5). Each
 *  says nothing was changed, so a refusal never reads as a half success. */
export const PRICE_CHANGE_REFUSAL_COPY: Readonly<Record<PriceChangeRefusalCode, string>> = {
  price_change_amount_invalid: 'That price cannot be recorded. Type it in euro, like 120 or 120.50.',
  price_change_not_account: 'This booking is not billed to a home, so its price is changed with Re-open in calculator. Nothing was changed.',
  price_change_cancelled: 'This run is cancelled, so its price cannot be changed. Nothing was changed.',
  price_change_taxi_passed: 'This run was passed to a taxi, so its price cannot be changed here. Nothing was changed.',
  price_change_invoiced: 'This run is on an account invoice, so its price cannot be changed. Withdraw that invoice on the Invoices tab first. Nothing was changed.',
};

function isPriceChangeRefusalCode(v: unknown): v is PriceChangeRefusalCode {
  return typeof v === 'string' && Object.hasOwn(PRICE_CHANGE_REFUSAL_COPY, v);
}

/**
 * ⭐⭐ board 2814 M6 (DD-8) - CHANGE AN ACCOUNT RUN'S PRICE (`POST
 * /api/bookings/:id/price`), the correction "Re-open in calculator" used to make.
 * `amountCents` is whole cents, the phone-in's wire form; the actor is the session.
 * `unchanged` is a success with no write (the figure was already the run's).
 */
export async function changeBookingPriceRequest(
  bookingId: number,
  amountCents: number,
): Promise<{ ok: true; value: { changed: boolean } } | { ok: false; message: string; code?: PriceChangeRefusalCode }> {
  try {
    const res = await postJson<{ outcome?: unknown }>(`/api/bookings/${String(bookingId)}/price`, { amountCents });
    return { ok: true, value: { changed: res.outcome === 'changed' } };
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    if (status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    if ((status === 400 || status === 409) && isPriceChangeRefusalCode(code)) {
      return { ok: false, message: PRICE_CHANGE_REFUSAL_COPY[code], code };
    }
    if (status === 404) return { ok: false, message: 'Nothing was changed. That run is no longer there. Reload the screen.' };
    // ⛔ NOT "nothing was changed": a lost answer may follow a change that landed.
    // The screen reloads the booking, so the price on it says which.
    return { ok: false, message: 'Something went wrong saving that price. Check the price on the booking, then try again.' };
  }
}

/** ⭐ board 2814 M7 (DD-7) - "Pass to a taxi"'s coded refusals, mirrored by hand
 *  (the server module pulls in `pg`); `test/server/app.spec.ts` pins the mirror. */
export type TaxiPassRefusalCode =
  | 'taxi_note_not_text'
  | 'taxi_note_too_long'
  | 'taxi_cancelled'
  | 'taxi_not_confirmed'
  | 'taxi_travelled'
  | 'taxi_payment_on_record'
  | 'taxi_job_invoiced'
  | 'taxi_account_invoiced'
  | 'taxi_month_issued';

/** The longest note the server takes, as Postgres counts it (`TAXI_NOTE_MAX` in
 *  core/taxi-pass.ts; `test/server/app.spec.ts` pins the two equal). */
export const TAXI_NOTE_MAX_CHARS = 200;

/** ⛔ The words are keyed on the CODE, never the server's sentence (SEC-5). */
export const TAXI_PASS_REFUSAL_COPY: Readonly<Record<TaxiPassRefusalCode, string>> = {
  taxi_note_not_text: 'The note could not be read. Type it again as plain words.',
  taxi_note_too_long: 'The note is too long. Keep it to 200 characters: the taxi firm or its booking number.',
  taxi_cancelled: 'This run is cancelled, so it cannot be passed to a taxi. Nothing was changed.',
  taxi_not_confirmed: 'This run is not confirmed yet, so it cannot be passed to a taxi. Nothing was changed.',
  taxi_travelled: 'This run is recorded as travelled, so it cannot be passed to a taxi. Nothing was changed.',
  taxi_payment_on_record: 'This run has a payment or a payment link on record, so it cannot be passed to a taxi. Nothing was changed.',
  taxi_job_invoiced: 'This run has an invoice or a receipt, so it cannot be passed to a taxi. Nothing was changed.',
  taxi_account_invoiced: 'This run is on an account invoice, so it cannot be passed to a taxi. Withdraw that invoice on the Invoices tab first. Nothing was changed.',
  taxi_month_issued: 'This month\u2019s invoice is issued. The run cannot go back on it.',
};

function isTaxiPassRefusalCode(v: unknown): v is TaxiPassRefusalCode {
  return typeof v === 'string' && Object.hasOwn(TAXI_PASS_REFUSAL_COPY, v);
}

export type TaxiPassReply =
  | { ok: true; outcome: 'passed'; tellTeam: boolean }
  | { ok: true; outcome: 'already' }
  | { ok: false; message: string; code?: TaxiPassRefusalCode };

/**
 * ⭐⭐ board 2814 M7 (DD-7) - PASS A RUN TO A TAXI (`POST /api/bookings/:id/taxi`).
 * `note` is what Helen typed, trimmed on the server; an empty one is no note.
 * `already`: it was passed before, and the note on record is kept.
 */
export async function passToTaxiRequest(bookingId: number, note: string): Promise<TaxiPassReply> {
  try {
    const res = await postJson<{ outcome?: unknown; tellTeam?: unknown }>(
      `/api/bookings/${String(bookingId)}/taxi`,
      { note },
    );
    return res.outcome === 'already'
      ? { ok: true, outcome: 'already' }
      : { ok: true, outcome: 'passed', tellTeam: res.tellTeam === true };
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    if (status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    if ((status === 400 || status === 409) && isTaxiPassRefusalCode(code)) {
      return { ok: false, message: TAXI_PASS_REFUSAL_COPY[code], code };
    }
    if (status === 404) return { ok: false, message: 'Nothing was changed. That run is no longer there. Reload the screen.' };
    // ⛔ NOT "nothing was changed": a lost answer may follow a pass that landed.
    return { ok: false, message: 'Something went wrong passing this run. Check the booking, then try again.' };
  }
}

/** ⭐ board 2814 M7 (S13) - TAKE THE RUN BACK (`POST /api/bookings/:id/taxi/undo`). */
export async function undoTaxiPassRequest(
  bookingId: number,
): Promise<{ ok: true; outcome: 'undone' | 'already' } | { ok: false; message: string; code?: TaxiPassRefusalCode }> {
  try {
    const res = await postJson<{ outcome?: unknown }>(`/api/bookings/${String(bookingId)}/taxi/undo`, {});
    return { ok: true, outcome: res.outcome === 'already' ? 'already' : 'undone' };
  } catch (e: unknown) {
    const status = e instanceof ApiError ? e.status : 0;
    const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
    if (status === 401) return { ok: false, message: SIGNED_OUT_COPY };
    if (status === 409 && isTaxiPassRefusalCode(code)) return { ok: false, message: TAXI_PASS_REFUSAL_COPY[code], code };
    if (status === 404) return { ok: false, message: 'Nothing was changed. That run is no longer there. Reload the screen.' };
    return { ok: false, message: 'Something went wrong taking this run back. Check the booking, then try again.' };
  }
}

/**
 * What a RE-SEND reports back. The route's own allowlist: two booleans about what
 * happened and the two figures, ⛔ never the Stripe object it happened to.
 */
export type ApiResentAccountInvoice = {
  accountInvoiceId: number;
  sentToStripe: boolean;
  ourNumberIsTheReference: boolean;
  lineCount: number;
  totalCents: number;
};

/**
 * Friendly copy for the RE-SEND route, keyed on STATUS and never on the server
 * body (SEC-5), like `issueFailureCopy` above and separate from it for the same
 * reason: the two routes' refusals are about different things.
 *
 * ⛔ THE REFUSALS, ENUMERATED FROM `handlePostAccountInvoiceSend`:
 *   404 - no such invoice.
 *   409 - already_sent, not_issuable, no_lines, no_billing_email, payer_changed.
 *   503 - account invoicing is not configured on this deployment.
 *   502 - the send itself failed, and the route GUARANTEES the row is unchanged.
 *   400 - a body or an id the route could not read.
 *
 * ⚠️ FIVE REFUSALS SHARE 409 AND THE ROUTE ATTACHES NO MACHINE-READABLE `code`
 * TO THEM, so this cannot tell "already sent" from "that payer has no billing
 * email" and does not pretend to: it says what is true of all five (this press
 * sent nothing and changed nothing) and names the causes as possibilities.
 * ⭐ board 3059: ONE 409 DOES CARRY A CODE, `resident_names_unavailable` (the
 * payer's names switch is on and the names could not be read, so Stripe was
 * never called), and it is keyed AHEAD of the status arm, because its remedy is
 * the plain one: press Send again in a moment.
 *
 * ⛔ 502 IS ANSWERED HONESTLY RATHER THAN FOLDED INTO THE UNKNOWN ARM, because
 * the route can afford a promise the ISSUE route cannot: no document is minted by
 * a send, so a failed one leaves the invoice exactly as it was and may be tried
 * again. The default arm still claims nothing - `postJson` also rejects when no
 * response arrived at all, and a send may have happened behind it.
 */
function resendFailureCopy(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  const code = e instanceof ApiError ? (e.body as { code?: unknown } | null)?.code : undefined;
  if (status === 409 && code === ('resident_names_unavailable' satisfies AccountInvoiceIssueRefusalCode)) {
    return 'Nothing was sent, and the invoice is unchanged. The resident names could not be read just now. Try Send again in a moment.';
  }
  if (status === 409) {
    return 'Nothing was sent, and the invoice is unchanged. It may already have been sent, or the home may have no email address for invoices, or it may be billed to a different organisation now. Reload the screen and check.';
  }
  if (status === 404) {
    return 'Nothing was sent. Something on this screen is out of date. Reload it and check.';
  }
  if (status === 503) {
    return 'Nothing was sent. Invoices cannot be sent from this deployment. Tell Alex.';
  }
  if (status === 502) {
    return 'That invoice could not be sent just now. It is unchanged, so it is safe to try again in a moment.';
  }
  if (status === 400) {
    return 'Nothing was sent. The screen asked for something the server could not read. Reload it and try again.';
  }
  return 'Could not tell whether that invoice was sent. Reload the screen and check before trying again.';
}

/**
 * ⭐ SEND AN ISSUED DOCUMENT THAT NEVER WENT OUT - the remedy for the one state
 * the issue path reports and no screen could act on.
 *
 * ⛔ WHY IT IS NOT A SECOND ISSUE. The number is spent, the lines are frozen and
 * the runs are marked invoiced the moment the ledger row commits, so a Stripe
 * failure at issue arrives as `sentToStripe: false` on a 200. Pressing Issue
 * again then answers 409 off the payer-period index and the invoice STILL has no
 * `stripe_invoice_id`. This route is the only way back to asking for the money.
 *
 * ⛔⛔ AND THE "NEVER TWICE" RULE IS THE ROUTE'S, NOT THIS SCREEN'S. The service
 * refuses `already_sent` while `stripe_invoice_id IS NOT NULL`, so a duplicate
 * press cannot put a second live invoice in front of a payer. The client OFFERS
 * the action only where the server itself reported the document unsent, and
 * SURFACES the refusal if it is wrong; it does not re-derive the rule, which
 * would put a second, drifting copy of a money guarantee in the browser.
 *
 * ⛔ NO BODY FIELDS AT ALL. The actor is the session, and nothing about a send is
 * settable by the request that asks for it.
 */
export async function resendAccountInvoice(
  accountInvoiceId: number,
): Promise<AdminResult<ApiResentAccountInvoice>> {
  try {
    return {
      ok: true,
      value: await postJson<ApiResentAccountInvoice>(
        `/api/account-invoices/${String(accountInvoiceId)}/send`,
        {},
      ),
    };
  } catch (e: unknown) {
    return { ok: false, message: resendFailureCopy(e) };
  }
}

// ---------------------------------------------------------------------------
// ⭐ T1447 M6 — THE RECOVERY ACTIONS: withdraw, record a bank payment, and check
// what Stripe currently holds.
//
// ⛔ WHY THEY EXIST. A wrong invoice was unrecoverable from any screen: the
// number is allocated and committed BEFORE Stripe is called, in a different
// transaction, and `voidIssuedAccountInvoice` had no route — so THREE live
// error strings (the send route's `payer_changed`, the service's own, and
// `cancelInvoicedCopy` above) told Helen to void a document using a control that
// did not exist. These are that control.
// ---------------------------------------------------------------------------

/** What a WITHDRAWAL reports back. ⛔ The route's own allowlist: what happened,
 *  never the Stripe object it happened to. */
export type ApiVoidedAccountInvoice = {
  accountInvoiceId: number;
  /** false = it was ALREADY withdrawn. Idempotent, not a failure. */
  changed: boolean;
  priorStatus: string;
  /** A CLOSED VOCABULARY the server owns. ⛔ `not-attempted` is NOT "it worked":
   *  it means the document never reached Stripe, so there was nothing to
   *  withdraw there — a different fact, and folding the two would tell Helen a
   *  document had been pulled out of a payer's inbox that was never sent to it. */
  stripeWithdrawal: 'not-attempted' | 'draft-deleted' | 'voided' | 'already-withdrawn'
    /** board 2764 M4/M5: a NIL invoice (nothing due) that Stripe settled at EUR 0
     *  the moment it was finalised. It cannot be cancelled there and asks for
     *  nothing, so only our copy is withdrawn. */
    | 'nil-settled';
};

/**
 * Friendly copy for the WITHDRAW route, keyed on STATUS and never on the server
 * body (SEC-5) — the rule `adminFailure`, `issueFailureCopy` and
 * `resendFailureCopy` all state, and a separate map from all three because these
 * refusals are about different things.
 *
 * ⛔ THE REFUSALS, ENUMERATED FROM `handlePostAccountInvoiceVoid`:
 *   404 — no such invoice.
 *   409 — the invoice is marked PAID. ⛔ The one refusal that is about MONEY:
 *         withdrawing a settled document would re-open the booking→account link
 *         on money already received.
 *   503 — it went out through Stripe and this deployment cannot withdraw it there.
 *   502 — the Stripe withdrawal failed. ⛔ THE ROW IS GUARANTEED UNCHANGED,
 *         because the withdrawal runs Stripe FIRST and commits locally only on
 *         success — a promise the ISSUE route cannot make and this one can.
 *   400 — a body or an id the route could not read.
 */
function voidFailureCopy(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 409) {
    return 'Nothing was withdrawn. That invoice is marked paid, so it cannot be taken back. Money recorded against it would be left attached to nothing. Reload the screen and check.';
  }
  if (status === 404) {
    return 'Nothing was withdrawn. Something on this screen is out of date. Reload it and check.';
  }
  if (status === 503) {
    return 'Nothing was withdrawn. That invoice went out through Stripe and cannot be taken back from this deployment. Tell Alex.';
  }
  if (status === 502) {
    return 'That invoice could not be withdrawn just now. Nothing has changed, so it is safe to try again in a moment.';
  }
  if (status === 400) {
    return 'Nothing was withdrawn. The screen asked for something the server could not read. Reload it and try again.';
  }
  return 'Could not tell whether that invoice was withdrawn. Reload the screen and check before trying again.';
}

/**
 * ⭐ WITHDRAW AN ISSUED DOCUMENT — at Stripe first, then here.
 *
 * ⛔ NO BODY FIELDS AT ALL. The actor is the session and lands in `voided_by`;
 * a body field would make a money action attributable to anyone who could type.
 *
 * ⛔ AND THE CLIENT DOES NOT RE-DERIVE THE RULES. It offers the action only where
 * the SERVER's own status says a withdrawal is possible, and surfaces the refusal
 * if it is wrong. Re-deriving "is this voidable" in the browser would put a
 * second, drifting copy of a money guarantee where nothing can pin it.
 */
export async function voidAccountInvoice(
  accountInvoiceId: number,
): Promise<AdminResult<ApiVoidedAccountInvoice>> {
  try {
    return {
      ok: true,
      value: await postJson<ApiVoidedAccountInvoice>(
        `/api/account-invoices/${String(accountInvoiceId)}/void`,
        {},
      ),
    };
  } catch (e: unknown) {
    return { ok: false, message: voidFailureCopy(e) };
  }
}

/** What a MARK-PAID reports back. */
export type ApiMarkedPaidAccountInvoice = {
  accountInvoiceId: number;
  /** The FROZEN total the document asks for — the figure the operator's typed
   *  amount had to equal. */
  totalCents: number;
  paidAt: string;
};

/**
 * Friendly copy for the MARK-PAID route, keyed on STATUS (SEC-5).
 *
 * ⭐ THE 409 ARM IS SPLIT, UNLIKE EVERY OTHER MAP IN THIS FILE, and the reason is
 * that this route attaches a machine-readable `expectedCents` to exactly one of
 * its two 409s. A mismatch is the ordinary, recoverable case — the operator
 * mistyped, or a short payment arrived — and it needs the FIGURE, because
 * without it she has to leave the task to find out what to type, which is how a
 * wrong number gets typed twice. The other 409 (already settled or withdrawn)
 * carries no such field and gets its own sentence.
 */
function markPaidFailureCopy(e: unknown): string {
  const status = e instanceof ApiError ? e.status : 0;
  const body = e instanceof ApiError ? (e.body as { expectedCents?: unknown } | null) : null;
  const expected = typeof body?.expectedCents === 'number' ? body.expectedCents : null;
  if (status === 409 && expected !== null) {
    return `Nothing was recorded. That is not what this invoice asks for. It asks for ${euro(expected)}. `
      + 'If the home has paid part of it, or paid two invoices together, that needs sorting before it can be marked paid.';
  }
  if (status === 409) {
    return 'Nothing was recorded. That invoice is already settled or has been withdrawn. Reload the screen and check.';
  }
  if (status === 404) {
    return 'Nothing was recorded. Something on this screen is out of date. Reload it and check.';
  }
  if (status === 400) {
    return 'Nothing was recorded. The amount has to be a plain figure in euro and cent. Try again.';
  }
  return 'Could not tell whether that invoice was marked paid. Reload the screen and check before trying again.';
}

/**
 * ⭐ RECORD THAT A HOME'S BANK PAYMENT ARRIVED.
 *
 * ⛔⛔ THE AMOUNT IS THE ONE THE OPERATOR TYPED, IN **CENTS**, AND IT IS NEVER
 * DERIVED HERE. The server compares it to the frozen total EXACTLY, and says
 * why: two homes on identical recurring contracts have identical monthly totals,
 * so an inexact comparator "is how one home's money pays another's bill". A
 * caller that passed `inv.cents` — which this screen HAS, one field away — would
 * make that comparison a tautology and delete the control. The parameter is
 * named for its unit for the same reason the wire field is: €1,250.00 typed as
 * `1250` into a cents field is €12.50, and on some invoice a unit slip will
 * MATCH by coincidence rather than reject.
 *
 * ⚠️ AND THE COMPARATOR'S HONEST LIMIT: it is vacuous in exactly the case its
 * own header names. Two homes with identical totals means an amount received
 * from home B equals home A's total exactly, so amount equality cannot detect a
 * MISATTRIBUTION when the amounts are equal by construction. It catches a SHORT
 * PAY and a LUMP SUM — both real, both money — and that is all it should be
 * claimed to catch.
 *
 * ⛔ NO STRIPE CALL HAPPENS ANYWHERE BEHIND THIS. Marking paid out of band is
 * FINAL AND UNALTERABLE at Stripe (measured), and mark-paid exists to record
 * money that arrived on the BANK rail, which Stripe never saw.
 */
export async function markAccountInvoicePaidByOperator(
  accountInvoiceId: number,
  amountReceivedCents: number,
): Promise<AdminResult<ApiMarkedPaidAccountInvoice>> {
  try {
    return {
      ok: true,
      value: await postJson<ApiMarkedPaidAccountInvoice>(
        `/api/account-invoices/${String(accountInvoiceId)}/mark-paid`,
        { amountReceivedCents },
      ),
    };
  } catch (e: unknown) {
    return { ok: false, message: markPaidFailureCopy(e) };
  }
}

/** What Stripe currently holds for one of OUR documents. ⛔ `sentToStripe:
 *  false` is an ORDINARY state, not a failure: issue commits before the outbound
 *  call, so a row that never reached Stripe is exactly what the ledger says. */
export type ApiInvoiceAtStripe =
  | { accountInvoiceId: number; sentToStripe: false }
  | {
    accountInvoiceId: number;
    sentToStripe: true;
    status: string;
    amountDueCents: number;
    amountPaidCents: number;
    /** ⛔ DERIVED SERVER-SIDE, ONCE. The screen never re-derives a money fact
     *  from a provider vocabulary — two definitions of "can this still be paid"
     *  is how a screen and a runbook end up disagreeing. */
    stillPayable: boolean;
  };

/**
 * ⭐ READ ONE OF OUR INVOICES BACK FROM STRIPE — the account twin of the deposit
 * screen's **Refresh payment** button.
 *
 * ⛔ THE ONLY ARGUMENT IS **OUR** ROW ID. A client-supplied Stripe id has nowhere
 * to enter: the route's path takes `(\d+)` and the Stripe id is read from our own
 * stored column inside the service. Stripe metadata is never consulted either —
 * it is editable by anyone with Dashboard access.
 */
export async function fetchInvoiceAtStripe(
  accountInvoiceId: number,
): Promise<ApiInvoiceAtStripe> {
  return getJson<ApiInvoiceAtStripe>(`/api/account-invoices/${String(accountInvoiceId)}/stripe`);
}

// ---------------------------------------------------------------------------
// T1141 M5 — operator device push registration
// ---------------------------------------------------------------------------

/**
 * One registered device on the CALLING operator's account.
 *
 * ⛔ NO `endpoint`, NO `p256dh`, NO `auth`, AND THAT IS THE SERVER'S SHAPE RATHER
 * THAN A CHOICE MADE HERE. Those three columns together ARE the capability to
 * push to a device (migration v36's `COMMENT ON` rules it), so `GET
 * /api/push/status` never puts them on the wire and this type could not carry
 * them if it wanted to.
 */
export type PushDeviceRow = {
  subscriptionId: number;
  createdAt: string;
  updatedAt: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  failureCount: number;
  /**
   * The server's own verdict on whether this device has gone quiet, computed by
   * `isPushSuccessStale` in the module that owns the send.
   *
   * ⚠️ MEASURED FROM `created_at` WHEN NOTHING HAS EVER SUCCEEDED, so a device
   * registered this morning that no enquiry has arrived for yet is NOT stale.
   * ⛔ The client does NOT re-derive it from the timestamps: two definitions of
   * "quiet" is how the screen and the runbook end up disagreeing.
   */
  lastSuccessStale: boolean;
};

export type PushServerStatus = {
  /**
   * ⚠️ "THE SERVER COULD SEND", NOT "THIS DEVICE IS SIGNED UP" — the two are
   * independent and the screen has to say which is which. This is false whenever
   * VAPID is unconfigured, which is the DEFAULT state everywhere in this repo,
   * and it is the whole reason a green control is not sufficient evidence: a
   * device can register perfectly against a server structurally incapable of
   * ever sending to it.
   */
  pushEnabled: boolean;
  maxSubscriptions: number;
  /**
   * How many devices the SERVER holds for this operator.
   *
   * ⛔⛔ THIS IS THE FIELD THAT MAKES A GREEN CONTROL FALSIFIABLE (T1141 review,
   * F1). `readDeviceState()` asks the BROWSER only - a permission and a live
   * `PushSubscription` - and both of those can be perfectly healthy while the
   * server holds ZERO rows for this operator. That is a reachable state, not a
   * hypothetical: the app-launch re-registration swallows its failures by design
   * (a 401 before sign-in, a 409 on a colleague's iPad, a 429 on a heavy day), so
   * the POST that would have created the row can fail every launch with nothing
   * saying so. The screen compares the two answers; see `pushCoverageWarning`.
   */
  subscriptionCount: number;
  /** How long the server waits before calling a device quiet (7 days as shipped).
   *  Carried so the copy can stay in step with the server's rule rather than
   *  hard-coding a period the screen does not own. */
  staleAfterMs: number;
  devices: PushDeviceRow[];
};

/**
 * WHY THE SCREEN CANNOT TRUST THE BROWSER ALONE (T1141 review, F1).
 *
 * Two independent facts, and the operator can only act if the screen says which
 * disagrees:
 *  - `server-has-no-record`: this browser holds a live subscription and the
 *    server holds NO row for this operator. Nothing will ever arrive here.
 *  - `device-gone-quiet`: the server holds rows, and at least one has had no
 *    successful send for longer than its own staleness rule. That catches the
 *    failure modes the send path's death detector cannot see, including any
 *    Apple-side revocation whose server signal is undocumented.
 */
export type PushCoverageWarning = 'server-has-no-record' | 'device-gone-quiet';

/**
 * Does the SERVER disagree with this browser about whether this operator is
 * covered? Callers pass the status ONLY when the browser says `on`.
 *
 * ⛔ `null` STATUS RETURNS `null`, NEVER A WARNING. A failed status read is not
 * evidence of anything, and warning on it would train the operator to ignore the
 * one message that means her alerts are not arriving.
 */
export function pushCoverageWarning(status: PushServerStatus | null): PushCoverageWarning | null {
  if (status === null) return null;
  // Order matters: "the server has never heard of this device" is the stronger
  // statement and its remedy is different, so it is never reported as staleness.
  if (status.subscriptionCount === 0) return 'server-has-no-record';
  return status.devices.some((d) => d.lastSuccessStale) ? 'device-gone-quiet' : null;
}

/** Server field names are snake_case on the wire; this is the only place that
 *  knows. Kept as a hand-written mapping rather than a cast so a renamed column
 *  surfaces here rather than as `undefined` three layers away. */
type WirePushStatus = {
  push_enabled: boolean;
  max_subscriptions: number;
  /** ⛔ THESE THREE WERE MISSING UNTIL T1141's REVIEW (F1), and the server had
   *  been sending all three since M6. A field absent from this type is dropped on
   *  the floor at the wire boundary, silently - which is what left the control
   *  reading the browser alone and showing a green chip against a server holding
   *  zero rows. `app.ts`'s own comment claimed this was "the reader that stops
   *  last_success_at being a durable signal nobody reads"; it was not one. */
  subscription_count: number;
  stale_after_ms: number;
  subscriptions: Array<{
    subscription_id: number;
    created_at: string;
    updated_at: string;
    last_success_at: string | null;
    last_failure_at: string | null;
    failure_count: number;
    last_success_stale: boolean;
  }>;
};

export async function fetchPushServerStatus(): Promise<PushServerStatus> {
  // ⛔ getJson, NOT getOr. `getOr` would turn "the status read failed" into
  // "no devices are registered", and those must never read the same: the second
  // is what the screen shows when notifications are genuinely off, and acting on
  // it means an operator re-enabling something that was already working.
  const body = await getJson<WirePushStatus>('/api/push/status');
  return {
    pushEnabled: body.push_enabled,
    maxSubscriptions: body.max_subscriptions,
    subscriptionCount: body.subscription_count,
    staleAfterMs: body.stale_after_ms,
    devices: body.subscriptions.map((s) => ({
      subscriptionId: s.subscription_id,
      createdAt: s.created_at,
      updatedAt: s.updated_at,
      lastSuccessAt: s.last_success_at,
      lastFailureAt: s.last_failure_at,
      failureCount: s.failure_count,
      lastSuccessStale: s.last_success_stale,
    })),
  };
}

/**
 * The VAPID application server key `pushManager.subscribe` needs.
 *
 * ⚠️ FETCHED AT RUNTIME BECAUSE THERE IS NO OTHER CHANNEL — not for freshness.
 * No Dockerfile here declares an ARG, `scripts/build-client.mjs` has no `define`,
 * and there is zero `process.env` under `src/client`, so a build-time injection
 * mechanism would have to be invented for a value that changes per deployment.
 * It is NOT a secret: it travels to every push service in the `k=` parameter.
 * A 503 means the server has no VAPID configuration at all.
 */
export async function fetchPushPublicKey(): Promise<string> {
  const body = await getJson<{ public_key: string }>('/api/push/public-key');
  return body.public_key;
}

/**
 * Register (or re-register) THIS device.
 *
 * ⛔ THE BODY IS EXACTLY `PushSubscription.toJSON()`'s shape and carries no
 * operator id. The row is keyed on the session's operator server-side; a body
 * field naming whose device this is would be ignored, and the route says so.
 *
 * ⚠️ IT IS AN UPSERT ON `endpoint`, WHICH IS LOAD-BEARING RATHER THAN TIDY: iOS
 * supports no `pushsubscriptionchange` event at all, so an installed kiosk
 * re-POSTs on EVERY app launch. Without the upsert that path would collide with
 * the UNIQUE index every single time.
 */
export async function postPushSubscription(subscription: {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}): Promise<number> {
  const body = await postJson<{ subscription_id: number }>('/api/push/subscribe', subscription);
  return body.subscription_id;
}

/**
 * Remove one of the calling operator's own devices.
 *
 * ⚠️ A 404 IS TREATED AS SUCCESS BY THE CALLER, NOT SWALLOWED HERE. The route
 * answers 404 both for "no such row" and for "that row is not yours", precisely
 * so it cannot be used to enumerate ids across operators; the disable path in
 * `push-register.ts` argues that case where it can be read.
 */
export async function deletePushSubscription(subscriptionId: number): Promise<void> {
  await deleteJson<{ deleted: boolean }>(
    `/api/push/subscribe?subscription_id=${String(subscriptionId)}`,
  );
}

/**
 * The TWO reasons POST /api/push/subscribe answers 409.
 *
 * ⛔ A HAND-WRITTEN MIRROR OF `PushRefusalCode` IN `server/app.ts`, and it cannot
 * be imported from there: that module pulls in `pg`, which would drag a node-only
 * dependency into the browser bundle (the same reason `foldLogin` in
 * `screens/admin/Operators.tsx` is duplicated rather than imported). The mirror
 * is NOT free to rot: `test/server/push-subscribe.spec.ts` asserts the two unions
 * are mutually assignable, so either side gaining a member fails the build.
 */
export type PushRefusalCode = 'push_row_limit' | 'push_other_owner';

/**
 * ⛔ ONE 409 SENTENCE FOR BOTH REFUSALS WAS THE DEFECT (T1141 review, F3). Every
 * 409 used to read "this iPad is already registered to somebody else", so an
 * operator refused because her ACCOUNT is full was pointed at a repair that
 * cannot work: signing in as somebody else does not free a row on her own list.
 * Two refusals, two remedies, two sentences.
 *
 * ⚠️ THE ROW-LIMIT COPY NAMES NO NUMBER. The server's own sentence carries one
 * and this copy is keyed off the CODE (SEC-5), so restating the cap here would be
 * a second definition of it, free to drift from `maxPushSubscriptions`.
 */
const PUSH_REFUSAL_COPY: Record<PushRefusalCode | 'unknown', string> = {
  push_row_limit:
    'This account already has as many iPads registered as it can hold. On an iPad you no longer use, turn its notifications off, then try again here.',
  push_other_owner:
    'This iPad is already registered to somebody else. Sign in on it as yourself, or call the office.',
  // ⚠️ NEITHER SPECIFIC SENTENCE ON AN UNCODED 409. Guessing between them is how
  // the wrong remedy reached the operator in the first place; this one is true of
  // both and blames nobody.
  unknown:
    'Notifications could not be changed for this iPad. Try again in a moment, and call the office if it keeps happening.',
};

/** Read the refusal code off an untrusted response body (never rendered raw),
 *  exactly as `sendErrorCodeOf` does for the send codes. */
function pushRefusalCodeOf(e: unknown): PushRefusalCode | 'unknown' {
  const body = e instanceof ApiError ? e.body : null;
  if (typeof body !== 'object' || body === null || !('code' in body)) return 'unknown';
  const code = (body as { code: unknown }).code;
  return code === 'push_row_limit' || code === 'push_other_owner' ? code : 'unknown';
}

/**
 * Friendly copy from the STATUS, never the server body (SEC-5), mirroring
 * `adminFailure` and `portalFailureCopy` above.
 *
 * ⚠️ 503 IS THE CONFIGURATION CASE AND MUST NOT READ AS THE OPERATOR'S FAULT.
 * It means this server holds no VAPID keypair, which no amount of tapping on an
 * iPad can fix.
 */
export function pushFailureCopy(e: unknown, verb: string): string {
  const status = e instanceof ApiError ? e.status : 0;
  if (status === 503) {
    return 'Notifications are not set up on this server yet. Nothing you did caused this - tell Alex.';
  }
  if (status === 429) {
    return 'Notifications have been switched on and off a lot on this account today. Try again tomorrow, or call the office.';
  }
  if (status === 409) return PUSH_REFUSAL_COPY[pushRefusalCodeOf(e)];
  if (status === 401) return SIGNED_OUT_COPY;
  return `Could not ${verb}. Try again in a moment.`;
}
